/**
 * FactSet Spreadsheet intent wrappers.
 *
 * Mirrors the role of `intentBridgeService.ts` for the Presentation namespace,
 * but kept as a sibling module so the Excel and PowerPoint code paths stay
 * independent. Every function in this file is a thin, typed wrapper over the
 * shared transport in `factsetIntentTransport.ts`.
 *
 * Scaling pattern: to add another Excel intent, append one exported
 * `*ViaIntents` function + its types to this file. No central registry to
 * edit, no PowerPoint coupling.
 *
 * Pre-announced FactSet roadmap (not yet on the wire):
 *  - `factset.Spreadsheet.ExportWorkbook` — closes the "download workbook
 *    bytes" surface (see `f2_docs/excel/finster-docs/missing-intents-v0.md`
 *    §6.4). New wrapper goes here when it lands. **NB:** the
 *    out-of-catalog `factset.Spreadsheet.GetWorksheetsAsBase64` (wired
 *    below) provides an interim surface for the same use case until
 *    `ExportWorkbook` lands.
 *  - **Charting namespace shift** —
 *    `f2_docs/charting/chart-primitives-preview.md` describes a future
 *    migration of the chart-family intents from `factset.Charting.*` /
 *    `factset.Spreadsheet.Charting.*` to `spreadsheet.charting.*` (and
 *    `presentation.charting.*` for PowerPoint). **The preview is a
 *    work-in-progress and the production catalog has NOT shifted** —
 *    every wire intent name in this file stays on the current
 *    `factset.*` form. Do not rename when the preview lands; switch
 *    only when the production catalog (`chart-tools.md`) flips.
 *
 * Pre-announced 0-based index migration (not yet on the wire):
 *  - `getActiveExcelContext`, `getWorksheets` (i.e. `getWorkbookSheets`),
 *    `addWorksheet` (`options.index`), `getTables`, `getCharts` will move
 *    from 1-based to 0-based indices. All future tools default to 0-based.
 *  - `getRangeInfo` cell coordinates are NOT in scope (see the per-intent
 *    Notes blocks in `excel-docs/intent-details.md`).
 *  - When the migration lands, flip the JSDoc on
 *    `ActiveExcelWorksheetInfo.index` and `AddWorksheetOptions.index`
 *    from "1-based" to "0-based" — the field types are unchanged.
 *  - Tracked in `f2_docs/excel/finster-docs/missing-intents-v0.md`
 *    → `#open-zero-index-migration`.
 *
 * Current coverage:
 *
 *   `factset.Spreadsheet.*` catalog — 1:1 with the rows in
 *   `excel-tools.md` (the Charting sub-namespace lives in
 *   `chart-tools.md` and is listed separately below). Both
 *   `GetRangeSnapshot` (2026-05-11) and `GetWorksheetsAsBase64`
 *   (2026-05-12, ⚗️ Experimental, PR #55) graduated from
 *   out-of-catalog "best-effort" status when FactSet shipped their
 *   rows. The new `SetWorksheetVisibility` row (2026-05-12, PR #55)
 *   wires the per-sheet sibling of `Hide*` / `Show*` and is reachable
 *   from the planner via `SheetModification.action = "setVisibility"`.
 *   Each wrapper has a per-intent §block below with full request /
 *   response shape, captured payloads, and call-site notes. Quick
 *   index (alphabetized):
 *
 *    - ActivateWorksheet, AddActiveGraph, AddImageFromBase64,
 *      AddNamedObject, AddWorkbook, AddWorksheet,
 *      ApplicationCalculate, ApplyFormatting, BeginUndo, ClearRange,
 *      CopyWorksheet, DeleteComment, DeleteNote, DeleteRange,
 *      DeleteShape, DeleteWorksheet, EndUndo, FreezeUnfreezePanes,
 *      GetActiveCellAddress, GetActiveExcelContext, GetAllComments,
 *      GetAllNotes, GetCalculationMode, GetComment, GetNote,
 *      GetOpenWorkbooks, GetRangeAsCsv, GetRangeInfo, GetRangeSnapshot,
 *      GetSelectedAddressRange, GetTables, GetWorkbookConnections,
 *      GetWorkbookNamedObjects, GetWorkbookSheets,
 *      GetWorksheetsAsBase64, GroupColumns, GroupRows,
 *      HarmonizedRefreshWorksheets, HideColumns, HideRows,
 *      InsertContentsToRange, InsertRange,
 *      InsertWorksheetsFromBase64, ModifyShapes, MoveRange,
 *      MoveSheet, ParseFormulas, RemoveNamedObject,
 *      RenameWorksheet, ResizeExcelElements, Search, SelectRange,
 *      SelectShapes, SetCalculationMode, SetComment, SetNote,
 *      SetWorksheetVisibility, ShowColumns, ShowRows, SortRange,
 *      UngroupColumns, UngroupRows
 *
 *   `factset.Spreadsheet.Charting.*` sub-namespace — 7 production
 *   wrappers covering native Excel chart enumeration, creation,
 *   update, series writes, series removal, and image export. Lives
 *   in `chart-tools.md` (moved out of `excel-tools.md` 2026-05-12).
 *   Routed via the `OI Remote Iframe` relay (NOT the workstation
 *   target):
 *
 *    - GetCharts (rich typed shape, Summary/Full detail, sheet +
 *      chartIndex filters), AddExcelChart, UpdateExcelChart,
 *      AddSeriesToChart, UpdateSeriesInChart, RemoveSeriesFromChart,
 *      GetChartImageAsBase64 (returns a full `data:image/png;base64,…`
 *      URI — `format` is accepted but ignored by the atlas per
 *      f2_docs PR #56, 2026-05-13; output is always PNG).
 *
 *   `factset.Spreadsheet.*` stub — wire wrapper only, planner-disabled
 *   until the upstream client lands. Tracked in `missing-intents-v0.md`
 *   §6.26.
 *
 *    - InsertTtfApiResponse
 *
 *   `factset.Charting.*` host-agnostic ActiveGraph reads + writes —
 *   paired with `factset.Spreadsheet.AddActiveGraph` (full-insert
 *   write side, see above). The `factset.Charting.*` namespace is
 *   shared with PowerPoint; per the chart-tools.md catalog these
 *   intents operate on whichever ActiveGraph OLE object is currently
 *   selected in the host. Routed via the `OI Remote Iframe` relay.
 *
 *    - GetActiveGraphXml — read the FDSChart XML of the selected
 *                          ActiveGraph. Throws when nothing is selected.
 *                          **NOT exposed to the planner** — the surface
 *                          is too immature for round-trip mutations.
 *                          Wrapper kept wired for dev-tools dialog and
 *                          explicit non-agent callers only; the
 *                          workbook-context extractor no longer invokes
 *                          it (no more `selectedActiveGraphXml` on
 *                          `WorkbookContent`).
 *    - SetActiveGraphXml — in-place replace the FDSChart XML on the
 *                          currently-selected ActiveGraph. Same OLE
 *                          shape / anchor / size, new XML body.
 *                          Optional `refresh` flag (default `true`)
 *                          triggers a chart re-bind after applying
 *                          the new XML. **NOT exposed to the planner**
 *                          — the planner-emittable
 *                          `ActiveGraphModification.action` union no
 *                          longer contains `"update-selected"`; the
 *                          applier branch is a defensive `trackSkip`.
 *                          Wrapper kept wired for explicit non-agent
 *                          callers only.
 *
 *   `factset.*` sibling-namespace (OI Remote Iframe) — wrappers that
 *   live OUTSIDE the `factset.Spreadsheet.*` catalog. Same transport,
 *   different host-side relay + casing conventions:
 *
 *     • Wire intent name is CapitalizedCamelCase (`factset.PreviewChart`)
 *       while the request `context.type` is lowerCamelCase
 *       (`factset.previewChart`).
 *     • Request needs `appId: "OI Remote Iframe"` for the host to route
 *       to the right relay.
 *     • Most responses use a two-level `data.body.data` envelope; the
 *       Download* pair uses a flatter `data.bodyAsBase64`. The wrappers
 *       unwrap before returning.
 *
 *    - PreviewChart                — chat-bubble inline chart preview.
 *    - DownloadExcelChart          — `.xlsx` with a NATIVE Excel chart.
 *    - DownloadActiveGraph         — `.xlsx` with a FactSet ActiveGraph
 *                                    OLE embed (no native chart).
 *    - GetPaneStatus               — read-side of `FreezeUnfreezePanes`;
 *                                    "what's the current freeze state
 *                                    of the active sheet?". Takes no
 *                                    arguments; response shape inferred
 *                                    (TODO: capture on first call).
 *    - GetStylesets                — chart-family styleset catalog read.
 *    - GetTemplatesList            — template-library catalog read.
 *    - GetUserAvailableTemplates   — per-user entitlement filter for the
 *                                    template library.
 *    - RefreshCalculate            — async template-instantiation kickoff;
 *                                    request `body` is a STRINGIFIED JSON;
 *                                    response is HTTP 202 / `{ id, status }`.
 *                                    Pair with `GetRefreshStatus` below to
 *                                    poll for terminal completion.
 *    - GetRefreshStatus            — polling counterpart of
 *                                    `RefreshCalculate`. Wire field is
 *                                    `refreshId` (NOT `id`); takes the
 *                                    `id` from `RefreshCalculate`'s
 *                                    resolved value. Two-level
 *                                    `data.body.data` envelope (captured
 *                                    2026-05-05); inner body is
 *                                    `{ id, status }`. Captured progression
 *                                    for one job: `queued (202)` →
 *                                    `executing (202)` → `created (201)`.
 *                                    Terminal-success is `"created"` +
 *                                    HTTP `201`; terminal-failure response
 *                                    is still pending capture.
 *                                    `pollRefreshCalculate` exposes the
 *                                    typical wait-for-terminal-status loop.
 *    - GetRefresh                  — refresh-result retrieval intent.
 *                                    Returns workbook bytes when ready
 *                                    (`bodyAsBase64`) or a status payload
 *                                    while still in progress.
 *
 *   Each sibling-namespace intent has a per-intent §block below with the
 *   full wire shape and conventions; per-intent capture also lives in
 *   `excel-docs/intent-details.md`, and the wrapper / dispatcher /
 *   consumer summary table is in `missing-intents-v0.md` §4.4.
 */

import {
  raiseIntent,
  DEFAULT_TIMEOUT_MS,
} from "../../../shared/factset/factsetIntentTransport";
import type {
  OpenWorkbookInfo,
  WorkbookConnection,
  WorkbookNamedObject,
  FactsetChartParameters,
  FactsetChartPreview,
  FactsetDownloadExcelChart,
  FactsetStyleset,
  FactsetGetStylesetsResult,
  FactsetGetActiveGraphXmlResult,
  FactsetTemplate,
  FactsetGetTemplatesListResult,
  FactsetGetUserAvailableTemplatesResult,
  FactsetDownloadTemplateRequest,
  FactsetDownloadTemplate,
  FactsetRefreshCalculateRequest,
  FactsetRefreshCalculateResult,
  FactsetGetRefreshRequest,
  FactsetGetRefreshResult,
} from "../../types";
import {
  findChartCapability,
  suggestChartCapability,
  isKnownUnsupportedChartType,
  CHART_CAPABILITY_ERROR_PREFIX,
  type ChartCapability,
  type ChartSystem,
} from "../../chartCapabilityRegistry";
import {
  CHART_CREATOR_SUMMARY_TABLE_API,
  DEFAULT_CHART_FORMAT,
  DEFAULT_ESTIMATE_PARAMS,
  normalizeActiveGraphChartParameters,
} from "../../chartCreatorPayload";
import { isSingleCellAddress } from "../../excelCellAddress";

/**
 * Client wait budget for deterministic host operations that can legitimately
 * take minutes on large workbooks. A transport timeout only detaches our
 * response listener; it cannot cancel work already running in the FactSet
 * host, so these operations must not inherit the 15-second fast-intent default.
 */
export const LONG_RUNNING_FACTSET_INTENT_TIMEOUT_MS = 5 * 60_000;

// ============================================================================
// Chart-family validators (aligned with f2_docs/charting preview spec)
// ============================================================================
//
// Preview docs (`f2_docs/charting/chart-primitives-preview.md` and
// `chart-schema-preview.md`) lay out three contracts the chart-family
// wrappers in this file are expected to enforce client-side before the
// payload reaches the wire:
//
//   1. `colorRgb` MUST match `^#[0-9A-Fa-f]{6}$` — no 3-digit shorthand,
//      no named colors, no missing `#`. Anything else the host silently
//      ignores; we warn-and-drop on our side so the planner gets a
//      single, loud signal instead of a silent no-op.
//   2. `chartInfo.series[]` MUST NOT be sent on `addExcelChart` /
//      `updateExcelChart`. Series writes flow exclusively through the
//      dedicated `addSeriesToChart` / `updateSeriesInChart` /
//      `removeSeriesFromChart` primitives.
//   3. `ChartTargetOptions.workbookName` MUST include the `.xlsx`
//      extension; without it the host fails to resolve the workbook.
//
// These helpers thread through every chart-family wrapper that accepts
// a `chartInfo` / `series` body. They are tolerant — they sanitise the
// payload in-place when they can, drop the offending field when they
// can't, and never throw. Throwing would force the planner to round-trip
// a fix; warn-and-strip lets the rest of the call succeed. Side effects
// are limited to one `console.warn` per offending field (so a payload
// with three bad colors logs three warns) and the local payload-shape
// mutation. The wrapper boundary uses `deepCloneChartPayload` (NOT a
// shallow spread) before sanitising — `sanitizeColorsInPlace` recurses
// into nested objects (`title.font`, `series[i].fill`, etc.) and
// mutates `colorRgb` fields via `delete` / reassignment, which would
// otherwise leak back through the caller's still-shared nested
// references. A spread copies only the top-level keys.

const COLOR_RGB_REGEX = /^#[0-9A-Fa-f]{6}$/;

/**
 * Validate a `colorRgb` value against the strict `#RRGGBB` contract
 * documented in `f2_docs/charting/chart-schema-preview.md`. Returns the
 * cleaned uppercase form on success, `undefined` on any other input
 * (so the caller can omit the key from the wire payload). Emits a
 * single `console.warn` per bad input so a planner regression is
 * loud in the dev console.
 */
function validateColorRgb(value: unknown, fieldPath: string): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== "string") {
    console.warn(
      `[chart-validator] ${fieldPath}: expected string \`#RRGGBB\`, got ${typeof value}; dropping`
    );
    return undefined;
  }
  if (!COLOR_RGB_REGEX.test(value)) {
    console.warn(
      `[chart-validator] ${fieldPath}: \`${value}\` is not a strict \`#RRGGBB\` (no 3-digit shorthand, no named colors, no missing \`#\`); dropping`
    );
    return undefined;
  }
  return value.toUpperCase();
}

/**
 * Walk a chart-family payload and rewrite any `colorRgb` it encounters
 * through `validateColorRgb`. Operates in-place on `obj`: invalid
 * values are `delete`d from their parent object, valid values are
 * reassigned to the uppercase canonical form. Recurses into plain
 * objects + arrays; leaves primitives untouched.
 *
 * **Important**: callers MUST pass a deep clone (use
 * `deepCloneChartPayload`). A shallow `{ ...obj }` is not enough —
 * this walker mutates nested objects (`title.font`, `series[i].fill`,
 * `chartArea.line`, etc.) which a spread still aliases to the caller.
 */
function sanitizeColorsInPlace(obj: unknown, path = ""): void {
  if (!obj || typeof obj !== "object") return;
  if (Array.isArray(obj)) {
    obj.forEach((item, i) => sanitizeColorsInPlace(item, `${path}[${i}]`));
    return;
  }
  const record = obj as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    const childPath = path ? `${path}.${key}` : key;
    if (key === "colorRgb") {
      const cleaned = validateColorRgb(record[key], childPath);
      if (cleaned === undefined) {
        delete record[key];
      } else {
        record[key] = cleaned;
      }
      continue;
    }
    const child = record[key];
    if (child && typeof child === "object") {
      sanitizeColorsInPlace(child, childPath);
    }
  }
}

const SERIES_COLOR_RGB_REGEX = /^#?[0-9A-Fa-f]{6}$/;

/**
 * Series color payloads in `chart-tools.md` use bare RGB strings
 * (`"FF5733"`), while chart-level objects commonly use `"#RRGGBB"`.
 * Normalize only series payloads to the series contract and accept both
 * inputs so planner/tool callers can use either convention.
 */
function sanitizeSeriesColorsInPlace(obj: unknown, path = ""): void {
  if (!obj || typeof obj !== "object") return;
  if (Array.isArray(obj)) {
    obj.forEach((item, i) => sanitizeSeriesColorsInPlace(item, `${path}[${i}]`));
    return;
  }
  const record = obj as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    const childPath = path ? `${path}.${key}` : key;
    if (key === "colorRgb") {
      const value = record[key];
      if (value === undefined || value === null) {
        continue;
      }
      if (typeof value !== "string" || !SERIES_COLOR_RGB_REGEX.test(value)) {
        console.warn(
          `[chart-validator] ${childPath}: \`${String(value)}\` is not a valid series RGB color (expected RRGGBB or #RRGGBB); dropping`
        );
        delete record[key];
      } else {
        record[key] = value.replace(/^#/, "").toUpperCase();
      }
      continue;
    }
    const child = record[key];
    if (child && typeof child === "object") {
      sanitizeSeriesColorsInPlace(child, childPath);
    }
  }
}

/**
 * Drop `series` from a `chartInfo` payload bound for `addExcelChart` /
 * `updateExcelChart`. Per the preview spec these primitives ignore
 * series; we strip + warn so a planner regression can't smuggle series
 * into a chart-level update. Operates in-place; safe to call when
 * `chartInfo` itself is `undefined` (no-op).
 */
function stripSeriesFromChartInfo(
  chartInfo: unknown,
  primitive: string
): void {
  if (!chartInfo || typeof chartInfo !== "object") return;
  const record = chartInfo as Record<string, unknown>;
  if ("series" in record) {
    console.warn(
      `[chart-validator] ${primitive}: \`chartInfo.series\` is not applied by this primitive (use addSeriesToChart / updateSeriesInChart / removeSeriesFromChart); dropping the smuggled series field.`
    );
    delete record.series;
  }
}

/**
 * Deep clone a chart-family payload before sanitising it. The chart
 * `chartInfo` / `series` bodies are plain JSON-serialisable data (no
 * functions, no `Date`s, no `Map`s, no class instances) so a
 * `structuredClone` is the right tool, with `JSON.parse(JSON.stringify())`
 * as a defensive fallback for environments where the global isn't
 * available (the Excel add-in targets modern Edge WebView 2 / Chromium
 * which supports `structuredClone` natively, but the wrapper is also
 * unit-testable from Node, and older Node runtimes don't expose it).
 *
 * Why a deep clone, not a shallow spread: `sanitizeColorsInPlace`
 * recurses into nested objects (`title.font`, `series[i].fill`,
 * `chartArea.line`, every per-point override under `series[i].points`,
 * etc.) and mutates `colorRgb` keys via `delete` / reassignment. A
 * shallow `{ ...obj }` only copies the top-level keys; every nested
 * object is still aliased to the planner's original payload, so the
 * mutations would leak back to the caller. A deep clone severs every
 * level so the wrapper's sanitisation is genuinely local.
 */
function deepCloneChartPayload<T>(value: T): T {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * Re-export the workbook-introspection element types so callers that
 * already import them from this module (`excelHostOps.ts`,
 * downstream call sites) keep working. The canonical definitions
 * live in `excel/types.ts` next to `WorkbookContent` — that's what
 * `enrichWorkbookContentViaBridge` ultimately writes into. Keeping a
 * single source of truth means structural drift between the two
 * layers is impossible.
 */
export type { OpenWorkbookInfo, WorkbookConnection, WorkbookNamedObject };

// ============================================================================
// factset.Spreadsheet.InsertContentsToRange
// ============================================================================

/**
 * One cell's worth of content in an `InsertContentsToRange` call.
 *
 * `row` and `col` are **0-based offsets from `options.destinationCell`**,
 * despite the spec wording ("1-based row/col index"). Verified empirically:
 *   - `dest="A1"` + `row:0, col:0` → A1
 *   - `dest="A1"` + `row:1, col:1` → B2
 * `formula` carries either a literal value stringified by the caller or a
 * leading-`=` formula. `numberFormat` is an optional Excel number-format
 * string applied to this single cell only.
 *
 * Note on payload size: today every cell is one entry, so a range write is
 * N×M entries on the wire. An additive 2D `values`/`formulas` shape could
 * collapse this, but it's deferred until payload size is a demonstrated
 * bottleneck. Revisit if large range writes start showing meaningful
 * latency; until then, continue expanding 2D data into per-cell entries
 * in `excelHostOps.ts::buildContentsForRange`.
 */
export interface SpreadsheetCellContent {
  row: number;
  col: number;
  formula: string;
  numberFormat?: string;
}

/**
 * Per-call options for `InsertContentsToRange`.
 *
 * `destinationCell` anchors the whole payload — always set it. Omitting it
 * has been observed to cause the host to drop the write silently. Contents'
 * `row/col` are 0-based offsets from this anchor.
 *
 * `destinationCell` accepts the full Excel address syntax (per
 * `f2_docs/excel/excel-tools.md` §`InsertContentsToRange`), including
 * Excel external-reference prefixes for cross-workbook writes:
 *  - bare `"A1"` — targets the currently active sheet on the active workbook.
 *  - sheet-qualified `"Sheet1!A1"` / `"'My Sheet'!A1"` — targets a specific
 *    sheet on the active workbook (host activates it implicitly).
 *  - external-ref `"'[Book.xlsx]Sheet1'!A1"` — targets a specific
 *    workbook+sheet without depending on which workbook is currently
 *    focused. This is what the plan-burst path uses to keep writes pinned
 *    to the run's origin workbook even after the user clicks into another
 *    workbook mid-burst. The `[book]sheet` prefix is always quoted; the
 *    unquoted `"[Book.xlsx]Sheet1!A1"` variant is rejected outright by the
 *    host whenever the workbook filename contains a hyphen.
 *
 * Production code should NOT format the external-ref string here — go
 * through `excelHostOps.ts::destinationCellOf` (and the `workbookName`
 * field on `ExcelHostOpOptions`) so quoting rules stay consistent.
 *
 * `displayAlerts` and `selectRange` map straight to host options of the same
 * name (see `f2_docs/excel/excel-tools.md` §`InsertContentsToRange`):
 *
 *  - `displayAlerts: false` suppresses Excel's native "There's already data
 *    here. Do you want to replace it?" modal when the write target is
 *    non-empty (the host wraps the COM write in `Application.DisplayAlerts =
 *    False` for the duration). Required for non-interactive automation;
 *    otherwise the host blocks waiting for the user. Matches the rationale
 *    on `DeleteWorksheetOptions.displayAlerts`. Closes AI-2615.
 *  - `selectRange: false` keeps the host viewport where the user left it
 *    instead of scrolling to the just-written range. Finster's applier
 *    emits an explicit `SelectRange` intent only when the planner asks for
 *    navigation (`select_range` action), so the implicit nav is just noise.
 *
 * The raw wrapper below is a thin pass-through and applies no defaults; the
 * Finster policy defaults live one layer up in the dispatchers
 * (`excelHostOps.ts`'s `setCellContents` / `fillRangeContents`) so a caller
 * that needs the literal wire shape can still hit the host's `true`
 * defaults by going through the wrapper directly.
 */
export interface SpreadsheetInsertContentsOptions {
  destinationCell?: string;
  refresh?: boolean;
  displayAlerts?: boolean;
  selectRange?: boolean;
  undoInfo?: {
    trackUndo?: boolean;
    undoEntryText?: string;
  };
}

/**
 * Insert one or more cell contents into the active spreadsheet via the
 * `factset.Spreadsheet.InsertContentsToRange` intent.
 *
 * Rejects if the transport is unavailable, the host replies with an error,
 * or the timeout elapses.
 *
 * Pass-through by design — applies no Finster-side defaults to
 * `options.displayAlerts` / `options.selectRange`, so the wire shape stays
 * faithful to `excel-tools.md`. Production callers should go through
 * `excelHostOps.ts::setCellContents` / `fillRangeContents`, which apply the
 * `displayAlerts: false` / `selectRange: false` Finster defaults.
 */
export async function insertContentsToRangeViaIntents(
  contents: SpreadsheetCellContent[],
  options?: SpreadsheetInsertContentsOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  await raiseIntent(
    "factset.Spreadsheet.InsertContentsToRange",
    {
      type: "factset.spreadsheet.insertContentsToRange",
      contents,
      options: options ?? {},
    },
    timeoutMs
  );
}

// ============================================================================
// factset.Spreadsheet.GetActiveCellAddress
// ============================================================================

/**
 * Shape of the response context for `GetActiveCellAddress`.
 *
 * Per `excel-tools.md` today the host returns ONLY a bare cell address
 * (e.g. `"B3"`) — no sheet prefix and no `sheetName` field. The sheet-name
 * extension proposed in `missing-intents-v0.md` §8.1 has not landed.
 */
interface ActiveCellAddressResponse {
  type?: string;
  address?: unknown;
}

/**
 * Resolve the address of the currently active cell via
 * `factset.Spreadsheet.GetActiveCellAddress`.
 *
 * Rejects if the transport is unavailable, the host replies with an error,
 * the timeout elapses, or the response is missing a string `address`.
 *
 * Note: the returned string is usually a bare cell like `"B3"` with no sheet
 * prefix (see `ActiveCellAddressResponse`). Do not infer the active sheet
 * from it.
 */
export async function getActiveCellAddressViaIntents(
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<string> {
  // `raiseIntent` already unwraps `event.data.result.context`, so the
  // resolved value IS the context object for this intent.
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetActiveCellAddress",
    { type: "fdc3.nothing" },
    timeoutMs
  )) as ActiveCellAddressResponse | undefined;
  const address = ctx?.address;
  if (typeof address !== "string" || address.length === 0) {
    throw new Error(
      "GetActiveCellAddress: response did not include a string `address`"
    );
  }
  return address;
}

// ============================================================================
// factset.Spreadsheet.ResizeExcelElements
// ============================================================================

/**
 * Which dimension a resize item affects: row height or column width.
 */
export type ResizeAxis = "ROW" | "COL";

/**
 * How the resize is computed.
 *  - `FIT`      — fit the row/column to its contents. This is the 1:1
 *                 replacement for `range.format.autofitColumns()` /
 *                 `autofitRows()` in Office.js.
 *  - `AUTO`     — host-internal "auto" heuristic; NOT equivalent to Office's
 *                 autofit. Observed to return `success: [true]` without
 *                 visibly resizing — use `FIT` for autofit instead.
 *  - `ABSOLUTE` — explicit size in the host's native unit; requires
 *                 `absoluteSize` to be set on the item.
 */
export type ResizeMode = "AUTO" | "FIT" | "ABSOLUTE";

/**
 * Range reference shape shared with `GetRangeInfo`, `DeleteRange`, and
 * `InsertRange`. The host also accepts `"relative"` reference types; we
 * expose both shapes but Finster only emits `"absolute"` today.
 */
export interface ResizeTargetAddress {
  address: string;
  referenceType: "absolute" | "relative";
  worksheetName?: string;
  workbookName?: string;
}

/**
 * One resize operation. Pair an address + axis with a mode. `absoluteSize`
 * is required iff `resizeMode === "ABSOLUTE"`.
 */
export interface ResizeItem {
  resizeTarget: ResizeTargetAddress;
  resizeMode: ResizeMode;
  resizeAxis: ResizeAxis;
  absoluteSize?: number;
}

/**
 * Resize one or more rows/columns via `factset.Spreadsheet.ResizeExcelElements`.
 *
 * Use `FIT` + axis for Finster's existing autofit calls. The public
 * `excel-tools.md` row currently describes `AUTO` as auto-fit and `FIT` as
 * fit-to-space, but current host testing showed `AUTO` can acknowledge without
 * visibly resizing. Multiple items are applied in order by the host.
 *
 * Observed wire shape (from the captured examples in
 * `excel-docs/intent-details.md` lines 497–512 — a log of real
 * payloads, not a contracted schema): the response looks like
 * `{ success: boolean[] }` with one entry per `items[]` element. The
 * structural observation (boolean **array**, not the scalar boolean
 * `excel-tools.md` documents) is what we rely on; we surface any
 * `false` entry as an error tagged with the failing item index so a
 * partial failure in a multi-item batch isn't silently swallowed. If
 * the host instead returns a single `{ success: true }` (the legacy /
 * scalar shape), we fall through without inspecting it.
 *
 * Rejects if the transport is unavailable, the host replies with an error,
 * the timeout elapses, or any item in the response array is `false`.
 */
export async function resizeExcelElementsViaIntents(
  items: ResizeItem[],
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const response = await raiseIntent(
    "factset.Spreadsheet.ResizeExcelElements",
    {
      type: "factset.spreadsheet.resizeExcelElements",
      items,
    },
    timeoutMs
  );

  // Defensive: response shape is `{ success: boolean[] }` on the wire,
  // but older / single-item responses sometimes look like `{ success:
  // true }`. Accept either; only the array path can carry partial
  // failures, so that's the only one we validate.
  if (response && typeof response === "object") {
    const successField = (response as { success?: unknown }).success;
    if (Array.isArray(successField)) {
      const failureIndex = successField.findIndex((v) => v === false);
      if (failureIndex >= 0) {
        throw new Error(
          `ResizeExcelElements: item ${failureIndex} failed (response success=${JSON.stringify(
            successField
          )})`
        );
      }
    }
  }
}

// ============================================================================
// factset.Spreadsheet.SetCalculationMode
// ============================================================================

/**
 * Workbook calculation mode. `"manual"` disables recalculation until the
 * mode is flipped back to `"automatic"`, which is useful for bracketing a
 * burst of writes so each intent doesn't trigger its own recalc pass.
 */
export type CalculationMode = "automatic" | "automaticExceptTables" | "manual";

/**
 * Set the workbook's calculation mode via `factset.Spreadsheet.SetCalculationMode`.
 *
 * Callers that use this for "bracket a batch" semantics should always pair
 * a `"manual"` call with a `"automatic"` call in a `try/finally`, and should
 * swallow errors from the bracketing calls — a calc-mode flip failing must
 * never take down the write path it wraps.
 */
export async function setCalculationModeViaIntents(
  mode: CalculationMode,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  await raiseIntent(
    "factset.Spreadsheet.SetCalculationMode",
    {
      type: "factset.spreadsheet.setCalculationMode",
      mode,
    },
    timeoutMs
  );
}

// ============================================================================
// factset.Spreadsheet.GetActiveExcelContext
// ============================================================================
//
// The host returns a fully structured `activeExcelContext` object
// directly. (A legacy JSON-encoded-string envelope, and the matching
// `adaptActiveExcelContext` JSON.parse shim, were removed when the
// typed payload landed — restore from git history if a host ever
// reverts.)
//
// Captured wire shape (see `excel-docs/intent-details.md` →
// "Get Active Excel Context"):
//
//   {
//     "activeExcelContext": {
//       "workbookName": "Book1",
//       "activeSheet": { "index": 3, "name": "Sheet3", "sheetType": "xlWorksheet" },
//       "selection":   { "type": "Range", "address": "A1", "referenceType": "absolute" },
//       "isBusy": false,
//       "isInEditMode": false
//     }
//   }
//
// `excel-tools.md` (FactSet's public catalog) still describes the legacy
// JSON-blob shape — see `missing-intents-v0.md` §2.2 for the doc-update
// ask. The wrapper below trusts the wire payload, not the catalog.

/**
 * Lightweight descriptor for one worksheet as surfaced by
 * `GetActiveExcelContext`. Only the fields Finster actually consumes.
 */
export interface ActiveExcelWorksheetInfo {
  name: string;
  /**
   * Position of the sheet in the workbook. **Currently 1-based** on the
   * wire (matches `GetWorkbookSheets.sheets[].index`); FactSet has
   * pre-announced a host-side migration to **0-based** for this intent
   * (along with `GetWorkbookSheets`, `AddWorksheet.options.index`,
   * `GetTables`, and `GetCharts`). When the migration lands, the field
   * type is unchanged but this doc flips and the bridge consumer in
   * `excelService.ts::extractWorkbookContentViaBridge`
   * (`activeSheetIndex: active.index`) starts producing values that match
   * the Office.js `worksheet.position` instead of being silently
   * off-by-one. See `f2_docs/excel/finster-docs/missing-intents-v0.md`
   * → `#open-zero-index-migration`.
   */
  index: number;
  /**
   * Excel sheet-type enum string from the host (e.g. `"xlWorksheet"`,
   * `"xlChart"`). Optional — not all callers need it. Captured as
   * `"xlWorksheet"` in the typed payload.
   */
  sheetType?: string;
}

/**
 * Selection descriptor surfaced by `GetActiveExcelContext`. Mirrors
 * the wire shape verbatim: `type` is the host's selection-kind enum
 * (e.g. `"Range"`, `"Cell"`),
 * `address` is the bare A1-style range, and `referenceType` follows the
 * `RangeAddress.referenceType` enum used elsewhere in this file.
 *
 * Note: unlike `GetSelectedAddressRange`, this `address` does NOT carry
 * a `worksheetName` — the active sheet is implied by `activeSheet.name`.
 * Use `getSelectedAddressRangeViaIntents()` when you need the explicit
 * sheet prefix.
 */
export interface ActiveExcelSelection {
  type: string;
  address: string;
  referenceType: "absolute" | "relative" | string;
}

/**
 * Typed shape Finster consumes from `GetActiveExcelContext`. Mirrors
 * the wire payload — every field except `activeSheet` is defensively
 * optional so the wrapper remains forgiving against field-set drift
 * on the host side (the "structural facts" from `intent-details.md`
 * are what we rely on; per-field detail is still example-level until
 * FactSet documents a contract).
 */
export interface ActiveExcelContext {
  workbookName?: string;
  activeSheet: ActiveExcelWorksheetInfo;
  selection?: ActiveExcelSelection;
  /**
   * `true` while the host application is busy (e.g. a long calc or a
   * modal is open). Consumers may want to short-circuit writes when
   * this is set.
   */
  isBusy?: boolean;
  /**
   * `true` while the user is mid-edit on a cell (Excel's edit mode
   * blocks most automation). Same advice as `isBusy`.
   */
  isInEditMode?: boolean;
  /**
   * Defensive carry-over: the legacy payload sometimes surfaced a flat
   * sheet list alongside `activeSheet`. The current payload doesn't
   * include it (use `getWorkbookSheetsViaIntents` for that), but the
   * field is kept optional so older host captures still typecheck
   * without ceremony.
   */
  sheets?: ActiveExcelWorksheetInfo[];
}

interface GetActiveExcelContextResponse {
  type?: string;
  activeExcelContext?: unknown;
}

function asRecord(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : undefined;
}

function pickWorksheetInfo(v: unknown): ActiveExcelWorksheetInfo | undefined {
  const rec = asRecord(v);
  if (!rec) return undefined;
  const name = typeof rec.name === "string" ? rec.name : undefined;
  const index = typeof rec.index === "number" ? rec.index : undefined;
  if (!name || index === undefined) return undefined;
  const sheetType = typeof rec.sheetType === "string" ? rec.sheetType : undefined;
  return sheetType !== undefined
    ? { name, index, sheetType }
    : { name, index };
}

function pickSelection(v: unknown): ActiveExcelSelection | undefined {
  const rec = asRecord(v);
  if (!rec) return undefined;
  const type = typeof rec.type === "string" ? rec.type : undefined;
  const address = typeof rec.address === "string" ? rec.address : undefined;
  const referenceType =
    typeof rec.referenceType === "string" ? rec.referenceType : undefined;
  if (!type || !address || !referenceType) return undefined;
  return { type, address, referenceType };
}

/**
 * Resolve the active workbook/worksheet context via
 * `factset.Spreadsheet.GetActiveExcelContext`.
 *
 * Reads the structured `activeExcelContext` object directly from the
 * wire payload. Rejects if the transport is unavailable, the host
 * replies with an error, the timeout elapses, or the response is
 * missing the required `activeSheet.{name,index}` pair.
 *
 * **Regression note.** If a host ever reverts to the legacy
 * JSON-encoded-string shape (`"activeExcelContext": "{\"...\"}"`),
 * this wrapper will throw. The legacy shim was removed deliberately —
 * restore it from git history if that situation comes back.
 */
export async function getActiveExcelContextViaIntents(
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<ActiveExcelContext> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetActiveExcelContext",
    { type: "fdc3.nothing" },
    timeoutMs
  )) as GetActiveExcelContextResponse | undefined;

  const direct = asRecord(ctx?.activeExcelContext);
  if (!direct) {
    throw new Error(
      "GetActiveExcelContext: response did not include a structured `activeExcelContext` object (host may have regressed to the legacy JSON-string shape — see `spreadsheetIntents.ts` for the removed shim)"
    );
  }

  const active =
    pickWorksheetInfo(direct.activeSheet) ??
    pickWorksheetInfo(direct.worksheet) ??
    pickWorksheetInfo(direct.sheet);
  if (!active) {
    throw new Error(
      "GetActiveExcelContext: could not find `{ name, index }` for the active sheet in the response payload"
    );
  }

  const workbookName =
    typeof direct.workbookName === "string" ? direct.workbookName : undefined;
  const selection = pickSelection(direct.selection);
  const isBusy = typeof direct.isBusy === "boolean" ? direct.isBusy : undefined;
  const isInEditMode =
    typeof direct.isInEditMode === "boolean" ? direct.isInEditMode : undefined;

  const sheetsRaw = Array.isArray(direct.sheets) ? direct.sheets : undefined;
  const sheets = sheetsRaw
    ?.map(pickWorksheetInfo)
    .filter((x): x is ActiveExcelWorksheetInfo => x !== undefined);

  return {
    workbookName,
    activeSheet: active,
    selection,
    isBusy,
    isInEditMode,
    sheets,
  };
}

// ============================================================================
// Shared range-address shape (GetRangeInfo, GetRangeAsCsv, ParseFormulas)
// ============================================================================

/**
 * Pixel offsets used by `referenceType: "relative"` range references —
 * row/column distances from the active cell. All four legs are optional
 * and default to 0.
 */
export interface RangeOffset {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

/**
 * Range reference accepted by `GetRangeInfo`, `GetRangeAsCsv`, and
 * `ParseFormulas`. Two modes:
 *  - `referenceType: "absolute"` — `address` is an A1-style range, optionally
 *    with `Sheet!` prefix or via separate `worksheetName`/`workbookName`.
 *  - `referenceType: "relative"` — `address` is interpreted relative to the
 *    active cell using `offset.{top,bottom,left,right}`.
 *
 * Finster only emits absolute references today; the relative shape is
 * exposed for future callers.
 */
export interface RangeAddress {
  address: string;
  referenceType: "absolute" | "relative";
  worksheetName?: string;
  workbookName?: string;
  offset?: RangeOffset;
}

// ============================================================================
// factset.Spreadsheet.GetRangeInfo
// ============================================================================

/**
 * Per-call options for `GetRangeInfo`. All optional. `propertyGroups` /
 * `properties` filter what the host returns; `includeEmptyCells` toggles
 * sparse-vs-dense semantics for the response.
 */
export interface GetRangeInfoOptions {
  properties?: string[];
  propertyGroups?: string[];
  includeEmptyCells?: boolean;
}

/**
 * Response context shape for `GetRangeInfo`. The `rangeInfo` payload is
 * intentionally typed as `unknown` until the host's range-info schema
 * graduates: today the doc only documents `{ rangeInfo: { ... } }` with
 * an opaque body. Callers that need shape narrowing should cast at the
 * use site, alongside a `console.info` log of the raw payload during
 * the first call (the now-removed `adaptActiveExcelContext` followed
 * the same one-shot logging pattern — see git history if you need a
 * reference implementation).
 */
interface GetRangeInfoResponse {
  type?: string;
  rangeInfo?: unknown;
}

/**
 * Read values, formulas, and metadata for a range via
 * `factset.Spreadsheet.GetRangeInfo`.
 *
 * Returns the raw `rangeInfo` payload; callers narrow it locally.
 * Rejects if the transport is unavailable, the host replies with an
 * error, the timeout elapses, or the response is missing `rangeInfo`.
 */
export async function getRangeInfoViaIntents(
  address: RangeAddress,
  options?: GetRangeInfoOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<unknown> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetRangeInfo",
    {
      type: "factset.spreadsheet.getRangeInfo",
      address,
      ...(options ? { options } : {}),
    },
    timeoutMs
  )) as GetRangeInfoResponse | undefined;
  if (ctx?.rangeInfo === undefined) {
    throw new Error("GetRangeInfo: response did not include `rangeInfo`");
  }
  return ctx.rangeInfo;
}

// ============================================================================
// factset.Spreadsheet.GetRangeAsCsv
// ============================================================================

interface GetRangeAsCsvResponse {
  type?: string;
  csv?: unknown;
}

/**
 * Export a range as a CSV string via `factset.Spreadsheet.GetRangeAsCsv`.
 *
 * `options` is a serialized options string passed to the underlying
 * primitive — opaque to Finster today. Rejects if the host replies with
 * an error, the timeout elapses, or the response is missing a string
 * `csv`.
 */
export async function getRangeAsCsvViaIntents(
  address: RangeAddress,
  options?: string,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<string> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetRangeAsCsv",
    {
      type: "factset.spreadsheet.getRangeAsCsv",
      address,
      ...(options !== undefined ? { options } : {}),
    },
    timeoutMs
  )) as GetRangeAsCsvResponse | undefined;
  if (typeof ctx?.csv !== "string") {
    throw new Error("GetRangeAsCsv: response did not include a string `csv`");
  }
  return ctx.csv;
}

// ============================================================================
// factset.Spreadsheet.GetWorkbookSheets
// ============================================================================

/**
 * Per-call options for `GetWorkbookSheets`. Mirrors the doc's
 * `options.properties` filter for which extra per-sheet property keys
 * to include in the response.
 *
 * The host today recognises three property strings; we surface them as
 * a typed enum so call sites can't typo a property name. Requesting a
 * property here causes each sheet entry in the response to include the
 * matching field (a sheet's `usedRange` is `null` / absent when the
 * sheet is empty; `visibility` always populates if requested;
 * `isProtected` is a boolean).
 *
 * Without `properties`, the host returns the minimal `{ name, index,
 * sheetType }` shape — preserved for callers that don't need the
 * extra detail.
 */
export type GetWorkbookSheetsProperty =
  | "visibility"
  | "usedRange"
  | "isProtected";

export interface GetWorkbookSheetsOptions {
  properties?: ReadonlyArray<GetWorkbookSheetsProperty>;
}

interface GetWorkbookSheetsResponse {
  type?: string;
  sheets?: unknown;
}

/**
 * List sheets in a workbook via `factset.Spreadsheet.GetWorkbookSheets`.
 *
 * Pass `workbookName: undefined` (or omit) to target the active workbook —
 * the wrapper sends `fdc3.nothing` in that case, matching the doc's
 * "send fdc3.nothing to get all sheets from the active workbook" path.
 *
 * Returns the raw `sheets` payload (typed array; per-element shape
 * documented in `intent-details.md` → "Get Workbook Sheets" — callers
 * narrow at the use site rather than baking the shape into the
 * wrapper). Rejects if the host replies with an error, the timeout
 * elapses, or `sheets` is missing or null. A genuine empty workbook
 * is `{ sheets: [] }` and returns `[]`; a null/omitted `sheets` field
 * used to be treated as the same empty list, which hid transport
 * failures from the agent.
 */
export async function getWorkbookSheetsViaIntents(
  workbookName?: string,
  options?: GetWorkbookSheetsOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<unknown> {
  const useNothing = workbookName === undefined && options === undefined;
  const requestContext: Record<string, unknown> = useNothing
    ? { type: "fdc3.nothing" }
    : {
        type: "factset.spreadsheet.getWorkbookSheets",
        ...(workbookName !== undefined ? { workbookName } : {}),
        ...(options ? { options } : {}),
      };
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetWorkbookSheets",
    requestContext,
    timeoutMs
  )) as GetWorkbookSheetsResponse | undefined;
  if (ctx?.sheets == null) {
    throw new Error("GetWorkbookSheets: response did not include `sheets`");
  }
  return ctx.sheets;
}

// ============================================================================
// factset.Spreadsheet.GetSelectedAddressRange
// ============================================================================

interface GetSelectedAddressRangeResponse {
  type?: string;
  addressRange?: unknown;
}

/**
 * Resolve the currently selected range address via
 * `factset.Spreadsheet.GetSelectedAddressRange`.
 *
 * Returns the bare address string (e.g. `"$A$1:$A$10"`, sheet-prefixed
 * when worksheet info is available — `"Sheet1!$A$1:$A$10"`).
 *
 * **Wire shape:** the host returns an object — captured in
 * `excel-docs/intent-details.md` §"Get Selected Address Range":
 *
 * ```json
 * {
 *   "addressRange": {
 *     "address": "$A$1:$A$10",
 *     "referenceType": "absolute",
 *     "worksheetName": "Sheet1"
 *   }
 * }
 * ```
 *
 * For forward-compat we also accept the legacy flat-string shape
 * (`addressRange: "$A$1:$A$10"`) in case the host or a future build
 * regresses to it. When `worksheetName` is present we prepend it as a
 * `Sheet1!` prefix so callers can disambiguate cross-sheet selections;
 * `getActiveCellAddress` returns a bare address today, so this is the
 * only intent in the cursor pair that can carry sheet info.
 *
 * Rejects on transport errors, timeout, or a response missing a
 * usable `addressRange.address` string.
 */
export async function getSelectedAddressRangeViaIntents(
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<string> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetSelectedAddressRange",
    { type: "fdc3.nothing" },
    timeoutMs
  )) as GetSelectedAddressRangeResponse | undefined;
  const raw = ctx?.addressRange;

  if (typeof raw === "string" && raw.length > 0) {
    return raw;
  }

  if (raw && typeof raw === "object") {
    const obj = raw as { address?: unknown; worksheetName?: unknown };
    const address = obj.address;
    if (typeof address === "string" && address.length > 0) {
      const sheet = obj.worksheetName;
      return typeof sheet === "string" && sheet.length > 0
        ? `${sheet}!${address}`
        : address;
    }
  }

  throw new Error(
    "GetSelectedAddressRange: response did not include a usable `addressRange.address`"
  );
}

// ============================================================================
// factset.Spreadsheet.HarmonizedRefreshWorksheets
// ============================================================================

/**
 * Options for `HarmonizedRefreshWorksheets`. Pass `undefined` (or an empty
 * object) to refresh the currently selected sheet(s) with default options —
 * the wrapper sends `fdc3.nothing` in that case.
 *
 * Per FactSet's `excel-tools.md`, `fdc3.nothing` refreshes the **currently
 * selected sheet(s)** — NOT the entire workbook. To refresh other tabs, pass
 * an explicit `sheetNames` list.
 */
export interface HarmonizedRefreshOptions {
  sheetNames?: string[];
  disableProgressIndicator?: boolean;
}

interface HarmonizedRefreshResponse {
  type?: string;
  message?: unknown;
}

/**
 * Refresh one or more worksheets via
 * `factset.Spreadsheet.HarmonizedRefreshWorksheets`.
 *
 * **Routing: HOST refresh, NOT cloud.** This intent runs in the
 * in-process FactSet runtime that ships with the Excel add-in and
 * does **not** consume any cloud refresh quota — it's the "press the
 * Refresh button in the FactSet ribbon" path, just driven
 * programmatically. The quota-capped path is `RefreshCalculate`
 * below, which runs server-side template instantiation. Everyday
 * `=FDS(...)` writes from the AI planner come through here, so the
 * Excel chat's auto-refresh on a multi-step plan is free.
 *
 * Resolves to the host's human-readable `message` string (or `""` if the
 * host omits it). Rejects on transport / host errors and on timeout.
 */
export async function harmonizedRefreshWorksheetsViaIntents(
  options?: HarmonizedRefreshOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<string> {
  const hasFields =
    options !== undefined &&
    (options.sheetNames !== undefined ||
      options.disableProgressIndicator !== undefined);
  const requestContext: Record<string, unknown> = hasFields
    ? {
        type: "factset.spreadsheet.harmonizedRefreshWorksheets",
        ...(options.sheetNames !== undefined
          ? { sheetNames: options.sheetNames }
          : {}),
        ...(options.disableProgressIndicator !== undefined
          ? { disableProgressIndicator: options.disableProgressIndicator }
          : {}),
      }
    : { type: "fdc3.nothing" };
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.HarmonizedRefreshWorksheets",
    requestContext,
    timeoutMs
  )) as HarmonizedRefreshResponse | undefined;
  return typeof ctx?.message === "string" ? ctx.message : "";
}

/**
 * Pull the worksheet names out of a raw `GetWorkbookSheets.sheets`
 * payload. Keeps `xlWorksheet` entries (and entries whose `sheetType`
 * the host omitted, to stay forgiving against field-set drift) and
 * drops chart sheets (`xlChart`) and any other non-worksheet kind —
 * those carry no `=FDS(...)` formulas, so refreshing them by name is at
 * best a no-op and at worst a host rejection.
 */
function pickRefreshableWorksheetNames(rawSheets: unknown): string[] {
  if (!Array.isArray(rawSheets)) return [];
  const names: string[] = [];
  for (const entry of rawSheets) {
    const rec = asRecord(entry);
    if (!rec) continue;
    const name = typeof rec.name === "string" ? rec.name : undefined;
    if (!name) continue;
    const sheetType =
      typeof rec.sheetType === "string" ? rec.sheetType : undefined;
    if (sheetType !== undefined && sheetType !== "xlWorksheet") continue;
    names.push(name);
  }
  return names;
}

/**
 * Refresh **every** worksheet in a workbook via
 * `factset.Spreadsheet.HarmonizedRefreshWorksheets`.
 *
 * **Why this exists.** `fdc3.nothing` (the empty-options shape on
 * `harmonizedRefreshWorksheetsViaIntents`) refreshes only the currently
 * selected sheet(s) — NOT the whole workbook (per FactSet's
 * `excel-tools.md`). A genuine "refresh the entire workbook" therefore
 * has to enumerate the sheets and pass them explicitly. This helper does
 * exactly that: it lists the workbook's sheets via `GetWorkbookSheets`,
 * keeps the worksheets (chart sheets have no FDS data), and forwards the
 * full name list to the refresh intent so every tab fetches its data
 * up-front instead of going stale until the user activates it.
 *
 * Same routing as `harmonizedRefreshWorksheetsViaIntents`: a HOST
 * refresh that consumes no cloud quota.
 *
 * @param workbookName Target workbook; omit for the active workbook.
 * @param options.disableProgressIndicator Suppress the host refresh UI.
 * @param timeoutMs Timeout for the refresh call (the slow leg). Sheet
 *   enumeration always uses the default wrapper timeout.
 *
 * Resolves to the host message and the explicit worksheet scope confirmed by
 * the host. If the workbook reports no
 * refreshable worksheets, it falls back to the plain `fdc3.nothing`
 * refresh so the call still does something useful rather than throwing.
 */
export interface HarmonizedRefreshAllWorksheetsResult {
  message: string;
  requestedSheetNames: string[];
  refreshedSheetNames: string[];
  failedSheetNames: string[];
  worksheetResults: Array<{
    sheetName: string;
    ok: boolean;
    message: string;
    error?: string;
  }>;
  scopeConfirmed: boolean;
}

/**
 * Refresh an explicit worksheet scope one sheet at a time.
 *
 * The FactSet host accepts multiple worksheet names in one request, but large
 * batches can resolve successfully while leaving formulas unresolved. Serial
 * single-sheet calls match the reliable ribbon workflow and let callers
 * distinguish complete from partial acknowledgement. `timeoutMs` is a shared
 * wall-clock budget for the entire sequence, not a fresh budget per sheet.
 */
export async function harmonizedRefreshWorksheetsSequentiallyViaIntents(
  requestedNames: string[],
  options?: { disableProgressIndicator?: boolean },
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<HarmonizedRefreshAllWorksheetsResult> {
  const seen = new Set<string>();
  const requestedSheetNames: string[] = [];
  for (const rawName of requestedNames) {
    const sheetName = rawName.trim();
    const key = sheetName.toLowerCase();
    if (!sheetName || seen.has(key)) continue;
    seen.add(key);
    requestedSheetNames.push(sheetName);
  }

  const refreshedSheetNames: string[] = [];
  const failedSheetNames: string[] = [];
  const worksheetResults: HarmonizedRefreshAllWorksheetsResult["worksheetResults"] = [];
  const deadline = Date.now() + Math.max(0, timeoutMs);

  for (const sheetName of requestedSheetNames) {
    const remainingMs = deadline - Date.now();
    if (remainingMs <= 0) {
      failedSheetNames.push(sheetName);
      worksheetResults.push({
        sheetName,
        ok: false,
        message: "",
        error: "FactSet worksheet refresh budget exhausted.",
      });
      continue;
    }

    try {
      const message = await harmonizedRefreshWorksheetsViaIntents(
        {
          sheetNames: [sheetName],
          ...(options?.disableProgressIndicator !== undefined
            ? { disableProgressIndicator: options.disableProgressIndicator }
            : {}),
        },
        remainingMs,
      );
      refreshedSheetNames.push(sheetName);
      worksheetResults.push({ sheetName, ok: true, message });
    } catch (error: unknown) {
      failedSheetNames.push(sheetName);
      worksheetResults.push({
        sheetName,
        ok: false,
        message: "",
        error: String(error),
      });
    }
  }

  const scopeConfirmed =
    requestedSheetNames.length > 0 &&
    refreshedSheetNames.length === requestedSheetNames.length;
  let message = "";
  if (scopeConfirmed) {
    const messages = new Set(
      worksheetResults.map((result) => result.message).filter(Boolean),
    );
    message =
      messages.size === 1
        ? (messages.values().next().value ?? "")
        : `Refreshed ${refreshedSheetNames.length} worksheets.`;
  } else if (refreshedSheetNames.length > 0) {
    message =
      `Refreshed ${refreshedSheetNames.length} of ${requestedSheetNames.length} worksheets; ` +
      `${failedSheetNames.length} failed.`;
  } else if (requestedSheetNames.length > 0) {
    message = `Failed to refresh ${requestedSheetNames.length} worksheets.`;
  }

  return {
    message,
    requestedSheetNames,
    refreshedSheetNames,
    failedSheetNames,
    worksheetResults,
    scopeConfirmed,
  };
}

export async function harmonizedRefreshAllWorksheetsViaIntents(
  workbookName?: string,
  options?: { disableProgressIndicator?: boolean },
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<HarmonizedRefreshAllWorksheetsResult> {
  const rawSheets = await getWorkbookSheetsViaIntents(workbookName);
  const sheetNames = pickRefreshableWorksheetNames(rawSheets);
  if (sheetNames.length === 0) {
    const message = await harmonizedRefreshWorksheetsViaIntents(
      options,
      timeoutMs,
    );
    return {
      message,
      requestedSheetNames: [],
      refreshedSheetNames: [],
      failedSheetNames: [],
      worksheetResults: [],
      scopeConfirmed: false,
    };
  }
  return harmonizedRefreshWorksheetsSequentiallyViaIntents(
    sheetNames,
    options,
    timeoutMs,
  );
}

// ============================================================================
// factset.Spreadsheet.ApplicationCalculate
// ============================================================================

/**
 * Calculation modes accepted by `ApplicationCalculate`:
 *  - `recalculate` — recalculates changed cells (default)
 *  - `full` — recalculates all cells
 *  - `fullRebuild` — recalculates all cells and rebuilds dependencies
 */
export type ApplicationCalculateType = "recalculate" | "full" | "fullRebuild";

interface ApplicationCalculateResponse {
  type?: string;
  message?: unknown;
}

/**
 * Trigger a workbook recalculation via
 * `factset.Spreadsheet.ApplicationCalculate`. Pass `undefined` for the
 * default (`recalculate`) — the wrapper sends `fdc3.nothing` in that case.
 *
 * Resolves to the host's `message` (or `""`).
 */
export async function applicationCalculateViaIntents(
  calculationType?: ApplicationCalculateType,
  timeoutMs: number = LONG_RUNNING_FACTSET_INTENT_TIMEOUT_MS
): Promise<string> {
  const requestContext: Record<string, unknown> =
    calculationType === undefined
      ? { type: "fdc3.nothing" }
      : {
          type: "factset.spreadsheet.applicationCalculate",
          calculationType,
        };
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.ApplicationCalculate",
    requestContext,
    timeoutMs
  )) as ApplicationCalculateResponse | undefined;
  return typeof ctx?.message === "string" ? ctx.message : "";
}

// ============================================================================
// factset.Spreadsheet.GetCalculationMode
// ============================================================================

interface GetCalculationModeResponse {
  type?: string;
  calculationMode?: unknown;
}

const CALCULATION_MODES: ReadonlyArray<CalculationMode> = [
  "automatic",
  "automaticExceptTables",
  "manual",
];

/**
 * Read the current workbook calculation mode via
 * `factset.Spreadsheet.GetCalculationMode`. Returns the same
 * `CalculationMode` union accepted by `setCalculationModeViaIntents`.
 *
 * Rejects if the response value is not one of the documented modes.
 */
export async function getCalculationModeViaIntents(
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<CalculationMode> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetCalculationMode",
    { type: "fdc3.nothing" },
    timeoutMs
  )) as GetCalculationModeResponse | undefined;
  const mode = ctx?.calculationMode;
  if (
    typeof mode !== "string" ||
    !CALCULATION_MODES.includes(mode as CalculationMode)
  ) {
    throw new Error(
      `GetCalculationMode: unrecognized \`calculationMode\` (${String(mode)})`
    );
  }
  return mode as CalculationMode;
}

// ============================================================================
// factset.Spreadsheet.ParseFormulas
// ============================================================================

/**
 * Parsing depth for `ParseFormulas`:
 *  - `StringOnly` — formula text only (cheap, default for Finster)
 *  - `WithRangeValues` — additionally resolves referenced range values
 */
export type FormulaParsingLevel = "StringOnly" | "WithRangeValues";

interface ParseFormulasResponse {
  type?: string;
  parsedFormulas?: unknown;
}

/**
 * Parse formulas in a range via `factset.Spreadsheet.ParseFormulas`.
 *
 * Returns the raw `parsedFormulas` array (typed; per-element shape
 * pending capture in `intent-details.md` — narrow at the use site).
 * Rejects if the host replies with an error, the timeout elapses, or
 * the response is missing `parsedFormulas`.
 */
export async function parseFormulasViaIntents(
  address: RangeAddress,
  parsingLevel?: FormulaParsingLevel,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<unknown> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.ParseFormulas",
    {
      type: "factset.spreadsheet.parseFormulas",
      address,
      ...(parsingLevel !== undefined ? { parsingLevel } : {}),
    },
    timeoutMs
  )) as ParseFormulasResponse | undefined;
  if (ctx?.parsedFormulas === undefined) {
    throw new Error(
      "ParseFormulas: response did not include `parsedFormulas`"
    );
  }
  return ctx.parsedFormulas;
}

// ============================================================================
// factset.Spreadsheet.Charting.GetCharts
// ============================================================================
//
// Same naming gotcha as `Charting.AddExcelChart` (cost us a debugging
// session on 2026-05-05): the intent lives in the `Charting`
// sub-namespace — `factset.Spreadsheet.Charting.GetCharts` — even
// though `excel-tools.md` still lists the older un-namespaced
// `factset.Spreadsheet.GetCharts` plus a stringified-blob `charts`
// payload. The current source of truth is the working wire capture
// against `OI Remote Iframe` and the catalog header in
// `f2_docs/excel/chart-tools.md` (which puts every Excel charting
// intent under `factset.Spreadsheet.Charting.*`).
//
// Wire shape (request, per [f2_docs/excel/chart-tools.md](../../../../../../f2_docs/excel/chart-tools.md), updated 2026-05-12):
//
//   {
//     type: "factset.spreadsheet.charting.getCharts",
//     options: {
//       workbookName?: string,
//       sheetRef?:    { index?: number, name?: string, codeName?: string },
//       type?:        string,                 // shape-kind filter: "Chart" | "OLEObject"
//       chartIndex?:  number,                 // 0-based; return a single chart
//       detail?:      "Summary" | "Full",     // verbosity (default "Summary")
//     }
//   }
//
// Pass `undefined` (or an empty options object) and we send the
// namespaced `factset.spreadsheet.charting.getCharts` context with no
// `options` field — host then defaults to active workbook + all sheets
// at `"Summary"` detail. We **MUST NOT** send `fdc3.nothing` here
// (cost us a debugging session on 2026-05-06): the legacy
// un-namespaced `factset.Spreadsheet.GetCharts` accepted that fallback,
// but the new `Charting.*` namespace runtime-rejects it with a generic
// `"Failed to get charts"` even though the schema validation passes.
// Always emit the namespaced context type, even when there's nothing
// in `options`.
//
// Default-verbosity change (2026-05-12): chart-tools.md flipped the
// default from `"Full"` to `"Summary"` and renamed the lighter level
// from `"Lite"` to `"Summary"`. Old `"Lite"` value is gone on the
// wire — callers that need axes + per-series styling + chartGroups
// must opt into `"Full"` explicitly now.
//
// Wire shape (response, captured 2026-05-05):
//
//   {
//     charts: ChartingChartInfo[]
//   }
//
// `ChartingChartInfo` (typed below) is the rich per-element payload
// — id / index / sheetRef / position (in points) / name / chartType /
// title / legend / axes / series — replacing the legacy
// "JSON-encoded string" shape `excel-tools.md` still documents.
//
// Routing: same `OI Remote Iframe` relay as the rest of the
// `Charting.*` family.

/**
 * Verbosity hint for `GetCharts`. Per
 * [f2_docs/excel/chart-tools.md](../../../../../../f2_docs/excel/chart-tools.md)
 * (2026-05-12):
 *
 * - `"Summary"` (default) — core chart metadata: `chartType`, `title`,
 *   and a `series` list with each series's `name`, `values`,
 *   `categories`, and `chartType`.
 * - `"Full"` — everything in `Summary` plus `chartStyle`, `legend`,
 *   `plotBy`, `displayBlanksAs`, `chartArea`, `plotArea`, `axes`,
 *   per-series styling (`fill`, `line`, `marker`, `leaderLines`,
 *   `points`, …), and `chartGroups`.
 *
 * `OLEObject` entries (ActiveGraph OLE objects) are currently
 * unaffected by `detail` — their shape is identity + position only.
 *
 * The legacy `"Lite"` value is gone from the wire as of 2026-05-12;
 * callers that previously sent `"Lite"` must now send `"Summary"`.
 */
export type ChartingDetail = "Summary" | "Full";

/**
 * Sheet target for `GetCharts`. Per chart-tools.md, the backend
 * resolves in order `index` → `name` → `codeName`. Omit to read every
 * sheet in the target workbook.
 */
export interface GetChartsSheetRef {
  index?: number;
  name?: string;
  codeName?: string;
}

/**
 * Per-call options for `Charting.GetCharts`. All fields are optional;
 * the host falls back to active-workbook / all-sheets / `"Summary"`
 * detail when any are omitted.
 */
export interface GetChartsOptions {
  workbookName?: string;
  sheetRef?: GetChartsSheetRef;
  /**
   * Filter by shape kind: `"Chart"` (native Excel charts) or
   * `"OLEObject"` (ActiveGraph OLE objects). Omit to return both.
   */
  type?: string;
  /**
   * 0-based index of a single chart within the resolved sheet's chart
   * objects collection. When set, the result is filtered to that one
   * chart only. Requires `sheetRef` to be meaningful (or relies on the
   * active sheet otherwise).
   */
  chartIndex?: number;
  /** Response detail level (default `"Summary"`). */
  detail?: ChartingDetail;
}

/**
 * Excel COM `Font` properties as returned by `Charting.GetCharts`.
 * `colorRgb` is a CSS-style `#RRGGBB` hex string.
 */
export interface ChartingFontInfo {
  name?: string;
  size?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  colorRgb?: string;
}

/** Excel COM fill properties. `colorRgb` is `#RRGGBB`. */
export interface ChartingFillInfo {
  isVisible?: boolean;
  colorRgb?: string;
}

/** Excel COM line / border properties. `dashStyle` mirrors `MsoLineDashStyle`. */
export interface ChartingLineInfo {
  isVisible?: boolean;
  colorRgb?: string;
  weight?: number;
  dashStyle?: string;
}

/**
 * Marker styling for a chart series. `style` mirrors `XlMarkerStyle`.
 * Markers carry their own fill + border per the
 * `f2_docs/charting/chart-schema-preview.md` class diagram.
 */
export interface ChartingMarkerInfo {
  style?: string;
  size?: number;
  fill?: ChartingFillInfo;
  line?: ChartingLineInfo;
}

/**
 * Data-label config (series-level or per-point) returned by
 * `GetCharts` at `detail: "Full"`. Mirrors the writable shape on
 * `addSeriesToChart` / `updateSeriesInChart`.
 */
export interface ChartingDataLabelsInfo {
  showValue?: boolean;
  showCategoryName?: boolean;
  showSeriesName?: boolean;
  /** `XlDataLabelPosition` enum string, e.g. `"xlLabelPositionAbove"`. */
  position?: string;
  numberFormat?: string;
  showBubbleSize?: boolean;
  showPercentage?: boolean;
  showLegendKey?: boolean;
  separator?: string;
  font?: ChartingFontInfo;
  fill?: ChartingFillInfo;
  /** Label border styling (`ChartLineInfo`). */
  border?: ChartingLineInfo;
}

/** Per-point overrides on a series — fill / line / explosion / data label. */
export interface ChartingPointInfo {
  /** 0-based point index within the series. */
  index?: number;
  fill?: ChartingFillInfo;
  line?: ChartingLineInfo;
  explosion?: number;
  /** Toggle this single point's data label on/off. */
  hasDataLabel?: boolean;
  /** Per-point data-label override. */
  dataLabel?: ChartingDataLabelsInfo;
}

/** Per-entry legend overrides — typically just a font override. */
export interface ChartingLegendEntryInfo {
  /** 0-based legend entry index. */
  index?: number;
  font?: ChartingFontInfo;
}

/**
 * Per-axis metadata. The host returns one entry per axis on the chart
 * (typically `xlCategory` + `xlValue` for column / line / bar charts).
 * Category-only fields (`categoryType`) and value-only fields
 * (`minimumScale`, `maximumScale`, `majorUnit`, `minorUnit`,
 * `scaleType`, `majorGridLine`) are optional so a single shape covers
 * both axis types. Full surface mirrors
 * `f2_docs/charting/chart-schema-preview.md::ChartAxisInfo`.
 */
export interface ChartingAxisInfo {
  axisType?: string;
  axisGroup?: string;
  /** Whether this axis is enabled (maps to `Chart.HasAxis`). */
  enabled?: boolean;
  hasTitle?: boolean;
  titleText?: string;
  titleFont?: ChartingFontInfo;
  hasMajorGridlines?: boolean;
  hasMinorGridlines?: boolean;
  reversePlotOrder?: boolean;
  numberFormat?: string;
  categoryType?: string;
  minimumScale?: number;
  maximumScale?: number;
  majorUnit?: number;
  minorUnit?: number;
  scaleType?: string;
  /** `XlDisplayUnit` enum string, e.g. `"xlThousands"` (value axes only). */
  displayUnit?: string;
  hasDisplayUnitLabel?: boolean;
  /** `XlTickLabelPosition` enum string. */
  tickLabelPosition?: string;
  tickMarkSpacing?: number;
  tickLabelSpacing?: number;
  /** `XlTickMark` enum string, e.g. `"xlTickMarkOutside"`. */
  majorTickMark?: string;
  minorTickMark?: string;
  /** `XlAxisCrosses` enum string. */
  crosses?: string;
  crossesAt?: number;
  minimumScaleIsAuto?: boolean;
  maximumScaleIsAuto?: boolean;
  majorUnitIsAuto?: boolean;
  minorUnitIsAuto?: boolean;
  /** `XlTimeUnit` enum string (date axes only). */
  baseUnit?: string;
  baseUnitIsAuto?: boolean;
  tickLabelFont?: ChartingFontInfo;
  axisLine?: ChartingLineInfo;
  majorGridLine?: ChartingLineInfo;
  minorGridLine?: ChartingLineInfo;
}

/**
 * Per-series metadata. `categories` / `values` / `xValues` /
 * `bubbleSizes` are Excel-style A1 ranges
 * (`"Sheet1!$A$1:$A$10"`). Full surface mirrors
 * `f2_docs/charting/chart-schema-preview.md::ChartSeriesInfo`. Note
 * that per the preview's "data label lifecycle" rule, label fields
 * (`hasDataLabels` + `dataLabels` + per-point `hasDataLabel` +
 * `dataLabel`) flow exclusively through `updateSeriesInChart` writes,
 * not `updateExcelChart`. Read side surfaces them at `detail: "Full"`.
 *
 * `errorBars` / `trendlines` are explicitly deferred per the preview
 * and intentionally NOT modelled here.
 */
export interface ChartingSeriesInfo {
  index?: number;
  name?: string;
  categories?: string;
  values?: string;
  /** X-values range (scatter/bubble charts only). */
  xValues?: string;
  /** Bubble sizes range (bubble charts only). */
  bubbleSizes?: string;
  chartType?: string;
  axisGroup?: string;
  plotOrder?: number;
  smooth?: boolean;
  explosion?: number;
  invertIfNegative?: boolean;
  hasDataLabels?: boolean;
  /** Toggle error bars on/off. The structured `errorBars` object is
   *  explicitly deferred per the preview, so we only surface the
   *  boolean here for read-side completeness. */
  hasErrorBars?: boolean;
  hasLeaderLines?: boolean;
  fill?: ChartingFillInfo;
  line?: ChartingLineInfo;
  marker?: ChartingMarkerInfo;
  dataLabels?: ChartingDataLabelsInfo;
  leaderLines?: ChartingLineInfo;
  points?: ChartingPointInfo[];
}

/**
 * Per-chart-group rendering options (gap width / overlap / pie /
 * bubble / etc.). One group per discrete chart-type in the chart;
 * combo charts surface multiple groups. Mirrors
 * `f2_docs/charting/chart-schema-preview.md::ChartGroupInfo`.
 */
export interface ChartingChartGroupInfo {
  /** 0-based chart-group index. */
  index?: number;
  /** Gap between bars/columns (%). */
  gapWidth?: number;
  /** Bar/column overlap (%). */
  overlap?: number;
  varyByCategories?: boolean;
  /** Pie chart rotation angle (degrees). */
  firstSliceAngle?: number;
  /** Doughnut hole size (%). */
  doughnutHoleSize?: number;
  /** `XlChartSplitType` enum string (pie-of-pie). */
  splitType?: string;
  splitValue?: number;
  secondPlotSize?: number;
  /** Bubble size scale (%). */
  bubbleScale?: number;
  showNegativeBubbles?: boolean;
  /** `XlSizeRepresents` enum string. */
  sizeRepresents?: string;
  hasSeriesLines?: boolean;
  hasDropLines?: boolean;
  hasHiLoLines?: boolean;
  hasUpDownBars?: boolean;
  /** `XlAxisGroup` enum string. */
  axisGroup?: string;
}

/**
 * One chart entry returned by `Charting.GetCharts`. Mirrors the
 * captured wire shape verbatim (Excel COM property names: `chartType`
 * is the `xl*` enum, `title.font.colorRgb` is `#RRGGBB`, `position` is
 * in points, etc.).
 *
 * The shape is shared across both response variants — entries with
 * `type: "Chart"` (native Excel charts) carry the chart-metadata
 * fields (`chartType`, `title`, `series`, …); entries with
 * `type: "OLEObject"` (ActiveGraph OLE objects) carry the OLE-specific
 * fields (`progId`, `oleType`, `isActiveGraph`) and a basic
 * identity + position payload. The `detail` request parameter controls
 * how much chart metadata is returned for native `Chart` entries
 * only — `OLEObject` rows are unaffected.
 *
 * - `"Summary"` (default) — `chartType` + `title` + slim `series`
 *   (`name`, `values`, `categories`, `chartType`).
 * - `"Full"` — adds `chartStyle`, `chartColorIndex`, `legend`,
 *   `plotBy`, `displayBlanksAs`, `plotVisibleOnly`, `hasDataTable`,
 *   `categoryLabelLevel`, `seriesNameLevel`, `showAllFieldButtons`,
 *   `chartArea`, `plotArea`, `axes`, full per-series styling
 *   (`fill`, `line`, `marker`, `leaderLines`, `points`, …), and
 *   `chartGroups`.
 */
export interface ChartingChartInfo {
  /** `"Chart"` for native Excel charts, `"OLEObject"` for ActiveGraph OLE objects. */
  type?: string;
  id?: number;
  index?: number;
  sheetRef?: { index?: number; name?: string; codeName?: string };
  position?: { top: number; left: number; width: number; height: number };
  name?: string;
  /** OLE-only: COM ProgID, e.g. `"FDSCHART.FDSChartCtrl.1"` for an ActiveGraph. */
  progId?: string;
  /** OLE-only: `xlOLEEmbed` or `xlOLELink`. */
  oleType?: string;
  /** OLE-only: convenience flag for entries whose `progId` is the ActiveGraph control. */
  isActiveGraph?: boolean;
  chartType?: string;
  chartStyle?: number;
  /** "Full" detail: theme color index (legacy Excel chart-color tinting). */
  chartColorIndex?: number;
  title?: {
    hasTitle?: boolean;
    text?: string;
    includeInLayout?: boolean;
    /** `XlChartElementPosition` enum string. */
    position?: string;
    font?: ChartingFontInfo;
  };
  legend?: {
    hasLegend?: boolean;
    position?: string;
    includeInLayout?: boolean;
    font?: ChartingFontInfo;
    /** Per-entry font overrides — `{ index: 0-based, font: ... }`. */
    legendEntries?: ChartingLegendEntryInfo[];
  };
  plotBy?: string;
  displayBlanksAs?: string;
  plotVisibleOnly?: boolean;
  hasDataTable?: boolean;
  showDataLabelsOverMaximum?: boolean;
  /** "Full" detail: per chart-tools.md captured payload. */
  categoryLabelLevel?: number;
  seriesNameLevel?: number;
  /** Pivot-chart field-button toggles (Excel-only per
   *  chart-schema-preview.md PPT compatibility table). */
  showAllFieldButtons?: boolean;
  showAxisFieldButtons?: boolean;
  showLegendFieldButtons?: boolean;
  showReportFilterFieldButtons?: boolean;
  showValueFieldButtons?: boolean;
  chartArea?: {
    fill?: ChartingFillInfo;
    line?: ChartingLineInfo;
    font?: ChartingFontInfo;
    roundedCorners?: boolean;
    shadow?: boolean;
  };
  plotArea?: {
    fill?: ChartingFillInfo;
    line?: ChartingLineInfo;
    insideLeft?: number;
    insideTop?: number;
    insideWidth?: number;
    insideHeight?: number;
  };
  axes?: ChartingAxisInfo[];
  series?: ChartingSeriesInfo[];
  /** "Full" detail: per-chart-group rendering options (gap/overlap/etc). */
  chartGroups?: ChartingChartGroupInfo[];
}

interface GetChartsResponse {
  type?: string;
  charts?: unknown;
}

const GET_CHARTS_APP_ID = { appId: "OI Remote Iframe" };

/**
 * List charts in a workbook via `factset.Spreadsheet.Charting.GetCharts`.
 *
 * Returns the typed `charts` array (`ChartingChartInfo[]`). Pass
 * `undefined` (or an empty options object) to fall back to the host's
 * active-workbook / all-sheets / `"Summary"` detail defaults. Rejects
 * when the response is missing `charts` or comes back in the legacy
 * string-blob shape (which would indicate a host regression to the
 * pre-`Charting`-namespace world).
 *
 * Always sends `context.type = "factset.spreadsheet.charting.getCharts"`,
 * even when no options are provided. The legacy un-namespaced
 * `factset.Spreadsheet.GetCharts` accepted `fdc3.nothing` as a "use
 * defaults" sentinel; the new `Charting.*` namespace runtime-rejects
 * `fdc3.nothing` with a generic `"Failed to get charts"` runtime error
 * (schema passes; the host's chart-engine handler bails when the
 * context type doesn't match).
 *
 * Optional knobs:
 * - `workbookName` / `sheetRef` — narrow to a specific workbook / sheet.
 * - `type` — filter by shape kind: `"Chart"` for native Excel charts,
 *   `"OLEObject"` for ActiveGraph OLE objects. Omit for both.
 * - `chartIndex` — 0-based; return a single chart from the resolved
 *   sheet's chart collection.
 * - `detail` — `"Summary"` (default) returns identity + core metadata
 *   + a slim per-series list; `"Full"` adds axes, full per-series
 *   styling, `chartGroups`, etc.
 *
 * Note: callers that previously consumed the wrapper's untyped
 * `unknown[]` return are still safe — `ChartingChartInfo[]` widens to
 * `unknown[]` cleanly. New callers can read structured fields directly
 * (id / position / chartType / series) instead of casting through
 * `adaptOpaqueArray`.
 */
export async function getChartsViaIntents(
  options?: GetChartsOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<ChartingChartInfo[]> {
  const optionFields: Record<string, unknown> = {
    ...(options?.workbookName !== undefined
      ? { workbookName: options.workbookName }
      : {}),
    ...(options?.sheetRef !== undefined ? { sheetRef: options.sheetRef } : {}),
    ...(options?.type !== undefined ? { type: options.type } : {}),
    ...(options?.chartIndex !== undefined
      ? { chartIndex: options.chartIndex }
      : {}),
    ...(options?.detail !== undefined ? { detail: options.detail } : {}),
  };
  const requestContext: Record<string, unknown> = {
    type: "factset.spreadsheet.charting.getCharts",
    ...(Object.keys(optionFields).length > 0 ? { options: optionFields } : {}),
  };
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.Charting.GetCharts",
    requestContext,
    timeoutMs,
    GET_CHARTS_APP_ID
  )) as GetChartsResponse | undefined;
  const raw = ctx?.charts;
  if (raw === undefined) {
    throw new Error("GetCharts: response did not include `charts`");
  }
  if (Array.isArray(raw)) return raw as ChartingChartInfo[];
  // Defensive: a few read-side intents wrap their array in `{ items: [...] }`.
  // Accept that shape too rather than fail loudly.
  const rec = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : undefined;
  if (rec && Array.isArray(rec.items)) return rec.items as ChartingChartInfo[];
  throw new Error(
    "GetCharts: `charts` was not a structured array (host may have regressed to the legacy JSON-string shape — see git history for the removed shim)"
  );
}

// ============================================================================
// factset.Spreadsheet.GetTables
// ============================================================================
//
// Same story as `GetCharts`: `tables` arrives as a typed array on the
// wire (`tables: []` baseline), legacy string-blob shape gone.
// `excel-tools.md` still describes the legacy shape and flags the
// intent Experimental — doc ask in `missing-intents-v0.md` §2.2.

/**
 * Per-call options for `GetTables`. Today only `workbookName` is documented.
 */
export interface GetTablesOptions {
  workbookName?: string;
}

interface GetTablesResponse {
  type?: string;
  tables?: unknown;
}

/**
 * List tables in a workbook via `factset.Spreadsheet.GetTables`.
 * Mirrors `getChartsViaIntents` — typed-array return, defensive
 * `{ items: [...] }` fallback, hard rejection on the legacy
 * string-blob shape.
 *
 * @param options - Optional workbook target; omission uses the active workbook.
 * @param timeoutMs - Maximum bridge call duration.
 * @returns Raw structured table records supplied by the host.
 * @throws When the bridge fails or returns an invalid shape.
 */
export async function getTablesViaIntents(
  options?: GetTablesOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<unknown[]> {
  const useNothing =
    options === undefined || options.workbookName === undefined;
  const requestContext: Record<string, unknown> = useNothing
    ? { type: "fdc3.nothing" }
    : {
        type: "factset.spreadsheet.getTables",
        options: { workbookName: options.workbookName },
      };
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetTables",
    requestContext,
    timeoutMs
  )) as GetTablesResponse | undefined;
  const raw = ctx?.tables;
  if (raw === undefined) {
    throw new Error("GetTables: response did not include `tables`");
  }
  if (Array.isArray(raw)) return raw;
  const rec = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : undefined;
  if (rec && Array.isArray(rec.items)) return rec.items as unknown[];
  throw new Error(
    "GetTables: `tables` was not a structured array (host may have regressed to the legacy JSON-string shape — see git history for the removed shim)"
  );
}

/** Workbook-level iterative-calculation settings returned by Excel. */
export interface IterativeCalculationSettings {
  /** Whether iterative calculation is enabled. */
  enabled: boolean;
  /** Maximum calculation iterations configured in Excel. */
  maxIterations: number;
  /** Maximum convergence delta configured in Excel. */
  maxChange: number;
}

/** One circular-reference location reported by a worksheet. */
export interface CircularReferenceEntry {
  /** Worksheet containing the circular reference. */
  worksheetName: string;
  /** Host-reported A1 address of the circular reference. */
  address: string;
}

/** Normalized circular-reference intent result. */
export interface GetCircularReferencesResult {
  /** Workbook iterative-calculation configuration. */
  iterativeCalculation: IterativeCalculationSettings;
  /** Valid circular-reference locations returned by the host. */
  worksheets: CircularReferenceEntry[];
}

interface GetCircularReferencesResponse {
  iterativeCalculation?: unknown;
  worksheets?: unknown;
}

function diagnosticFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function coerceIterativeCalculation(
  raw: unknown
): IterativeCalculationSettings {
  const rec = asRecord(raw);
  return {
    enabled: rec?.enabled === true,
    maxIterations: diagnosticFiniteNumber(rec?.maxIterations)
      ? rec.maxIterations
      : 0,
    maxChange: diagnosticFiniteNumber(rec?.maxChange) ? rec.maxChange : 0,
  };
}

function coerceCircularReferences(raw: unknown): CircularReferenceEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((value) => {
    const rec = asRecord(value);
    const worksheetName =
      typeof rec?.worksheetName === "string" ? rec.worksheetName : "";
    const address = typeof rec?.address === "string" ? rec.address : "";
    return worksheetName && address ? [{ worksheetName, address }] : [];
  });
}

/**
 * Read circular-reference metadata from the targeted workbook.
 *
 * @param workbookName - Optional workbook target; omission uses the active workbook.
 * @param timeoutMs - Maximum bridge call duration.
 * @param signal - Optional caller cancellation signal.
 * @returns Iterative calculation settings and normalized worksheet references.
 * @throws When the bridge call fails or is cancelled.
 */
export async function getCircularReferencesViaIntents(
  workbookName?: string,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  signal?: AbortSignal
): Promise<GetCircularReferencesResult> {
  const requestContext = workbookName
    ? {
        type: "factset.spreadsheet.getCircularReferences",
        workbookName,
      }
    : { type: "fdc3.nothing" };
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetCircularReferences",
    requestContext,
    timeoutMs,
    undefined,
    signal
  )) as GetCircularReferencesResponse | undefined;
  return {
    iterativeCalculation: coerceIterativeCalculation(ctx?.iterativeCalculation),
    worksheets: coerceCircularReferences(ctx?.worksheets),
  };
}

/** One normalized cell error returned by the diagnostic intent. */
export interface CellErrorEntry {
  /** A1 address of the error cell. */
  address: string;
  /** Excel or caller-requested error marker. */
  error: string;
  /** Formula in the error cell, when supplied by the host. */
  formula?: string;
}

/** Error cells grouped under one worksheet. */
export interface WorksheetCellErrors {
  /** Worksheet containing the reported errors. */
  worksheetName: string;
  /** Standard Excel errors such as `#REF!` and `#VALUE!`. */
  excelErrors?: CellErrorEntry[];
  /** Additional caller-requested markers such as `#Calc`. */
  otherErrors?: CellErrorEntry[];
}

/** Normalized cell-error intent result. */
export interface GetCellErrorsResult {
  /** Host acknowledgement; absent acknowledgements retain catalog-compatible success. */
  success: boolean;
  /** Valid error groups returned by the host. */
  results: WorksheetCellErrors[];
  /** Optional host diagnostic when the operation was unsuccessful. */
  error?: string;
}

interface GetCellErrorsResponse {
  success?: unknown;
  results?: unknown;
  error?: unknown;
}

function coerceCellErrorList(raw: unknown): CellErrorEntry[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const entries = raw.flatMap((value) => {
    const rec = asRecord(value);
    const address = typeof rec?.address === "string" ? rec.address : "";
    const error = typeof rec?.error === "string" ? rec.error : "";
    const formula = typeof rec?.formula === "string" ? rec.formula : undefined;
    return address && error
      ? [{ address, error, ...(formula ? { formula } : {}) }]
      : [];
  });
  return entries.length > 0 ? entries : undefined;
}

function coerceCellErrorResults(raw: unknown): WorksheetCellErrors[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((value) => {
    const rec = asRecord(value);
    const worksheetName =
      typeof rec?.worksheetName === "string" ? rec.worksheetName : "";
    if (!worksheetName) return [];
    const excelErrors = coerceCellErrorList(rec?.excelErrors);
    const otherErrors = coerceCellErrorList(rec?.otherErrors);
    return [
      {
        worksheetName,
        ...(excelErrors ? { excelErrors } : {}),
        ...(otherErrors ? { otherErrors } : {}),
      },
    ];
  });
}

/**
 * Read standard Excel and selected host-specific cell errors.
 *
 * @param workbookName - Optional workbook target; omission uses the active workbook.
 * @param otherErrors - Additional exact error markers to scan.
 * @param timeoutMs - Maximum bridge call duration.
 * @param signal - Optional caller cancellation signal.
 * @returns Normalized worksheet error groups and host success state.
 * @throws When the bridge call fails or is cancelled.
 */
export async function getCellErrorsViaIntents(
  workbookName?: string,
  otherErrors?: readonly string[],
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  signal?: AbortSignal
): Promise<GetCellErrorsResult> {
  const hasFields = Boolean(workbookName) || Boolean(otherErrors?.length);
  const requestContext = hasFields
    ? {
        type: "factset.spreadsheet.getCellErrors",
        ...(workbookName ? { workbookName } : {}),
        ...(otherErrors?.length ? { otherErrors: Array.from(otherErrors) } : {}),
      }
    : { type: "fdc3.nothing" };
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetCellErrors",
    requestContext,
    timeoutMs,
    undefined,
    signal
  )) as GetCellErrorsResponse | undefined;
  return {
    success: ctx?.success !== false,
    results: coerceCellErrorResults(ctx?.results),
    ...(typeof ctx?.error === "string" && ctx.error
      ? { error: ctx.error }
      : {}),
  };
}

// ============================================================================
// factset.Spreadsheet.DeleteRange
// ============================================================================

/**
 * Build a diagnostic suffix from a host response that reported
 * `success=false`. Picks up any of the common host-side diagnostic fields
 * (`error`, `message`, `reason`) so the thrown error carries the *reason*
 * the host rejected the operation, not just the bare `success=false` flag.
 *
 * Returns `""` when no diagnostic fields are present, so callers can
 * unconditionally append the suffix without checking.
 */
function formatHostFailureSuffix(
  ctx: { error?: unknown; message?: unknown; reason?: unknown } | undefined
): string {
  if (!ctx) return "";
  const parts: string[] = [];
  for (const key of ["error", "message", "reason"] as const) {
    const value = ctx[key];
    if (value === undefined || value === null) continue;
    const str = typeof value === "string" ? value : JSON.stringify(value);
    if (!str || str === '""') continue;
    parts.push(`${key}=${str}`);
  }
  return parts.length === 0 ? "" : ` (${parts.join("; ")})`;
}

/**
 * Direction to shift remaining cells when deleting a range. Mirrors the
 * host's `shift` enum on `factset.spreadsheet.deleteRange`. Only the
 * `Shift*` variants are exposed today — `EntireRow`/`EntireColumn` would
 * be added here when Finster grows full-row/column delete semantics.
 */
export type DeleteRangeShift = "ShiftLeft" | "ShiftUp";

interface DeleteRangeResponse {
  type?: string;
  success?: unknown;
  error?: unknown;
  message?: unknown;
  reason?: unknown;
}

/**
 * Delete a range and shift surrounding cells via
 * `factset.Spreadsheet.DeleteRange`.
 *
 * Resolves on host success; rejects if the transport is unavailable, the
 * host replies with an error, or the timeout elapses. The host's `success`
 * flag is checked defensively — a falsy value is treated as failure even
 * when the envelope itself is well-formed.
 */
export async function deleteRangeViaIntents(
  address: RangeAddress,
  shift?: DeleteRangeShift,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.DeleteRange",
    {
      type: "factset.spreadsheet.deleteRange",
      address,
      ...(shift !== undefined ? { shift } : {}),
    },
    timeoutMs
  )) as DeleteRangeResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `DeleteRange: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.InsertRange
// ============================================================================

/**
 * Direction to shift existing cells when inserting blank cells into a
 * range. Mirrors the host's `shift` enum on
 * `factset.spreadsheet.insertRange`. Only the `Shift*` variants are
 * exposed today — `EntireRow`/`EntireColumn` would be added here when
 * Finster grows full-row/column insert semantics.
 */
export type InsertRangeShift = "ShiftRight" | "ShiftDown";

/**
 * Which adjacent cells `InsertRange` should copy formatting from. Mirrors
 * the host's `copyOrigin` enum.
 */
export type InsertRangeCopyOrigin = "LeftOrAbove" | "RightOrBelow";

/**
 * Optional fields for `InsertRange` beyond the required `address`.
 */
export interface InsertRangeOptions {
  shift?: InsertRangeShift;
  copyOrigin?: InsertRangeCopyOrigin;
}

interface InsertRangeResponse {
  type?: string;
  success?: unknown;
  error?: unknown;
  message?: unknown;
  reason?: unknown;
}

/**
 * Insert blank cells into a range and shift existing cells via
 * `factset.Spreadsheet.InsertRange`.
 *
 * Resolves on host success; rejects if the transport is unavailable, the
 * host replies with an error, or the timeout elapses. The host's `success`
 * flag is checked defensively (see `deleteRangeViaIntents`).
 */
export async function insertRangeViaIntents(
  address: RangeAddress,
  options?: InsertRangeOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.InsertRange",
    {
      type: "factset.spreadsheet.insertRange",
      address,
      ...(options?.shift !== undefined ? { shift: options.shift } : {}),
      ...(options?.copyOrigin !== undefined
        ? { copyOrigin: options.copyOrigin }
        : {}),
    },
    timeoutMs
  )) as InsertRangeResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `InsertRange: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.ClearRange
// ============================================================================

/**
 * Which property family `ClearRange` removes from the target range. Mirrors
 * the host's `clearType` enum on `factset.spreadsheet.clearRange.options`.
 * Host default is `"All"` (clears every property family in one pass).
 */
export type ClearRangeClearType =
  | "Comments"
  | "Contents"
  | "Formats"
  | "Hyperlinks"
  | "Notes"
  | "Outline"
  | "All";

/**
 * Optional fields for `ClearRange` beyond the required `range`.
 */
export interface ClearRangeOptions {
  clearType?: ClearRangeClearType;
}

interface ClearRangeResponse {
  type?: string;
  success?: unknown;
  error?: unknown;
  message?: unknown;
  reason?: unknown;
}

/**
 * Clear contents / formats / etc. from a range via
 * `factset.Spreadsheet.ClearRange`.
 *
 * Pure in-place clear: cells stay, surrounding cells don't shift. Distinct
 * from `DeleteRange` which removes the cells and shifts neighbours into the
 * gap. Use this for "wipe" semantics — `RangeModification.action = "clear"`
 * is the planner-side surface.
 *
 * Wire-shape note: the host expects the address payload under the field
 * name `range` (a `RangeReferenceAddress`), NOT `address` like
 * `DeleteRange` / `InsertRange`. The wrapper handles the rename — callers
 * pass a `RangeAddress` value and it's serialised correctly.
 *
 * Resolves on host success; rejects if the transport is unavailable, the
 * host replies with an error, or the timeout elapses. The host's `success`
 * flag is checked defensively (mirrors `deleteRangeViaIntents`).
 */
export async function clearRangeViaIntents(
  range: RangeAddress,
  options?: ClearRangeOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.ClearRange",
    {
      type: "factset.spreadsheet.clearRange",
      range,
      ...(options?.clearType !== undefined
        ? { options: { clearType: options.clearType } }
        : {}),
    },
    timeoutMs
  )) as ClearRangeResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `ClearRange: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.SortRange
// ============================================================================

/**
 * Sort direction for an individual `SortRangeKey`. Mirrors the host's
 * `order` enum; defaults to `"Ascending"` when omitted.
 */
export type SortRangeOrder = "Ascending" | "Descending";

/**
 * Treat-text-as-number policy for an individual `SortRangeKey`. Mirrors the
 * host's `dataOption` enum; defaults to `"Normal"` when omitted.
 */
export type SortRangeDataOption = "Normal" | "TextAsNumbers";

/**
 * Header detection policy for `SortRange.options.header`. Mirrors the host's
 * `header` enum; defaults to `"Guess"` when omitted.
 */
export type SortRangeHeader = "Guess" | "Yes" | "No";

/**
 * Sort orientation for `SortRange.options.orientation`. `"TopToBottom"`
 * sorts rows by the values in the key column; `"LeftToRight"` sorts
 * columns by the values in the key row. Defaults to `"TopToBottom"`.
 */
export type SortRangeOrientation = "TopToBottom" | "LeftToRight";

/**
 * Chinese-character sort method for `SortRange.options.sortMethod`. Mirrors
 * the host's `sortMethod` enum; defaults to `"PinYin"`.
 */
export type SortRangeMethod = "PinYin" | "Stroke";

/**
 * One sort key. `key` is a cell reference inside the sort column or row
 * (e.g. `"B1"` for the first column of `A1:D20`); no sheet prefix. The
 * host accepts 1–3 keys per `SortRange` call; the wrapper rejects empty
 * arrays and arrays with more than 3 entries before the host call.
 */
export interface SortRangeKey {
  key: string;
  order?: SortRangeOrder;
  dataOption?: SortRangeDataOption;
}

/**
 * Optional fields for `SortRange` beyond the required `range` and
 * `sortKeys`. Mirrors `factset.spreadsheet.sortRange.options`.
 */
export interface SortRangeOptions {
  header?: SortRangeHeader;
  matchCase?: boolean;
  orientation?: SortRangeOrientation;
  sortMethod?: SortRangeMethod;
}

interface SortRangeResponse {
  type?: string;
  success?: unknown;
  error?: unknown;
  message?: unknown;
  reason?: unknown;
}

/**
 * Sort a non-Pivot-Table range via `factset.Spreadsheet.SortRange`.
 *
 * Wire-shape note: address payload field is `range` (a
 * `RangeReferenceAddress`), not `address` like `DeleteRange` / `InsertRange`.
 *
 * Validates `sortKeys.length` is in `1..3` before dispatching — the catalog
 * spec caps multi-key sort at 3 entries and zero-length is meaningless.
 *
 * Resolves on host success; rejects if the transport is unavailable, the
 * host replies with an error, the timeout elapses, or the wrapper's input
 * validation fails. The host's `success` flag is checked defensively
 * (mirrors `deleteRangeViaIntents`).
 */
export async function sortRangeViaIntents(
  range: RangeAddress,
  sortKeys: SortRangeKey[],
  options?: SortRangeOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  if (!Array.isArray(sortKeys) || sortKeys.length === 0 || sortKeys.length > 3) {
    throw new Error(
      `SortRange: sortKeys must have 1–3 entries (got ${sortKeys?.length ?? 0})`
    );
  }
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.SortRange",
    {
      type: "factset.spreadsheet.sortRange",
      range,
      sortKeys,
      ...(options ? { options } : {}),
    },
    timeoutMs
  )) as SortRangeResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `SortRange: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.ApplyFormatting
// ============================================================================

/**
 * Font sub-object on `ApplyFormattingProperties.font`. Mirrors the host's
 * `Range.Font` shape on the wire. All fields optional — the host applies
 * only the fields you set and leaves the rest untouched.
 *
 * Numeric value spaces match Excel COM constants (`XlUnderlineStyle`,
 * `XlColorIndex`, `XlThemeColor`):
 *   - `underline`: `2` = single, `-4119` = double, `-4142` = none,
 *     `4` = single accounting, `5` = double accounting.
 *   - `color`: BGR-encoded 24-bit integer (`(B<<16)|(G<<8)|R`). Hex
 *     `#RRGGBB` from the planner schema is converted in `excelHostOps.ts`.
 *   - `colorIndex` / `themeColor`: Excel's palette / theme enums (`-4105`
 *     = automatic for `colorIndex`).
 *   - `tintAndShade`: `[-1, 1]` float; `0` = no tint.
 */
export interface ApplyFormattingFont {
  bold?: boolean;
  italic?: boolean;
  strikethrough?: boolean;
  subscript?: boolean;
  superscript?: boolean;
  size?: number;
  color?: number;
  colorIndex?: number;
  name?: string;
  underline?: number;
  themeColor?: number;
  tintAndShade?: number;
}

/**
 * One stop in a fill gradient (`linearGradient` / `rectangularGradient`).
 *  - `position`: `[0, 1]` along the gradient axis.
 *  - `color` / `themeColor` / `tintAndShade`: same conventions as
 *    `ApplyFormattingFont`.
 */
export interface ApplyFormattingGradientStop {
  position: number;
  color?: number;
  tintAndShade?: number;
  themeColor?: number;
}

/**
 * Linear gradient sub-object on `ApplyFormattingInterior.linearGradient`.
 * `degree` is the gradient angle in degrees.
 */
export interface ApplyFormattingLinearGradient {
  degree?: number;
  colorStops?: ApplyFormattingGradientStop[];
}

/**
 * Rectangular gradient sub-object on `ApplyFormattingInterior.rectangularGradient`.
 * Each `rectangle*` value is `[0, 1]` describing the inner rectangle's edges.
 */
export interface ApplyFormattingRectangularGradient {
  rectangleLeft?: number;
  rectangleRight?: number;
  rectangleTop?: number;
  rectangleBottom?: number;
  colorStops?: ApplyFormattingGradientStop[];
}

/**
 * Interior (cell fill) sub-object on `ApplyFormattingProperties.interior`.
 * Mirrors the host's `Range.Interior` shape. `pattern` uses Excel's
 * `XlPattern` enum (`1` = solid, `-4142` = none).
 */
export interface ApplyFormattingInterior {
  color?: number;
  colorIndex?: number;
  pattern?: number;
  patternColor?: number;
  patternColorIndex?: number;
  tintAndShade?: number;
  themeColor?: number;
  patternTintAndShade?: number;
  patternThemeColor?: number;
  linearGradient?: ApplyFormattingLinearGradient;
  rectangularGradient?: ApplyFormattingRectangularGradient;
}

/**
 * One border edge on `ApplyFormattingBorders.{edge*,inside*,diagonal*}`.
 *
 *   - `lineStyle`: `XlLineStyle` enum (`1` = continuous, `-4115` = dash,
 *     `-4118` = dot, `4` = dash-dot, `5` = dash-dot-dot, `-4119` = double,
 *     `13` = slant-dash-dot, `-4142` = none).
 *   - `weight`: `XlBorderWeight` enum (`1` = hairline, `2` = thin,
 *     `-4138` = medium, `4` = thick).
 */
export interface ApplyFormattingBorder {
  color?: number;
  colorIndex?: number;
  lineStyle?: number;
  weight?: number;
  tintAndShade?: number;
  themeColor?: number;
}

/**
 * Borders sub-object on `ApplyFormattingProperties.borders`. Each named
 * field targets one of Excel's seven border positions plus the two
 * diagonals.
 */
export interface ApplyFormattingBorders {
  edgeLeft?: ApplyFormattingBorder;
  edgeRight?: ApplyFormattingBorder;
  edgeTop?: ApplyFormattingBorder;
  edgeBottom?: ApplyFormattingBorder;
  insideHorizontal?: ApplyFormattingBorder;
  insideVertical?: ApplyFormattingBorder;
  diagonalDown?: ApplyFormattingBorder;
  diagonalUp?: ApplyFormattingBorder;
}

/**
 * Range-level formatting payload accepted by `factset.Spreadsheet.ApplyFormatting`.
 * Mirrors the host's combined `Range.{Style,Interior,Font,Borders,…}` surface
 * verbatim — every field is optional and the host applies only what's set.
 *
 * Alignment fields use Excel's `XlHAlign` / `XlVAlign` enums:
 *   - `horizontalAlignment`: `1` = general, `-4131` = left, `-4108` = center,
 *     `-4152` = right, `5` = fill, `-4130` = justify.
 *   - `verticalAlignment`: `-4160` = top, `-4108` = center, `-4107` = bottom,
 *     `-4130` = justify.
 *   - `orientation`: text rotation in degrees (`-90..90`) or `xlVertical = -4166`.
 */
export interface ApplyFormattingProperties {
  styleName?: string;
  fillColor?: number;
  interior?: ApplyFormattingInterior;
  font?: ApplyFormattingFont;
  borders?: ApplyFormattingBorders;
  horizontalAlignment?: number;
  verticalAlignment?: number;
  orientation?: number;
  indentLevel?: number;
  wrapText?: boolean;
  shrinkToFit?: boolean;
  rowHeight?: number;
  columnWidth?: number;
  numberFormat?: string;
  numberFormatLocal?: string;
  rowHidden?: boolean;
  columnHidden?: boolean;
  locked?: boolean;
}

/**
 * One range-formatting operation in an `ApplyFormatting.items[]` payload.
 * The host applies `formatting` to `range` then moves to the next item; a
 * single call can format many disjoint ranges in one round-trip.
 */
export interface ApplyFormattingItem {
  range: RangeAddress;
  formatting: ApplyFormattingProperties;
}

interface ApplyFormattingResponse {
  type?: string;
  errors?: unknown[];
}

/**
 * Apply font / fill / border / alignment / number-format / sizing
 * properties to one or more ranges via `factset.Spreadsheet.ApplyFormatting`.
 *
 * The host's response context carries an `errors[]` array (empty on full
 * success). We pass it back to the caller verbatim — the dispatcher decides
 * whether a non-empty `errors` is fatal or just a warn. Rejects only when
 * the transport itself fails (timeout, host-level error envelope).
 *
 * Reachable via `RangeModification.action = "format"` (and as a follow-up
 * after `"fill"` when the modification carries a `format` payload).
 */
export async function applyFormattingViaIntents(
  items: ApplyFormattingItem[],
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<unknown[]> {
  if (!Array.isArray(items) || items.length === 0) {
    throw new Error("ApplyFormatting: items[] must have at least one entry");
  }
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.ApplyFormatting",
    {
      type: "factset.spreadsheet.applyFormatting",
      items,
    },
    timeoutMs
  )) as ApplyFormattingResponse | undefined;
  return Array.isArray(ctx?.errors) ? ctx!.errors : [];
}

// ============================================================================
// factset.Spreadsheet.MoveRange
// ============================================================================

/**
 * Optional fields for `MoveRange` beyond the required `source` and
 * `destination`.
 *
 *  - `preserveSource: true` → copy semantics (source cells remain populated
 *    after the move, both ranges hold the same values).
 *  - `preserveSource: false` (or omitted) → cut-and-paste semantics
 *    (default; source cells are cleared, destination receives them).
 *
 * Captured wire shape carries `preserveSource` under an `options` object;
 * this wrapper handles the nesting so callers pass a flat record.
 */
export interface MoveRangeOptions {
  preserveSource?: boolean;
}

interface MoveRangeResponse {
  type?: string;
  success?: unknown;
  error?: unknown;
  message?: unknown;
  reason?: unknown;
}

/**
 * Move (or copy, when `preserveSource: true`) the cells of `source` into
 * `destination` via `factset.Spreadsheet.MoveRange`.
 *
 * Both `source` and `destination` are full `RangeAddress` records — sheet
 * targeting via `worksheetName` is supported on both sides, so callers
 * can move cells between sheets in the same `MoveRange` call.
 * `destination` is interpreted as the **top-left anchor** of the moved
 * block (a cell address like `"D1"`); the host expands it to match
 * `source`'s shape.
 *
 * Wire-shape note: the request fields are `source` / `destination` /
 * `options.preserveSource` (not `address` / `address2` like some other
 * range intents). Captured response is
 * `{ type: "factset.spreadsheet.moveRangeResult", success: true }`; we
 * defensively check the `success` flag the same way `deleteRangeViaIntents`
 * does. Captured payload pair lives in `excel-docs/intent-details.md`.
 *
 * Resolves on host success; rejects if the transport is unavailable, the
 * host replies with an error, the timeout elapses, or the host's
 * `success` flag comes back falsy.
 */
export async function moveRangeViaIntents(
  source: RangeAddress,
  destination: RangeAddress,
  options?: MoveRangeOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.MoveRange",
    {
      type: "factset.spreadsheet.moveRange",
      source,
      destination,
      ...(options?.preserveSource !== undefined
        ? { options: { preserveSource: options.preserveSource } }
        : {}),
    },
    timeoutMs
  )) as MoveRangeResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `MoveRange: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.MoveSheet
// ============================================================================

/**
 * Optional fields for `MoveSheet` beyond the required `newIndex` (the
 * target position). All fields land under the wire `options` object.
 *
 *  - `worksheetName`  — which sheet to move. Omit to move the active sheet.
 *  - `sourceWorkbookName` — source workbook (active by default).
 *  - `targetWorkbookName` — destination workbook for cross-workbook moves.
 *    When equal to `sourceWorkbookName` (or both omitted) the sheet just
 *    re-orders within its own workbook.
 *  - `selectSheet: true` — activate the moved sheet at its new home.
 *
 * **Indexing note.** `newIndex` is **0-based** as captured (e.g. `0` puts
 * the sheet at the leftmost tab). This matches FactSet's pre-announced
 * 0-based migration; the field type and semantics won't change when the
 * other index-bearing intents flip. See
 * `f2_docs/excel/finster-docs/missing-intents-v0.md` →
 * `#open-zero-index-migration`.
 *
 * **Catalog status (2026-05-04 refresh):** the public `excel-tools.md`
 * row marks `MoveSheet` as ⚗️ Experimental. Wire shape captured here is
 * accepted on the host; promote the catalog row when the host removes
 * the experimental marker.
 */
export interface MoveSheetOptions {
  worksheetName?: string;
  sourceWorkbookName?: string;
  targetWorkbookName?: string;
  selectSheet?: boolean;
}

/**
 * Response payload returned on `MoveSheet` success. Mirrors the host's
 * `factset.spreadsheet.moveSheetResult` context verbatim — useful when
 * the host normalises the requested index (`newIndex` may differ from
 * the requested value if e.g. it had to be clamped) or when the move
 * crossed workbooks (so callers can reconcile `originalWorkbookName`
 * vs `newWorkbookName`).
 */
export interface MoveSheetResult {
  name: string;
  originalIndex: number;
  newIndex: number;
  originalWorkbookName: string;
  newWorkbookName: string;
}

interface MoveSheetResponse {
  type?: string;
  success?: unknown;
  error?: unknown;
  message?: unknown;
  reason?: unknown;
  name?: unknown;
  originalIndex?: unknown;
  newIndex?: unknown;
  originalWorkbookName?: unknown;
  newWorkbookName?: unknown;
}

/**
 * Move a worksheet to a new position via `factset.Spreadsheet.MoveSheet`.
 *
 * Reorders sheets in the active workbook, OR (when
 * `options.targetWorkbookName !== options.sourceWorkbookName`) moves the
 * sheet into a different open workbook. `newIndex` is 0-based on the
 * destination workbook. The host's response carries the actually-applied
 * indices and workbook names; the wrapper returns them so callers can
 * surface drift in their `trackOperation` log.
 *
 * Resolves with the parsed `MoveSheetResult` on host success; rejects
 * if the transport is unavailable, the host replies with an error, the
 * timeout elapses, or the host's `success` flag comes back falsy.
 */
export async function moveSheetViaIntents(
  newIndex: number,
  options?: MoveSheetOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<MoveSheetResult> {
  if (!Number.isInteger(newIndex) || newIndex < 0) {
    throw new Error(
      `MoveSheet: newIndex must be a non-negative integer (got ${newIndex})`
    );
  }
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.MoveSheet",
    {
      type: "factset.spreadsheet.moveSheet",
      newIndex,
      ...(options ? { options } : {}),
    },
    timeoutMs
  )) as MoveSheetResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `MoveSheet: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
  return {
    name: typeof ctx?.name === "string" ? ctx.name : "",
    originalIndex:
      typeof ctx?.originalIndex === "number" ? ctx.originalIndex : -1,
    newIndex: typeof ctx?.newIndex === "number" ? ctx.newIndex : newIndex,
    originalWorkbookName:
      typeof ctx?.originalWorkbookName === "string"
        ? ctx.originalWorkbookName
        : "",
    newWorkbookName:
      typeof ctx?.newWorkbookName === "string" ? ctx.newWorkbookName : "",
  };
}

// ============================================================================
// factset.Spreadsheet.AddWorksheet
// ============================================================================

/**
 * Per-call options for `AddWorksheet`. All fields optional; an empty /
 * undefined object falls back to `fdc3.nothing` (host-default name,
 * append-at-end, active workbook).
 *
 *  - `worksheetName` — requested name; the host may sanitise / truncate.
 *    Applied name is not returned; read it back via `getWorkbookSheets()`.
 *  - `workbookName` — target a non-active workbook (matched against
 *    `getOpenWorkbooks().workbooks[].name`).
 *  - `index` — 1-based insertion position today; omit to append at end.
 *    A pre-announced FactSet 0-based migration will flip this to 0-based;
 *    type is unchanged. See `missing-intents-v0.md #open-zero-index-migration`.
 *  - `selectWorksheet: true` — activate the new sheet after creation.
 *
 * Catalog status: `excel-tools.md` `AddWorksheet` still documents only
 * the legacy `fdc3.nothing` input. The typed shape this wrapper sends is
 * accepted on the wire but undocumented — see `missing-intents-v0.md`
 * §2.2 + `#open-addworksheet-params`.
 */
export interface AddWorksheetOptions {
  worksheetName?: string;
  workbookName?: string;
  index?: number;
  selectWorksheet?: boolean;
}

interface AddWorksheetResponse {
  type?: string;
  message?: unknown;
}

/**
 * Add a new worksheet via `factset.Spreadsheet.AddWorksheet`.
 *
 * When `options` carries any field, the wrapper sends a typed request
 * (`type: "factset.spreadsheet.addWorksheet"`) so the host honours name,
 * position, and target-workbook fields. Otherwise it sends
 * `fdc3.nothing` for backwards compatibility with hosts that haven't
 * shipped the options shape yet (and to keep parameterless callers
 * untouched). Returns the host's `message` string (or `""`).
 */
export async function addWorksheetViaIntents(
  options?: AddWorksheetOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<string> {
  const hasFields =
    options !== undefined &&
    (options.worksheetName !== undefined ||
      options.workbookName !== undefined ||
      options.index !== undefined ||
      options.selectWorksheet !== undefined);
  const requestContext: Record<string, unknown> = hasFields
    ? {
        type: "factset.spreadsheet.addWorksheet",
        options: {
          ...(options.worksheetName !== undefined
            ? { worksheetName: options.worksheetName }
            : {}),
          ...(options.workbookName !== undefined
            ? { workbookName: options.workbookName }
            : {}),
          ...(options.index !== undefined ? { index: options.index } : {}),
          ...(options.selectWorksheet !== undefined
            ? { selectWorksheet: options.selectWorksheet }
            : {}),
        },
      }
    : { type: "fdc3.nothing" };
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.AddWorksheet",
    requestContext,
    timeoutMs
  )) as AddWorksheetResponse | undefined;
  return typeof ctx?.message === "string" ? ctx.message : "";
}

// ============================================================================
// factset.Spreadsheet.ActivateWorksheet
// ============================================================================

/**
 * Per-call options for `ActivateWorksheet`. `workbookName` targets a
 * non-active workbook; omit to operate on the active one.
 */
export interface ActivateWorksheetOptions {
  workbookName?: string;
}

interface ActivateWorksheetResponse {
  type?: string;
  success?: unknown;
  message?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Activate a worksheet by name via `factset.Spreadsheet.ActivateWorksheet`.
 *
 * The response shape isn't yet captured in `intent-details.md`, so the
 * wrapper accepts both `{ success: ... }` and `{ message: ... }` styles
 * defensively. A `success === false` is treated as failure with the
 * shared diagnostic-suffix formatter.
 */
export async function activateWorksheetViaIntents(
  worksheetName: string,
  options?: ActivateWorksheetOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.ActivateWorksheet",
    {
      type: "factset.spreadsheet.activateWorksheet",
      worksheetName,
      ...(options?.workbookName !== undefined
        ? { options: { workbookName: options.workbookName } }
        : {}),
    },
    timeoutMs
  )) as ActivateWorksheetResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `ActivateWorksheet: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.DeleteWorksheet
// ============================================================================

/**
 * Per-call options for `DeleteWorksheet`.
 *
 *  - `workbookName` targets a non-active workbook.
 *  - `displayAlerts: false` suppresses the Excel "are you sure?" modal
 *    and is required for non-interactive automation; otherwise the host
 *    blocks waiting for the user.
 */
export interface DeleteWorksheetOptions {
  workbookName?: string;
  displayAlerts?: boolean;
}

interface DeleteWorksheetResponse {
  type?: string;
  success?: unknown;
  message?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Delete a worksheet by name via `factset.Spreadsheet.DeleteWorksheet`.
 *
 * Defaults to `displayAlerts: false` because Finster is non-interactive;
 * callers that need the host modal can pass `displayAlerts: true`
 * explicitly. Deleting the only remaining sheet is host-rejected.
 */
export async function deleteWorksheetViaIntents(
  worksheetName: string,
  options?: DeleteWorksheetOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const optionsBlock: Record<string, unknown> = {
    displayAlerts: options?.displayAlerts ?? false,
    ...(options?.workbookName !== undefined
      ? { workbookName: options.workbookName }
      : {}),
  };
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.DeleteWorksheet",
    {
      type: "factset.spreadsheet.deleteWorksheet",
      worksheetName,
      options: optionsBlock,
    },
    timeoutMs
  )) as DeleteWorksheetResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `DeleteWorksheet: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.RenameWorksheet
// ============================================================================

/**
 * Per-call options for `RenameWorksheet`. `workbookName` targets a
 * non-active workbook; omit to operate on the active one.
 */
export interface RenameWorksheetOptions {
  workbookName?: string;
}

interface RenameWorksheetResponse {
  type?: string;
  success?: unknown;
  message?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Rename a worksheet via `factset.Spreadsheet.RenameWorksheet`.
 *
 * The host enforces Excel naming rules (31-char cap, no `: \ / ? * [ ]`)
 * and may sanitise `newName` rather than rejecting it. Read the post-
 * rename sheet list via `getWorkbookSheets()` when the applied name
 * matters to the caller (e.g. for verifier prompts).
 */
export async function renameWorksheetViaIntents(
  worksheetName: string,
  newName: string,
  options?: RenameWorksheetOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.RenameWorksheet",
    {
      type: "factset.spreadsheet.renameWorksheet",
      worksheetName,
      newName,
      ...(options?.workbookName !== undefined
        ? { options: { workbookName: options.workbookName } }
        : {}),
    },
    timeoutMs
  )) as RenameWorksheetResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `RenameWorksheet: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.SetWorksheetVisibility
// ============================================================================
//
// Show, hide, or very-hide a worksheet. Per-sheet sibling of the
// per-row / per-column `HideColumns` / `HideRows` / `ShowColumns` /
// `ShowRows` family — operates on whole worksheets rather than on
// row/column ranges.
//
// **Cataloged 2026-05-12 (PR #55)** in `f2_docs/excel/excel-tools.md`.
//
// `"VeryHidden"` cannot be un-hidden through the Excel UI — only
// programmatically by another `SetWorksheetVisibility` call with
// `visibility: "Visible"`. The host accepts the three Excel-native
// states (`Visible` / `Hidden` / `VeryHidden`) verbatim; map planner
// payloads through these strings rather than e.g. a boolean.
//
// Wire shape (per excel-tools.md, captured 2026-05-12):
//
//   request : {
//     type: "factset.spreadsheet.setWorksheetVisibility",
//     worksheetName?: <string>,   // optional 2026-05-12; defaults to active sheet
//     visibility: "Visible" | "Hidden" | "VeryHidden",
//     options?: { workbookName?: <string> }
//   }
//   response: { success: true }
//
// Routing: `appId: "OI Remote Iframe"` per the catalog row
// (mandatory; the wrapper passes `SET_WORKSHEET_VISIBILITY_APP_ID`
// on the `raiseIntent` call).

const SET_WORKSHEET_VISIBILITY_APP_ID = { appId: "OI Remote Iframe" };

/** Excel-native worksheet visibility states, as the host accepts them. */
export type WorksheetVisibility = "Visible" | "Hidden" | "VeryHidden";

/**
 * Per-call options for `SetWorksheetVisibility`. `workbookName`
 * targets a non-active workbook; omit to operate on the active one.
 */
export interface SetWorksheetVisibilityOptions {
  workbookName?: string;
}

interface SetWorksheetVisibilityResponse {
  type?: string;
  success?: unknown;
  message?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Set the visibility of a worksheet via
 * `factset.Spreadsheet.SetWorksheetVisibility`.
 *
 * `worksheetName` is **optional** (host change 2026-05-12) — omit to
 * operate on the active worksheet of the active workbook. Pass an
 * explicit name to target a specific sheet.
 *
 * `"VeryHidden"` removes the sheet from the Excel UI entirely (no
 * right-click "Unhide…" surface) — only another programmatic
 * `SetWorksheetVisibility` to `"Visible"` brings it back. Use
 * `"Hidden"` when the user should be able to unhide via the ribbon.
 *
 * Cataloged 2026-05-12 (PR #55). Routed through
 * `appId: "OI Remote Iframe"` (same constraint as the sibling
 * `SelectRange` / `GetRangeSnapshot` / `GetWorksheetsAsBase64`
 * wrappers).
 */
export async function setWorksheetVisibilityViaIntents(
  worksheetName: string | undefined,
  visibility: WorksheetVisibility,
  options?: SetWorksheetVisibilityOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.SetWorksheetVisibility",
    {
      type: "factset.spreadsheet.setWorksheetVisibility",
      ...(worksheetName !== undefined ? { worksheetName } : {}),
      visibility,
      ...(options?.workbookName !== undefined
        ? { options: { workbookName: options.workbookName } }
        : {}),
    },
    timeoutMs,
    SET_WORKSHEET_VISIBILITY_APP_ID
  )) as SetWorksheetVisibilityResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `SetWorksheetVisibility: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.AddWorkbook
// ============================================================================

interface AddWorkbookResponse {
  type?: string;
  message?: unknown;
}

/**
 * Add a new workbook via `factset.Spreadsheet.AddWorkbook`. Returns the
 * host's `message` (or `""`).
 */
export async function addWorkbookViaIntents(
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<string> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.AddWorkbook",
    { type: "fdc3.nothing" },
    timeoutMs
  )) as AddWorkbookResponse | undefined;
  return typeof ctx?.message === "string" ? ctx.message : "";
}

// ============================================================================
// factset.Spreadsheet.CopyWorksheet
// ============================================================================

/**
 * Options for `CopyWorksheet`. Pass `undefined` (or an empty object) to
 * copy the active sheet with the host's default placement — the wrapper
 * sends `fdc3.nothing` in that case.
 */
export interface CopyWorksheetOptions {
  sheetName?: string;
  location?: "before" | "after";
}

interface CopyWorksheetResponse {
  type?: string;
  result?: unknown;
}

/**
 * Copy a worksheet via `factset.Spreadsheet.CopyWorksheet`. Returns the
 * raw `result` payload (opaque — caller narrows at the use site).
 */
export async function copyWorksheetViaIntents(
  options?: CopyWorksheetOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<unknown> {
  const hasFields =
    options !== undefined &&
    (options.sheetName !== undefined || options.location !== undefined);
  const requestContext: Record<string, unknown> = hasFields
    ? {
        type: "factset.spreadsheet.copyWorksheet",
        ...(options.sheetName !== undefined ? { sheetName: options.sheetName } : {}),
        ...(options.location !== undefined ? { location: options.location } : {}),
      }
    : { type: "fdc3.nothing" };
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.CopyWorksheet",
    requestContext,
    timeoutMs
  )) as CopyWorksheetResponse | undefined;
  return ctx?.result;
}

// ============================================================================
// factset.Spreadsheet.InsertWorksheetsFromBase64
// ============================================================================

/**
 * Options for `InsertWorksheetsFromBase64`'s `addWorkbook` toggle —
 * mirrors the doc's `addWorkbook.{enabled,name}` shape.
 */
export interface InsertWorksheetsAddWorkbook {
  enabled?: boolean;
  name?: string;
}

/**
 * Options accepted by `InsertWorksheetsFromBase64`: `workbookName` is nested
 * under the wire `options` field, while `addWorkbook` is serialized at the
 * request root. Some deployed hosts reject combined create-and-insert, so the
 * native-chart path uses `AddWorkbook` followed by a targeted insertion.
 */
export interface InsertWorksheetsFromBase64Options {
  /** Target workbook name; defaults to the active workbook when omitted. */
  workbookName?: string;
  addWorkbook?: InsertWorksheetsAddWorkbook;
}

interface InsertWorksheetsFromBase64Response {
  type?: string;
  sheetNames?: unknown;
}

/**
 * Insert worksheets from a base64-encoded `.xlsx` file via
 * `factset.Spreadsheet.InsertWorksheetsFromBase64`. Returns the host's
 * `sheetNames` array (or `[]` if missing/non-array).
 *
 * `workbookName` belongs under `options`; `addWorkbook` remains a root field,
 * matching the documented request example and the original live host
 * contract. Sending `addWorkbook` in both places causes some host builds to
 * reject the request with `InternalError`.
 */
export async function insertWorksheetsFromBase64ViaIntents(
  base64File: string,
  options: InsertWorksheetsFromBase64Options = {},
  timeoutMs: number = LONG_RUNNING_FACTSET_INTENT_TIMEOUT_MS
): Promise<string[]> {
  const optionsField: Record<string, unknown> = {};
  if (options.workbookName) optionsField.workbookName = options.workbookName;
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.InsertWorksheetsFromBase64",
    {
      type: "factset.spreadsheet.insertWorksheetsFromBase64",
      base64File,
      ...(options.addWorkbook ? { addWorkbook: options.addWorkbook } : {}),
      ...(Object.keys(optionsField).length > 0 ? { options: optionsField } : {}),
    },
    timeoutMs
  )) as InsertWorksheetsFromBase64Response | undefined;
  const raw = ctx?.sheetNames;
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === "string");
}

// ============================================================================
// factset.Spreadsheet.AddActiveGraph
// ============================================================================

interface AddActiveGraphResponse {
  type?: string;
  message?: unknown;
}

/**
 * Optional positioning for the inserted ActiveGraph. Pixel-ish units,
 * relative to the active sheet's top-left corner (0,0). The wrapper
 * default — `320x200`, a roughly 8:5 landscape — is intentionally
 * compact (about 5 default-Excel columns wide × 10 default-Excel
 * rows tall) so the chart lives alongside cell content rather than
 * blanketing the worksheet. Chat-path callers can override every
 * field; see {@link AddActiveGraphCoords} for the override slots.
 *
 * **Per-chart-type overrides ride on top.**
 * `insertChatChartIntoWorkbookViaIntents` (in `excelService.ts`)
 * inspects the chart payload before calling `addActiveGraph` and
 * swaps in a near-square footprint (`260x220`) for pie / segment-
 * breakdown FactSet types (`FIN_REV_BUS_SEG`, `FIN_REV_GEO_SEG`) where
 * a wide letterbox would crop the slice labels. The chat-path LLM
 * can also emit `chart.position` to size and place the chart based on
 * workbook context — guidelines live in the `answerWorkbookQuestion`
 * prompt block in `excelAIService.ts`. Other callers that already
 * know their chart type are encouraged to pass coords explicitly
 * rather than rely on the landscape default.
 *
 * **Pixel-to-cell rule of thumb** (default Excel column width 64px,
 * row height 20px): `left = (col_index_zero_based) * 64`,
 * `top = (row_index_zero_based) * 20`. So column G ≈ `left=384`,
 * row 22 ≈ `top=420`. The chat prompt block carries the same table
 * so the LLM can anchor charts to the user's data programmatically.
 *
 * **Sizing history.** Earlier defaults all felt wrong: the captured
 * payload's `800x200` (4:1) squished the series lines and stretched
 * the chart across most of the visible window; `800x480` (5:3)
 * over-corrected and took over the sheet; `800x320` (2.5:1) and
 * `560x320` (7:4) were still too wide for the typical compact layout
 * the chat path drops charts into; `440x260` (12:7) still felt too
 * big in practice. `320x200` is the current compact default — the
 * agent should size up for dense / multi-series / long-history
 * charts via `chart.position`.
 */
export interface AddActiveGraphCoords {
  /** Top edge of the chart (pixel-ish). Default `100`. */
  top?: number;
  /** Left edge of the chart (pixel-ish). Default `5`. */
  left?: number;
  /** Width of the chart (pixel-ish). Default `320`. */
  width?: number;
  /** Height of the chart (pixel-ish). Default `200`. */
  height?: number;
}

const DEFAULT_ACTIVE_GRAPH_COORDS: Required<AddActiveGraphCoords> = {
  top: 100,
  left: 5,
  width: 320,
  height: 200,
};

/**
 * Encode every literal `,` in an FDSChart XML body as the FactSet-
 * custom `&cmma` entity reference so the FDSChart control's downstream
 * parser can data-bind the chart correctly.
 *
 * **Why this exists.** `factset.PreviewChart` returns `fdsChartXml`
 * with raw commas in formula / style / data fields (e.g.
 * `FG_PRICE(<Var ID="SD1">-5AY</Var>,<Var ID="ED1">NOW</Var>,…)`,
 * `AG2StyleTag:style=P_PRICE_PLOT_STYLE,def_var=def_plot`). When the
 * same XML is fed to `factset.Spreadsheet.AddActiveGraph` inside the
 * `COORD_*,CHART_XML=` envelope, the host correctly creates the OLE
 * shell at the requested coords, but the FDSChart control's internal
 * binder then rejects the chart definition silently and renders the
 * unloaded "Powered by FactSet" placeholder instead of the chart.
 * Captured working `AddActiveGraph` payloads — including the one the
 * Finster team was given as the canonical reference — encode every
 * literal comma as `&cmma`, so we mirror that on the way out.
 *
 * **Scope.** Apply ONLY to the XML body that goes after `CHART_XML=`.
 * The leading `COORD_*` envelope keys must keep their literal commas
 * because the host's envelope parser reads those as the key separator.
 *
 * **Idempotence.** Replaces only literal `,` characters; if any input
 * already contains `&cmma` we leave those entities alone. Running this
 * twice on the same string is a no-op (after the first pass there are
 * no literal `,` left).
 */
function escapeCommasForActiveGraph(fdsChartXml: string): string {
  return fdsChartXml.replace(/,/g, "&cmma");
}

/**
 * Wrap an FDSChart XML body in the comma-separated `COORD_*,CHART_XML=`
 * envelope the host expects on the `activeGraphContent` field, with the
 * XML body's literal commas re-encoded as `&cmma` entities. Exposed for
 * tests and for any caller that wants to construct the envelope
 * explicitly; production callers should just call
 * `addActiveGraphViaIntents(fdsChartXml, coords?)` and let the wrapper
 * build the envelope.
 *
 * **Wire shape.**
 * ```
 * COORD_TOP=N,COORD_LEFT=N,COORD_WIDTH=N,COORD_HEIGHT=N,CHART_XML=<XML with commas as `&cmma`>
 * ```
 *
 * The host parses this string by reading the four `COORD_*` keys
 * greedily until it hits `,CHART_XML=`, then treats *everything* after
 * `CHART_XML=` as the XML body. The body's literal commas are encoded
 * as `&cmma` so the FDSChart control's downstream binder doesn't
 * mis-split formula / style fields; see
 * `escapeCommasForActiveGraph` JSDoc for the full reasoning. Don't
 * URL-encode the XML, don't wrap it in quotes, don't double-escape the
 * `&lt;`/`&gt;` content `previewChart` already produced; the only
 * Finster-side rewrite is the comma escape.
 */
export function buildAddActiveGraphContent(
  fdsChartXml: string,
  coords?: AddActiveGraphCoords
): string {
  const c = { ...DEFAULT_ACTIVE_GRAPH_COORDS, ...coords };
  const escapedXml = escapeCommasForActiveGraph(fdsChartXml);
  return (
    `COORD_TOP=${c.top},` +
    `COORD_LEFT=${c.left},` +
    `COORD_WIDTH=${c.width},` +
    `COORD_HEIGHT=${c.height},` +
    `CHART_XML=${escapedXml}`
  );
}

/**
 * Insert a graph into the active worksheet via
 * `factset.Spreadsheet.AddActiveGraph`.
 *
 * **Wire shape — `activeGraphContent` is NOT bare XML.** The host's
 * field is a comma-separated key=value envelope, and the XML body
 * inside has its literal commas re-encoded:
 *
 * ```
 * COORD_TOP=100,COORD_LEFT=5,COORD_WIDTH=320,COORD_HEIGHT=200,CHART_XML=<FDSChart …>…<Property>FORMULA = …<Var ID="SD1">-1AY</Var>&cmma…</Property>…</FDSChart>
 * ```
 *
 * Two things to know about that string:
 *
 *   1. The four `COORD_*` keys position and size the chart on the
 *      active sheet; `CHART_XML=` is the magic key after which the
 *      host stops looking for further envelope keys and treats the
 *      remainder of the string as the FDSChart XML body.
 *
 *   2. Inside the XML body, every literal `,` is re-encoded as the
 *      FactSet-custom `&cmma` entity reference. This is required by
 *      the FDSChart control's downstream binder — without it the OLE
 *      shape gets created at the requested coords but renders the
 *      unloaded "Powered by FactSet" placeholder instead of the
 *      chart. See `escapeCommasForActiveGraph` JSDoc for the full
 *      reasoning.
 *
 * **Caller contract — pass the bare `fdsChartXml` from `previewChart`.**
 * The wrapper prepends the `COORD_*,CHART_XML=` envelope and runs the
 * `,` → `&cmma` body rewrite automatically with sensible coord
 * defaults (see `DEFAULT_ACTIVE_GRAPH_COORDS`). Pass an
 * `AddActiveGraphCoords` object to override anchor/size.
 *
 * **Defensive sniff — pre-wrapped strings pass through unchanged.** If
 * a caller passes a string that already starts with `COORD_TOP=` or
 * already contains `,CHART_XML=`, the wrapper does NOT re-wrap it AND
 * does NOT re-run the comma escape — that lets a future caller
 * hand-roll a non-default envelope (e.g. a test fixture) without us
 * silently double-wrapping or re-escaping characters inside their
 * already-encoded body.
 *
 * **Returns** a host status message (e.g. `"Active graph added
 * successfully"`), NOT a sheet name. See `excelHostOps::addActiveGraph`
 * JSDoc for the return-shape gotcha.
 */
export async function addActiveGraphViaIntents(
  fdsChartXml: string,
  coords?: AddActiveGraphCoords,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<string> {
  const alreadyWrapped =
    fdsChartXml.startsWith("COORD_TOP=") || fdsChartXml.includes(",CHART_XML=");
  const activeGraphContent = alreadyWrapped
    ? fdsChartXml
    : buildAddActiveGraphContent(fdsChartXml, coords);

  const ctx = (await raiseIntent(
    "factset.Spreadsheet.AddActiveGraph",
    {
      type: "factset.spreadsheet.addActiveGraph",
      activeGraphContent,
    },
    timeoutMs
  )) as AddActiveGraphResponse | undefined;
  return typeof ctx?.message === "string" ? ctx.message : "";
}

// ============================================================================
// factset.Charting.SetActiveGraphXml
// ============================================================================
//
// Sibling write-side intent for `factset.Charting.GetActiveGraphXml` (above)
// and complementary to `factset.Spreadsheet.AddActiveGraph`. Lives in the
// host-agnostic `factset.Charting.*` namespace, so it works in both Excel
// and PowerPoint host contexts.
//
// **What it does — in-place replace.** Unlike `AddActiveGraph`, which
// inserts a brand-new ActiveGraph OLE object at user-specified coords on
// the active sheet, `SetActiveGraphXml` mutates an existing ActiveGraph in
// place: same OLE shape, same anchor/size, just a new `fdsChartXml` body.
// When `sheetRef + chartIndex` are supplied the host targets that chart;
// otherwise it falls back to the currently-selected ActiveGraph.
//
// **Wire shape — chart-tools.md current contract.**
//   Request : {
//                type: "factset.charting.setActiveGraphXml",
//                options: {
//                  fdsChartXml: "<FDSChart …>…</FDSChart>",
//                  refresh: true,          // optional, defaults true
//                  workbookName?: "Book1.xlsx",
//                  sheetRef?: { name: "Sheet1" },
//                  chartIndex?: 0,
//                },
//              }
//   Response: { type: "factset.charting.setActiveGraphXmlResult",
//               message: "ActiveGraph XML updated successfully" }
//
// **fdsChartXml IS bare XML — no `COORD_*,CHART_XML=` envelope.** Unlike
// `AddActiveGraph`, this intent does NOT take a comma-separated key=value
// envelope and does NOT need its commas re-encoded as `&cmma`. Pass the
// raw `<FDSChart …>…</FDSChart>` string straight through.
//
// **Targeting.** Prefer passing `sheetRef + chartIndex` when the caller has
// a runtime-resolved host target. Without those fields the host operates on
// the currently selected ActiveGraph OLE object; with nothing selected it
// returns an error rather than silently no-oping.
//
// **Why `refresh: true` is the default.** The host re-runs the chart's
// data binding only when `refresh` is true; with `refresh: false` the XML
// is replaced but the FDSChart control keeps showing the previously-
// rendered values until the next manual refresh. That's confusing for the
// "set new XML and let me see the new chart immediately" common case, so
// we default to `true` and let callers explicitly opt out.

interface SetActiveGraphXmlResponse {
  type?: string;
  message?: unknown;
}

export interface SetActiveGraphXmlOptions {
  refresh?: boolean;
  workbookName?: string;
  sheetRef?: GetChartsSheetRef;
  chartIndex?: number;
  timeoutMs?: number;
}

const SET_ACTIVE_GRAPH_XML_INTENT_NAME = "factset.Charting.SetActiveGraphXml";
const SET_ACTIVE_GRAPH_XML_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Replace the FDSChart XML of an existing ActiveGraph OLE object via
 * `factset.Charting.SetActiveGraphXml`.
 *
 * **NOT exposed to the planner.** The planner-emittable
 * `ActiveGraphModification.action` union no longer contains
 * `"update-selected"`, and the applier branch is a defensive
 * `trackSkip`. This wrapper remains wired for explicit non-agent
 * callers (e.g. a future verifier, an internal dev tool). The
 * underlying surface is too immature for round-trip mutations from
 * the LLM.
 *
 * **Caller contract — pass the bare `<FDSChart …>…</FDSChart>` body.**
 * Unlike `addActiveGraphViaIntents`, this intent does NOT take a
 * `COORD_*,CHART_XML=` envelope and does NOT need its commas re-encoded
 * as `&cmma` entities. Pass the raw FDSChart XML straight through; the
 * host's downstream binder handles it as-is.
 *
 * **Targeting.** Pass `sheetRef + chartIndex` for a resolved host target.
 * When omitted, the host mutates whichever ActiveGraph OLE object the user
 * selected. With nothing selected the host rejects the call; let the
 * rejection propagate so the UI can surface fallback selection guidance.
 *
 * **`refresh` semantics.** When `true` (default), the host re-runs the
 * chart's data binding so the rendered chart reflects the new XML
 * immediately. Pass `false` only if the caller has a reason to defer
 * the visual refresh (e.g. mutating multiple charts in a batch).
 *
 * Returns the host status message (e.g. `"ActiveGraph XML updated
 * successfully"`), NOT a sheet name.
 */
export async function setActiveGraphXmlViaIntents(
  fdsChartXml: string,
  options: SetActiveGraphXmlOptions = {}
): Promise<string> {
  const {
    refresh = true,
    workbookName,
    sheetRef,
    chartIndex,
    timeoutMs = DEFAULT_TIMEOUT_MS,
  } = options;
  const ctx = (await raiseIntent(
    SET_ACTIVE_GRAPH_XML_INTENT_NAME,
    {
      type: "factset.charting.setActiveGraphXml",
      options: {
        fdsChartXml,
        refresh,
        ...(workbookName ? { workbookName } : {}),
        ...(sheetRef ? { sheetRef } : {}),
        ...(typeof chartIndex === "number" ? { chartIndex } : {}),
      },
    },
    timeoutMs,
    SET_ACTIVE_GRAPH_XML_APP_ID
  )) as SetActiveGraphXmlResponse | undefined;
  return typeof ctx?.message === "string" ? ctx.message : "";
}

// ============================================================================
// factset.PreviewChart  (NB: NOT in the `factset.Spreadsheet.*` namespace)
// ============================================================================
//
// Build a FactSet-rendered chart from a parameter set (chartType, tickers,
// frequency, date window, optional annotations / styleset / formatting).
// The host returns the rendered FDSChart XML plus an optional PNG
// preview. The XML feeds straight into `addActiveGraphViaIntents`, which
// wraps it in the host's `COORD_*,CHART_XML=` envelope — see that
// function's JSDoc for the wire shape:
//
//   const preview = await previewChartViaIntents(params);
//   if (preview.fdsChartXml) {
//     await addActiveGraphViaIntents(preview.fdsChartXml);
//   }
//
// **Departures from sibling Spreadsheet intents — read carefully, the
// surface differs in three ways from anything else in this file:**
//
//   1. **Intent name uses CapitalizedCamelCase, not lowerCamelCase.**
//      The wire intent name is `factset.PreviewChart` (capital `P`),
//      while every `factset.Spreadsheet.*` intent uses lowerCamelCase
//      verbs (`factset.Spreadsheet.getRangeInfo`, etc.). Confusingly,
//      the **context type** for this same intent IS lowerCamelCase
//      (`factset.previewChart`). We send both — see constants below.
//
//   2. **Different target appId.** This intent routes to
//      `OI Remote Iframe` (a remote-iframe relay), NOT the default
//      `com.factset.workstation` target every other intent uses. The
//      transport's `raiseIntent` takes an optional `appId` override —
//      we pass `PREVIEW_CHART_APP_ID` to opt into the relay.
//
//   3. **HTTP-style response envelope inside `result.context`.** The
//      response context type is `factset.common.previewChartResult`
//      (NB: `factset.common.*`, not `factset.spreadsheet.*`) and its
//      payload is nested as
//      `{ data: { status, contentType, body: { data: <fields> } } }` —
//      the host runs the chart build on a backend service and
//      forwards the raw HTTP response back through the intent reply.
//      `extractPreviewBody` walks this nesting plus any flatter shapes.
//
// See captured payload + envelope details in
// `excel-docs/intent-details.md` → "Preview Chart".

const PREVIEW_CHART_INTENT_NAME = "factset.PreviewChart";
const PREVIEW_CHART_CONTEXT_TYPE = "factset.previewChart";
const PREVIEW_CHART_APP_ID = { appId: "OI Remote Iframe" };

/** Per-call options for `previewChartViaIntents`. */
export interface PreviewChartOptions {
  /** When `true`, the host loads the chart in edit mode (lets a
   *  follow-up call return the same chart with overrides). Mirrors
   *  the captured payload's `data.isEdit`. Default `false`. */
  isEdit?: boolean;
  /** Whether the host should render a PNG preview alongside the XML.
   *  Captured payload uses `"None"` (and the captured response has
   *  `pngData: null`); default to `"Png"` so the chat has something
   *  to render before the user clicks "Add to Excel". Pass through
   *  verbatim so future enums (`"Both"` etc.) work without a wrapper
   *  update. */
  generatePreview?: "None" | "Png" | string;
  /** Per-call timeout override. Defaults to the five-minute long-running
   *  host-operation budget because first renders can be slow. */
  timeoutMs?: number;
  /** Abort the wait for the host's reply. The host has no way to
   *  receive a cancel signal, but the client stops waiting and the
   *  returned promise rejects with `DOMException("AbortError")` —
   *  which the chat-runtime's user-cancel branch already handles. */
  signal?: AbortSignal;
}

/**
 * Inner-body shape after envelope unwrapping. Loose typing because the
 * host returns these as raw JSON values that we coerce below.
 */
interface PreviewChartResponseInner {
  wasStylesetChanged?: unknown;
  fdsChartXml?: unknown;
  pngData?: unknown;
  annotationsJson?: unknown;
  error?: unknown;
}

/**
 * Recursively search `value` for the first object-shaped node carrying
 * a string `fdsChartXml`. Used as the fallback after the documented
 * shape walk below — if the host ever rearranges the envelope, we'd
 * rather find the payload one level deeper than silently return `{}`
 * and surface a "preview returned no XML" error to the user.
 *
 * The walk is bounded (`maxDepth`) and skips arrays / primitives, so
 * pathological inputs can't blow the stack.
 */
function findFdsChartXmlNode(
  value: unknown,
  maxDepth: number = 6
): PreviewChartResponseInner | undefined {
  if (maxDepth < 0) return undefined;
  if (!value || typeof value !== "object") return undefined;
  if (Array.isArray(value)) return undefined;

  const obj = value as Record<string, unknown>;
  if (typeof obj["fdsChartXml"] === "string") {
    return obj as PreviewChartResponseInner;
  }
  for (const child of Object.values(obj)) {
    const hit = findFdsChartXmlNode(child, maxDepth - 1);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * Walk the response context and pull out the `fdsChartXml`-bearing
 * inner body. `raiseIntent` already unwraps `event.data.result.context`,
 * so the value reaching us here is whatever the host placed inside
 * `result.context` — and the host wraps the preview-chart payload in
 * an HTTP-style `{ data: { status, contentType, body: { data: ... } } }`
 * envelope. Tolerate that nested form, the obvious flatter forms, and
 * fall back to a bounded recursive search so a future host rearrange
 * doesn't silently break the chat preview.
 */
function extractPreviewBody(ctx: unknown): PreviewChartResponseInner {
  if (!ctx || typeof ctx !== "object") return {};

  const c = ctx as Record<string, unknown>;

  // Shape 1 (captured): { data: { status, contentType, body: { data: <inner> } } }
  const dataLevel = c["data"];
  if (dataLevel && typeof dataLevel === "object") {
    const d = dataLevel as Record<string, unknown>;
    const body = d["body"];
    if (body && typeof body === "object") {
      const innerData = (body as Record<string, unknown>)["data"];
      if (innerData && typeof innerData === "object") {
        return innerData as PreviewChartResponseInner;
      }
      // Some hosts may collapse the second `.data` level.
      if ("fdsChartXml" in (body as Record<string, unknown>)) {
        return body as PreviewChartResponseInner;
      }
    }
    // Shape 2: { data: <inner> }
    if ("fdsChartXml" in d) {
      return d as PreviewChartResponseInner;
    }
  }

  // Shape 3 (already-unwrapped): { fdsChartXml, ... } — handle in case
  // the host normalises the envelope later.
  if ("fdsChartXml" in c) {
    return c as PreviewChartResponseInner;
  }

  // Shape 4 (defensive): the host rearranges the envelope (`result.data`,
  // `payload.body`, etc.). A bounded recursive search is cheap and
  // guarantees a populated `fdsChartXml` reaches the caller.
  const recursive = findFdsChartXmlNode(c);
  if (recursive) return recursive;

  return {};
}

/**
 * Coerce the host's `pngData` into a renderable data URI. The captured
 * trace returns `null`; when populated, the value is expected to be
 * either a full `data:image/png;base64,...` URI or a bare base64 string
 * (per the `addImageFromBase64` precedent — both forms accepted).
 */
function normalizePngData(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0) return undefined;
  if (value.startsWith("data:")) return value;
  return `data:image/png;base64,${value}`;
}

/**
 * Per-caller param-shape guard for the three chart-parameter body
 * builders below. Throws with a `<callerLabel>:` prefix so the
 * surfaced error points back at which intent rejected the inputs.
 *
 * Invariants enforced here:
 *  - `tickers` must be a non-empty array.
 *  - `chartType` must be a non-empty trimmed string. We deliberately
 *    do NOT default this — substituting `"P_PRICE"` for a missing
 *    chartType would silently render the wrong chart (e.g. user asks
 *    for EV/EBITDA, gets a price chart). Failing fast lets the
 *    chat-bubble error UI render a graceful "Chart unavailable"
 *    placeholder instead.
 *  - `chartType` must resolve to a FactSet ActiveGraph capability in
 *    the registry. This is the client-side registry gate — see
 *    `assertChartTypeInRegistry` below. Canonical codes are mirrored
 *    in `f2_docs/charting/chart-creator-api-parameter-skill.md` §1
 *    and `chartCapabilityRegistry.ts`. Rejected codes raise a
 *    `CHART_CAPABILITY_ERROR_PREFIX`-tagged error before any host
 *    call, so the chat UI can show "Closest available: ..." instead
 *    of an opaque `ChartTypeInvalid` round trip.
 */
function assertChartParametersValid(
  params: FactsetChartParameters,
  callerLabel: string
): void {
  if (!Array.isArray(params.tickers) || params.tickers.length === 0) {
    throw new Error(
      `${callerLabel}: \`tickers\` is required (e.g. ["AAPL-US"])`
    );
  }
  const trimmedChartType =
    typeof params.chartType === "string" ? params.chartType.trim() : "";
  if (trimmedChartType.length === 0) {
    throw new Error(
      `${callerLabel}: \`chartType\` is required (e.g. "VAL_PE2")`
    );
  }
  assertChartTypeInRegistry(trimmedChartType, "factset_activegraph", callerLabel);
}

/**
 * Reject chart types the registry doesn't know about, BEFORE we make
 * any host call. The registry is the single source of truth for the
 * 27 FactSet ActiveGraph codes and the 24 native Excel `xl*` types
 * the agent can emit — see `chartCapabilityRegistry.ts`.
 *
 * Why client-side validation (rather than relying on the host's
 * `ChartTypeInvalid` reply):
 *
 *  - We save a round-trip and a noisy log on every guessed code.
 *  - We can surface a focused, actionable message that names the
 *    closest valid alternatives instead of the generic
 *    `ChartTypeInvalid`.
 *  - Known-unsupported requests (Sankey / treemap / etc.) get a
 *    dedicated message that explains the capability isn't available,
 *    rather than implying the user mistyped a real code.
 *
 * Throws with a structured, user-readable message on rejection so
 * the existing chat / planner try-catch wrappers surface it through
 * their normal error UI without further changes (see
 * `ExcelApp.tsx::handleQuery` preview catch and
 * `excelService.ts::applyWorkbookModificationsViaIntents` AddExcelChart
 * catch for the call sites we rely on).
 */
function assertChartTypeInRegistry(
  chartType: string,
  expectedSystem: ChartSystem,
  callerLabel: string,
): string {
  // Normalize whitespace centrally so every caller is protected
  // regardless of whether it trimmed beforehand. `findChartCapability`
  // does exact-match comparison (`c.id === id`), so a value like
  // `"xlColumnClustered "` with trailing space would otherwise pass the
  // upstream `length === 0` non-empty checks but fail the lookup,
  // producing a confusing "not in the catalog" error for a valid type.
  //
  // We RETURN the normalized id so callers can also use it on the wire
  // — validating with a trimmed value while sending the untrimmed
  // original would let the host re-reject what we just accepted (see
  // Bugbot PR-201 "Validated chartType not normalized before host
  // transmission").
  const trimmed = typeof chartType === "string" ? chartType.trim() : "";
  const cap = findChartCapability(trimmed);
  if (cap !== null && cap.system === expectedSystem) {
    return trimmed;
  }
  // Surface the trimmed value in the error message so the user sees the
  // actual id we tried to look up (not the whitespace-padded original).
  throw new Error(buildUnknownChartTypeMessage(trimmed, expectedSystem, callerLabel, cap));
}

/**
 * Universal-fallback capability per chart system. Used by
 * `buildUnknownChartTypeMessage` when the user asks for a
 * known-unsupported chart family (Sankey, treemap, etc.) and the
 * alias-based suggester returns no match — without this, the error
 * message would name no alternative at all, which the chat-bubble UI
 * just renders as a flat "not supported today" with no recovery path.
 *
 * Scoped to known-unsupported families only. We deliberately do NOT
 * fall back to these for arbitrary typos / unknown codes, since
 * suggesting `P_PRICE` for "PE_RATIO" would silently mislead the user
 * away from `VAL_PE2`. The alias matcher already handles those.
 */
function fallbackSuggestion(system: ChartSystem): readonly ChartCapability[] {
  const id = system === "factset_activegraph" ? "P_PRICE" : "xlColumnClustered";
  const c = findChartCapability(id);
  return c ? [c] : [];
}

/**
 * Render the user-facing error message for an unknown / wrong-system
 * chart type. Four branches:
 *
 *  - The code matches a capability in the OTHER system — tell the
 *    caller they picked the right code but the wrong path (e.g. an
 *    `xl*` value handed to `previewChart`).
 *  - The code (or a free-form phrase passed as a code) hits the
 *    known-unsupported list — say so explicitly. If the alias
 *    matcher returned nothing, fall back to the universal default
 *    (`P_PRICE` / `xlColumnClustered`) so the user always sees at
 *    least one actionable alternative.
 *  - Otherwise — "unknown chart type" with the top system-matching
 *    suggestions from the registry's alias matcher.
 *
 * The chat handler renders `previewErr.message` verbatim into the
 * chart bubble's placeholder; the planner / `addExcelChart` apply
 * loop logs it.
 */
function buildUnknownChartTypeMessage(
  chartType: string,
  expectedSystem: ChartSystem,
  callerLabel: string,
  wrongSystemCapability: ReturnType<typeof findChartCapability>,
): string {
  const systemLabel =
    expectedSystem === "factset_activegraph"
      ? "FactSet ActiveGraph (PreviewChart / AddActiveGraph)"
      : "native Excel chart (AddExcelChart)";

  if (wrongSystemCapability !== null) {
    const otherLabel =
      wrongSystemCapability.system === "factset_activegraph"
        ? "FactSet ActiveGraph"
        : "native Excel chart";
    return (
      `${CHART_CAPABILITY_ERROR_PREFIX}${callerLabel}: \`chartType\` "${chartType}" is a ${otherLabel} type ` +
      `but this call expects a ${systemLabel} type. Route the request through the ${otherLabel} ` +
      `code path instead, or pick a ${systemLabel} type.`
    );
  }

  // System-aware suggester: filter the pool by `expectedSystem`
  // BEFORE capping, so a native-Excel caller cannot have its slots
  // crowded out by FactSet entries (Ozan / Bugbot PR-201 finding).
  let suggestions = suggestChartCapability(chartType, 3, { system: expectedSystem });

  // Known-unsupported families (Sankey/treemap/waterfall/…) often
  // miss every alias. Fall back to the universal default for the
  // requested system so the user always sees a usable alternative.
  if (suggestions.length === 0 && isKnownUnsupportedChartType(chartType)) {
    suggestions = fallbackSuggestion(expectedSystem);
  }

  const suggestionText =
    suggestions.length > 0
      ? ` Closest available: ${suggestions
          .map((c) => `\`${c.id}\` (${c.displayName})`)
          .join(", ")}.`
      : "";

  if (isKnownUnsupportedChartType(chartType)) {
    return (
      `${CHART_CAPABILITY_ERROR_PREFIX}${callerLabel}: chart type "${chartType}" is not supported today.` +
      suggestionText
    );
  }

  return (
    `${CHART_CAPABILITY_ERROR_PREFIX}${callerLabel}: \`chartType\` "${chartType}" is not in the ${systemLabel} catalog ` +
    `(see chartCapabilityRegistry.ts for the full list of supported codes).` +
    suggestionText
  );
}

/**
 * Module-private — only the three chart-parameter body builders below
 * call this. Assembles the `data` body shared by
 * `factset.PreviewChart`, `factset.DownloadExcelChart`, and
 * `factset.DownloadActiveGraph`. Their captured request bodies are
 * byte-for-byte identical except for two booleans (`isEdit`,
 * `generatePreview`), so the body assembly lives here and the
 * per-intent wrappers just pass through their target defaults.
 *
 * **Field order matters** — the captured payload's key order is
 * preserved here verbatim. Re-ordering keys would change the
 * `JSON.stringify` output; do NOT touch the order without
 * re-capturing against a live host.
 *
 * Default handling per field:
 *  - `chartType`: trimmed; non-empty enforced by the caller's
 *    `assertChartParametersValid` guard. We still trim defensively
 *    here in case a future builder forgets the guard.
 *  - `frequency` / `startDate` / `endDate`: empty/whitespace coerced
 *    to chart-family timeline defaults via `normalizeActiveGraphChartParameters`.
 *    Timeline defaults pick a window for the chart the LLM picked; they don't
 *    substitute a different analysis.
 *  - `horizon` / `stylesetId` / `mathFunction`: `?? ""` — empty IS
 *    the host's "no value" sentinel for these fields.
 *  - `quickLabel` / `currencies` / `estimateParameters` /
 *    `chartFormat`: structural defaults (numeric 0, empty array,
 *    full estimate-params object, format-defaults spread).
 *  - `annotations`: optional list with per-entry defaulting.
 *  - `summaryTable`: opt-in via `options`, and only `DownloadExcelChart`
 *    opts in. Chart Creator builds the table when it generates the
 *    workbook, so the field means nothing on the preview and
 *    ActiveGraph paths and must stay absent from their bodies.
 *
 * The host expects the inner payload (`{ data: { chartParameters, ... } }`)
 * inside a JSON-string `body` field, NOT inline — see the captured
 * request in `intent-details.md`. Stringify here so callers don't
 * have to know about that quirk.
 */
function buildChartParametersBody(
  params: FactsetChartParameters,
  isEdit: boolean,
  generatePreview: "None" | "Png" | string,
  options: { summaryTable?: FactsetChartParameters["summaryTable"] } = {}
): string {
  const normalizedParams = normalizeActiveGraphChartParameters(params);
  const companyNames = normalizedParams.companyNames ?? { single: [], aggregated: [] };
  const companyNamesJson = JSON.stringify({
    SingleCompanies: companyNames.single ?? [],
    AggregatedCompanies: companyNames.aggregated ?? [],
  });

  const annotations = (normalizedParams.annotations ?? []).map((a) => ({
    content: a.content,
    date: a.date,
    dateTime: a.dateTime ?? new Date().toISOString(),
    entity: a.entity ?? normalizedParams.tickers[0],
    isPeerGroup: a.isPeerGroup ?? false,
  }));

  const summaryTable =
    CHART_CREATOR_SUMMARY_TABLE_API && options.summaryTable
      ? options.summaryTable
      : undefined;

  const body = {
    data: {
      chartParameters: {
        chartType: normalizedParams.chartType ?? "",
        companyNamesJson,
        tickers: normalizedParams.tickers,
        frequency: normalizedParams.frequency ?? "D",
        startDate: normalizedParams.startDate ?? "-1AY",
        endDate: normalizedParams.endDate ?? "NOW",
        horizon: normalizedParams.horizon ?? "",
        annotations,
        stylesetId: normalizedParams.stylesetId ?? "",
        mathFunction: normalizedParams.mathFunction ?? "",
        quickLabel: normalizedParams.quickLabel ?? 0,
        currencies: normalizedParams.currencies ?? [],
        estimateParameters: normalizedParams.estimateParameters ?? DEFAULT_ESTIMATE_PARAMS,
        ...(summaryTable ? { summaryTable } : {}),
      },
      chartFormat: { ...DEFAULT_CHART_FORMAT, ...(normalizedParams.chartFormat ?? {}) },
      isEdit,
      generatePreview,
    },
  };

  return JSON.stringify(body);
}

/**
 * Build the `factset.PreviewChart` request body. Validates inputs,
 * then delegates to the shared assembler with preview-specific
 * defaults (`isEdit: false`, `generatePreview: "Png"` — the chat UI
 * needs a PNG to render inline).
 *
 * Both shape AND catalog membership of `chartType` are enforced
 * client-side via `assertChartParametersValid` →
 * `assertChartTypeInRegistry` before the host call. Canonical codes
 * are mirrored in `chartCapabilityRegistry.ts` (from
 * `f2_docs/charting/chart-creator-api-parameter-skill.md` §1) so the
 * planner prompt and the gate cannot drift. Rejected codes raise a
 * `CHART_CAPABILITY_ERROR_PREFIX`-tagged error which the chat bubble
 * renders verbatim with closest-available alternatives.
 */
function buildPreviewChartBody(
  params: FactsetChartParameters,
  options: PreviewChartOptions
): string {
  assertChartParametersValid(params, "previewChart");
  return buildChartParametersBody(
    params,
    options.isEdit ?? false,
    options.generatePreview ?? "Png"
  );
}

/**
 * Render a FactSet chart preview via `factset.previewChart`.
 *
 * Returns a normalised `FactsetChartPreview` with the FDSChart XML, an
 * optional PNG data URI, and the host's annotation-validation JSON.
 * The XML is fed to `addActiveGraphViaIntents` on insert; the PNG is
 * rendered inline in the chat.
 *
 * Rejects only on transport/timeout errors (or invalid input — empty
 * `tickers`). A host-reported `error` is surfaced through the returned
 * object's `error` field; callers decide whether an empty `fdsChartXml`
 * is fatal.
 */
export async function previewChartViaIntents(
  parameters: FactsetChartParameters,
  options: PreviewChartOptions = {}
): Promise<FactsetChartPreview> {
  const body = buildPreviewChartBody(parameters, options);
  const timeoutMs = options.timeoutMs ?? LONG_RUNNING_FACTSET_INTENT_TIMEOUT_MS;

  const ctx = await raiseIntent(
    PREVIEW_CHART_INTENT_NAME,
    { type: PREVIEW_CHART_CONTEXT_TYPE, body },
    timeoutMs,
    PREVIEW_CHART_APP_ID,
    options.signal
  );

  const inner = extractPreviewBody(ctx);

  return {
    fdsChartXml:
      typeof inner.fdsChartXml === "string" ? inner.fdsChartXml : undefined,
    pngDataUri: normalizePngData(inner.pngData),
    annotationsJson:
      typeof inner.annotationsJson === "string"
        ? inner.annotationsJson
        : undefined,
    wasStylesetChanged:
      typeof inner.wasStylesetChanged === "boolean"
        ? inner.wasStylesetChanged
        : undefined,
    error:
      inner.error === null
        ? null
        : typeof inner.error === "string"
          ? inner.error
          : undefined,
  };
}

// ============================================================================
// factset.DownloadExcelChart  (NB: NOT in the `factset.Spreadsheet.*` namespace)
// ============================================================================
//
// Build a FactSet chart workbook from a chart-parameter set and return
// inline `.xlsx` bytes (base64). Same chart family as
// `factset.PreviewChart` — routed to the host's "produce an .xlsx
// chart workbook" endpoint instead of the "render PNG / FDSChart XML"
// one. The request body is byte-for-byte the same shape as
// `factset.PreviewChart` (`{ data: { chartParameters, chartFormat,
// isEdit, generatePreview } }` JSON-stringified into `context.body`),
// just with different captured defaults that match a download
// workflow:
//
//   - `isEdit: true`           — downloads carry edit-mode metadata so a
//                                follow-up call can round-trip the same
//                                chart.
//   - `generatePreview: "None"` — no PNG side-channel is needed when the
//                                goal is the workbook itself.
//
// Both defaults are overridable per call. Captured request + response
// shapes live in `excel-docs/intent-details.md` → "Download Excel
// Chart". Tracked alongside the other sibling-namespace intents in
// `f2_docs/excel/finster-docs/missing-intents-v0.md` §4.4 (wrapper /
// dispatcher / consumer summary); per-intent capture stays in
// `intent-details.md`, mirroring how `factset.PreviewChart` is
// documented.
//
// **Departures from sibling Spreadsheet intents — three shared with
// `previewChart`, one (the response envelope) DIFFERENT. Read carefully
// before mirroring previewChart's response-handling code:**
//
//   1. **Intent name uses CapitalizedCamelCase, not lowerCamelCase.**
//      Wire intent name is `factset.DownloadExcelChart`. Confusingly,
//      the request `context.type` IS lowerCamelCase
//      (`factset.downloadExcelChart`). Send both — see constants below.
//      Same casing-split convention as `factset.PreviewChart`.
//   2. **Different target appId.** Routes to `"OI Remote Iframe"`,
//      NOT the default `"com.factset.workstation"`. Wrappers must
//      override the appId per call.
//   3. **Request body is a JSON string, not an inline object.** Same
//      as `previewChart` — the host expects `context.body` to be a
//      JSON-stringified blob.
//   4. **Response envelope is FLATTER than `previewChart`'s.** The
//      result context type is `factset.common.downloadExcelChartResult`
//      (note the `factset.common.*` namespace — same family as
//      `factset.common.previewChartResult`), but the payload shape is
//      `{ data: { status, contentType, bodyAsBase64 } }` — ONE level
//      of nesting, not the two-level `data.body.data` envelope
//      `previewChart` uses. The `.xlsx` bytes sit directly on
//      `data.bodyAsBase64`. Captured 2026-04-30 against a live
//      FactSet Workstation host (HTTP-200, MIME
//      `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`).
//      `extractDownloadExcelChartBody` walks this single level and
//      falls back to a bounded recursive search if the host ever
//      rearranges.

const DOWNLOAD_EXCEL_CHART_INTENT_NAME = "factset.DownloadExcelChart";
const DOWNLOAD_EXCEL_CHART_CONTEXT_TYPE = "factset.downloadExcelChart";
const DOWNLOAD_EXCEL_CHART_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Per-call options for `downloadExcelChartViaIntents`.
 *
 * Mirrors `PreviewChartOptions` field-for-field; the only difference
 * is the resolved defaults inside `buildDownloadExcelChartBody`.
 */
export interface DownloadExcelChartOptions {
  /** When `true`, the host produces an edit-mode workbook (the chart
   *  is round-trippable through a follow-up call). Default `true` —
   *  matches the captured payload's `data.isEdit`. */
  isEdit?: boolean;
  /** Whether the host should attach a PNG preview alongside the
   *  workbook bytes. The captured payload uses `"None"` — there's no
   *  reason to ask for a PNG when the goal is the workbook. Pass
   *  through verbatim so future enums (`"Png"`, `"Both"`, etc.) work
   *  without a wrapper update. Default `"None"`. */
  generatePreview?: "None" | "Png" | string;
  /** Per-call timeout override. Defaults to the five-minute long-running
   *  host-operation budget, matching `previewChart`. */
  timeoutMs?: number;
  /** Abort the wait for the host's reply. Same semantics as
   *  `PreviewChartOptions.signal`. */
  signal?: AbortSignal;
}

/**
 * Inner body shape returned by the host (the `data` level of
 * `result.context`). Captured 2026-04-30:
 *
 *   { status: 200,
 *     contentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
 *     bodyAsBase64: "UEsDBBQ…" /* PK-magic prefix → confirmed valid xlsx *\/ }
 *
 * Fields are typed as `unknown` so the wrapper coerces defensively at
 * the boundary — host-side typing changes (string → number for
 * `status`, etc.) won't cascade into the public surface.
 */
interface DownloadExcelChartResponseInner {
  /** HTTP-style status code. `200` is success; non-200 surfaces as an
   *  `error` on the public type so callers don't have to inspect it. */
  status?: unknown;
  /** MIME type of the returned workbook —
   *  `"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"`
   *  on the captured trace. */
  contentType?: unknown;
  /** Base64-encoded `.xlsx` bytes (no `data:` prefix). The base64
   *  payload starts with `UEsDBBQ` (PK ZIP magic), confirming a real
   *  Open-XML workbook on the wire. */
  bodyAsBase64?: unknown;
  /** Carry any other fields verbatim — the host catalog may grow. */
  [key: string]: unknown;
}

/**
 * Bounded recursive search for the first object-shaped node carrying
 * `bodyAsBase64`. Used as the defensive fallback after the documented
 * walk below — if the host ever rearranges the envelope (extra wrapper
 * level, renamed `data` field, etc.), we'd rather find the workbook
 * one level deeper than silently return `{}` and surface a "no bytes"
 * error to the user.
 *
 * The walk is bounded (`maxDepth`) and skips arrays / primitives, so
 * pathological inputs can't blow the stack.
 */
function findBodyAsBase64Node(
  value: unknown,
  maxDepth: number = 6
): Record<string, unknown> | undefined {
  if (maxDepth < 0) return undefined;
  if (!value || typeof value !== "object") return undefined;
  if (Array.isArray(value)) return undefined;

  const obj = value as Record<string, unknown>;
  if (typeof obj["bodyAsBase64"] === "string") return obj;
  for (const child of Object.values(obj)) {
    const hit = findBodyAsBase64Node(child, maxDepth - 1);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * Walk the response context and pull out the `data` level (the host's
 * inner body). Captured shape (2026-04-30):
 *
 *   { type: "factset.common.downloadExcelChartResult",
 *     data: { status, contentType, bodyAsBase64 } }
 *
 * Note that this envelope is FLATTER than `previewChart`'s — there is
 * no `data.body.data` second level. The base64 bytes sit directly on
 * `data.bodyAsBase64`. We try the captured shape first, then the
 * already-unwrapped flat form (in case the host normalises later),
 * then a bounded recursive search.
 */
function extractDownloadExcelChartBody(
  ctx: unknown
): DownloadExcelChartResponseInner {
  if (!ctx || typeof ctx !== "object") return {};

  const c = ctx as Record<string, unknown>;

  // Shape 1 (CAPTURED 2026-04-30): { type, data: { status, contentType, bodyAsBase64 } }
  const dataLevel = c["data"];
  if (dataLevel && typeof dataLevel === "object" && !Array.isArray(dataLevel)) {
    const d = dataLevel as Record<string, unknown>;
    if (typeof d["bodyAsBase64"] === "string") {
      return d as DownloadExcelChartResponseInner;
    }
    // Defensive: the host might wrap one level deeper later. Walk
    // `data.body.data` as `previewChart` does, just in case.
    const body = d["body"];
    if (body && typeof body === "object" && !Array.isArray(body)) {
      const inner = (body as Record<string, unknown>)["data"];
      if (inner && typeof inner === "object" && !Array.isArray(inner)) {
        if (typeof (inner as Record<string, unknown>)["bodyAsBase64"] === "string") {
          return inner as DownloadExcelChartResponseInner;
        }
      }
      if (typeof (body as Record<string, unknown>)["bodyAsBase64"] === "string") {
        return body as DownloadExcelChartResponseInner;
      }
    }
  }

  // Shape 2 (already-unwrapped flat object — `bodyAsBase64` at the top).
  if (typeof c["bodyAsBase64"] === "string") {
    return c as DownloadExcelChartResponseInner;
  }

  // Shape 3 (defensive): bounded recursive search for any node
  // carrying `bodyAsBase64`. Cheap insurance against future host
  // rearrangement.
  const recursive = findBodyAsBase64Node(c);
  if (recursive) return recursive as DownloadExcelChartResponseInner;

  // No bytes anywhere — return the top-level object so the caller's
  // `raw` escape hatch still carries something useful for diagnosis.
  return c as DownloadExcelChartResponseInner;
}

/**
 * Build the `factset.DownloadExcelChart` request body. Validates
 * inputs, then delegates to the shared assembler with
 * download-specific defaults (`isEdit: true` for edit-mode metadata,
 * `generatePreview: "None"` — no PNG side-channel when the goal is
 * the workbook bytes).
 *
 * Same `chartType` semantics as `buildPreviewChartBody`: forwarded
 * verbatim to the host (catalog lives server-side); throws if
 * missing/empty so an LLM that forgets to pick a chartType doesn't
 * silently get a price-chart workbook.
 *
 * This is the one body that opts into `summaryTable`. It is the legacy
 * twin of `buildDownloadExcelChartPayload`, so a user without the
 * office-tools scope gets the same table the API path would send.
 */
function buildDownloadExcelChartBody(
  params: FactsetChartParameters,
  options: DownloadExcelChartOptions
): string {
  assertChartParametersValid(params, "downloadExcelChart");
  return buildChartParametersBody(
    params,
    options.isEdit ?? true,
    options.generatePreview ?? "None",
    { summaryTable: params.summaryTable }
  );
}

/**
 * Coerce an unknown to `string | undefined`. Used to defend the
 * public `FactsetDownloadExcelChart` typing against the response
 * shape being still-untyped.
 */
function asOptionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * Coerce an arbitrary value to a finite number, or `undefined`. Used
 * to surface the host's `status` field robustly — it has been observed
 * as a JS number on the wire, but stringly-typed numeric statuses
 * (`"200"`) would be a plausible host-side regression.
 */
function asOptionalFiniteNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

/**
 * Request a downloadable Excel chart workbook via
 * `factset.DownloadExcelChart`.
 *
 * Returns `{ workbookBase64, contentType, status, error?, raw }`. On
 * the captured-2026-04-30 shape, `workbookBase64` carries the raw
 * `.xlsx` bytes (PK ZIP magic confirmed) and `contentType` is
 * `"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"`.
 *
 * **Error semantics.** A transport / timeout failure rejects the
 * promise (matches every other wrapper in this file). A *host-reported*
 * failure resolves the promise but surfaces through `error`:
 *   - Non-200 `status` → `error = "Host returned status N"`.
 *   - 200 but no `bodyAsBase64` → `error = "Host returned status 200 but no bodyAsBase64"`.
 *   - 200 with bytes → `error = null` (success).
 *   - No `status` at all but bytes present → `error = undefined` (caller
 *     should treat as success — bytes are the source of truth).
 * Callers can treat `workbookBase64` as the success signal and `error`
 * as a richer diagnostic without writing their own status-code logic.
 *
 * Rejects on transport / timeout errors or invalid input (empty
 * `tickers`).
 */
export async function downloadExcelChartViaIntents(
  parameters: FactsetChartParameters,
  options: DownloadExcelChartOptions = {}
): Promise<FactsetDownloadExcelChart> {
  const body = buildDownloadExcelChartBody(parameters, options);
  const timeoutMs = options.timeoutMs ?? LONG_RUNNING_FACTSET_INTENT_TIMEOUT_MS;

  const ctx = await raiseIntent(
    DOWNLOAD_EXCEL_CHART_INTENT_NAME,
    { type: DOWNLOAD_EXCEL_CHART_CONTEXT_TYPE, body },
    timeoutMs,
    DOWNLOAD_EXCEL_CHART_APP_ID,
    options.signal
  );

  const inner = extractDownloadExcelChartBody(ctx);

  const workbookBase64 = asOptionalString(inner.bodyAsBase64);
  const contentType = asOptionalString(inner.contentType);
  const status = asOptionalFiniteNumber(inner.status);

  // Resolve the synthesised error message from the wire-side status +
  // bytes presence. See `FactsetDownloadExcelChart.error` JSDoc above
  // for the full table of cases.
  let error: string | null | undefined;
  if (status !== undefined && status !== 200) {
    error = `Host returned status ${status}`;
  } else if (status === 200 && !workbookBase64) {
    error = "Host returned status 200 but no bodyAsBase64";
  } else if (workbookBase64) {
    error = null;
  } else {
    error = undefined;
  }

  return {
    workbookBase64,
    contentType,
    status,
    error,
    raw: inner as Record<string, unknown>,
  };
}

// ============================================================================
// factset.DownloadActiveGraph  (NB: NOT in the `factset.Spreadsheet.*` namespace)
// ============================================================================
//
// Sibling of `factset.DownloadExcelChart`. Same `OI Remote Iframe` relay,
// same casing-split conventions (`factset.DownloadActiveGraph` for the
// wire intent, `factset.downloadActiveGraph` for the request
// `context.type`), same JSON-stringified `context.body`, same
// chart-parameter / chart-format request shape, and the **same** flatter
// response envelope (`data.bodyAsBase64` directly on the first level —
// no `data.body.data` second nesting). The request bodies are
// byte-for-byte identical to `DownloadExcelChart`'s — only the wire
// intent name differs.
//
// **What's actually different is what's INSIDE the `.xlsx` bytes.**
// The two intents both return Open-XML workbook bytes
// (`application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`),
// but the chart is encoded differently:
//
//   - `DownloadExcelChart` → workbook contains a NATIVE Excel chart
//     (`xl/charts/chart1.xml`). Editable as a regular Excel chart in
//     any Excel (Office, web, LibreOffice, Sheets via import). Fully
//     decoupled from FactSet's data layer; portable.
//   - `DownloadActiveGraph` → workbook contains a FactSet ActiveGraph
//     as an **OLE-embedded object** (`xl/embeddings/oleObject1.bin`)
//     plus a static EMF picture (`xl/media/image1.emf`) and a VML
//     drawing (`xl/drawings/vmlDrawing1.vml`) used as the OLE's
//     visual fallback. Inside FactSet workstations (which have the
//     ActiveGraph plugin), the OLE deserialises back into a live
//     FactSet chart that re-pulls fresh data and re-opens with the
//     same `chartParameters` for editing. Outside FactSet, the EMF
//     image is what the user sees — the chart is no longer
//     interactive.
//
// **Choose by user intent, not by interchangeability.** The two
// intents are sibling building blocks for two different product
// experiences. Picking one of them is a UX decision, not an
// optimisation:
//
//   - Use `DownloadExcelChart` when the user wants to **share a
//     chart with anyone, anywhere**. The output is a regular Excel
//     workbook the recipient can open in any Excel without FactSet.
//   - Use `DownloadActiveGraph` when the user wants to **embed a
//     live FactSet chart in their own Excel model** so they (or a
//     teammate on the same FactSet entitlement) can re-edit the
//     chart through FactSet and re-pull fresh data on demand.
//
// Same caveats as `DownloadExcelChart` apply — Finster code paths
// that consume these bytes via
// `factset.Spreadsheet.InsertWorksheetsFromBase64` will lose the
// chart object during import (the host primitive copies worksheet
// cell data only — chart drawings AND OLE embeds are stripped).
// The viable consumers are:
//   - The wrapper / dispatcher (this module + `excelHostOps.ts`),
//     for any future caller that genuinely needs the bytes
//     (e.g. real "save .xlsx to disk" flow outside the iframe).
//   - A non-bridge browser file-download path (`<a download>` /
//     `msSaveOrOpenBlob` / clipboard data URL) where the bytes can
//     reach the file system.
//
// Like `DownloadExcelChart`, this intent is tracked alongside the
// other sibling-namespace intents in
// `f2_docs/excel/finster-docs/missing-intents-v0.md` §4.4 (wrapper /
// dispatcher / consumer summary). `excel-docs/intent-details.md` →
// "Download Active Graph" remains the per-intent capture source.
//
// Captured 2026-04-30 against a live FactSet Workstation host
// (HTTP-200, MIME `application/vnd.openxmlformats-officedocument.
// spreadsheetml.sheet`). Captured workbook contained the OLE +
// EMF + VML triple described above; no `xl/charts/chart1.xml`.

const DOWNLOAD_ACTIVE_GRAPH_INTENT_NAME = "factset.DownloadActiveGraph";
const DOWNLOAD_ACTIVE_GRAPH_CONTEXT_TYPE = "factset.downloadActiveGraph";
const DOWNLOAD_ACTIVE_GRAPH_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Per-call options for `downloadActiveGraphViaIntents`.
 *
 * Field-for-field identical to `DownloadExcelChartOptions`. Kept as a
 * separate type so future divergence (e.g. an `embedFormat` field
 * specific to OLE embeds, an OLE-version override, etc.) lands on the
 * sibling without disturbing `DownloadExcelChartOptions`. If the two
 * stay identical for long enough that drift becomes implausible, the
 * two `Options` types could collapse into a shared
 * `DownloadChartOptions` — but the file convention is one block per
 * intent, so resist hoisting until a third sibling lands.
 */
export interface DownloadActiveGraphOptions {
  /** When `true`, the host produces an edit-mode workbook (the
   *  ActiveGraph OLE is round-trippable through a follow-up call).
   *  Default `true` — matches the captured payload's `data.isEdit`. */
  isEdit?: boolean;
  /** Whether the host should attach a PNG preview alongside the
   *  workbook bytes. The captured payload uses `"None"`. Pass-through
   *  string so future enums work without a wrapper update. Default
   *  `"None"`. */
  generatePreview?: "None" | "Png" | string;
  /** Per-call timeout override. Uses the same five-minute default as
   *  `previewChart` / `downloadExcelChart`. */
  timeoutMs?: number;
  /** Abort the wait for the host's reply. Same semantics as
   *  `PreviewChartOptions.signal`. */
  signal?: AbortSignal;
}

/**
 * Inner body shape returned by the host (the `data` level of
 * `result.context`). Captured 2026-04-30:
 *
 *   { status: 200,
 *     contentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
 *     bodyAsBase64: "UEsDBBQ…" /* PK-magic prefix → confirmed valid xlsx *\/ }
 *
 * Byte-identical to `DownloadExcelChartResponseInner`. Kept as a
 * separate type symbol so the wrapper's defensive coercions read
 * cleanly per intent and a future host divergence (e.g. an
 * ActiveGraph-specific `embedVersion` field) doesn't widen the
 * sibling's surface.
 *
 * Fields are typed as `unknown` so the wrapper coerces defensively at
 * the boundary — host-side typing changes (string → number for
 * `status`, etc.) won't cascade into the public surface.
 */
interface DownloadActiveGraphResponseInner {
  /** HTTP-style status code. `200` is success; non-200 surfaces as an
   *  `error` on the public type so callers don't have to inspect it. */
  status?: unknown;
  /** MIME type of the returned workbook —
   *  `"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"`
   *  on the captured trace. Same MIME as `DownloadExcelChart`; the
   *  difference between the two intents is what's INSIDE the
   *  workbook, not the wire MIME. */
  contentType?: unknown;
  /** Base64-encoded `.xlsx` bytes (no `data:` prefix). The base64
   *  payload starts with `UEsDBBQ` (PK ZIP magic), confirming a real
   *  Open-XML workbook on the wire. The workbook contains
   *  `xl/embeddings/oleObject1.bin` (the FactSet ActiveGraph OLE
   *  object) + `xl/media/image1.emf` (its EMF visual fallback) +
   *  `xl/drawings/vmlDrawing1.vml` — NOT `xl/charts/chart1.xml`. */
  bodyAsBase64?: unknown;
  /** Carry any other fields verbatim — the host catalog may grow
   *  (e.g. an `embedVersion` field tied to the OLE object's schema
   *  version) and the wrapper shouldn't lose data the caller might
   *  want to inspect via the public `raw` escape hatch. */
  [key: string]: unknown;
}

/**
 * Public-facing download-active-graph result. Mirrors
 * `FactsetDownloadExcelChart` — same fields, same coercion rules,
 * same `error`-synthesis semantics. The bytes are surfaced as
 * `workbookBase64` (NOT `bodyAsBase64`) for naming consistency with
 * the sibling result.
 *
 * **Note on field name.** "workbook" is a slight misnomer here in
 * the sense that the workbook is a *carrier* for an OLE embed; the
 * primary payload is the FactSet ActiveGraph object. We keep the
 * field name as `workbookBase64` because (a) the wire MIME type is
 * the workbook MIME type, (b) consumers receive a real `.xlsx` they
 * can hand to a host primitive that opens workbooks, and (c) it
 * keeps the public surface symmetric with `FactsetDownloadExcelChart`
 * so a caller can switch between the two intents by changing one
 * function name.
 */
export interface FactsetDownloadActiveGraph {
  /** Base64-encoded `.xlsx` bytes (no `data:` prefix). The workbook
   *  carries the FactSet ActiveGraph as an OLE-embedded object plus
   *  a static EMF preview image. Mapped from the wire-side
   *  `data.bodyAsBase64`. */
  workbookBase64?: string;
  /** MIME type the host returned —
   *  `"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"`
   *  for `.xlsx`. Mapped from the wire-side `data.contentType`. */
  contentType?: string;
  /** Numeric HTTP-style status the host reported (200 on success). */
  status?: number;
  /** Synthesised error message — same semantics as
   *  `FactsetDownloadExcelChart.error`:
   *    - non-200 status → `"Host returned status N"`
   *    - 200 with no bytes → `"Host returned status 200 but no bodyAsBase64"`
   *    - 200 with bytes → `null` (success)
   *    - no status, bytes present → `undefined` (treat as success). */
  error?: string | null;
  /** Raw inner body — defensive escape hatch for callers that want
   *  to introspect fields the wrapper hasn't taught itself about. */
  raw?: Record<string, unknown>;
}

/**
 * Walk the response context and pull out the `data` level.
 * Structurally identical to `extractDownloadExcelChartBody` — same
 * captured envelope shape, same defensive fallback walks. Kept as a
 * parallel implementation rather than calling the sibling directly
 * so each intent block is self-contained per the file convention,
 * and so a future divergence (e.g. ActiveGraph response gaining an
 * extra envelope level) lands on this sibling without touching
 * `extractDownloadExcelChartBody`.
 */
function extractDownloadActiveGraphBody(
  ctx: unknown
): DownloadActiveGraphResponseInner {
  if (!ctx || typeof ctx !== "object") return {};

  const c = ctx as Record<string, unknown>;

  // Shape 1 (CAPTURED 2026-04-30): { type, data: { status, contentType, bodyAsBase64 } }
  // Same single-level envelope as `DownloadExcelChart` — there is no
  // `data.body.data` second level for either of these siblings on the
  // captured trace.
  const dataLevel = c["data"];
  if (dataLevel && typeof dataLevel === "object" && !Array.isArray(dataLevel)) {
    const d = dataLevel as Record<string, unknown>;
    if (typeof d["bodyAsBase64"] === "string") {
      return d as DownloadActiveGraphResponseInner;
    }
    // Defensive: the host might wrap one level deeper later. Walk
    // `data.body.data` as `previewChart` does, just in case — kept
    // symmetric with `extractDownloadExcelChartBody` so a future
    // host-side normalisation can land on either intent without
    // breaking the other.
    const body = d["body"];
    if (body && typeof body === "object" && !Array.isArray(body)) {
      const inner = (body as Record<string, unknown>)["data"];
      if (inner && typeof inner === "object" && !Array.isArray(inner)) {
        if (typeof (inner as Record<string, unknown>)["bodyAsBase64"] === "string") {
          return inner as DownloadActiveGraphResponseInner;
        }
      }
      if (typeof (body as Record<string, unknown>)["bodyAsBase64"] === "string") {
        return body as DownloadActiveGraphResponseInner;
      }
    }
  }

  // Shape 2 (already-unwrapped flat object — `bodyAsBase64` at the top).
  if (typeof c["bodyAsBase64"] === "string") {
    return c as DownloadActiveGraphResponseInner;
  }

  // Shape 3 (defensive): bounded recursive search for any node carrying
  // `bodyAsBase64`. Reuses the helper defined in the `DownloadExcelChart`
  // block since the search criterion (`bodyAsBase64` on an object node)
  // is unchanged. Cheap insurance against future host rearrangement.
  const recursive = findBodyAsBase64Node(c);
  if (recursive) return recursive as DownloadActiveGraphResponseInner;

  // No bytes anywhere — return the top-level object so the caller's
  // `raw` escape hatch still carries something useful for diagnosis.
  return c as DownloadActiveGraphResponseInner;
}

/**
 * Build the `factset.DownloadActiveGraph` request body. Validates
 * inputs, then delegates to the shared assembler with the same
 * defaults as `buildDownloadExcelChartBody` — the captured
 * ActiveGraph payload carries `isEdit: true` + `generatePreview: "None"`,
 * and its body matches the DownloadExcelChart body except for
 * `summaryTable`, which only the Excel-chart download opts into — an
 * ActiveGraph carries no summary table. Otherwise only the wire intent
 * name (set by the caller via `DOWNLOAD_ACTIVE_GRAPH_INTENT_NAME`)
 * differs.
 */
function buildDownloadActiveGraphBody(
  params: FactsetChartParameters,
  options: DownloadActiveGraphOptions
): string {
  assertChartParametersValid(params, "downloadActiveGraph");
  return buildChartParametersBody(
    params,
    options.isEdit ?? true,
    options.generatePreview ?? "None"
  );
}

/**
 * Request a downloadable workbook with a FactSet ActiveGraph
 * OLE-embedded inside, via `factset.DownloadActiveGraph`.
 *
 * Returns `{ workbookBase64, contentType, status, error?, raw }`.
 * On the captured-2026-04-30 shape, `workbookBase64` carries the
 * raw `.xlsx` bytes (PK ZIP magic confirmed; workbook contains
 * `xl/embeddings/oleObject1.bin` + `xl/media/image1.emf` for the
 * ActiveGraph + EMF fallback) and `contentType` is the standard
 * `.xlsx` MIME.
 *
 * **Error semantics.** A transport / timeout failure rejects the
 * promise (matches every other wrapper in this file). A *host-reported*
 * failure resolves the promise but surfaces through `error`:
 *   - Non-200 `status` → `error = "Host returned status N"`.
 *   - 200 but no `bodyAsBase64` → `error = "Host returned status 200 but no bodyAsBase64"`.
 *   - 200 with bytes → `error = null` (success).
 *   - No `status` at all but bytes present → `error = undefined` (caller
 *     should treat as success — bytes are the source of truth).
 * Callers can treat `workbookBase64` as the success signal and `error`
 * as a richer diagnostic without writing their own status-code logic.
 * Same table as `downloadExcelChartViaIntents` — kept inline rather
 * than cross-referenced so the next dev reading just this function
 * doesn't have to chase the sibling.
 *
 * **When to call this vs `downloadExcelChartViaIntents`.** See the
 * section comment above this block for the product distinction.
 * TL;DR — call this when the user wants a *live, re-editable
 * FactSet chart embedded in their Excel*; call the sibling when the
 * user wants a portable native-Excel chart they can share with
 * anyone.
 *
 * Rejects on transport / timeout errors or invalid input (empty
 * `tickers`). Pass `options.signal` to make the wait abortable;
 * abort rejects the promise with a `DOMException` named
 * `AbortError`. Late host replies after abort are dropped silently
 * by the transport (the host has no cancel surface).
 */
export async function downloadActiveGraphViaIntents(
  parameters: FactsetChartParameters,
  options: DownloadActiveGraphOptions = {}
): Promise<FactsetDownloadActiveGraph> {
  const body = buildDownloadActiveGraphBody(parameters, options);
  const timeoutMs = options.timeoutMs ?? LONG_RUNNING_FACTSET_INTENT_TIMEOUT_MS;

  const ctx = await raiseIntent(
    DOWNLOAD_ACTIVE_GRAPH_INTENT_NAME,
    { type: DOWNLOAD_ACTIVE_GRAPH_CONTEXT_TYPE, body },
    timeoutMs,
    DOWNLOAD_ACTIVE_GRAPH_APP_ID,
    options.signal
  );

  const inner = extractDownloadActiveGraphBody(ctx);

  const workbookBase64 = asOptionalString(inner.bodyAsBase64);
  const contentType = asOptionalString(inner.contentType);
  const status = asOptionalFiniteNumber(inner.status);

  // Same error-synthesis table as `downloadExcelChartViaIntents`.
  let error: string | null | undefined;
  if (status !== undefined && status !== 200) {
    error = `Host returned status ${status}`;
  } else if (status === 200 && !workbookBase64) {
    error = "Host returned status 200 but no bodyAsBase64";
  } else if (workbookBase64) {
    error = null;
  } else {
    error = undefined;
  }

  return {
    workbookBase64,
    contentType,
    status,
    error,
    raw: inner as Record<string, unknown>,
  };
}

// ============================================================================
// factset.Charting.GetActiveGraphXml  (NB: NOT in the `factset.Spreadsheet.*` namespace)
// ============================================================================
//
// Inverse of `factset.Spreadsheet.AddActiveGraph` — read the
// currently-active FactSet ActiveGraph chart's serialised
// `<FDSChart xmlns="http://xml.factset.com/chart">…</FDSChart>` XML
// back from the host. Round-trippable: feeding the result back to
// `addActiveGraphViaIntents` re-inserts the same chart. Same XML
// dialect that `previewChart` returns on `fdsChartXml`.
//
// Wire shape (request, captured 2026-05-06):
//
//   intent  = "factset.Charting.GetActiveGraphXml"  (note: NOT
//             `factset.Spreadsheet.Charting.*` — different namespace
//             from the `Charting.GetCharts` / `Charting.AddExcelChart`
//             family, despite the similar-sounding name).
//   appId   = "OI Remote Iframe"
//   context = { type: "fdc3.nothing" }    (no input — operates on
//             whatever ActiveGraph chart the host considers "active"
//             at the time of the call).
//
// Wire shape (response, captured 2026-05-06):
//
//   {
//     context: {
//       type: "factset.charting.getActiveGraphXmlResult",
//       fdsChartXml: "<FDSChart xmlns=\"http://xml.factset.com/chart\">\r\n…</FDSChart>"
//     }
//   }
//
//   `raiseIntent` already unwraps `event.data.result.context`, so the
//   value reaching the wrapper is the inner `{ type, fdsChartXml }`
//   object. No nested `data.body.data` walking needed (unlike
//   `getStylesets` / `getRefreshStatus`); the field sits directly on
//   the unwrapped context.
//
// Behavioural notes (captured payload):
//
//   - The XML carries the chart's metadata AND every captured data
//     point (Y values, X dates, plot/axis settings, the
//     `XMLTemplate` blob the host used to regenerate it). It is
//     therefore much larger than `previewChart`'s XML, which can
//     elide the data block when the host re-pulls it on insert.
//     Plan storage / transport accordingly — captured sample for one
//     1-year price chart was ~22 KB of XML.
//   - Round-tripping through `addActiveGraph` preserves the captured
//     data points verbatim. That is the correct behaviour for
//     "export this chart as-is" / "stash a backup", but the WRONG
//     behaviour for "send this chart's recipe to a teammate so they
//     can re-pull on their entitlement" — the recipient gets the
//     captured data, not a fresh refresh, until they explicitly
//     trigger a host-side refresh.
//   - "No active chart" responses have not been captured. The
//     wrapper coerces a missing / non-string `fdsChartXml` to
//     `undefined` rather than throwing — callers can treat that as
//     "no active chart" instead of having to special-case a sentinel.
//
// Pairs with the not-yet-wired `factset.Charting.SetActiveGraphXml`
// (the inverse write — replace the active chart's XML with a new
// one). Tracked separately in `intent-hookup-status.md`.

const GET_ACTIVE_GRAPH_XML_INTENT_NAME = "factset.Charting.GetActiveGraphXml";
const GET_ACTIVE_GRAPH_XML_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Per-call options for `getActiveGraphXmlViaIntents`. The captured
 * request carries no body fields, but the option bag is exposed for
 * cancellation symmetry with the rest of the `OI Remote Iframe`
 * intents and so a future host extension (workbook scoping, chart
 * targeting) lands without breaking the no-arg call site.
 */
export interface GetActiveGraphXmlOptions {
  /** Per-call timeout override. The XML can be large (~20 KB+ on a
   *  rich chart) but the host serialises it locally — no
   *  data-pull latency band — so default to the shared
   *  `DEFAULT_TIMEOUT_MS` rather than the chart-build 4× bump that
   *  `previewChart` / `download*` use. */
  timeoutMs?: number;
  /** Abort the wait for the host's reply. Same semantics as
   *  `PreviewChartOptions.signal`. */
  signal?: AbortSignal;
}

/**
 * Read the currently-active FactSet ActiveGraph chart's serialised
 * `<FDSChart>` XML via `factset.Charting.GetActiveGraphXml`.
 *
 * **NOT exposed to the planner.** The workbook-context extractor no
 * longer invokes this wrapper, so the LLM never sees the FDSChart XML
 * in its context payload. Kept wired for the dev-tools FactSet Tools
 * dialog and explicit non-agent callers only — round-trip mutations
 * from the LLM are deferred until the surface stabilises.
 *
 * Returns `{ fdsChartXml }`, with `fdsChartXml` set to the
 * round-trippable XML string when the host returned one and
 * `undefined` when the host elided the field (treat as "no active
 * chart" / "host has nothing to serialise"). The XML is suitable as
 * the `activeGraphContent` argument to
 * `addActiveGraphViaIntents`.
 *
 * Rejects only on transport / timeout errors. A host that
 * unexpectedly omits `fdsChartXml` resolves to `{ fdsChartXml:
 * undefined }` rather than throwing — the field is best-effort.
 */
export async function getActiveGraphXmlViaIntents(
  options: GetActiveGraphXmlOptions = {}
): Promise<FactsetGetActiveGraphXmlResult> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const ctx = (await raiseIntent(
    GET_ACTIVE_GRAPH_XML_INTENT_NAME,
    { type: "fdc3.nothing" },
    timeoutMs,
    GET_ACTIVE_GRAPH_XML_APP_ID,
    options.signal
  )) as { fdsChartXml?: unknown } | undefined;

  const fdsChartXml =
    typeof ctx?.fdsChartXml === "string" && ctx.fdsChartXml.length > 0
      ? ctx.fdsChartXml
      : undefined;

  return { fdsChartXml };
}

// ============================================================================
// factset.GetStylesets  (NB: NOT in the `factset.Spreadsheet.*` namespace)
// ============================================================================
//
// Read the host's styleset catalog. The response carries:
//   - `stylesets[]` — every styleset the host knows about, as
//     `{ id, name }`. The `id` is exactly the value the chart-family
//     intents (`PreviewChart`, `DownloadExcelChart`, `DownloadActiveGraph`)
//     accept on `chartParameters.stylesetId`; the `name` is suitable
//     for a picker UI.
//   - `currentStylesetId` — the host's current default. Match against
//     `stylesets[].id` to pre-select / highlight in a picker.
//
// Captured 2026-05-01 against a live FactSet Workstation host. Captured
// id set: `fds_classic`, `fds_default`, `fds_basic`, `PA_Carbon`,
// `PA_QUARTZ`, `PA_Legacy`, `PA_PEARL`. Note the mixed casing
// (`fds_*` vs `PA_*`) — pass through verbatim, do NOT normalise.
//
// **Departures from sibling Spreadsheet intents — three shared with
// `previewChart`, plus a no-input quirk:**
//
//   1. **Intent name uses CapitalizedCamelCase, not lowerCamelCase.**
//      The wire intent name is `factset.GetStylesets` (capital `G`).
//      Same casing-split convention as `factset.PreviewChart` and the
//      `Download*` siblings.
//   2. **Different target appId.** Routes to `"OI Remote Iframe"`,
//      NOT the default `"com.factset.workstation"`. Wrappers must
//      override the appId per call.
//   3. **Response envelope is the two-level `data.body.data` form**
//      (same as `previewChart`'s — NOT the flatter one-level form
//      `Download*` uses). The result context type is
//      `factset.common.getStylesetsResult` (note the
//      `factset.common.*` namespace). `extractGetStylesetsBody`
//      walks the documented nesting plus the obvious flatter
//      shapes and falls back to a bounded recursive `stylesets`-
//      array search.
//   4. **Request takes no input.** The captured request uses
//      `context.type: "fdc3.nothing"` — there are no per-call options
//      (no workbook scoping, no styleset-id filter). If the host
//      grows arguments later, add them to a `GetStylesetsOptions`
//      shape on the wrapper without breaking the no-arg call site.
//
// See captured payload + envelope details in
// `excel-docs/intent-details.md` → "Get Stylesets". Like the other
// chart-family intents (`PreviewChart`, `Download*`), this is
// tracked alongside the sibling-namespace intents in
// `f2_docs/excel/finster-docs/missing-intents-v0.md` §4.4.

const GET_STYLESETS_INTENT_NAME = "factset.GetStylesets";
const GET_STYLESETS_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Per-call options for `getStylesetsViaIntents`. The captured request
 * carries no fields (input is `fdc3.nothing`), but the option bag is
 * exposed today so a future host extension (workbook scoping, filtering)
 * lands without breaking the no-arg call site, and so call sites can
 * already pass `signal` for cancellation symmetry with the other
 * `OI Remote Iframe` intents.
 */
export interface GetStylesetsOptions {
  /** Per-call timeout override. Catalog reads are fast; default to the
   *  shared `DEFAULT_TIMEOUT_MS` rather than the chart-build 4× bump
   *  that `previewChart` / `download*` use. */
  timeoutMs?: number;
  /** Abort the wait for the host's reply. Same semantics as
   *  `PreviewChartOptions.signal`. */
  signal?: AbortSignal;
}

/**
 * Inner-body shape after envelope unwrapping. Loose typing because the
 * host returns these as raw JSON values that we coerce below.
 */
interface GetStylesetsResponseInner {
  stylesets?: unknown;
  currentStylesetId?: unknown;
  [key: string]: unknown;
}

/**
 * Recursively search `value` for the first object-shaped node carrying
 * a `stylesets` array. Used as the fallback after the documented
 * shape walk below — if the host ever rearranges the envelope, we'd
 * rather find the catalog one level deeper than silently return an
 * empty list.
 *
 * The walk is bounded (`maxDepth`) and skips arrays / primitives, so
 * pathological inputs can't blow the stack.
 */
function findStylesetsNode(
  value: unknown,
  maxDepth: number = 6
): GetStylesetsResponseInner | undefined {
  if (maxDepth < 0) return undefined;
  if (!value || typeof value !== "object") return undefined;
  if (Array.isArray(value)) return undefined;

  const obj = value as Record<string, unknown>;
  if (Array.isArray(obj["stylesets"])) {
    return obj as GetStylesetsResponseInner;
  }
  for (const child of Object.values(obj)) {
    const hit = findStylesetsNode(child, maxDepth - 1);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * Walk the response context and pull out the `stylesets`-bearing inner
 * body. `raiseIntent` already unwraps `event.data.result.context`, so
 * the value reaching us here is whatever the host placed inside
 * `result.context`. The captured envelope is the two-level
 * `{ data: { status, contentType, body: { data: { stylesets,
 * currentStylesetId } } } }` form — same shape as `previewChart`'s
 * response, NOT the flatter `Download*` form. Tolerate that nested
 * form, the obvious flatter forms, and fall back to a bounded
 * recursive search.
 */
function extractGetStylesetsBody(ctx: unknown): GetStylesetsResponseInner {
  if (!ctx || typeof ctx !== "object") return {};

  const c = ctx as Record<string, unknown>;

  // Shape 1 (CAPTURED 2026-05-01): { data: { status, contentType, body: { data: <inner> } } }
  const dataLevel = c["data"];
  if (dataLevel && typeof dataLevel === "object" && !Array.isArray(dataLevel)) {
    const d = dataLevel as Record<string, unknown>;
    const body = d["body"];
    if (body && typeof body === "object" && !Array.isArray(body)) {
      const innerData = (body as Record<string, unknown>)["data"];
      if (
        innerData &&
        typeof innerData === "object" &&
        !Array.isArray(innerData) &&
        Array.isArray((innerData as Record<string, unknown>)["stylesets"])
      ) {
        return innerData as GetStylesetsResponseInner;
      }
      // Some hosts may collapse the second `.data` level.
      if (Array.isArray((body as Record<string, unknown>)["stylesets"])) {
        return body as GetStylesetsResponseInner;
      }
    }
    // Shape 2: { data: <inner> }
    if (Array.isArray(d["stylesets"])) {
      return d as GetStylesetsResponseInner;
    }
  }

  // Shape 3 (already-unwrapped): { stylesets, currentStylesetId } —
  // handle in case the host normalises the envelope later.
  if (Array.isArray(c["stylesets"])) {
    return c as GetStylesetsResponseInner;
  }

  // Shape 4 (defensive): bounded recursive search for any node
  // carrying a `stylesets` array. Cheap insurance against future
  // host rearrangement.
  const recursive = findStylesetsNode(c);
  if (recursive) return recursive;

  return {};
}

/**
 * Coerce the wire-side `stylesets[]` payload into the typed
 * `FactsetStyleset[]` surface. Filters out entries that are missing
 * either field — a styleset without an `id` can't be selected by the
 * chart intents anyway, and one without a `name` has no UI label.
 */
function coerceStylesets(value: unknown): FactsetStyleset[] {
  if (!Array.isArray(value)) return [];
  const out: FactsetStyleset[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    if (typeof e["id"] !== "string" || typeof e["name"] !== "string") continue;
    out.push({ id: e["id"], name: e["name"] });
  }
  return out;
}

/**
 * Read the host's styleset catalog via `factset.GetStylesets`.
 *
 * Returns `{ stylesets, currentStylesetId? }`. The `id` field on each
 * entry is exactly what the chart-family intents (`PreviewChart`,
 * `DownloadExcelChart`, `DownloadActiveGraph`) accept on
 * `chartParameters.stylesetId`. Use `currentStylesetId` to highlight
 * the host's active default in a picker.
 *
 * Rejects only on transport / timeout errors. A host that returns an
 * empty catalog resolves to `{ stylesets: [], currentStylesetId:
 * undefined }` — callers should treat empty as "host didn't surface
 * any stylesets" and fall back to the chart-family default
 * (`stylesetId: ""` / omit).
 */
export async function getStylesetsViaIntents(
  options: GetStylesetsOptions = {}
): Promise<FactsetGetStylesetsResult> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const ctx = await raiseIntent(
    GET_STYLESETS_INTENT_NAME,
    { type: "fdc3.nothing" },
    timeoutMs,
    GET_STYLESETS_APP_ID,
    options.signal
  );

  const inner = extractGetStylesetsBody(ctx);

  return {
    stylesets: coerceStylesets(inner.stylesets),
    currentStylesetId:
      typeof inner.currentStylesetId === "string"
        ? inner.currentStylesetId
        : undefined,
  };
}

// ============================================================================
// factset.GetTemplatesList  (NB: NOT in the `factset.Spreadsheet.*` namespace)
// ============================================================================
//
// Read the host's template library catalog. The response carries one
// entry per `.xlsx` template the host ships, with everything a picker
// UI / instantiation flow needs:
//   - `name` / `description` / `extendedDescription` for picker copy.
//   - `category` for grouping (folder-style strings like
//     `"Company Overview/"`, `"Financials ＆ Estimates/Standardized
//     Financials/"`; preserve verbatim — the fullwidth `＆` U+FF06 is
//     intentional on the wire).
//   - `key` — lowercased `.xlsx` filename, the host's stable per-
//     template identifier; prefer over `name` for keys / dedup /
//     cross-refresh diffs (`name` can include trailing whitespace and
//     human styling).
//   - `templateUrl` / `previewUrl` — URLs to fetch the `.xlsx` bytes
//     and the PDF preview respectively. `previewUrl` is `null` on
//     entries the host doesn't ship a preview for.
//   - `metadata.inputs[]` — input parameters the template wants at
//     instantiation time (`inputType` is a string enum: captured values
//     include `"main_identifier"`, `"start_date"`, `"end_date"`,
//     `"currency"`, `"frequency"`).
//   - `metadata.identifierGroups` — open-ended map (captured shape is
//     `{ "group1": { "nbEntries": N }, ... }`) for templates that take
//     multi-identifier slots (e.g. peer-comp templates).
//
// **Departures from sibling Spreadsheet intents — three shared with
// `previewChart` / `getStylesets`, plus an envelope quirk:**
//
//   1. **Intent name uses CapitalizedCamelCase, not lowerCamelCase.**
//      The wire intent name is `factset.GetTemplatesList` (capital
//      `G`, capital `T`, capital `L`). Same casing-split convention
//      as `factset.GetStylesets` and `factset.PreviewChart`.
//   2. **Different target appId.** Routes to `"OI Remote Iframe"`,
//      NOT the default `"com.factset.workstation"`. Wrappers must
//      override the appId per call.
//   3. **Response envelope is the two-level `data.body.data` form**
//      (same as `previewChart` / `getStylesets`). Result context
//      type is `factset.common.getTemplatesListResult` (note the
//      `factset.common.*` namespace).
//   4. **Inner `data` is a RAW ARRAY of template objects, not an
//      object containing one.** This is the divergence from
//      `getStylesets`, where `body.data` was
//      `{ stylesets: [...], currentStylesetId: "..." }`. Here
//      `body.data` is just `[ { ...template1 }, { ...template2 }, ... ]`.
//      `extractGetTemplatesListArray` walks the documented nesting
//      plus the obvious flatter / wrapped shapes, then falls back
//      to a bounded recursive search for any array of objects whose
//      entries look like templates (`templateUrl` + `key`).
//   5. **Request takes no input.** The captured request uses
//      `context.type: "fdc3.nothing"` — there are no per-call
//      options today. If the host grows arguments later (category
//      filter, language filter, paging), add them to a
//      `GetTemplatesListOptions` shape on the wrapper without
//      breaking the no-arg call site.
//
// See captured payload + envelope details in
// `excel-docs/intent-details.md` → "Get Templates List". Like the
// other chart-family / library intents (`PreviewChart`, `Download*`,
// `GetStylesets`), this is tracked alongside the sibling-namespace
// intents in `f2_docs/excel/finster-docs/missing-intents-v0.md` §4.4.

const GET_TEMPLATES_LIST_INTENT_NAME = "factset.GetTemplatesList";
const GET_TEMPLATES_LIST_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Per-call options for `getTemplatesListViaIntents`. The captured
 * request carries no fields (input is `fdc3.nothing`), but the option
 * bag is exposed today so a future host extension (category filter,
 * language filter, paging) lands without breaking the no-arg call
 * site, and so call sites can already pass `signal` for cancellation
 * symmetry with the other `OI Remote Iframe` intents.
 */
export interface GetTemplatesListOptions {
  /** Per-call timeout override. Catalog reads are typically fast, but
   *  the captured payload is multi-KB per template (the host inlines
   *  long `extendedDescription` strings) and the catalog has many
   *  entries — keep this overridable. Defaults to the shared
   *  `DEFAULT_TIMEOUT_MS`. */
  timeoutMs?: number;
  /** Abort the wait for the host's reply. Same semantics as
   *  `GetStylesetsOptions.signal`. */
  signal?: AbortSignal;
}

/**
 * Recursively search `value` for the first array node whose entries
 * look like templates (object-shaped, with at least `templateUrl` and
 * `key`). Used as the fallback after the documented shape walk —
 * cheap insurance against a future host rearrangement of the envelope.
 *
 * The walk is bounded (`maxDepth`) and stops at the first matching
 * array; pathological inputs can't blow the stack.
 */
function findTemplatesArray(
  value: unknown,
  maxDepth: number = 6
): unknown[] | undefined {
  if (maxDepth < 0) return undefined;
  if (!value || typeof value !== "object") return undefined;
  if (Array.isArray(value)) {
    const looksLikeTemplates = value.some(
      (entry) =>
        entry !== null &&
        typeof entry === "object" &&
        !Array.isArray(entry) &&
        typeof (entry as Record<string, unknown>)["templateUrl"] === "string" &&
        typeof (entry as Record<string, unknown>)["key"] === "string"
    );
    return looksLikeTemplates ? value : undefined;
  }
  for (const child of Object.values(value as Record<string, unknown>)) {
    const hit = findTemplatesArray(child, maxDepth - 1);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * Walk the response context and pull out the templates array.
 * `raiseIntent` already unwraps `event.data.result.context`, so the
 * value reaching us here is whatever the host placed inside
 * `result.context`.
 *
 * Captured envelope (2026-05-01):
 *   `{ data: { status, contentType, body: { data: [<entries>] } } }`
 *
 * The inner `data` is a RAW array of template objects — there's no
 * wrapping `{ templates: [...] }` (unlike `getStylesets`). We
 * tolerate that nested form, the obvious flatter / wrapped forms,
 * and fall back to a bounded recursive search.
 */
function extractGetTemplatesListArray(ctx: unknown): unknown[] {
  if (!ctx || typeof ctx !== "object") return [];

  const c = ctx as Record<string, unknown>;

  // Shape 1 (CAPTURED 2026-05-01): { data: { status, contentType, body: { data: [<entries>] } } }
  const dataLevel = c["data"];
  if (dataLevel && typeof dataLevel === "object" && !Array.isArray(dataLevel)) {
    const d = dataLevel as Record<string, unknown>;
    const body = d["body"];
    if (body && typeof body === "object" && !Array.isArray(body)) {
      const innerData = (body as Record<string, unknown>)["data"];
      if (Array.isArray(innerData)) {
        return innerData;
      }
      // Defensive: a host that wraps the array as `{ templates: [...] }`
      // at body.data, mirroring the GetStylesets shape.
      if (
        innerData &&
        typeof innerData === "object" &&
        !Array.isArray(innerData) &&
        Array.isArray((innerData as Record<string, unknown>)["templates"])
      ) {
        return (innerData as Record<string, unknown>)["templates"] as unknown[];
      }
      // Some hosts may collapse the second `.data` level.
      if (Array.isArray((body as Record<string, unknown>)["data"])) {
        return (body as Record<string, unknown>)["data"] as unknown[];
      }
      if (Array.isArray((body as Record<string, unknown>)["templates"])) {
        return (body as Record<string, unknown>)["templates"] as unknown[];
      }
    }
    // Shape 2: { data: [<entries>] }
    if (Array.isArray(d["data"])) {
      return d["data"] as unknown[];
    }
    if (Array.isArray(d["templates"])) {
      return d["templates"] as unknown[];
    }
  }

  // Shape 3 (already-unwrapped): { templates: [<entries>] } — handle
  // in case the host normalises the envelope later.
  if (Array.isArray(c["templates"])) {
    return c["templates"] as unknown[];
  }

  // Shape 4 (defensive): bounded recursive search for any array node
  // whose entries look like templates.
  const recursive = findTemplatesArray(c);
  if (recursive) return recursive;

  return [];
}

/**
 * Coerce the wire-side templates payload into the typed
 * `FactsetTemplate[]` surface. Filters out entries that are missing
 * the host's stable identifier (`key`) or the URL needed to fetch the
 * `.xlsx` bytes (`templateUrl`) — without those two, a picker entry is
 * unusable. All other fields are pass-through with permissive defaults
 * (empty string / empty array / `null` for `previewUrl`) so a partial
 * host catalog still surfaces every entry the caller can act on.
 *
 * Unknown extra fields on the wire are preserved verbatim via the
 * `[extra: string]: unknown` index signature on `FactsetTemplate` —
 * a future host catalog growth (new metadata fields, localised name
 * variants) lands without a wrapper update.
 */
function coerceTemplates(value: unknown): FactsetTemplate[] {
  if (!Array.isArray(value)) return [];
  const out: FactsetTemplate[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const e = entry as Record<string, unknown>;

    const key = e["key"];
    const templateUrl = e["templateUrl"];
    if (typeof key !== "string" || typeof templateUrl !== "string") continue;

    const rawMetadata = e["metadata"];
    const metadata =
      rawMetadata && typeof rawMetadata === "object" && !Array.isArray(rawMetadata)
        ? (rawMetadata as Record<string, unknown>)
        : {};
    const rawInputs = metadata["inputs"];
    const inputs = Array.isArray(rawInputs)
      ? rawInputs.flatMap((input) => {
          if (!input || typeof input !== "object" || Array.isArray(input)) return [];
          const inputType = (input as Record<string, unknown>)["inputType"];
          return typeof inputType === "string" ? [{ ...(input as object), inputType }] : [];
        })
      : undefined;
    const rawIdentifierGroups = metadata["identifierGroups"];
    const identifierGroups =
      rawIdentifierGroups &&
      typeof rawIdentifierGroups === "object" &&
      !Array.isArray(rawIdentifierGroups)
        ? (rawIdentifierGroups as Record<string, unknown>)
        : undefined;

    const searchKeywordsRaw = e["searchKeywords"];
    const searchKeywords = Array.isArray(searchKeywordsRaw)
      ? searchKeywordsRaw.filter((s): s is string => typeof s === "string")
      : [];

    const previewUrl =
      typeof e["previewUrl"] === "string"
        ? (e["previewUrl"] as string)
        : null;

    const template: FactsetTemplate = {
      ...e,
      category: typeof e["category"] === "string" ? (e["category"] as string) : "",
      etag: typeof e["etag"] === "string" ? (e["etag"] as string) : "",
      modtime: typeof e["modtime"] === "string" ? (e["modtime"] as string) : "",
      language: typeof e["language"] === "string" ? (e["language"] as string) : "",
      key,
      templateUrl,
      previewUrl,
      name: typeof e["name"] === "string" ? (e["name"] as string) : "",
      description:
        typeof e["description"] === "string" ? (e["description"] as string) : "",
      extendedDescription:
        typeof e["extendedDescription"] === "string"
          ? (e["extendedDescription"] as string)
          : "",
      searchKeywords,
      metadata: {
        ...(inputs !== undefined ? { inputs } : {}),
        ...(identifierGroups !== undefined ? { identifierGroups } : {}),
      },
    };
    out.push(template);
  }
  return out;
}

/**
 * Read the host's template library catalog via
 * `factset.GetTemplatesList`.
 *
 * Returns `{ templates }` — every template the host's library exposes,
 * in host order. Each entry carries enough for a picker UI
 * (`name` / `description` / `category` / `previewUrl`) plus the
 * machinery for downstream instantiation (`templateUrl` to fetch the
 * `.xlsx` bytes, `metadata.inputs` to know which inputs to collect
 * before invoking the template).
 *
 * Rejects only on transport / timeout errors. A host that returns an
 * empty catalog resolves to `{ templates: [] }` — callers should
 * treat empty as "host didn't surface any templates" and fall back to
 * whatever the picker UI considers an empty state.
 */
export async function getTemplatesListViaIntents(
  options: GetTemplatesListOptions = {}
): Promise<FactsetGetTemplatesListResult> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const ctx = await raiseIntent(
    GET_TEMPLATES_LIST_INTENT_NAME,
    { type: "fdc3.nothing" },
    timeoutMs,
    GET_TEMPLATES_LIST_APP_ID,
    options.signal
  );

  const rawTemplates = extractGetTemplatesListArray(ctx);

  return {
    templates: coerceTemplates(rawTemplates),
  };
}

// ============================================================================
// factset.GetUserAvailableTemplates  (NB: NOT in the `factset.Spreadsheet.*`
//                                          namespace)
// ============================================================================
//
// Read the subset of template keys the **current user is entitled to**
// instantiate. Pairs with `getTemplatesList`: each entry returned here
// is exactly a value from `FactsetTemplate.key` (lowercased `.xlsx`
// filename). Use this to filter the full catalog from
// `getTemplatesList` down to the entries the picker UI should expose:
//
//   const [catalog, entitled] = await Promise.all([
//     getTemplatesList(),
//     getUserAvailableTemplates(),
//   ]);
//   const allowed = new Set(entitled.templateKeys);
//   const visible = catalog.templates.filter(t => allowed.has(t.key));
//
// **Departures from sibling Spreadsheet intents — three shared with
// `previewChart` / `getStylesets` / `getTemplatesList`, plus a flat-
// array quirk:**
//
//   1. **Intent name uses CapitalizedCamelCase, not lowerCamelCase.**
//      The wire intent name is `factset.GetUserAvailableTemplates`
//      (capital `G`, `U`, `A`, `T`). Same casing-split convention
//      as `factset.GetTemplatesList`.
//   2. **Different target appId.** Routes to `"OI Remote Iframe"`,
//      NOT the default `"com.factset.workstation"`. Wrappers must
//      override the appId per call.
//   3. **Response envelope is the two-level `data.body.data` form**
//      (same as `previewChart` / `getStylesets` / `getTemplatesList`).
//      Result context type is
//      `factset.common.getUserAvailableTemplatesResult` (note the
//      `factset.common.*` namespace).
//   4. **Inner `data` is a flat `string[]` of template keys**, not
//      an array of objects (`getTemplatesList`) and not an object
//      (`getStylesets`). The wrapper surfaces it under
//      `templateKeys` rather than emitting a bare `string[]`, so a
//      future host extension that returns per-entry entitlement
//      metadata (e.g. `[{ key, expiresAt, ... }]`) can land on a
//      different field without breaking this one.
//   5. **Request takes no input.** The captured request uses
//      `context.type: "fdc3.nothing"` — there are no per-call
//      options today.
//
// See captured payload + envelope details in
// `excel-docs/intent-details.md` → "Get User Available Templates".

const GET_USER_AVAILABLE_TEMPLATES_INTENT_NAME =
  "factset.GetUserAvailableTemplates";
const GET_USER_AVAILABLE_TEMPLATES_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Per-call options for `getUserAvailableTemplatesViaIntents`. The
 * captured request carries no fields (input is `fdc3.nothing`), but
 * the option bag is exposed today so a future host extension lands
 * without breaking the no-arg call site, and so call sites can
 * already pass `signal` for cancellation symmetry with the other
 * `OI Remote Iframe` intents.
 */
export interface GetUserAvailableTemplatesOptions {
  /** Per-call timeout override. The captured payload is small (a
   *  few hundred lowercased `.xlsx` filenames at most) so the
   *  default `DEFAULT_TIMEOUT_MS` is normally fine. */
  timeoutMs?: number;
  /** Abort the wait for the host's reply. */
  signal?: AbortSignal;
}

/**
 * Recursively search `value` for the first array node whose entries
 * look like template keys (all entries are non-empty strings). Used
 * as the fallback after the documented shape walk — cheap insurance
 * against a future host rearrangement.
 *
 * The walk is bounded (`maxDepth`) and stops at the first matching
 * array; pathological inputs can't blow the stack.
 */
function findTemplateKeysArray(
  value: unknown,
  maxDepth: number = 6
): string[] | undefined {
  if (maxDepth < 0) return undefined;
  if (!value || typeof value !== "object") return undefined;
  if (Array.isArray(value)) {
    if (
      value.length > 0 &&
      value.every((entry) => typeof entry === "string" && entry.length > 0)
    ) {
      return value as string[];
    }
    return undefined;
  }
  for (const child of Object.values(value as Record<string, unknown>)) {
    const hit = findTemplateKeysArray(child, maxDepth - 1);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * Walk the response context and pull out the template-keys array.
 * `raiseIntent` already unwraps `event.data.result.context`, so the
 * value reaching us here is whatever the host placed inside
 * `result.context`.
 *
 * Captured envelope (2026-05-01):
 *   `{ data: { status, contentType, body: { data: [<key>, <key>, ...] } } }`
 *
 * The inner `data` is a flat `string[]` — there's no wrapping object
 * (`{ templateKeys: [...] }`) and no per-entry object
 * (`[ { key }, ... ]`). We tolerate the documented form, the obvious
 * flatter / wrapped variants, and fall back to a bounded recursive
 * search for any array of non-empty strings.
 */
function extractGetUserAvailableTemplatesArray(ctx: unknown): string[] {
  if (!ctx || typeof ctx !== "object") return [];

  const c = ctx as Record<string, unknown>;

  // Shape 1 (CAPTURED 2026-05-01): { data: { status, contentType, body: { data: [<key>, ...] } } }
  const dataLevel = c["data"];
  if (dataLevel && typeof dataLevel === "object" && !Array.isArray(dataLevel)) {
    const d = dataLevel as Record<string, unknown>;
    const body = d["body"];
    if (body && typeof body === "object" && !Array.isArray(body)) {
      const innerData = (body as Record<string, unknown>)["data"];
      if (Array.isArray(innerData)) {
        return innerData.filter(
          (s): s is string => typeof s === "string" && s.length > 0
        );
      }
      // Defensive: a host that wraps the array as `{ templateKeys: [...] }`
      // at body.data, mirroring the GetStylesets shape.
      if (
        innerData &&
        typeof innerData === "object" &&
        !Array.isArray(innerData) &&
        Array.isArray((innerData as Record<string, unknown>)["templateKeys"])
      ) {
        return (
          (innerData as Record<string, unknown>)["templateKeys"] as unknown[]
        ).filter((s): s is string => typeof s === "string" && s.length > 0);
      }
      // Some hosts may collapse the second `.data` level.
      if (Array.isArray((body as Record<string, unknown>)["data"])) {
        return (
          (body as Record<string, unknown>)["data"] as unknown[]
        ).filter((s): s is string => typeof s === "string" && s.length > 0);
      }
      if (Array.isArray((body as Record<string, unknown>)["templateKeys"])) {
        return (
          (body as Record<string, unknown>)["templateKeys"] as unknown[]
        ).filter((s): s is string => typeof s === "string" && s.length > 0);
      }
    }
    // Shape 2: { data: [<key>, ...] }
    if (Array.isArray(d["data"])) {
      return (d["data"] as unknown[]).filter(
        (s): s is string => typeof s === "string" && s.length > 0
      );
    }
    if (Array.isArray(d["templateKeys"])) {
      return (d["templateKeys"] as unknown[]).filter(
        (s): s is string => typeof s === "string" && s.length > 0
      );
    }
  }

  // Shape 3 (already-unwrapped): { templateKeys: [<key>, ...] }
  if (Array.isArray(c["templateKeys"])) {
    return (c["templateKeys"] as unknown[]).filter(
      (s): s is string => typeof s === "string" && s.length > 0
    );
  }

  // Shape 4 (defensive): bounded recursive search for any array of
  // non-empty strings. Cheap insurance against future host
  // rearrangement.
  const recursive = findTemplateKeysArray(c);
  if (recursive) return recursive;

  return [];
}

/**
 * Read the entitlement-filtered template-key list via
 * `factset.GetUserAvailableTemplates`.
 *
 * Returns `{ templateKeys }` — every `FactsetTemplate.key` the
 * current user is entitled to instantiate, in host order. Pair with
 * `getTemplatesListViaIntents` to render an entitlement-aware
 * picker: filter the catalog by `templates.filter(t =>
 * keys.includes(t.key))`.
 *
 * Rejects only on transport / timeout errors. A host that returns
 * an empty entitlement list resolves to `{ templateKeys: [] }` —
 * callers should treat empty as "this user can't instantiate any
 * templates" and surface an empty-state picker accordingly.
 */
export async function getUserAvailableTemplatesViaIntents(
  options: GetUserAvailableTemplatesOptions = {}
): Promise<FactsetGetUserAvailableTemplatesResult> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const ctx = await raiseIntent(
    GET_USER_AVAILABLE_TEMPLATES_INTENT_NAME,
    { type: "fdc3.nothing" },
    timeoutMs,
    GET_USER_AVAILABLE_TEMPLATES_APP_ID,
    options.signal
  );

  return {
    templateKeys: extractGetUserAvailableTemplatesArray(ctx),
  };
}

// ============================================================================
// factset.DownloadTemplate  (NB: NOT in the `factset.Spreadsheet.*` namespace)
// ============================================================================
//
// Fetch the raw `.xlsx` bytes for a single template, **without
// instantiating it**. Pairs with `getTemplatesList`: the caller picks
// a `FactsetTemplate` from the catalog and asks the host to hand back
// the underlying workbook bytes (the same template a downstream
// `RefreshCalculate` would otherwise fill in with input values).
//
// **Two natural product surfaces** (both viable, sibling-not-rival):
//   - "Save template to disk." Hand `workbookBase64` to a Blob /
//     `<a download>` / `msSaveOrOpenBlob` flow and let the user keep
//     the empty template locally.
//   - "Open template inside Finster." Feed `workbookBase64` into a
//     pipeline that opens the `.xlsx` (e.g. for the user to fill in
//     manually before kicking off `RefreshCalculate` against the
//     same `key`). Note that the host's
//     `factset.Spreadsheet.InsertWorksheetsFromBase64` primitive
//     copies worksheet cell data only — that's fine for a vanilla
//     template (no chart drawings, no OLE embeds), but a future
//     template that ships with native charts would lose them
//     through that primitive.
//
// **Departures from sibling Spreadsheet intents — three shared with
// the rest of the `OI Remote Iframe` group, plus a request-shape
// quirk that distinguishes this intent from `RefreshCalculate`:**
//
//   1. **Intent name uses CapitalizedCamelCase, not lowerCamelCase.**
//      The wire intent name is `factset.DownloadTemplate` (capital
//      `D`, capital `T`). Same casing-split convention as
//      `factset.GetTemplatesList` / `factset.RefreshCalculate` /
//      `factset.DownloadExcelChart`.
//   2. **Different target appId.** Routes to `"OI Remote Iframe"`,
//      NOT the default `"com.factset.workstation"`. Wrappers must
//      override the appId per call.
//   3. **Response envelope is the FLATTER one-level form** —
//      `data.bodyAsBase64` directly under `data`, no
//      `data.body.data` second nesting. Same shape as
//      `DownloadExcelChart` / `DownloadActiveGraph`. Result context
//      type is `factset.common.downloadTemplateResult`.
//   4. **Request is a bare context object — `key` sits as a TOP-
//      LEVEL field on the context, not inside a stringified `body`
//      blob.** The captured wire payload is:
//        context: {
//          type: "factset.downloadTemplate",
//          key: "public company profile.xlsx"
//        }
//      That's the full request — there's no `body`, no `data:`
//      envelope. Compare with `RefreshCalculate`, which carries a
//      stringified `body` field containing
//      `{"data":{"key":...,"inputs":...}}`. Read intents in this
//      group typically use `"fdc3.nothing"` as the request type and
//      take no input; this is the one read intent that needs a
//      single string argument and chose to surface it as a
//      top-level field rather than introducing a stringified-body
//      pattern for one parameter.
//
// **Captured 2026-05-05** against a live FactSet Workstation host.
// HTTP-200, MIME `application/vnd.openxmlformats-officedocument.
// spreadsheetml.sheet`, base64 starts with `UEsDBBQ` (PK ZIP magic)
// confirming a real Open-XML workbook on the wire.
//
// See captured payload + envelope details in
// `excel-docs/intent-details.md` → "Download Template". Like the
// other chart-family / library intents (`PreviewChart`, `Download*`,
// `GetStylesets`, `GetTemplatesList`, `GetUserAvailableTemplates`,
// `RefreshCalculate`, `GetRefreshStatus`), this is tracked alongside
// the sibling-namespace intents in
// `f2_docs/excel/finster-docs/missing-intents-v0.md` §4.4.

const DOWNLOAD_TEMPLATE_INTENT_NAME = "factset.DownloadTemplate";
const DOWNLOAD_TEMPLATE_CONTEXT_TYPE = "factset.downloadTemplate";
const DOWNLOAD_TEMPLATE_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Per-call options for `downloadTemplateViaIntents`. The captured
 * request carries no fields beyond `key` (set on the request struct,
 * not the option bag), so today the only options are the two
 * transport-level controls every `OI Remote Iframe` wrapper exposes.
 *
 * Defaults to the five-minute long-running host-operation budget used by
 * `previewChart` / `downloadExcelChart` / `downloadActiveGraph`,
 * because the bytes returned are full `.xlsx` workbooks and the
 * round-trip latency is dominated by the host packaging the
 * template payload, not by client wire time.
 */
export interface DownloadTemplateOptions {
  /** Per-call timeout override. Defaults to the five-minute budget used by
   *  the other binary-download intents in this group. */
  timeoutMs?: number;
  /** Abort the wait for the host's reply. Same semantics as
   *  `DownloadExcelChartOptions.signal`. */
  signal?: AbortSignal;
}

/**
 * Inner body shape returned by the host (the `data` level of
 * `result.context`). Captured 2026-05-05:
 *
 *   { status: 200,
 *     contentType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
 *     bodyAsBase64: "UEsDBBQ…"  // PK-magic prefix → confirmed valid xlsx
 *   }
 *
 * Same flat envelope as `DownloadExcelChartResponseInner` /
 * `DownloadActiveGraphResponseInner`. Kept as a separate type symbol
 * so the wrapper's defensive coercions read cleanly per intent.
 *
 * Fields are typed as `unknown` so the wrapper coerces defensively
 * at the boundary — host-side typing changes (string → number for
 * `status`, etc.) won't cascade into the public surface.
 */
interface DownloadTemplateResponseInner {
  /** HTTP-style status code. `200` is success; non-200 surfaces as
   *  an `error` on the public type so callers don't have to inspect
   *  it. */
  status?: unknown;
  /** MIME type of the returned workbook —
   *  `"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"`
   *  on the captured trace. */
  contentType?: unknown;
  /** Base64-encoded `.xlsx` bytes (no `data:` prefix). The base64
   *  payload starts with `UEsDBBQ` (PK ZIP magic), confirming a real
   *  Open-XML workbook on the wire. */
  bodyAsBase64?: unknown;
  /** Carry any other fields verbatim — the host catalog may grow
   *  (e.g. an `etag` field tied to the `FactsetTemplate.etag`
   *  cache validator) and the wrapper shouldn't lose data the
   *  caller might want to inspect via the public `raw` escape
   *  hatch. */
  [key: string]: unknown;
}

/**
 * Walk the response context and pull out the `data` level.
 * Structurally identical to `extractDownloadExcelChartBody` /
 * `extractDownloadActiveGraphBody` — same captured envelope shape,
 * same defensive fallback walks. Kept as a parallel implementation
 * rather than calling the siblings directly so each intent block is
 * self-contained per the file convention, and so a future divergence
 * (e.g. the template response gaining an extra envelope level) lands
 * on this sibling without touching the others.
 */
function extractDownloadTemplateBody(
  ctx: unknown
): DownloadTemplateResponseInner {
  if (!ctx || typeof ctx !== "object") return {};

  const c = ctx as Record<string, unknown>;

  // Shape 1 (CAPTURED 2026-05-05): { type, data: { status, contentType, bodyAsBase64 } }
  const dataLevel = c["data"];
  if (dataLevel && typeof dataLevel === "object" && !Array.isArray(dataLevel)) {
    const d = dataLevel as Record<string, unknown>;
    if (typeof d["bodyAsBase64"] === "string") {
      return d as DownloadTemplateResponseInner;
    }
    // Defensive: the host might wrap one level deeper later. Walk
    // `data.body.data` as `previewChart` does, just in case — kept
    // symmetric with `extractDownloadExcelChartBody` so a future
    // host-side normalisation can land on either intent without
    // breaking the other.
    const body = d["body"];
    if (body && typeof body === "object" && !Array.isArray(body)) {
      const inner = (body as Record<string, unknown>)["data"];
      if (inner && typeof inner === "object" && !Array.isArray(inner)) {
        if (typeof (inner as Record<string, unknown>)["bodyAsBase64"] === "string") {
          return inner as DownloadTemplateResponseInner;
        }
      }
      if (typeof (body as Record<string, unknown>)["bodyAsBase64"] === "string") {
        return body as DownloadTemplateResponseInner;
      }
    }
  }

  // Shape 2 (already-unwrapped flat object — `bodyAsBase64` at the top).
  if (typeof c["bodyAsBase64"] === "string") {
    return c as DownloadTemplateResponseInner;
  }

  // Shape 3 (defensive): bounded recursive search for any node
  // carrying `bodyAsBase64`. Reuses the helper defined in the
  // `DownloadExcelChart` block since the search criterion
  // (`bodyAsBase64` on an object node) is unchanged.
  const recursive = findBodyAsBase64Node(c);
  if (recursive) return recursive as DownloadTemplateResponseInner;

  // No bytes anywhere — return the top-level object so the caller's
  // `raw` escape hatch still carries something useful for diagnosis.
  return c as DownloadTemplateResponseInner;
}

/**
 * Fetch the raw `.xlsx` bytes for a template by `key`, via
 * `factset.DownloadTemplate`.
 *
 * Returns `{ workbookBase64, contentType, status, error?, raw }`.
 * On the captured-2026-05-05 shape, `workbookBase64` carries the raw
 * `.xlsx` bytes (PK ZIP magic confirmed) and `contentType` is the
 * standard `.xlsx` MIME.
 *
 * **Pairing with `getTemplatesList`.** `request.key` must be a value
 * from `FactsetTemplate.key` returned by `getTemplatesListViaIntents`
 * — typically picked through a UI:
 *
 *   const { templates } = await getTemplatesListViaIntents();
 *   const picked = templates.find((t) => t.name === "Public Company Profile");
 *   const bytes = await downloadTemplateViaIntents({ key: picked.key });
 *
 * Use `getUserAvailableTemplatesViaIntents` first to scope the
 * picker to entries the current user is entitled to — the host will
 * still serve `downloadTemplate` for non-entitled keys today, but
 * filtering matches the entitlement-aware UX.
 *
 * **Error semantics — same table as `downloadExcelChartViaIntents` /
 * `downloadActiveGraphViaIntents`:**
 *   - Non-200 `status` → `error = "Host returned status N"`.
 *   - 200 but no `bodyAsBase64` → `error = "Host returned status 200 but no bodyAsBase64"`.
 *   - 200 with bytes → `error = null` (success).
 *   - No `status` at all but bytes present → `error = undefined`
 *     (caller should treat as success — bytes are the source of
 *     truth).
 *
 * Rejects on transport / timeout errors or invalid input (empty
 * `key`). Pass `options.signal` to make the wait abortable; abort
 * rejects the promise with a `DOMException` named `AbortError`. Late
 * host replies after abort are dropped silently by the transport
 * (the host has no cancel surface).
 */
export async function downloadTemplateViaIntents(
  request: FactsetDownloadTemplateRequest,
  options: DownloadTemplateOptions = {}
): Promise<FactsetDownloadTemplate> {
  const key = request.key?.trim();
  if (!key) {
    throw new Error(
      'downloadTemplate: `key` is required (e.g. "public company profile.xlsx")'
    );
  }

  const timeoutMs = options.timeoutMs ?? LONG_RUNNING_FACTSET_INTENT_TIMEOUT_MS;

  const ctx = await raiseIntent(
    DOWNLOAD_TEMPLATE_INTENT_NAME,
    { type: DOWNLOAD_TEMPLATE_CONTEXT_TYPE, key },
    timeoutMs,
    DOWNLOAD_TEMPLATE_APP_ID,
    options.signal
  );

  const inner = extractDownloadTemplateBody(ctx);

  const workbookBase64 = asOptionalString(inner.bodyAsBase64);
  const contentType = asOptionalString(inner.contentType);
  const status = asOptionalFiniteNumber(inner.status);

  // Same error-synthesis table as `downloadExcelChartViaIntents`.
  let error: string | null | undefined;
  if (status !== undefined && status !== 200) {
    error = `Host returned status ${status}`;
  } else if (status === 200 && !workbookBase64) {
    error = "Host returned status 200 but no bodyAsBase64";
  } else if (workbookBase64) {
    error = null;
  } else {
    error = undefined;
  }

  return {
    workbookBase64,
    contentType,
    status,
    error,
    raw: inner as Record<string, unknown>,
  };
}

// ============================================================================
// factset.RefreshCalculate  (NB: NOT in the `factset.Spreadsheet.*` namespace)
// ============================================================================
//
// Kick off async template instantiation. Pairs with
// `getTemplatesList` (catalog) and `getUserAvailableTemplates`
// (entitlement filter): the caller picks a `FactsetTemplate`,
// collects values for each `metadata.inputs[].inputType`, and
// invokes this intent to ask the host to compute the workbook.
//
// **This is the first WRITE intent in the `OI Remote Iframe` group,
// and it has TWO wire quirks the read intents above don't:**
//
//   1. **Request `body` is a STRINGIFIED JSON, not a structured
//      object.** The captured wire payload is:
//        context: {
//          type: "factset.refreshCalculate",
//          body: "{\n  \"data\": { \"key\": ..., \"inputs\": ... }\n}"
//        }
//      i.e. `body` is a string carrying JSON, not an object. The
//      wrapper serialises `{ data: { key, inputs } }` via
//      `JSON.stringify` and sets it on `body`. Whitespace doesn't
//      matter (the host parses the string with a normal JSON
//      parser); we use compact stringification.
//   2. **Response is async (HTTP 202, not 200).** The host returns
//      `data.status: 202` with `data.body.data: { id, status: "queued" }`
//      — the actual computation runs server-side. The `id` (UUID)
//      needs to be polled against a sibling status / retrieval
//      intent (TBD — see roadmap note below).
//
// **Departures from sibling Spreadsheet intents — three shared with
// `previewChart` / `getStylesets` / `getTemplatesList`, plus the two
// quirks above:**
//
//   1. **Intent name uses CapitalizedCamelCase, not lowerCamelCase.**
//      The wire intent name is `factset.RefreshCalculate`. Same
//      casing-split convention as `factset.GetTemplatesList`.
//   2. **Different target appId.** Routes to `"OI Remote Iframe"`,
//      NOT the default `"com.factset.workstation"`.
//   3. **Response envelope is the two-level `data.body.data` form**
//      (same as `previewChart` / `getStylesets` / `getTemplatesList`).
//      Result context type is `factset.common.refreshCalculateResult`.
//   4. **Request `context.type` is `"factset.refreshCalculate"`** (not
//      `"fdc3.nothing"`) — the only `OI Remote Iframe` intent so far
//      that takes structured input rather than a no-arg request.
//
// **Sibling intents now wired:**
//   - `factset.GetRefreshStatus` for one-shot status polling
//   - `factset.GetRefresh` for result retrieval (bytes when ready,
//     status payload otherwise)
//
// See captured payload + envelope details in
// `excel-docs/intent-details.md` → "Refresh Calculate".

const REFRESH_CALCULATE_INTENT_NAME = "factset.RefreshCalculate";
const REFRESH_CALCULATE_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Per-call options for `refreshCalculateViaIntents`. The captured
 * request carries the structured `request: { key, inputs }` payload
 * directly; per-call options are scoped to transport concerns.
 *
 * Defaults to the shared `DEFAULT_TIMEOUT_MS` because the **synchronous
 * leg is fast** — the host returns within ~2s on the captured trace
 * with the async job handle. The actual computation runs server-side
 * after this call resolves; do not bump the timeout to wait for it
 * (poll the sibling status intent instead, when it lands).
 */
export interface RefreshCalculateOptions {
  /** Per-call timeout override for the synchronous leg only.
   *  Defaults to the shared `DEFAULT_TIMEOUT_MS`. */
  timeoutMs?: number;
  /** Abort the wait for the host's reply. */
  signal?: AbortSignal;
}

/**
 * Inner-body shape after envelope unwrapping. Loose typing because
 * the host returns these as raw JSON values that we coerce below.
 */
interface RefreshCalculateResponseInner {
  id?: unknown;
  status?: unknown;
  [key: string]: unknown;
}

function stringifyRefreshCalculateDiagnostic(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Walk the response context and pull out the
 * `{ id, status }` async job handle. `raiseIntent` already unwraps
 * `event.data.result.context`, so the value reaching us here is
 * whatever the host placed inside `result.context`.
 *
 * Captured envelope (2026-05-01):
 *   `{ data: { status: 202, contentType, body: { data: { id, status } } } }`
 *
 * The HTTP-style `data.status` (202 — captured) is surfaced
 * separately on the public type; the inner `body.data.status` is
 * the **job** status string ("queued" — captured). Both are useful
 * — keep them distinct.
 */
function extractRefreshCalculateBody(ctx: unknown): {
  inner: RefreshCalculateResponseInner;
  httpStatus?: number;
} {
  if (!ctx || typeof ctx !== "object") return { inner: {} };

  const c = ctx as Record<string, unknown>;

  // Shape 1 (CAPTURED 2026-05-01): { data: { status, contentType, body: { data: { id, status } } } }
  const dataLevel = c["data"];
  if (dataLevel && typeof dataLevel === "object" && !Array.isArray(dataLevel)) {
    const d = dataLevel as Record<string, unknown>;
    const httpStatus = typeof d["status"] === "number" ? (d["status"] as number) : undefined;
    const body = d["body"];
    if (body && typeof body === "object" && !Array.isArray(body)) {
      const innerData = (body as Record<string, unknown>)["data"];
      if (innerData && typeof innerData === "object" && !Array.isArray(innerData)) {
        return { inner: innerData as RefreshCalculateResponseInner, httpStatus };
      }
      // Defensive: a host that collapses the second `.data` level.
      if (typeof (body as Record<string, unknown>)["id"] === "string") {
        return { inner: body as RefreshCalculateResponseInner, httpStatus };
      }
    }
    // Defensive: { data: { id, status } } (no body wrapper)
    if (typeof d["id"] === "string") {
      return { inner: d as RefreshCalculateResponseInner, httpStatus };
    }
    // We recognised the outer envelope and pulled an HTTP-style status
    // but couldn't locate the `id` anywhere underneath. Bubble the
    // status up with an empty `inner` so the caller can throw a
    // transport-level error (most likely a 4xx/5xx) instead of the
    // misleading "no id" message — that wording suggests a malformed
    // 200 OK body when the real problem is the host rejected the call.
    if (httpStatus !== undefined) {
      return { inner: {}, httpStatus };
    }
  }

  // Defensive: already-unwrapped { id, status }
  if (typeof c["id"] === "string") {
    return { inner: c as RefreshCalculateResponseInner };
  }

  return { inner: {} };
}

/**
 * Kick off async template instantiation via
 * `factset.RefreshCalculate`.
 *
 * Returns `{ id, status, httpStatus? }` — the async job handle. The
 * caller MUST poll the (TBD) sibling status / retrieval intent
 * keyed off `id` to learn when the workbook is ready; this wrapper
 * resolves as soon as the host returns the queued handle (typically
 * <2s on the captured trace), NOT when the computation completes.
 *
 * Validates input shape locally before sending: `key` must be a
 * non-empty `.xlsx` filename, `inputs` must be a plain object whose
 * values are all strings. Rejects synchronously (before the host
 * round-trip) with a descriptive error if either fails — keeping the
 * host log clean of obviously malformed requests.
 *
 * Rejects only on synchronous-leg transport / timeout errors or
 * malformed responses (missing `id`). Async-leg failures (the actual
 * computation failing server-side) surface only on the polling
 * sibling intent; an `httpStatus` outside `[200, 202]` here is also
 * surfaced as a wrapper-level error.
 */
export async function refreshCalculateViaIntents(
  request: FactsetRefreshCalculateRequest,
  options: RefreshCalculateOptions = {}
): Promise<FactsetRefreshCalculateResult> {
  if (!request || typeof request !== "object") {
    throw new Error("RefreshCalculate: `request` must be an object");
  }
  if (typeof request.key !== "string" || request.key.length === 0) {
    throw new Error("RefreshCalculate: `request.key` must be a non-empty string");
  }
  if (!request.key.toLowerCase().endsWith(".xlsx")) {
    throw new Error(
      `RefreshCalculate: \`request.key\` must end in \`.xlsx\` (got "${request.key}")`
    );
  }
  if (
    !request.inputs ||
    typeof request.inputs !== "object" ||
    Array.isArray(request.inputs)
  ) {
    throw new Error("RefreshCalculate: `request.inputs` must be a plain object");
  }
  for (const [k, v] of Object.entries(request.inputs)) {
    if (typeof v !== "string") {
      throw new Error(
        `RefreshCalculate: \`request.inputs.${k}\` must be a string (got ${typeof v})`
      );
    }
  }

  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const bodyString = JSON.stringify({
    data: {
      key: request.key,
      inputs: request.inputs,
    },
  });

  console.debug("[TEMPLATE_DIAG] RefreshCalculate request", {
    intent: REFRESH_CALCULATE_INTENT_NAME,
    appId: REFRESH_CALCULATE_APP_ID.appId,
    context: {
      type: "factset.refreshCalculate",
      body: bodyString,
    },
    bodyJson: bodyString,
  });

  const ctx = await raiseIntent(
    REFRESH_CALCULATE_INTENT_NAME,
    {
      type: "factset.refreshCalculate",
      body: bodyString,
    },
    timeoutMs,
    REFRESH_CALCULATE_APP_ID,
    options.signal
  );

  const { inner, httpStatus } = extractRefreshCalculateBody(ctx);
  const responseBody =
    ctx && typeof ctx === "object" && !Array.isArray(ctx)
      ? (ctx as { data?: { body?: unknown } }).data?.body
      : undefined;
  console.debug("[TEMPLATE_DIAG] RefreshCalculate response", {
    httpStatus,
    inner,
    responseBody,
    rawContext: ctx,
    rawContextJson: stringifyRefreshCalculateDiagnostic(ctx),
  });

  // Surface a transport-level rejection ahead of the body-shape check.
  // A 4xx/5xx response generally has no `id` underneath, and reporting
  // "did not include a body.data.id" for that case sends engineers
  // hunting for a host-bug that isn't there.
  if (httpStatus !== undefined && (httpStatus < 200 || httpStatus >= 300)) {
    console.warn("[TEMPLATE_DIAG] RefreshCalculate non-success", {
      httpStatus,
      request: { key: request.key, inputs: request.inputs },
      requestBodyJson: bodyString,
      responseBody,
      rawContext: ctx,
      rawContextJson: stringifyRefreshCalculateDiagnostic(ctx),
    });
    throw new Error(
      `RefreshCalculate: host returned non-success status ${httpStatus}`
    );
  }

  if (typeof inner.id !== "string" || inner.id.length === 0) {
    // Capture a truncated snapshot of the actual response so the next
    // time the host returns an unrecognised body shape we can diagnose
    // from the chat log alone instead of having to re-instrument.
    // Bounded to 280 chars to keep the chat-side error row readable.
    let shapeHint = "<empty>";
    try {
      const serialised = JSON.stringify(ctx);
      if (serialised && serialised.length > 0) {
        shapeHint = serialised.length > 280
          ? serialised.slice(0, 280) + "…"
          : serialised;
      }
    } catch {
      shapeHint = "<unserialisable>";
    }
    throw new Error(
      `RefreshCalculate: response did not include a \`body.data.id\` job handle (host returned: ${shapeHint})`
    );
  }

  return {
    id: inner.id,
    status: typeof inner.status === "string" ? inner.status : "",
    httpStatus,
  };
}

// ============================================================================
// factset.GetRefreshStatus  (NB: NOT in the `factset.Spreadsheet.*` namespace)
// ============================================================================
//
// Polling counterpart of `factset.RefreshCalculate`. Takes the `id`
// from a prior `refreshCalculateViaIntents` resolution and surfaces the
// host's current job status so the caller can wait for terminal
// completion before chaining downstream work (typically
// `getRefreshViaIntents` followed by `insertWorksheetsFromBase64` when
// bytes are available).
//
// **Wire shape (request — confirmed):**
//
//   ```
//   context: {
//     type: "factset.getRefreshStatus",
//     refreshId: "<id-from-refreshCalculate>"
//   }
//   ```
//
//   Note the wire field name is `refreshId`, NOT `id`. The wrapper
//   accepts the caller's `id` (matching `FactsetRefreshCalculateResult.id`)
//   and translates it on the wire so the two intents pair cleanly.
//
// **Wire shape (response — captured 2026-05-05, both samples are
// for the same `id` so they trace one job's progression):**
//
//   non-terminal "executing" sample (poll mid-flight):
//
//   ```
//   {
//     "data": {
//       "status": 202,
//       "contentType": "application/json; charset=utf-8",
//       "body": {
//         "data": {
//           "id": "a5b6e35b-b285-472d-ac87-4fc3b47e08d6",
//           "status": "executing"
//         }
//       }
//     }
//   }
//   ```
//
//   terminal-success "created" sample (final poll):
//
//   ```
//   {
//     "data": {
//       "status": 201,
//       "contentType": "application/json; charset=utf-8",
//       "body": {
//         "data": {
//           "id": "a5b6e35b-b285-472d-ac87-4fc3b47e08d6",
//           "status": "created"
//         }
//       }
//     }
//   }
//   ```
//
// Confirms the envelope is the **two-level `data.body.data`** form
// (Shape 1 in `extractRefreshStatusBody` — same as `refreshCalculate`).
// The HTTP code transitions `202` (Accepted, still working) → `201`
// (Created, resource ready); the inner job-status string transitions
// `"queued"` (initial, captured on `refreshCalculate`) → `"executing"`
// → `"created"` (terminal-success).
//
// **Terminal-failure sample is still pending** — `"failed"` /
// `"error"` / `"cancelled"` / `"timeout"` and the corresponding
// failure-payload fields (`error` / `message`) remain speculative
// until captured. Prune the speculative entries in
// `RefreshStatusValue` and `TERMINAL_REFRESH_STATUSES` once enough
// captures pin the value space down.
//
// **Conventions shared with the rest of the OI Remote Iframe group**
// (mirror `previewChart` / `download*` / `getStylesets` / `getTemplatesList`
// / `getUserAvailableTemplates` / `refreshCalculate`):
//   1. **Intent name** is CapitalizedCamelCase: `factset.GetRefreshStatus`.
//   2. **Request `context.type`** is lowerCamelCase: `factset.getRefreshStatus`.
//   3. **`appId`** is `"OI Remote Iframe"`.

const GET_REFRESH_STATUS_INTENT_NAME = "factset.GetRefreshStatus";
const GET_REFRESH_STATUS_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Per-call options for `getRefreshStatusViaIntents`. Polling concerns
 * (interval, overall deadline) live on `pollRefreshCalculate` instead;
 * this wrapper is one-shot.
 */
export interface GetRefreshStatusOptions {
  /** Per-call timeout override. Defaults to the shared `DEFAULT_TIMEOUT_MS`. */
  timeoutMs?: number;
  /** Abort the wait for the host's reply. */
  signal?: AbortSignal;
}

/**
 * Job-status string returned by `factset.RefreshCalculate` (initial
 * `"queued"` ack) and `factset.GetRefreshStatus` (subsequent polls).
 *
 * Observed progression for a single `id`:
 *
 *   ```
 *   queued (httpStatus 202)  →  executing (202)  →  created (201)
 *   ```
 *
 * **Captured non-terminal values** (the host is still working on the
 * job — keep polling):
 *   - `"queued"` — captured 2026-05-01 from `refreshCalculate`.
 *     Initial state: job has been accepted but server-side execution
 *     hasn't started yet. Pairs with HTTP `202 Accepted`.
 *   - `"executing"` — captured 2026-05-05 from `getRefreshStatus`.
 *     Server-side computation is in flight. Pairs with HTTP
 *     `202 Accepted`.
 *
 * **Captured terminal values:**
 *   - `"created"` — captured 2026-05-05 from `getRefreshStatus`.
 *     Terminal success: the workbook resource has been created and
 *     is ready to fetch / chain into `insertWorksheetsFromBase64`.
 *     Pairs with HTTP `201 Created` — the change in HTTP code from
 *     `202` (still-working) to `201` (resource created) is the
 *     reliable terminal marker even before the body status string
 *     is consulted.
 *
 * **Speculative terminal values** — pending capture; not yet
 * observed on the wire. The host has shown `"created"` rather than
 * `"succeeded"` / `"completed"` for the success path, so the entries
 * below are kept as defensive fallbacks but should be pruned once
 * we have enough captures to call the host's value space closed:
 *   - `"succeeded"` / `"completed"` — speculative terminal success.
 *   - `"failed"` / `"error"` — speculative terminal failure.
 *   - `"cancelled"` / `"canceled"` — speculative terminal cancel.
 *   - `"timeout"` / `"timedout"` — speculative terminal timeout.
 *
 * The `(string & {})` tail keeps the type open-ended for forward
 * compatibility — a host extension that introduces a new status
 * value lands without a wrapper update. Autocomplete still suggests
 * the documented set above.
 */
export type RefreshStatusValue =
  // Captured non-terminal:
  | "queued"
  | "executing"
  // Captured terminal-success:
  | "created"
  // Speculative terminal — not yet observed; kept as defensive
  // fallbacks until the host's value space is fully captured:
  | "succeeded"
  | "completed"
  | "failed"
  | "error"
  | "cancelled"
  | "canceled"
  | "timeout"
  | "timedout"
  // Forward-compat tail — keeps the union open while still letting
  // autocomplete suggest the documented set above.
  | (string & {});

/**
 * Body shape of the `factset.GetRefreshStatus` response after envelope
 * unwrapping. Inner `body.data` from the captured 2026-05-05 samples.
 *
 * Captured samples carry only `{ id, status }` — both for the
 * `"executing"` (mid-flight) and `"created"` (terminal-success) polls.
 * The host has NOT been observed to surface workbook bytes / a
 * download URL on the terminal-success body itself; downstream chain
 * (`insertWorksheetsFromBase64`) likely needs a separate retrieval
 * intent or fixed key/url derivation. The speculative
 * `workbookUrl` / `resultUrl` / `workbookBase64` / `error` /
 * `message` fields below are kept so callers can narrow on plausible
 * names without a follow-up type churn, but at runtime they're not
 * currently emitted.
 */
export interface RefreshStatusBody {
  /** Job id — captured value matches the `refreshId` echoed back
   *  from the request. Useful for sanity-checking that a delayed
   *  poll lined up with the right job. */
  id?: string;
  /** Job status string. See `RefreshStatusValue` for the captured /
   *  speculative value space. Captured: `"queued"` / `"executing"` /
   *  `"created"`. */
  status?: RefreshStatusValue;
  /** Some hosts emit a separate boolean terminal flag instead of a
   *  status enum; `pollRefreshCalculate` honours either as a
   *  terminal marker. Pending capture — not observed on the wire so
   *  far; the host has used the `status` enum exclusively. */
  complete?: unknown;
  done?: unknown;
  /** Speculative terminal-success payload fields — not observed on
   *  the wire yet. The captured `"created"` body carried no extra
   *  fields beyond `{ id, status }`. Workbook bytes / a download URL
   *  may surface here on a future host change, or via a separate
   *  retrieval intent not yet documented. */
  workbookUrl?: unknown;
  resultUrl?: unknown;
  workbookBase64?: unknown;
  /** Speculative terminal-failure payload fields. Pending capture —
   *  no failure response has been observed yet. */
  error?: unknown;
  message?: unknown;
  /** Carry any other fields verbatim — the host may grow the
   *  per-state body shape over time. */
  [key: string]: unknown;
}

/**
 * Result of `factset.GetRefreshStatus` after envelope unwrapping.
 *
 * `body` is the inner payload with the host's status fields. `httpStatus`
 * is the top-level HTTP-style code from the response envelope (mirrors
 * `FactsetRefreshCalculateResult.httpStatus`). Captured value space:
 *   - `202 Accepted` — non-terminal (`status: "queued"` / `"executing"`).
 *     Caller should keep polling.
 *   - `201 Created` — terminal-success (`status: "created"`).
 *     Workbook resource ready.
 *   - `404` (speculative) — `refreshId` is unknown / expired.
 *
 * Both `201` and `202` are 2xx, so `getRefreshStatusViaIntents`'s
 * non-success guard does NOT throw on either; `pollRefreshCalculate`
 * differentiates them via `body.status` (and `httpStatus` as a
 * secondary signal).
 */
export interface FactsetGetRefreshStatusResult {
  body: RefreshStatusBody;
  httpStatus?: number;
}

/**
 * Walk the response context and pull out the inner status body.
 * `raiseIntent` already unwraps `event.data.result.context`, so the
 * value reaching us here is whatever the host placed inside
 * `result.context`.
 *
 * Response shape is NOT YET CAPTURED — this extractor is mirrored from
 * `extractRefreshCalculateBody` and walks the same four envelopes:
 *   1. Two-level: `{ data: { status, body: { data: <body> } } }`
 *   2. Body-flat: `{ data: { status, body: <body> } }`
 *   3. Data-flat: `{ data: <body> }`
 *   4. Already unwrapped: `<body>`
 *
 * Returns `{ body: {} }` when the input doesn't match anything
 * obvious so the caller's defensive narrowing is the single place
 * that needs to handle a malformed host response.
 */
function extractRefreshStatusBody(ctx: unknown): {
  body: RefreshStatusBody;
  httpStatus?: number;
} {
  if (!ctx || typeof ctx !== "object") return { body: {} };

  const c = ctx as Record<string, unknown>;

  // Shape 1 / 2: { data: { status, body: ... } }
  const dataLevel = c["data"];
  if (dataLevel && typeof dataLevel === "object" && !Array.isArray(dataLevel)) {
    const d = dataLevel as Record<string, unknown>;
    const httpStatus = typeof d["status"] === "number" ? (d["status"] as number) : undefined;
    const body = d["body"];
    if (body && typeof body === "object" && !Array.isArray(body)) {
      const innerData = (body as Record<string, unknown>)["data"];
      if (innerData && typeof innerData === "object" && !Array.isArray(innerData)) {
        return { body: innerData as RefreshStatusBody, httpStatus };
      }
      return { body: body as RefreshStatusBody, httpStatus };
    }
    return { body: d as RefreshStatusBody, httpStatus };
  }

  // Shape 4: already unwrapped
  return { body: c as RefreshStatusBody };
}

/**
 * Poll the status of a `factset.RefreshCalculate` job via
 * `factset.GetRefreshStatus`.
 *
 * One-shot — for the typical wait-for-completion flow, use
 * `pollRefreshCalculate` below.
 *
 * Validates input shape locally before sending: `refreshId` must be
 * a non-empty string. Rejects synchronously (before the host
 * round-trip) with a descriptive error if it fails.
 *
 * Rejects on synchronous-leg transport / timeout errors and on any
 * non-2xx `httpStatus` from the host. Note: a `200` response
 * carrying `{ status: "queued" }` is NOT a wrapper-level error — the
 * caller is expected to keep polling.
 */
export async function getRefreshStatusViaIntents(
  refreshId: string,
  options: GetRefreshStatusOptions = {}
): Promise<FactsetGetRefreshStatusResult> {
  if (typeof refreshId !== "string" || refreshId.length === 0) {
    throw new Error("GetRefreshStatus: `refreshId` must be a non-empty string");
  }

  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const ctx = await raiseIntent(
    GET_REFRESH_STATUS_INTENT_NAME,
    {
      type: "factset.getRefreshStatus",
      refreshId,
    },
    timeoutMs,
    GET_REFRESH_STATUS_APP_ID,
    options.signal
  );

  const { body, httpStatus } = extractRefreshStatusBody(ctx);

  if (httpStatus !== undefined && (httpStatus < 200 || httpStatus >= 300)) {
    throw new Error(
      `GetRefreshStatus: host returned non-success status ${httpStatus} for refreshId="${refreshId}"`
    );
  }

  return { body, httpStatus };
}

// ============================================================================
// factset.GetRefresh  (NB: NOT in the `factset.Spreadsheet.*` namespace)
// ============================================================================
//
// Refresh-result retrieval sibling of `RefreshCalculate` /
// `GetRefreshStatus`. This endpoint can return either:
//
//   - workbook bytes (`status: 200`, `bodyAsBase64`) when the computed
//     template is ready, or
//   - a status payload (`status: 202` / `201`, `body.data: { id, status }`)
//     while the job is still progressing or has just reached terminal.
//
// Request shape mirrors `GetRefreshStatus` with an optional `orUrl`
// override used by some host builds:
//
//   context: {
//     type: "factset.getRefresh",
//     refreshId?: "<id-from-refreshCalculate>",
//     orUrl?: "<office-refresh-url>"
//   }
//
// Conventions match the rest of the OI Remote Iframe group:
//   - intent name: CapitalizedCamelCase (`factset.GetRefresh`)
//   - request context type: lowerCamelCase (`factset.getRefresh`)
//   - appId override: `"OI Remote Iframe"`

const GET_REFRESH_INTENT_NAME = "factset.GetRefresh";
const GET_REFRESH_CONTEXT_TYPE = "factset.getRefresh";
const GET_REFRESH_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Per-call options for `getRefreshViaIntents`.
 */
export interface GetRefreshOptions {
  /** Per-call timeout override. Defaults to five minutes because ready-path
   *  responses can include full `.xlsx` bytes. */
  timeoutMs?: number;
  /** Abort the wait for the host's reply. */
  signal?: AbortSignal;
}

/**
 * Inner body shape for `factset.GetRefresh` after one envelope unwrap.
 * Kept loose (`unknown`) so coercion remains defensive at the boundary.
 */
interface GetRefreshResponseInner {
  status?: unknown;
  contentType?: unknown;
  bodyAsBase64?: unknown;
  body?: unknown;
  [key: string]: unknown;
}

/**
 * Pull the host's inner `data` payload out of `result.context`.
 * Supports both byte payloads (`bodyAsBase64`) and JSON status payloads.
 */
function extractGetRefreshBody(ctx: unknown): GetRefreshResponseInner {
  if (!ctx || typeof ctx !== "object") return {};

  const c = ctx as Record<string, unknown>;

  // Shape 1: { data: { status, contentType, bodyAsBase64 } } (bytes-ready)
  // Shape 2: { data: { status, contentType, body: { data: { ...status } } } } (status-json)
  const dataLevel = c["data"];
  if (dataLevel && typeof dataLevel === "object" && !Array.isArray(dataLevel)) {
    const d = dataLevel as Record<string, unknown>;
    const body = d["body"];
    if (body && typeof body === "object" && !Array.isArray(body)) {
      const innerData = (body as Record<string, unknown>)["data"];
      if (innerData && typeof innerData === "object" && !Array.isArray(innerData)) {
        return {
          ...d,
          body: innerData,
        } as GetRefreshResponseInner;
      }
    }
    return d as GetRefreshResponseInner;
  }

  // Shape 3: already-unwrapped flat object.
  if (typeof c["bodyAsBase64"] === "string") {
    return c as GetRefreshResponseInner;
  }

  // Shape 4 (defensive): recursive search for a node carrying bytes.
  const recursive = findBodyAsBase64Node(c);
  if (recursive) return recursive as GetRefreshResponseInner;

  return c as GetRefreshResponseInner;
}

function extractGetRefreshStatusPayload(
  inner: GetRefreshResponseInner,
  ctx: unknown
): RefreshStatusBody | undefined {
  const body = inner.body;
  if (body && typeof body === "object" && !Array.isArray(body)) {
    const candidate = body as Record<string, unknown>;
    if (typeof candidate["id"] === "string" || typeof candidate["status"] === "string") {
      return candidate as RefreshStatusBody;
    }
  }

  const fallback = extractRefreshStatusBody(ctx).body;
  if (typeof fallback.id === "string" || typeof fallback.status === "string") {
    return fallback;
  }
  return undefined;
}

/**
 * Fetch refresh result bytes / status via `factset.GetRefresh`.
 *
 * Returns both shapes on one surface:
 *  - ready bytes (`workbookBase64`)
 *  - in-flight / terminal status payload (`refreshStatus`)
 */
export async function getRefreshViaIntents(
  request: FactsetGetRefreshRequest,
  options: GetRefreshOptions = {}
): Promise<FactsetGetRefreshResult> {
  if (!request || typeof request !== "object") {
    throw new Error("GetRefresh: `request` must be an object");
  }
  const refreshId =
    typeof request.refreshId === "string" ? request.refreshId.trim() : "";
  const orUrl = typeof request.orUrl === "string" ? request.orUrl.trim() : "";
  if (!refreshId && !orUrl) {
    throw new Error("GetRefresh: provide `refreshId` or `orUrl`");
  }

  const timeoutMs = options.timeoutMs ?? LONG_RUNNING_FACTSET_INTENT_TIMEOUT_MS;

  const ctx = await raiseIntent(
    GET_REFRESH_INTENT_NAME,
    {
      type: GET_REFRESH_CONTEXT_TYPE,
      ...(refreshId ? { refreshId } : {}),
      ...(orUrl ? { orUrl } : {}),
    },
    timeoutMs,
    GET_REFRESH_APP_ID,
    options.signal
  );

  const inner = extractGetRefreshBody(ctx);
  const workbookBase64 = asOptionalString(inner.bodyAsBase64);
  const contentType = asOptionalString(inner.contentType);
  const status = asOptionalFiniteNumber(inner.status);
  const refreshStatus = extractGetRefreshStatusPayload(inner, ctx);

  let error: string | null | undefined;
  if (status !== undefined && (status < 200 || status >= 300)) {
    error = `Host returned status ${status}`;
  } else if (status === 200 && !workbookBase64) {
    error = "Host returned status 200 but no bodyAsBase64";
  } else if (workbookBase64) {
    error = null;
  } else {
    error = undefined;
  }

  return {
    workbookBase64,
    contentType,
    status,
    refreshStatus,
    error,
    raw: inner as Record<string, unknown>,
  };
}

/**
 * Per-call options for `pollRefreshCalculate`.
 */
export interface PollRefreshCalculateOptions {
  /** Delay between successive `getRefreshStatus` calls. Defaults to 1500ms. */
  intervalMs?: number;
  /** Overall deadline for the whole polling loop. Defaults to five minutes. */
  timeoutMs?: number;
  /** Per-poll timeout passed through to `getRefreshStatusViaIntents`. */
  pollTimeoutMs?: number;
  /** Abort the polling loop. Aborts the in-flight poll too. */
  signal?: AbortSignal;
  /** Optional progress callback fired after every poll (terminal or
   *  non-terminal). Useful for surfacing intermediate "still
   *  running" UI updates. */
  onUpdate?: (result: FactsetGetRefreshStatusResult) => void;
}

/**
 * Terminal-status markers — `pollRefreshCalculate` returns the last
 * `getRefreshStatus` result and stops polling when `body.status`
 * matches any of these (case-insensitive).
 *
 * **Captured non-terminal values are explicitly NOT in this set:**
 *   - `"queued"` — captured 2026-05-01 from `refreshCalculate`'s
 *     initial response. Pairs with HTTP `202 Accepted`.
 *   - `"executing"` — captured 2026-05-05 from `getRefreshStatus`.
 *     Pairs with HTTP `202 Accepted`.
 *
 * **Captured terminal values:**
 *   - `"created"` — captured 2026-05-05 from `getRefreshStatus`.
 *     Terminal-success: workbook resource has been created. Pairs
 *     with HTTP `201 Created` — the HTTP code transition from `202`
 *     to `201` is itself a reliable terminal marker, but matching on
 *     `body.status` here keeps the contract self-describing.
 *
 * The remaining values below are still **speculative** — pending
 * capture in `excel-docs/intent-details.md`. Kept as defensive
 * fallbacks: the host has been observed to emit `"created"` rather
 * than `"succeeded"` / `"completed"` for the success path, and no
 * failure-mode response has been captured yet. Prune entries the
 * host doesn't actually emit once enough captures pin down the value
 * space.
 */
const TERMINAL_REFRESH_STATUSES: ReadonlySet<string> = new Set([
  // Captured terminal-success:
  "created",
  // Speculative terminal-success markers (not yet observed; kept as
  // defensive fallbacks):
  "succeeded",
  "completed",
  "complete",
  "done",
  // Speculative terminal-failure markers (not yet observed):
  "failed",
  "error",
  "cancelled",
  "canceled",
  "timeout",
  "timedout",
]);

function isTerminalRefreshStatus(body: RefreshStatusBody): boolean {
  const raw = body.status;
  if (typeof raw === "string") {
    if (TERMINAL_REFRESH_STATUSES.has(raw.toLowerCase())) return true;
  }
  if (body.complete === true) return true;
  if (body.done === true) return true;
  return false;
}

/**
 * Poll `factset.GetRefreshStatus` keyed off a `refreshCalculate` job
 * id until the host reports a terminal status, the overall deadline
 * elapses, or the supplied `AbortSignal` aborts.
 *
 * Pairs with `refreshCalculateViaIntents`. Captured progression for
 * a single `id`: `queued (202)` → `executing (202)` → `created (201)`.
 *
 * ```
 * const job = await refreshCalculateViaIntents({ key, inputs });
 * const final = await pollRefreshCalculate(job.id, { onUpdate: ... });
 * // Captured terminal-success is "created" + httpStatus 201:
 * if (String(final.body.status).toLowerCase() === "created") {
 *   // chain into insertWorksheetsFromBase64 / downstream consumer
 * }
 * ```
 *
 * Defensive about the partially-captured response shape:
 *   - Treats any of `body.status` ∈ `TERMINAL_REFRESH_STATUSES`,
 *     `body.complete === true`, or `body.done === true` as terminal.
 *     Captured non-terminal values (`"queued"` / `"executing"`) are
 *     correctly excluded; captured terminal-success (`"created"`) is
 *     included. The speculative terminal entries (`"succeeded"` /
 *     `"failed"` / etc.) are kept as defensive fallbacks until the
 *     host's value space is fully captured.
 *   - Resolves with the last `getRefreshStatusViaIntents` result on
 *     terminal — does NOT throw on a `failed` / `error` status, since
 *     the caller usually wants to inspect the body to surface a
 *     useful error message. (The wrapper still throws on transport
 *     errors and non-2xx HTTP statuses; note `201 Created` is 2xx, so
 *     the captured terminal-success response passes through cleanly.)
 *   - Rejects with `RefreshCalculatePollTimeoutError` when the overall
 *     `timeoutMs` deadline is exceeded; the error carries the last
 *     result so callers can decide whether to keep waiting.
 */
export async function pollRefreshCalculate(
  refreshId: string,
  options: PollRefreshCalculateOptions = {}
): Promise<FactsetGetRefreshStatusResult> {
  if (typeof refreshId !== "string" || refreshId.length === 0) {
    throw new Error("pollRefreshCalculate: `refreshId` must be a non-empty string");
  }

  const intervalMs = Math.max(50, options.intervalMs ?? 1500);
  const overallTimeoutMs =
    options.timeoutMs ?? LONG_RUNNING_FACTSET_INTENT_TIMEOUT_MS;
  const deadline = Date.now() + overallTimeoutMs;

  let lastResult: FactsetGetRefreshStatusResult | undefined;

  while (true) {
    if (options.signal?.aborted) {
      throw new Error(`pollRefreshCalculate: aborted for refreshId="${refreshId}"`);
    }

    const result = await getRefreshStatusViaIntents(refreshId, {
      timeoutMs: options.pollTimeoutMs,
      signal: options.signal,
    });
    lastResult = result;
    options.onUpdate?.(result);

    if (isTerminalRefreshStatus(result.body)) {
      return result;
    }

    if (Date.now() >= deadline) {
      const err = new Error(
        `pollRefreshCalculate: timed out after ${overallTimeoutMs}ms for refreshId="${refreshId}" (last status=${String(result.body.status ?? "")})`
      ) as Error & { lastResult?: FactsetGetRefreshStatusResult };
      err.name = "RefreshCalculatePollTimeoutError";
      err.lastResult = lastResult;
      throw err;
    }

    await new Promise<void>((resolve, reject) => {
      // `{ once: true }` cleans up `onAbort` on the abort path, but
      // when the timer resolves normally we have to detach manually.
      // Without it, every poll iteration would leave a stale closure
      // on `options.signal` (~40 listeners over a 60s/1.5s polling
      // session), each pinning the now-settled promise's `timer` and
      // `reject` references until the signal eventually fires abort
      // or is GC'd. Long-running poll loops would accumulate
      // unnecessary memory pressure.
      const signal = options.signal;
      const timer = setTimeout(() => {
        if (signal) {
          signal.removeEventListener("abort", onAbort);
        }
        resolve();
      }, intervalMs);
      const onAbort = () => {
        clearTimeout(timer);
        reject(new Error(`pollRefreshCalculate: aborted for refreshId="${refreshId}"`));
      };
      if (signal) {
        if (signal.aborted) {
          onAbort();
          return;
        }
        signal.addEventListener("abort", onAbort, { once: true });
      }
    });
  }
}

// ============================================================================
// factset.GetPaneStatus  (NB: NOT in the `factset.Spreadsheet.*` namespace)
// ============================================================================
//
// Read the current freeze-pane state. Pairs with
// `factset.Spreadsheet.FreezeUnfreezePanes` as the read-side of "what's
// the current freeze state?" — useful for round-trip flows (read →
// mutate → re-freeze), and for UI affordances that need to disable a
// "freeze" button when panes are already frozen.
//
// **Departures from sibling Spreadsheet intents — the standard three for
// the OI Remote Iframe family:**
//
//   1. **Intent name uses CapitalizedCamelCase, not lowerCamelCase.**
//      The wire intent name is `factset.GetPaneStatus`. Same casing
//      convention as `factset.GetStylesets` and `factset.PreviewChart`.
//   2. **Different target appId.** Routes to `"OI Remote Iframe"`, NOT
//      the default `"com.factset.workstation"` target.
//   3. **No arguments.** The screenshot in
//      `f2_docs/excel/finster-docs/missing-intents-v0.md` documents the
//      verb but no input fields; the captured request convention for
//      no-arg OI Remote Iframe reads (`getStylesets`,
//      `getTemplatesList`) uses `context.type = "fdc3.nothing"` — same
//      here.
//
// **Response shape — NOT YET CAPTURED.** No wire trace exists in
// `excel-docs/intent-details.md` as of 2026-05-12. The wrapper assumes
// the OI Remote Iframe family's standard two-level `data.body.data`
// envelope (mirrors `getStylesets`) with an inferred inner shape of
// `{ status, row?, column?, worksheetName?, workbookName? }`. On first
// real call: capture the actual response, refine `PaneStatus`, update
// the §4.4 row in `missing-intents-v0.md`, and drop the TODO comment
// in the wrapper body.

const GET_PANE_STATUS_INTENT_NAME = "factset.GetPaneStatus";
const GET_PANE_STATUS_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Per-call options for `getPaneStatusViaIntents`. The screenshot capture
 * shows no input fields; the option bag is here for cancellation
 * symmetry with the rest of the OI Remote Iframe family.
 */
export interface GetPaneStatusOptions {
  timeoutMs?: number;
  signal?: AbortSignal;
}

/**
 * Normalised result returned by `getPaneStatusViaIntents`. Inferred
 * shape — see the TODO at the top of this section. The `[key: string]:
 * unknown` index signature passes any extra fields the host returns
 * through to the caller for forward-compat.
 */
export interface PaneStatus {
  /** Whether the active sheet's panes are currently frozen. Inferred
   *  values are `"Frozen"` / `"Unfrozen"`; preserved as a `string` so
   *  forward-compat host enums (e.g. `"FreezeFirstRow"`) round-trip
   *  without a wrapper update. */
  status?: string;
  /** Freeze row index. Omitted when `status === "Unfrozen"`. Indexing
   *  basis (0-based vs 1-based) verifies on first capture. */
  row?: number;
  /** Freeze column index. Same caveat as `row`. */
  column?: number;
  /** Name of the worksheet the status applies to. */
  worksheetName?: string;
  /** Name of the workbook the status applies to. */
  workbookName?: string;
  [key: string]: unknown;
}

/**
 * Inner-body shape after envelope unwrapping. Loose typing because we
 * have no captured response yet.
 */
interface GetPaneStatusResponseInner {
  status?: unknown;
  row?: unknown;
  column?: unknown;
  worksheetName?: unknown;
  workbookName?: unknown;
  [key: string]: unknown;
}

/**
 * Walk the response context and pull out the inner body. Mirrors
 * `extractGetStylesetsBody` — same two-level `data.body.data` envelope
 * assumption (TODO: validate on first capture). Falls back to the
 * obvious flatter forms before giving up.
 */
function extractGetPaneStatusBody(ctx: unknown): GetPaneStatusResponseInner {
  if (!ctx || typeof ctx !== "object") return {};
  const c = ctx as Record<string, unknown>;
  const dataLevel = c["data"];
  if (dataLevel && typeof dataLevel === "object" && !Array.isArray(dataLevel)) {
    const d = dataLevel as Record<string, unknown>;
    const body = d["body"];
    if (body && typeof body === "object" && !Array.isArray(body)) {
      const innerData = (body as Record<string, unknown>)["data"];
      if (innerData && typeof innerData === "object" && !Array.isArray(innerData)) {
        return innerData as GetPaneStatusResponseInner;
      }
      if ("status" in (body as Record<string, unknown>)) {
        return body as GetPaneStatusResponseInner;
      }
    }
    if ("status" in d) {
      return d as GetPaneStatusResponseInner;
    }
  }
  if ("status" in c) {
    return c as GetPaneStatusResponseInner;
  }
  return {};
}

/**
 * Read the current freeze-pane state via `factset.GetPaneStatus`.
 *
 * **No arguments.** Resolves with the host's reported
 * `{ status, row?, column?, worksheetName?, workbookName? }` after
 * unwrapping the OI Remote Iframe two-level envelope. Inner shape is
 * inferred — see TODO at the top of this section.
 *
 * Rejects only on transport / timeout errors. The host returning
 * `{ status: "Unfrozen" }` (or omitting `row` / `column`) is the
 * normal "no panes frozen" case, not an error.
 */
export async function getPaneStatusViaIntents(
  options: GetPaneStatusOptions = {}
): Promise<PaneStatus> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const ctx = await raiseIntent(
    GET_PANE_STATUS_INTENT_NAME,
    { type: "fdc3.nothing" },
    timeoutMs,
    GET_PANE_STATUS_APP_ID,
    options.signal
  );
  const inner = extractGetPaneStatusBody(ctx);
  return {
    ...inner,
    status: typeof inner.status === "string" ? inner.status : undefined,
    row: typeof inner.row === "number" ? inner.row : undefined,
    column: typeof inner.column === "number" ? inner.column : undefined,
    worksheetName:
      typeof inner.worksheetName === "string" ? inner.worksheetName : undefined,
    workbookName:
      typeof inner.workbookName === "string" ? inner.workbookName : undefined,
  };
}

// ============================================================================
// factset.Spreadsheet.AddImageFromBase64
// ============================================================================

/**
 * Per-call options for `AddImageFromBase64`. The captured payload in
 * `excel-docs/intent-details.md` lines 911-930 only shows the five fields
 * below, but the doc is a log of one example — keep this surface tight
 * to the captured shape and let callers pass extra opaque fields if they
 * appear later.
 *
 * `dataUri` MUST be a full data URI (`data:image/png;base64,...`), not
 * bare base64 — the captured example is explicit on this. `anchorCell`
 * pins the image's top-left corner (sheet defaults to active when no
 * sheet prefix is included; the spec doesn't document a `sheetName`
 * field, so cross-sheet placement requires switching sheet first).
 * `name` is required by spec (`excel-tools.md` line 1448; AI-2617). The
 * wrapper pre-validates and throws fail-fast when missing.
 * `width` / `height` are pixels.
 */
export interface AddImageFromBase64Options {
  dataUri: string;
  anchorCell: string;
  name: string;
  width?: number;
  height?: number;
}

interface AddImageFromBase64Response {
  type?: string;
  message?: unknown;
  success?: unknown;
}

/**
 * Drop a base64 image onto the active sheet via
 * `factset.Spreadsheet.AddImageFromBase64`.
 *
 * Response is **not captured** in `intent-details.md`; we accept either
 * `{ message: string }` (matches sibling write intents like `addWorkbook`)
 * or `{ success: true }` (matches `setCalculationMode`/`deleteRange`).
 * Returns the host's `message` if present, otherwise `""`.
 *
 * Rejects only if the transport rejects (timeout, host error). Per-cell
 * silent no-ops cannot be detected with the current uncaptured response
 * shape; callers that need confirmation should re-read the sheet via
 * `getRangeInfo` after the call.
 */
export async function addImageFromBase64ViaIntents(
  options: AddImageFromBase64Options,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<string> {
  if (typeof options.dataUri !== "string" || !options.dataUri.startsWith("data:")) {
    throw new Error(
      "AddImageFromBase64: `dataUri` must be a full data URI (e.g. `data:image/png;base64,...`)"
    );
  }
  if (typeof options.anchorCell !== "string" || options.anchorCell.length === 0) {
    throw new Error("AddImageFromBase64: `anchorCell` is required");
  }
  if (typeof options.name !== "string" || options.name.length === 0) {
    throw new Error("AddImageFromBase64: `name` is required (per excel-tools.md)");
  }
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.AddImageFromBase64",
    {
      type: "factset.spreadsheet.addImageFromBase64",
      dataUri: options.dataUri,
      anchorCell: options.anchorCell,
      name: options.name,
      ...(options.width !== undefined ? { width: options.width } : {}),
      ...(options.height !== undefined ? { height: options.height } : {}),
    },
    timeoutMs
  )) as AddImageFromBase64Response | undefined;
  return typeof ctx?.message === "string" ? ctx.message : "";
}

// ============================================================================
// factset.Spreadsheet.Charting.AddExcelChart
// ============================================================================
//
// Native Excel chart insertion. Distinct from `AddActiveGraph` (above):
//
//  - `AddActiveGraph` lands a FactSet ActiveGraph **OLE embed** with a
//    pre-rendered FDSChart definition; data is bound by FactSet's chart
//    runtime, not by Excel.
//  - `AddExcelChart` (this intent) lands a **native Excel chart** — same
//    object you'd get from Insert → Chart in the Excel ribbon — bound to
//    a `sourceRange` on a worksheet via the standard Excel chart engine.
//
// Use the FactSet ActiveGraph path for ticker-driven / FDS-formula-driven
// previews where the chart must follow FactSet styling. Use this intent
// for everything else: the planner emitting `chart.create` from raw cell
// data, user-built data tables, etc.
//
// Naming gotcha (cost us a debugging session on 2026-05-05):
//
//   The intent lives in the `Charting` sub-namespace —
//   `factset.Spreadsheet.Charting.AddExcelChart` — even though the
//   request context type is `factset.spreadsheet.charting.addExcelChart`
//   (the response in turn lives at
//   `factset.spreadsheet.charting.addExcelChartResult`). Earlier wrapper
//   code sent `factset.Spreadsheet.AddExcelChart` (no `Charting`) and the
//   host correctly responded `Unknown intent`. The official catalog is
//   `f2_docs/excel/chart-tools.md`; treat that file as source of truth
//   for the sibling `Charting.*` intents (UpdateExcelChart,
//   AddSeriesToChart, UpdateSeriesInChart, RemoveSeriesFromChart).
//
// Routing:
//
//   The doc example uses `appId: "Finster"`, but in practice the OI
//   Remote Iframe relay routes this intent the same way it routes
//   `SelectRange`, `previewChart`, and the download wrappers, so we
//   keep the explicit `ADD_EXCEL_CHART_APP_ID = "OI Remote Iframe"`
//   override. Default workstation routing (`com.factset.workstation`)
//   has not been verified to register this intent.
//
// Wire shape (request, per `f2_docs/excel/chart-tools.md`):
//
//   {
//     type: "factset.spreadsheet.charting.addExcelChart",
//     options: {
//       sourceRange: "A1:C10",
//       chartType: "xlColumnClustered",  // any Excel `xl*` chart-type enum
//       workbookName?: string,
//       sheetRef?: { index?: number, name?: string },
//       chartIndex?: number,              // 0-based chart collection index
//       position?: { top, left, width, height },  // POINTS (not pixels)
//       chartTitle?: string,
//       chartInfo?: {
//         title?: { hasTitle?, text?, font?: { size?, bold? } },
//         legend?: { hasLegend?, position?: string }  // xlLegendPosition*
//       }
//     }
//   }
//
// Wire shape (response, captured 2026-05-05):
//
//   {
//     type: "factset.spreadsheet.charting.addExcelChartResult",
//     result: "<STRINGIFIED JSON>"
//   }
//
// Where the inner stringified JSON parses to:
//
//   {
//     type: "Chart",
//     id: 0, index: 0, name: "Sheet1 Chart 1",
//     chartType: "xlColumnClustered",
//     title:  { hasTitle: true,  text: "Quarterly Sales Report" },
//     legend: { hasLegend: true, position: "xlLegendPositionRight" }
//   }
//
// The wrapper unwraps that double-encoded payload and returns the parsed
// inner object. **Position is in points**, not cell-anchored — the
// dispatcher / call site is responsible for cell→point translation
// (see `excelHostOps::addExcelChart` for the planner-shape entry point
// that does the conversion).

/**
 * Sheet target for `AddExcelChart`. Mirrors the shared `SheetRef`
 * shape from chart-tools.md — at least one of `index` (0-based),
 * `name` (sheet tab), or `codeName` (VBA) must be set; the backend
 * resolves in that order.
 */
export interface AddExcelChartSheetRef {
  index?: number;
  name?: string;
  codeName?: string;
}

/**
 * Point-anchored position + size for the inserted chart. Coordinates
 * are relative to the destination sheet's top-left (0,0). Units are
 * Excel points (1pt = 1/72 inch), matching Excel's COM `Top` / `Left`
 * / `Width` / `Height` chart properties — *not* pixels.
 */
export interface AddExcelChartPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

// Legacy slim shape preserved here for any external/long-lived caller
// that still types its title/legend via the old names. Prefer the
// richer `UpdateExcelChartChartInfo` surface below.
export interface AddExcelChartTitleInfo {
  hasTitle?: boolean;
  text?: string;
  font?: { size?: number; bold?: boolean };
}

export interface AddExcelChartLegendInfo {
  hasLegend?: boolean;
  /** Excel COM `XlLegendPosition` enum string, e.g. `"xlLegendPositionBottom"`. */
  position?: string;
}

/**
 * Optional post-creation chart customization applied in the same call
 * as `AddExcelChart` — equivalent to immediately following the insert
 * with an `UpdateExcelChart`. Mirrors the full `UpdateExcelChartChartInfo`
 * surface (title / legend / axes / chartArea / plotArea / plotBy /
 * displayBlanksAs / etc.). **`series` on `chartInfo` is silently
 * dropped** by the host per `f2_docs/charting/chart-primitives-preview.md`
 * — use the dedicated series primitives (`AddSeriesToChart`,
 * `UpdateSeriesInChart`, `RemoveSeriesFromChart`) instead. The Finster
 * wrapper additionally strips it client-side and warns, so a planner
 * regression can't smuggle series into the create call.
 */
export type AddExcelChartInfo = UpdateExcelChartChartInfo;

/**
 * Per-call options for `AddExcelChart`. `sourceRange` and `chartType`
 * are the only required fields; everything else falls back to host
 * defaults (active workbook + active sheet, default chart placement).
 *
 * `chartType` is the **wire** Excel COM enum string (`"xlColumnClustered"`
 * etc.) — not the planner's friendlier `"column"` / `"line"` form. Map
 * planner enums to wire enums at the call site (see
 * `excelHostOps::addExcelChart`).
 */
export interface AddExcelChartOptions {
  sourceRange: string;
  chartType: string;
  workbookName?: string;
  sheetRef?: AddExcelChartSheetRef;
  chartIndex?: number;
  position?: AddExcelChartPosition;
  chartTitle?: string;
  chartInfo?: AddExcelChartInfo;
}

/**
 * Parsed inner payload returned on `AddExcelChart` success. Mirrors the
 * captured stringified-JSON shape verbatim — exposed so callers can
 * thread `name` / `index` into a `trackOperation` log.
 */
export interface AddExcelChartResult {
  /** Always `"Chart"` in the captured sample. */
  type?: string;
  /** Host-assigned chart id (numeric). */
  id?: number;
  /** Host-assigned chart index inside the destination sheet's chart collection. */
  index?: number;
  /** Auto-generated chart name like `"Sheet1 Chart 1"`. */
  name?: string;
  chartType?: string;
  title?: { hasTitle?: boolean; text?: string };
  legend?: { hasLegend?: boolean; position?: string };
}

interface AddExcelChartResponse {
  /**
   * Captured response carries `result` as a STRINGIFIED JSON. Tolerate
   * both string and already-parsed-object forms (different host versions
   * may collapse the stringification).
   */
  result?: unknown;
}

const ADD_EXCEL_CHART_APP_ID = { appId: "OI Remote Iframe" };
const ADD_EXCEL_CHART_CONTEXT_TYPE = "factset.spreadsheet.charting.addExcelChart";

function buildAddExcelChartContext(
  options: AddExcelChartOptions,
  normalizedChartType: string,
  safeChartInfo: AddExcelChartInfo | undefined,
): Record<string, unknown> {
  return {
    type: ADD_EXCEL_CHART_CONTEXT_TYPE,
    options: {
      ...(options.workbookName !== undefined
        ? { workbookName: options.workbookName }
        : {}),
      ...(options.sheetRef !== undefined ? { sheetRef: options.sheetRef } : {}),
      ...(options.chartIndex !== undefined ? { chartIndex: options.chartIndex } : {}),
      sourceRange: options.sourceRange,
      chartType: normalizedChartType,
      ...(options.position !== undefined ? { position: options.position } : {}),
      ...(options.chartTitle !== undefined
        ? { chartTitle: options.chartTitle }
        : {}),
      ...(safeChartInfo !== undefined ? { chartInfo: safeChartInfo } : {}),
    },
  };
}

/**
 * Insert a native Excel chart via
 * `factset.Spreadsheet.Charting.AddExcelChart`.
 *
 * Returns the parsed inner payload (`{ type, id, index, name, chartType,
 * title, legend }`) so callers can thread the host-assigned chart name
 * into their apply log. Rejects if the transport rejects, the host
 * returns a non-`result` envelope, or the inner JSON fails to parse.
 *
 * **Position is in points** (Excel's native chart unit, 1pt = 1/72 inch).
 * Planner-shape entries (cell-anchored startCell + width/height in
 * cells) are translated upstream by `excelHostOps::addExcelChart`.
 */
export async function addExcelChartViaIntents(
  options: AddExcelChartOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<AddExcelChartResult> {
  if (typeof options.sourceRange !== "string" || options.sourceRange.length === 0) {
    throw new Error("AddExcelChart: `sourceRange` is required");
  }
  if (typeof options.chartType !== "string" || options.chartType.length === 0) {
    throw new Error("AddExcelChart: `chartType` is required (e.g. `xlColumnClustered`)");
  }
  // Capture the registry-normalized chartType so the wire payload
  // below sends the trimmed id (not the caller's whitespace-padded
  // original). Validating one value and sending another lets the host
  // re-reject what we just accepted.
  const normalizedChartType = assertChartTypeInRegistry(
    options.chartType,
    "native_excel",
    "AddExcelChart",
  );
  // f2_docs/charting alignment: strip series from chartInfo (use the series
  // primitives instead) and run colors through the strict `#RRGGBB` validator.
  // The deep clone is mandatory — `sanitizeColorsInPlace` mutates nested objects
  // (`title.font`, `chartArea.fill`, etc.) and a shallow spread would leak
  // those mutations back to the caller.
  const safeChartInfo =
    options.chartInfo !== undefined
      ? deepCloneChartPayload(options.chartInfo as Record<string, unknown>)
      : undefined;
  if (safeChartInfo) {
    stripSeriesFromChartInfo(safeChartInfo, "AddExcelChart");
    sanitizeColorsInPlace(safeChartInfo, "chartInfo");
  }
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.Charting.AddExcelChart",
    buildAddExcelChartContext(
      options,
      normalizedChartType,
      safeChartInfo,
    ),
    timeoutMs,
    ADD_EXCEL_CHART_APP_ID
  )) as AddExcelChartResponse | undefined;

  const raw = ctx?.result;
  if (raw === undefined || raw === null) {
    throw new Error("AddExcelChart: response did not include `result`");
  }
  let parsed: unknown;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      throw new Error(
        `AddExcelChart: failed to parse \`result\` as JSON: ${
          e instanceof Error ? e.message : String(e)
        }`
      );
    }
  } else {
    parsed = raw;
  }
  if (!parsed || typeof parsed !== "object") {
    throw new Error("AddExcelChart: parsed `result` was not an object");
  }
  return parsed as AddExcelChartResult;
}

// ============================================================================
// factset.Spreadsheet.Charting.UpdateExcelChart
// ============================================================================
//
// Update chart-level properties on an existing native Excel chart —
// chart type, title, legend, axes, plot area, etc. Series properties
// are **not** applied by this intent; use `AddSeriesToChart`,
// `UpdateSeriesInChart`, `RemoveSeriesFromChart` for series writes.
//
// Wire shape:
//
//   `UpdateExcelChart` uses the same `options` envelope pattern as
//   `AddExcelChart`:
//     { type, options: { workbookName, sheetRef, chartIndex, chartInfo } }
//
// Targeting (per `f2_docs/excel/chart-tools.md`):
//
//   - Omit `sheetRef` AND `chartIndex` to target the currently
//     active chart.
//   - Provide both to target a specific chart on a specific sheet.
//   - Provide `workbookName` only to disambiguate when multiple
//     workbooks are open.
//
// Wire shape (request, captured 2026-05-05):
//
//   {
//     type: "factset.spreadsheet.charting.updateExcelChart",
//     options: {
//       workbookName?: string,
//       sheetRef?:    { index?: number, name?: string, codeName?: string },
//       chartIndex?:  number,
//       chartInfo: {
//       name?, chartType?, chartStyle?,
//       title?:    { hasTitle?, text?, font?, includeInLayout?, position? },
//       legend?:   { hasLegend?, position?, font?, includeInLayout?, legendEntries? },
//       plotBy?, displayBlanksAs?, plotVisibleOnly?, hasDataTable?,
//       chartArea?: { fill?, line?, font?, roundedCorners?, shadow? },
//       plotArea?:  { fill?, line?, insideLeft?, insideTop?, insideWidth?, insideHeight? },
//       axes?: AxisInfo[],
//       }
//     }
//   }
//
//   The host accepts XL enum values as **strings** (`"xlColumnClustered"`,
//   `"xlLegendPositionBottom"`, `"xlScaleLinear"`, `"xlCategoryScale"`,
//   `"xlThousands"`, `"xlNotPlotted"`, etc.) even though the catalog
//   table lists them as numeric enum values. Stick to strings — that's
//   what the working capture (2026-05-05) used.
//
// Wire shape (response, captured 2026-05-05):
//
//   {
//     result: {
//       success: boolean,
//       appliedProperties: string[],   // dotted paths, e.g. "title.text",
//                                      // "axis[xlValue,xlPrimary]"
//       warnings: string[]
//     }
//   }
//
//   Note `result` is a **structured object**, not a stringified-JSON
//   blob like `AddExcelChart`. The wrapper returns the inner object
//   directly — no JSON.parse step.
//
// Routing: same `OI Remote Iframe` relay as the rest of the
// `Charting.*` family.

/**
 * Sheet target for `UpdateExcelChart` (and the shared chart-targeting
 * shape across the `Charting.*` intents). At least one of `index` /
 * `name` / `codeName` must be set; the host resolves in that order.
 */
export interface UpdateExcelChartSheetRef {
  index?: number;
  name?: string;
  codeName?: string;
}

/**
 * Font properties on a `chartInfo` write. `colorRgb` is the same
 * `#RRGGBB` form `Charting.GetCharts` returns; the catalog also
 * documents an alternate `"FF0000"` form (no leading `#`) so callers
 * may pass either. Strings only.
 */
export interface UpdateExcelChartFont {
  name?: string;
  size?: number;
  bold?: boolean;
  italic?: boolean;
  /** Underline toggle. Preview spec includes it on `ChartFontInfo`; we
   *  expose it on writes for parity with the read-side
   *  `ChartingFontInfo`. */
  underline?: boolean;
  colorRgb?: string;
}

/** Solid-fill descriptor used by `chartArea`, `plotArea`, etc. */
export interface UpdateExcelChartFill {
  isVisible?: boolean;
  colorRgb?: string;
}

/** Line / border descriptor (axis lines, gridlines, area borders). */
export interface UpdateExcelChartLine {
  isVisible?: boolean;
  colorRgb?: string;
  weight?: number;
  /** `MsoLineDashStyle` enum string, e.g. `"msoLineSolid"`. */
  dashStyle?: string;
}

/** Title block on the chart. Set `hasTitle: true` to show the title. */
export interface UpdateExcelChartTitle {
  hasTitle?: boolean;
  text?: string;
  /** Include the title in the chart's auto-layout. */
  includeInLayout?: boolean;
  /** `XlChartElementPosition` enum string. */
  position?: string;
  font?: UpdateExcelChartFont;
}

/** Legend block on the chart. */
export interface UpdateExcelChartLegend {
  hasLegend?: boolean;
  /** `XlLegendPosition` enum string, e.g. `"xlLegendPositionBottom"`. */
  position?: string;
  includeInLayout?: boolean;
  font?: UpdateExcelChartFont;
  /** Per-entry font overrides — `{ index: 0-based, font: ... }`. */
  legendEntries?: Array<{ index?: number; font?: UpdateExcelChartFont }>;
}

/**
 * Axis descriptor. The host returns one entry per axis on the chart,
 * and accepts the same shape on writes. Category-only fields
 * (`categoryType`) and value-only fields (`minimumScale`,
 * `maximumScale`, `majorUnit`, `minorUnit`, `scaleType`,
 * `majorGridLine`, `minorGridLine`) are optional so a single shape
 * covers both axis types. `displayUnit` is value-axis-only on the
 * write side. Full surface mirrors
 * `f2_docs/charting/chart-schema-preview.md::ChartAxisInfo` —
 * `*IsAuto` toggles, tick-mark / crossing / display-unit fields, and
 * date-axis `baseUnit` are all addressable on the write path.
 */
export interface UpdateExcelChartAxis {
  /** `XlAxisType` enum string, e.g. `"xlCategory"` / `"xlValue"`. */
  axisType?: string;
  /** `XlAxisGroup` enum string, e.g. `"xlPrimary"` / `"xlSecondary"`. */
  axisGroup?: string;
  /** Whether this axis is enabled (maps to `Chart.HasAxis`). */
  enabled?: boolean;
  hasTitle?: boolean;
  titleText?: string;
  minimumScale?: number;
  maximumScale?: number;
  majorUnit?: number;
  minorUnit?: number;
  hasMajorGridlines?: boolean;
  hasMinorGridlines?: boolean;
  reversePlotOrder?: boolean;
  /** `XlScaleType` enum string (value axes only), e.g. `"xlScaleLinear"`. */
  scaleType?: string;
  /** `XlCategoryType` enum string (category axes only). */
  categoryType?: string;
  /** `XlDisplayUnit` enum string, e.g. `"xlThousands"`. */
  displayUnit?: string;
  hasDisplayUnitLabel?: boolean;
  numberFormat?: string;
  /** `XlTickLabelPosition` enum string. */
  tickLabelPosition?: string;
  tickMarkSpacing?: number;
  tickLabelSpacing?: number;
  /** `XlTickMark` enum string, e.g. `"xlTickMarkOutside"`. */
  majorTickMark?: string;
  minorTickMark?: string;
  /** `XlAxisCrosses` enum string, e.g. `"xlAxisCrossesAutomatic"`. */
  crosses?: string;
  crossesAt?: number;
  minimumScaleIsAuto?: boolean;
  maximumScaleIsAuto?: boolean;
  majorUnitIsAuto?: boolean;
  minorUnitIsAuto?: boolean;
  /** `XlTimeUnit` enum string (date axes only). */
  baseUnit?: string;
  baseUnitIsAuto?: boolean;
  tickLabelFont?: UpdateExcelChartFont;
  titleFont?: UpdateExcelChartFont;
  axisLine?: UpdateExcelChartLine;
  majorGridLine?: UpdateExcelChartLine;
  minorGridLine?: UpdateExcelChartLine;
}

/** Background area surrounding the entire chart. */
export interface UpdateExcelChartChartArea {
  fill?: UpdateExcelChartFill;
  line?: UpdateExcelChartLine;
  font?: UpdateExcelChartFont;
  roundedCorners?: boolean;
  shadow?: boolean;
}

/** Inner plot area where the data is rendered. Sizes are in points. */
export interface UpdateExcelChartPlotArea {
  fill?: UpdateExcelChartFill;
  line?: UpdateExcelChartLine;
  insideLeft?: number;
  insideTop?: number;
  insideWidth?: number;
  insideHeight?: number;
}

/**
 * Chart-level properties applied by `UpdateExcelChart`. All fields are
 * optional — supply only the ones you want to change. Series properties
 * are **not** applied here; use `UpdateSeriesInChart` for that.
 *
 * Full surface mirrors `f2_docs/charting/chart-schema-preview.md::ChartInfo`
 * minus `series` (excluded by primitive contract — see above) and
 * minus identity / read-only fields (`id`, `index`, `sheetRef`,
 * `position`, `name` is writable, the rest are not).
 */
export interface UpdateExcelChartChartInfo {
  /** Rename the chart shape. */
  name?: string;
  /** `XlChartType` enum string, e.g. `"xlColumnClustered"`, `"xlLine"`. */
  chartType?: string;
  /** Built-in chart-style number (1-48 in modern Excel). */
  chartStyle?: number;
  /** Theme color palette index (Chart.ChartColor). */
  chartColorIndex?: number;
  title?: UpdateExcelChartTitle;
  legend?: UpdateExcelChartLegend;
  /** `XlRowCol` enum string, `"xlRows"` or `"xlColumns"`. */
  plotBy?: string;
  /** `XlDisplayBlanksAs` enum string, e.g. `"xlNotPlotted"`. */
  displayBlanksAs?: string;
  plotVisibleOnly?: boolean;
  hasDataTable?: boolean;
  showDataLabelsOverMaximum?: boolean;
  /** Multi-level category label depth (Excel-only). */
  categoryLabelLevel?: number;
  /** Multi-level series name depth (Excel-only). */
  seriesNameLevel?: number;
  /** Pivot-chart field-button toggles (Excel-only). */
  showAllFieldButtons?: boolean;
  showAxisFieldButtons?: boolean;
  showLegendFieldButtons?: boolean;
  showReportFilterFieldButtons?: boolean;
  showValueFieldButtons?: boolean;
  chartArea?: UpdateExcelChartChartArea;
  plotArea?: UpdateExcelChartPlotArea;
  axes?: UpdateExcelChartAxis[];
  /** Chart-group settings (gap / overlap / pie / bubble — applied
   *  per group, identified by 0-based `index`). */
  chartGroups?: ChartingChartGroupInfo[];
}

/**
 * Per-call options for `Charting.UpdateExcelChart`. The wrapper nests these
 * under the wire `options` object; `chartInfo` is the only required field.
 */
export interface UpdateExcelChartOptions {
  chartInfo: UpdateExcelChartChartInfo;
  workbookName?: string;
  sheetRef?: UpdateExcelChartSheetRef;
  /** 0-based chart index on the target sheet. Omit to target the active chart. */
  chartIndex?: number;
}

/** Structured `result` payload returned on success. */
export interface UpdateExcelChartResult {
  success: boolean;
  /** Dotted property paths that were applied, e.g. `"title.text"`. */
  appliedProperties: string[];
  /** Non-fatal warnings from the host (e.g. invalid enum values that were ignored). */
  warnings: string[];
}

interface UpdateExcelChartResponse {
  /**
   * Captured 2026-05-05 as a structured object — distinct from
   * `AddExcelChart`'s stringified-JSON envelope. Tolerate the
   * stringified shape too in case the host collapses the structure on
   * future builds.
   */
  result?: unknown;
}

const UPDATE_EXCEL_CHART_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Update chart-level properties via
 * `factset.Spreadsheet.Charting.UpdateExcelChart`.
 *
 * Returns the structured `{ success, appliedProperties, warnings }`
 * payload directly so callers can confirm which properties were
 * applied (the host silently no-ops unknown / unsupported fields and
 * surfaces the rest in `warnings`). Rejects if the transport rejects
 * or the host returns a non-`result` envelope.
 *
 * Series writes are **not** supported here — use the per-series
 * intents (`AddSeriesToChart`, `UpdateSeriesInChart`,
 * `RemoveSeriesFromChart`).
 */
export async function updateExcelChartViaIntents(
  options: UpdateExcelChartOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<UpdateExcelChartResult> {
  if (!options.chartInfo || typeof options.chartInfo !== "object") {
    throw new Error("UpdateExcelChart: `chartInfo` is required");
  }
  // Registry gate: if the caller is changing the chart type, reject
  // values outside the native Excel catalog client-side. Mirrors the
  // gate in `addExcelChartViaIntents` so every native chart primitive
  // funnels through the same source of truth. `normalizedChartType`
  // holds the trimmed id so we can also write the clean value back
  // into the cloned payload before it hits the wire.
  const rawUpdateChartType =
    typeof options.chartInfo.chartType === "string"
      ? options.chartInfo.chartType.trim()
      : "";
  let normalizedChartType: string | undefined;
  if (rawUpdateChartType.length > 0) {
    normalizedChartType = assertChartTypeInRegistry(
      rawUpdateChartType,
      "native_excel",
      "UpdateExcelChart",
    );
  }
  // f2_docs/charting alignment: strip series from chartInfo (this primitive
  // ignores series — use the dedicated series CRUD primitives) and run colors
  // through the strict `#RRGGBB` validator. Deep clone mandatory — see
  // `deepCloneChartPayload` JSDoc.
  const safeChartInfo: Record<string, unknown> = deepCloneChartPayload(
    options.chartInfo as unknown as Record<string, unknown>
  );
  if (normalizedChartType !== undefined) {
    safeChartInfo.chartType = normalizedChartType;
  }
  stripSeriesFromChartInfo(safeChartInfo, "UpdateExcelChart");
  sanitizeColorsInPlace(safeChartInfo, "chartInfo");
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.Charting.UpdateExcelChart",
    {
      type: "factset.spreadsheet.charting.updateExcelChart",
      options: {
        ...(options.workbookName !== undefined
          ? { workbookName: options.workbookName }
          : {}),
        ...(options.sheetRef !== undefined ? { sheetRef: options.sheetRef } : {}),
        ...(options.chartIndex !== undefined
          ? { chartIndex: options.chartIndex }
          : {}),
        chartInfo: safeChartInfo,
      },
    },
    timeoutMs,
    UPDATE_EXCEL_CHART_APP_ID
  )) as UpdateExcelChartResponse | undefined;

  const raw = ctx?.result;
  if (raw === undefined || raw === null) {
    throw new Error("UpdateExcelChart: response did not include `result`");
  }
  let parsed: unknown;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      throw new Error(
        `UpdateExcelChart: failed to parse \`result\` as JSON: ${
          e instanceof Error ? e.message : String(e)
        }`
      );
    }
  } else {
    parsed = raw;
  }
  if (!parsed || typeof parsed !== "object") {
    throw new Error("UpdateExcelChart: parsed `result` was not an object");
  }
  const obj = parsed as Record<string, unknown>;
  return {
    success: obj.success === true,
    appliedProperties: Array.isArray(obj.appliedProperties)
      ? (obj.appliedProperties.filter((p) => typeof p === "string") as string[])
      : [],
    warnings: Array.isArray(obj.warnings)
      ? (obj.warnings.filter((w) => typeof w === "string") as string[])
      : [],
  };
}

// ============================================================================
// factset.Spreadsheet.Charting.AddSeriesToChart
// ============================================================================
//
// Append a new data series to an existing native Excel chart. Sibling
// of `Charting.UpdateExcelChart` — same top-level envelope (targeting
// fields are flat alongside `type`; the body is `series`, NOT
// `chartInfo`) and same `OI Remote Iframe` routing. Used by planner
// `ChartModification.action = "addSeries"`.
//
// Wire shape (request, captured 2026-05-05):
//
//   {
//     type: "factset.spreadsheet.charting.addSeriesToChart",
//     workbookName?: string,
//     sheetRef?:    { index?: number, name?: string, codeName?: string },
//     chartIndex?:  number,
//     series: {
//       values: string,           // required; A1 range with sheet prefix preferred
//       categories?: string,
//       name?: string,
//       chartType?: string,       // XlChartType — combo charts can mix per series
//       axisGroup?: string,       // "xlPrimary" | "xlSecondary" (host accepts strings)
//       plotOrder?: number,       // 1-based slot in the series collection
//       smooth?: boolean,
//       explosion?: number,       // pie/doughnut slice explosion in points
//       fill?: { isVisible?, colorRgb? },
//       line?: { isVisible?, colorRgb?, weight?, dashStyle? },
//       marker?: { style?, size?, fill?, line? },
//       dataLabels?: { showValue?, showCategoryName?, showSeriesName?,
//                      position?, numberFormat?, font?, fill?, border? }
//     }
//   }
//
//   Same string-vs-number gotcha as `UpdateExcelChart`: the catalog
//   table lists `axisGroup`, `marker.style`, `dataLabels.position`,
//   etc. as numeric enum values, but the working capture (and the
//   `Charting.GetCharts` response shape) consistently use the matching
//   `xl*` enum string. Send strings.
//
// Wire shape (response, captured 2026-05-05):
//
//   {
//     result: {
//       success: boolean,
//       seriesIndex: number   // 0-based slot of the newly added series
//     }
//   }
//
//   Smaller envelope than `UpdateExcelChart`'s `appliedProperties[]` —
//   no warnings array on this intent today (the host either succeeds
//   or rejects). The wrapper still tolerates a stringified `result` in
//   case the host collapses the structure on a future build.
//
// Targeting (per `f2_docs/excel/chart-tools.md`):
//
//   - Provide all three (`workbookName`, `sheetRef`, `chartIndex`) to
//     target a specific chart, or omit all three to operate on the
//     active chart.
//   - The catalog flags `workbookName` without `sheetRef`/`chartIndex`
//     as an error specifically for the series intents, so we either
//     send the full triple or none of it.

/** Marker styling on a series write. `style` is `XlMarkerStyle` enum string. */
export interface AddSeriesMarker {
  style?: string;
  size?: number;
  fill?: UpdateExcelChartFill;
  line?: UpdateExcelChartLine;
}

/**
 * Data-label config on a series write. Full surface per
 * `f2_docs/charting/chart-schema-preview.md::ChartDataLabelsInfo`.
 * `position` is `XlDataLabelPosition` enum string.
 */
export interface AddSeriesDataLabels {
  showValue?: boolean;
  showCategoryName?: boolean;
  showSeriesName?: boolean;
  position?: string;
  numberFormat?: string;
  showBubbleSize?: boolean;
  showPercentage?: boolean;
  showLegendKey?: boolean;
  separator?: string;
  font?: UpdateExcelChartFont;
  fill?: UpdateExcelChartFill;
  border?: UpdateExcelChartLine;
}

/**
 * Per-point override applied to a single data point inside the series.
 * Identified by 0-based `index`; only listed points are touched on
 * `updateSeriesInChart` writes. Per the preview's "data label
 * lifecycle" rule, `hasDataLabel` + `dataLabel` are this primitive's
 * exclusive surface — `updateExcelChart` does NOT touch them.
 */
export interface AddSeriesPointInfo {
  /** 0-based point index within the series. */
  index?: number;
  fill?: UpdateExcelChartFill;
  line?: UpdateExcelChartLine;
  /** Pie / doughnut slice explosion (per-slice override). */
  explosion?: number;
  /** Toggle this single point's data label on/off. */
  hasDataLabel?: boolean;
  /** Per-point data-label override (implicitly enables the label). */
  dataLabel?: AddSeriesDataLabels;
}

/**
 * Per-series payload used by `AddSeriesToChart` (and `UpdateSeriesInChart`
 * via `Partial<AddSeriesPayload>`). `values` is the only required field
 * on add — every other property defaults to Excel's chart-style
 * settings. Full surface mirrors
 * `f2_docs/charting/chart-schema-preview.md::ChartSeriesInfo` minus
 * `errorBars` / `trendlines` (deferred per the preview).
 */
export interface AddSeriesPayload {
  /** A1 range for the Y-values; sheet prefix preferred (e.g. `"Sheet1!$B$2:$B$10"`). */
  values: string;
  /** A1 range for the X-axis categories. */
  categories?: string;
  /** X-values range (scatter / bubble charts only). */
  xValues?: string;
  /** Bubble sizes range (bubble charts only). */
  bubbleSizes?: string;
  /** Series display name (legend label). May be a cell reference like `"Sheet1!$B$1"`. */
  name?: string;
  /** `XlChartType` enum string — set per-series for combo charts. */
  chartType?: string;
  /** `XlAxisGroup` enum string: `"xlPrimary"` or `"xlSecondary"`. */
  axisGroup?: string;
  /** Slot in the series collection. Omit to append.
   *  Wire today: 1-based (matches the captured FactSet payload and
   *  Excel COM's `Series.PlotOrder`). The preview spec
   *  (`chart-schema-preview.md`) globally indexes from 0; expect a
   *  future host migration to flip this to 0-based. Until then we
   *  send 1-based unchanged. */
  plotOrder?: number;
  /** Smooth the series line (line/scatter charts only). */
  smooth?: boolean;
  /** Pie / doughnut slice explosion distance in points. */
  explosion?: number;
  /** Invert series fill for negative values. */
  invertIfNegative?: boolean;
  /** `true` = enable data labels on every point; `false` = remove them all. */
  hasDataLabels?: boolean;
  /** Toggle leader lines on/off (pie charts with labels). */
  hasLeaderLines?: boolean;
  fill?: UpdateExcelChartFill;
  line?: UpdateExcelChartLine;
  marker?: AddSeriesMarker;
  /** Series-level data-label config (configuring this implicitly
   *  enables labels on every point). */
  dataLabels?: AddSeriesDataLabels;
  /** Leader-line styling (pie charts with labels). */
  leaderLines?: UpdateExcelChartLine;
  /** Per-point overrides — identified by 0-based `index`; on
   *  `updateSeriesInChart` only listed points are touched. */
  points?: AddSeriesPointInfo[];
}

/**
 * Per-call options for `Charting.AddSeriesToChart`. Top-level fields
 * are flat (NOT under `options`); `series` is the only required field.
 */
export interface AddSeriesToChartOptions {
  series: AddSeriesPayload;
  workbookName?: string;
  sheetRef?: UpdateExcelChartSheetRef;
  /** 0-based chart index on the target sheet. */
  chartIndex?: number;
}

/** Structured `result` payload returned on success. */
export interface AddSeriesToChartResult {
  success: boolean;
  /** 0-based index of the newly added series in the chart's series collection. */
  seriesIndex: number;
}

interface AddSeriesToChartResponse {
  result?: unknown;
}

const ADD_SERIES_TO_CHART_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Append a new data series to an existing chart via
 * `factset.Spreadsheet.Charting.AddSeriesToChart`.
 *
 * Returns the host-assigned `seriesIndex` so callers can thread it
 * into a follow-up `UpdateSeriesInChart` / `RemoveSeriesFromChart`
 * call. Rejects if the transport rejects, the host returns a non-`result`
 * envelope, or the inner payload is missing `seriesIndex`.
 */
export async function addSeriesToChartViaIntents(
  options: AddSeriesToChartOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<AddSeriesToChartResult> {
  if (!options.series || typeof options.series !== "object") {
    throw new Error("AddSeriesToChart: `series` is required");
  }
  if (
    typeof options.series.values !== "string" ||
    options.series.values.length === 0
  ) {
    throw new Error(
      "AddSeriesToChart: `series.values` is required (A1 range, e.g. 'Sheet1!$B$2:$B$10')"
    );
  }
  // Registry gate: per-series `chartType` is the combo-chart escape
  // hatch (mix bars + lines etc.). When the planner supplies one,
  // validate it the same way `AddExcelChart` validates the chart-wide
  // type — host-side rejection of a bad combo type is opaque, and
  // surfacing a `CHART_CAPABILITY_ERROR_PREFIX`-tagged message lets
  // the chat bubble render it as a normal capability error. Capture
  // the normalized value so the cloned wire payload uses the trimmed
  // id, not the caller's whitespace-padded original.
  const rawSeriesChartType =
    typeof options.series.chartType === "string"
      ? options.series.chartType.trim()
      : "";
  let normalizedSeriesChartType: string | undefined;
  if (rawSeriesChartType.length > 0) {
    normalizedSeriesChartType = assertChartTypeInRegistry(
      rawSeriesChartType,
      "native_excel",
      "AddSeriesToChart",
    );
  }
  // f2_docs/charting alignment: sanitize color fields before they hit the
  // wire. Deep clone mandatory — see
  // `deepCloneChartPayload` JSDoc (sanitiser mutates nested
  // `fill` / `line` / `marker` / `dataLabels` / `points[i]` objects).
  const safeSeries: Record<string, unknown> = deepCloneChartPayload(
    options.series as unknown as Record<string, unknown>
  );
  if (normalizedSeriesChartType !== undefined) {
    safeSeries.chartType = normalizedSeriesChartType;
  }
  sanitizeSeriesColorsInPlace(safeSeries, "series");
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.Charting.AddSeriesToChart",
    {
      type: "factset.spreadsheet.charting.addSeriesToChart",
      options: {
        ...(options.workbookName !== undefined
          ? { workbookName: options.workbookName }
          : {}),
        ...(options.sheetRef !== undefined ? { sheetRef: options.sheetRef } : {}),
        ...(options.chartIndex !== undefined
          ? { chartIndex: options.chartIndex }
          : {}),
        series: safeSeries,
      },
    },
    timeoutMs,
    ADD_SERIES_TO_CHART_APP_ID
  )) as AddSeriesToChartResponse | undefined;

  const raw = ctx?.result;
  if (raw === undefined || raw === null) {
    throw new Error("AddSeriesToChart: response did not include `result`");
  }
  let parsed: unknown;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      throw new Error(
        `AddSeriesToChart: failed to parse \`result\` as JSON: ${
          e instanceof Error ? e.message : String(e)
        }`
      );
    }
  } else {
    parsed = raw;
  }
  if (!parsed || typeof parsed !== "object") {
    throw new Error("AddSeriesToChart: parsed `result` was not an object");
  }
  const obj = parsed as Record<string, unknown>;
  if (typeof obj.seriesIndex !== "number") {
    throw new Error(
      "AddSeriesToChart: `result.seriesIndex` was missing or non-numeric"
    );
  }
  return {
    success: obj.success === true,
    seriesIndex: obj.seriesIndex,
  };
}

// ============================================================================
// factset.Spreadsheet.Charting.UpdateSeriesInChart
// ============================================================================
//
// Wire shape (request, captured 2026-05-06):
//
//   {
//     type: "factset.spreadsheet.charting.updateSeriesInChart",
//     workbookName?: string,
//     sheetRef?:    { index?: number, name?: string, codeName?: string },
//     chartIndex?:  number,
//     seriesIndex:  number,           // REQUIRED — 0-based slot of the
//                                     //            series to mutate
//     series: {                       // REQUIRED — partial series body;
//                                     //            every field optional
//       name?: string,
//       values?: string,
//       categories?: string,
//       chartType?: string,           // XlChartType enum string
//       axisGroup?: string,           // "xlPrimary" | "xlSecondary"
//       plotOrder?: number,
//       smooth?: boolean,
//       explosion?: number,
//       fill?: { isVisible?, colorRgb? },
//       line?: { isVisible?, colorRgb?, weight?, dashStyle? },
//       marker?: { style?, size?, fill?, line? },
//       dataLabels?: { showValue?, showCategoryName?, showSeriesName?,
//                      position?, numberFormat?, font?, fill?, border? }
//     }
//   }
//
// Wire shape (response, captured 2026-05-06):
//
//   {
//     result: {
//       success: boolean,
//       appliedProperties: string[]     // e.g. ["name", "line",
//                                       //       "marker.style", "marker.size"]
//     }
//   }
//
//   No `warnings[]` on this intent today (parity with `AddSeriesToChart`,
//   not `UpdateExcelChart`). Wrapper still tolerates a stringified
//   `result` defensively the same way the sibling wrappers do.
//
// Targeting (per `f2_docs/excel/chart-tools.md`):
//
//   - Same triple-or-none rule as `AddSeriesToChart`: provide all of
//     `workbookName` + `sheetRef` + `chartIndex`, or omit all three to
//     operate on the active chart. The bridge dispatcher in
//     `excelService.ts` always passes the full triple from the per-apply
//     `safeGetActiveExcelContext()` resolution to avoid the
//     "missing workbookName ⇒ host runtime-rejects" failure mode that
//     bit `AddExcelChart` / `UpdateExcelChart` (cost a debug session
//     2026-05-06 for those siblings).
//   - `seriesIndex` is the 0-based slot returned by `AddSeriesToChart`
//     or the matching `index` field on `Charting.GetCharts` series rows.

/** Reuse `AddSeriesPayload` for the partial body — every field is
 *  already optional on the planner side, and the wire envelope accepts
 *  the same JSON shape (host applies only the keys present). */
export type UpdateSeriesPayload = Partial<AddSeriesPayload>;

/**
 * Per-call options for `Charting.UpdateSeriesInChart`. `seriesIndex`
 * + `series` are the only required fields; targeting fields follow
 * the same triple-or-none rule as `AddSeriesToChart`.
 */
export interface UpdateSeriesInChartOptions {
  /** 0-based slot of the series to mutate. Required. */
  seriesIndex: number;
  /** Partial series body. Every field optional. */
  series: UpdateSeriesPayload;
  workbookName?: string;
  sheetRef?: UpdateExcelChartSheetRef;
  /** 0-based chart index on the target sheet. */
  chartIndex?: number;
}

/** Structured `result` payload returned on success. */
export interface UpdateSeriesInChartResult {
  success: boolean;
  /** Property paths the host actually applied, in input order. */
  appliedProperties: string[];
}

interface UpdateSeriesInChartResponse {
  result?: unknown;
}

const UPDATE_SERIES_IN_CHART_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Mutate properties on an existing series via
 * `factset.Spreadsheet.Charting.UpdateSeriesInChart`. Returns
 * `appliedProperties[]` so callers can confirm which fields actually
 * landed (the host silently no-ops unknown / unsupported keys, the
 * same way `UpdateExcelChart` does).
 *
 * Rejects if the transport rejects, the host returns a non-`result`
 * envelope, or the inner payload is missing `success`/`appliedProperties`.
 */
export async function updateSeriesInChartViaIntents(
  options: UpdateSeriesInChartOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<UpdateSeriesInChartResult> {
  if (typeof options.seriesIndex !== "number" || !Number.isFinite(options.seriesIndex)) {
    throw new Error("UpdateSeriesInChart: `seriesIndex` is required (0-based number)");
  }
  if (!options.series || typeof options.series !== "object") {
    throw new Error("UpdateSeriesInChart: `series` is required (partial series body)");
  }
  if (Object.keys(options.series).length === 0) {
    throw new Error(
      "UpdateSeriesInChart: `series` must contain at least one property to update"
    );
  }
  // Registry gate: this primitive lets callers retype a single series
  // (e.g. swap a bar series to a line in a combo chart). Validate the
  // proposed `chartType` against the native catalog when present, so
  // an `xlMadeUp` request is rejected client-side before the host has
  // to silently no-op it. Capture the normalized value so the cloned
  // payload sends the trimmed id, not the caller's whitespace-padded
  // original.
  const rawUpdatedSeriesChartType =
    typeof options.series.chartType === "string"
      ? options.series.chartType.trim()
      : "";
  let normalizedSeriesChartType: string | undefined;
  if (rawUpdatedSeriesChartType.length > 0) {
    normalizedSeriesChartType = assertChartTypeInRegistry(
      rawUpdatedSeriesChartType,
      "native_excel",
      "UpdateSeriesInChart",
    );
  }
  // f2_docs/charting alignment: sanitize color fields before they hit the wire.
  // Per the preview, data-label lifecycle
  // (`hasDataLabels`, `dataLabels`, `points[].hasDataLabel`,
  // `points[].dataLabel`) is exclusively this primitive's
  // responsibility — `updateExcelChart` does NOT touch them — so the
  // full per-point surface flows through here. Deep clone mandatory
  // — see `deepCloneChartPayload` JSDoc (sanitiser mutates nested
  // `fill` / `line` / `marker` / `dataLabels` / `points[i]` objects).
  const safeSeries: Record<string, unknown> = deepCloneChartPayload(
    options.series as unknown as Record<string, unknown>
  );
  if (normalizedSeriesChartType !== undefined) {
    safeSeries.chartType = normalizedSeriesChartType;
  }
  sanitizeSeriesColorsInPlace(safeSeries, "series");
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.Charting.UpdateSeriesInChart",
    {
      type: "factset.spreadsheet.charting.updateSeriesInChart",
      options: {
        ...(options.workbookName !== undefined
          ? { workbookName: options.workbookName }
          : {}),
        ...(options.sheetRef !== undefined ? { sheetRef: options.sheetRef } : {}),
        ...(options.chartIndex !== undefined
          ? { chartIndex: options.chartIndex }
          : {}),
        seriesIndex: options.seriesIndex,
        series: safeSeries,
      },
    },
    timeoutMs,
    UPDATE_SERIES_IN_CHART_APP_ID
  )) as UpdateSeriesInChartResponse | undefined;

  const raw = ctx?.result;
  if (raw === undefined || raw === null) {
    throw new Error("UpdateSeriesInChart: response did not include `result`");
  }
  let parsed: unknown;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      throw new Error(
        `UpdateSeriesInChart: failed to parse \`result\` as JSON: ${
          e instanceof Error ? e.message : String(e)
        }`
      );
    }
  } else {
    parsed = raw;
  }
  if (!parsed || typeof parsed !== "object") {
    throw new Error("UpdateSeriesInChart: parsed `result` was not an object");
  }
  const obj = parsed as Record<string, unknown>;
  if (obj.success === false) {
    throw new Error("UpdateSeriesInChart: host reported success=false");
  }
  if (obj.success !== true) {
    throw new Error("UpdateSeriesInChart: `result.success` was missing or non-true");
  }
  if (!Array.isArray(obj.appliedProperties)) {
    throw new Error(
      "UpdateSeriesInChart: `result.appliedProperties` was missing or non-array"
    );
  }
  const applied = obj.appliedProperties.filter(
    (p): p is string => typeof p === "string"
  );
  return {
    success: true,
    appliedProperties: applied,
  };
}

// ============================================================================
// factset.Spreadsheet.Charting.RemoveSeriesFromChart
// ============================================================================
//
// Wire shape (request, captured 2026-05-06):
//
//   {
//     type: "factset.spreadsheet.charting.removeSeriesFromChart",
//     workbookName?: string,
//     sheetRef?:    { index?: number, name?: string, codeName?: string },
//     chartIndex?:  number,
//     seriesIndex:  number              // REQUIRED — 0-based slot to remove
//   }
//
// Wire shape (response): not yet captured. Catalog claims a structured
//   `{ success: boolean }` envelope (sibling of `AddSeriesToChart`'s
//   `{ success, seriesIndex }`); the wrapper accepts any of:
//     - `{ result: { success: true } }` (catalog-spec shape)
//     - `{ result: "{...}" }`            (stringified, defensive)
//     - `{ result: <truthy non-object> }` (treat as success)
//   and only rejects on an absent `result` or an explicit
//   `success: false`.
//
// Same triple-or-none targeting rule as the other Charting series
// intents — `seriesIndex` is the 0-based slot returned by
// `AddSeriesToChart` or `Charting.GetCharts`'s series rows.

/** Per-call options for `Charting.RemoveSeriesFromChart`. */
export interface RemoveSeriesFromChartOptions {
  /** 0-based slot of the series to remove. Required. */
  seriesIndex: number;
  workbookName?: string;
  sheetRef?: UpdateExcelChartSheetRef;
  /** 0-based chart index on the target sheet. */
  chartIndex?: number;
}

/** Structured `result` payload returned on success. */
export interface RemoveSeriesFromChartResult {
  success: boolean;
}

interface RemoveSeriesFromChartResponse {
  result?: unknown;
}

const REMOVE_SERIES_FROM_CHART_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Remove a series from an existing chart via
 * `factset.Spreadsheet.Charting.RemoveSeriesFromChart`.
 *
 * Note that removing series shifts the 0-based indices of every
 * series after the removed slot — callers that issue multiple removes
 * back-to-back should either re-fetch indices via `Charting.GetCharts`
 * between calls or remove from highest-index to lowest so earlier
 * indices stay stable.
 */
export async function removeSeriesFromChartViaIntents(
  options: RemoveSeriesFromChartOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<RemoveSeriesFromChartResult> {
  if (typeof options.seriesIndex !== "number" || !Number.isFinite(options.seriesIndex)) {
    throw new Error("RemoveSeriesFromChart: `seriesIndex` is required (0-based number)");
  }
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.Charting.RemoveSeriesFromChart",
    {
      type: "factset.spreadsheet.charting.removeSeriesFromChart",
      options: {
        ...(options.workbookName !== undefined
          ? { workbookName: options.workbookName }
          : {}),
        ...(options.sheetRef !== undefined ? { sheetRef: options.sheetRef } : {}),
        ...(options.chartIndex !== undefined
          ? { chartIndex: options.chartIndex }
          : {}),
        seriesIndex: options.seriesIndex,
      },
    },
    timeoutMs,
    REMOVE_SERIES_FROM_CHART_APP_ID
  )) as RemoveSeriesFromChartResponse | undefined;

  const raw = ctx?.result;
  if (raw === undefined || raw === null) {
    throw new Error("RemoveSeriesFromChart: response did not include `result`");
  }
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      // Defensive: a non-JSON, non-empty string `result` from a future
      // host build counts as success rather than a hard reject — there
      // is nothing on the wire today saying the host MUST always send
      // a structured object back. An explicit `success: false` from a
      // structured payload is still rejected by the check below.
      return { success: true };
    }
  }
  if (!parsed || typeof parsed !== "object") {
    return { success: true };
  }
  const obj = parsed as Record<string, unknown>;
  if (obj.success === false) {
    throw new Error("RemoveSeriesFromChart: host reported success=false");
  }
  return { success: true };
}

// ============================================================================
// factset.Spreadsheet.Charting.GetChartImageAsBase64
// ============================================================================
//
// Render a native Excel chart as a base64-encoded PNG image. Mirrors
// the other chart-family writes (`AddExcelChart`, `UpdateExcelChart`,
// `AddSeriesToChart`, `UpdateSeriesInChart`, `RemoveSeriesFromChart`)
// in routing + catalog placement — `factset.Spreadsheet.Charting.*`,
// `OI Remote Iframe` relay — but pure read-side: produces the bytes
// for an inline preview, a "save this chart as image" affordance, or
// a vision-model verification loop (see preview doc reference below).
//
// **`format` is accepted but ignored (per `chart-tools.md`, updated
// 2026-05-13, f2_docs PR #56).** The host atlas always returns PNG
// regardless of what `format` the caller emits; the
// `oi-remote-iframe` Zod schema still accepts the field for backward
// compatibility (so already-shipped callers don't break) but the
// value has no effect on the output. The wrapper keeps the typed
// `ChartImageFormat` union, keeps the legacy `"Jpeg" → "Jpg"`
// translation, and keeps emitting whatever the caller passes —
// purely for backward compatibility with the wire schema — but
// **the response is always a PNG data URI**. Callers asking for
// `"Jpg"` / `"Bmp"` / `"Gif"` will receive PNG bytes; downstream
// "save as <fmt>" UI should hard-code `.png` going forward.
//
// **Preview reference.** `f2_docs/charting/chart-primitives-preview.md`
// documents the preview-spec shape (`GetChartImageAsBase64Result =
// { success: bool, dataUri: string, error?: string }`). Today the
// production catalog example (`chart-tools.md`) shows only the
// `dataUri` field on success — we accept either shape and surface
// a `success: false` payload as a thrown error so the contract is
// uniform across both host builds.
//
// Wire shape (request, per chart-tools.md updated 2026-05-13):
//
//   {
//     type: "factset.spreadsheet.charting.getChartImageAsBase64",
//     workbookName?: string,
//     sheetRef?: { index?: number, name?: string, codeName?: string },
//     chartIndex?: number,
//     format?: "Png" | "Jpg" | "Bmp" | "Gif"  // accepted but ignored
//                                              //   by atlas — output
//                                              //   is always PNG
//   }
//
//   Omitting both `sheetRef` and `chartIndex` targets the currently
//   active chart. Same convention as the other chart-family intents
//   (`UpdateExcelChart`, the series ops).
//
// Wire shape (response, per chart-tools.md / preview spec):
//
//   {
//     success?: boolean,                  // preview shape; absent on
//                                          //   older host builds — assume
//                                          //   true when `dataUri` is set
//     dataUri: "data:image/png;base64,iVBOR...",
//     error?: string                       // populated on success=false
//   }
//
//   The host returns a FULL data URI (including the `data:image/png;
//   base64,` prefix), not a raw base64 string. Wrapper returns the
//   complete data URI so it can be dropped into an `<img>` tag
//   verbatim. Callers that need the raw bytes should strip the prefix
//   after the comma themselves.

const GET_CHART_IMAGE_AS_BASE64_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Wire-side enum for `getChartImageAsBase64`. **CAPITALIZED** — the
 * `oi-remote-iframe` Zod schema rejects lowercase variants. Callers
 * should not pre-normalise to lowercase; let the wire form propagate.
 *
 * **Accepted but ignored by atlas (per chart-tools.md, 2026-05-13).**
 * The host always returns PNG regardless of the value. The union is
 * retained purely so already-shipped callers continue to pass schema
 * validation; new callers should omit `format` entirely.
 */
export type ChartImageFormat = "Png" | "Jpg" | "Bmp" | "Gif";

/**
 * Per-call options for `getChartImageAsBase64ViaIntents`. All fields
 * are optional; omitting `sheetRef` AND `chartIndex` targets the
 * currently active chart, mirroring the rest of the chart-family
 * intents. `format` defaults to `"Png"`.
 */
export interface GetChartImageAsBase64Options {
  /**
   * 0-based index of the chart inside its host sheet's chart
   * collection. Omit to target the active chart (paired with
   * `sheetRef` omission).
   */
  chartIndex?: number;
  /**
   * Sheet to read from. Either `name` (preferred), `index` (0-based),
   * or `codeName` (VBA). Omit to use the active sheet.
   */
  sheetRef?: AddExcelChartSheetRef;
  /** Workbook to read from. Omit to use the active workbook. */
  workbookName?: string;
  /**
   * Image format. **Accepted but ignored** by the host atlas (per
   * `chart-tools.md`, updated 2026-05-13 / f2_docs PR #56) — the
   * output is always PNG regardless of what the caller passes. The
   * field is kept on the wire so already-shipped callers don't trip
   * the `oi-remote-iframe` Zod schema; new callers should omit it.
   *
   * If supplied, use the capitalized enum values — lowercase variants
   * are rejected by the schema. The legacy `"Jpeg"` spelling is
   * auto-translated to `"Jpg"` at the boundary with a deprecation
   * warning.
   */
  format?: ChartImageFormat | "Jpeg";
}

interface GetChartImageAsBase64Response {
  type?: string;
  /**
   * Preview-spec success flag (`chart-primitives-preview.md`).
   * Absent on older host builds — treat as `true` when `dataUri`
   * is set, and surface as a thrown error otherwise.
   */
  success?: unknown;
  /** Primary payload field per chart-tools.md: full data URI. */
  dataUri?: unknown;
  /**
   * Legacy fallback fields — older catalog drafts described an
   * `imageBase64` field, and `AddExcelChart`-style hosts wrap the
   * body in `result`. Both accepted defensively in case we hit a
   * pre-2026-05-13 host build.
   */
  imageBase64?: unknown;
  result?: unknown;
  error?: unknown;
  message?: unknown;
  reason?: unknown;
}

/**
 * Capture an Excel chart as a base64 image via
 * `factset.Spreadsheet.Charting.GetChartImageAsBase64`.
 *
 * **Returns a PNG data URI** (`"data:image/png;base64,..."`) — the
 * host atlas always renders PNG regardless of any `format` value the
 * caller passes (per `chart-tools.md`, updated 2026-05-13 / f2_docs
 * PR #56). The full data URI can be dropped into an `<img src="…">`
 * tag verbatim; callers that need the raw bytes should strip
 * everything up to and including the first `,`.
 *
 * Defensive fallback: older host builds (pre-2026-05-13) returned a
 * bare base64 string under `imageBase64` or `result` (no data:
 * prefix). The wrapper detects either shape and synthesises a
 * `data:image/png;base64,…` URI so callers get a stable return
 * contract regardless of which host build is on the other end.
 *
 * Rejects if the transport rejects, the host replies with
 * `success: false` (preview-spec failure path) or any explicit error
 * field, or the response contains none of `dataUri` / `imageBase64` /
 * `result`.
 */
export async function getChartImageAsBase64ViaIntents(
  options: GetChartImageAsBase64Options,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<string> {
  if (
    options.chartIndex !== undefined &&
    (typeof options.chartIndex !== "number" || !Number.isFinite(options.chartIndex))
  ) {
    throw new Error(
      "GetChartImageAsBase64: `chartIndex` must be a 0-based number when provided"
    );
  }
  // `format` is kept on the wire for backward compatibility with the
  // oi-remote-iframe Zod schema, even though the atlas ignores it and
  // always returns PNG (chart-tools.md 2026-05-13). The legacy
  // `"Jpeg"` spelling is still translated to `"Jpg"` so any leftover
  // callers continue to pass schema validation.
  let format: ChartImageFormat | undefined;
  if (options.format !== undefined) {
    const raw = options.format;
    if (raw === "Jpeg") {
      console.warn(
        "[GetChartImageAsBase64] `format: \"Jpeg\"` is deprecated; chart-tools.md uses `\"Jpg\"`. Translating before send. (Note: atlas ignores `format` and always returns PNG.)"
      );
      format = "Jpg";
    } else if (raw === "Png" || raw === "Jpg" || raw === "Bmp" || raw === "Gif") {
      format = raw;
    } else {
      throw new Error(
        `GetChartImageAsBase64: \`format\` must be one of "Png" / "Jpg" / "Bmp" / "Gif" (capitalized), got ${JSON.stringify(raw)}`
      );
    }
  }
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.Charting.GetChartImageAsBase64",
    {
      type: "factset.spreadsheet.charting.getChartImageAsBase64",
      ...(options.workbookName !== undefined
        ? { workbookName: options.workbookName }
        : {}),
      ...(options.sheetRef !== undefined ? { sheetRef: options.sheetRef } : {}),
      ...(options.chartIndex !== undefined
        ? { chartIndex: options.chartIndex }
        : {}),
      ...(format !== undefined ? { format } : {}),
    },
    timeoutMs,
    GET_CHART_IMAGE_AS_BASE64_APP_ID
  )) as GetChartImageAsBase64Response | undefined;

  // Preview-spec failure path: `success: false` with an `error`
  // string. Surface as a thrown error before checking payload fields.
  if (ctx?.success === false) {
    throw new Error(
      `GetChartImageAsBase64: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }

  // Primary path: chart-tools.md returns a full data URI under
  // `dataUri`. Dropped straight into the result.
  if (typeof ctx?.dataUri === "string" && ctx.dataUri.length > 0) {
    return ctx.dataUri;
  }
  // Legacy fallback: bare base64 under `imageBase64` or `result` from
  // a pre-2026-05-13 host build (no `data:` prefix). Always synthesise
  // with `image/png` because the host's documented behavior is
  // PNG-only — synthesising `image/jpeg` from PNG bytes would be a lie.
  if (typeof ctx?.imageBase64 === "string" && ctx.imageBase64.length > 0) {
    return `data:image/png;base64,${ctx.imageBase64}`;
  }
  if (typeof ctx?.result === "string" && ctx.result.length > 0) {
    return `data:image/png;base64,${ctx.result}`;
  }
  throw new Error(
    `GetChartImageAsBase64: response did not include a \`dataUri\` (or legacy \`imageBase64\` / \`result\`)${formatHostFailureSuffix(ctx)}`
  );
}

// ============================================================================
// factset.Spreadsheet.FreezeUnfreezePanes
// ============================================================================
//
// Freeze or unfreeze panes on a worksheet — the workbook-level "lock
// the first N rows / first M columns so they stay visible while
// scrolling" affordance. Catalog row in
// [excel-tools.md](../../../../../../f2_docs/excel/excel-tools.md).
//
// Wire shape (captured 2026-05-06):
//
//   request : {
//     type: "factset.spreadsheet.freezeUnfreezePanes",
//     freezeMode: "Freeze" | "Unfreeze",
//     options?: {
//       row?: number,           // 0-based row index to freeze AT
//       column?: number,        // 0-based column index to freeze AT
//       worksheetName?: string, // defaults to active worksheet
//       workbookName?: string,  // defaults to active workbook
//     }
//   }
//   response: { message: "Panes frozen successfully" }
//
// **Sheet flicker.** When the target worksheet / workbook differs from
// the host's current active sheet / workbook, the host temporarily
// activates the target to set its freeze state, then restores the
// previously active one. Callers may see a brief UI flicker; this is
// per excel-tools.md and not a Finster-side bug.

/** Wire-side enum for `freezeUnfreezePanes`. */
export type FreezeMode = "Freeze" | "Unfreeze";

/**
 * Optional fields under `freezeUnfreezePanes.options`. Required-shape:
 * either `row`, `column`, or both must be set when `freezeMode ===
 * "Freeze"` (a zero of both would freeze nothing). `Unfreeze` mode
 * ignores `row` / `column` — the host clears any existing freeze
 * regardless.
 */
export interface FreezeUnfreezePanesOptions {
  row?: number;
  column?: number;
  worksheetName?: string;
  workbookName?: string;
}

interface FreezeUnfreezePanesResponse {
  type?: string;
  message?: unknown;
  success?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Freeze or unfreeze panes on a worksheet via
 * `factset.Spreadsheet.FreezeUnfreezePanes`.
 *
 * Returns the host's success message when present (typical:
 * `"Panes frozen successfully"` / `"Panes unfrozen successfully"`),
 * otherwise `""`. Rejects only on transport failure, timeout, or an
 * explicit `success === false` envelope from a future host build.
 *
 * Reachable via `WorksheetModification.freezePanes` from the planner;
 * see `excelService.ts::applyWorkbookModificationsViaIntents` for the
 * dispatcher.
 */
export async function freezeUnfreezePanesViaIntents(
  freezeMode: FreezeMode,
  options?: FreezeUnfreezePanesOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<string> {
  if (freezeMode !== "Freeze" && freezeMode !== "Unfreeze") {
    throw new Error(
      `FreezeUnfreezePanes: \`freezeMode\` must be "Freeze" or "Unfreeze", got ${JSON.stringify(freezeMode)}`
    );
  }
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.FreezeUnfreezePanes",
    {
      type: "factset.spreadsheet.freezeUnfreezePanes",
      freezeMode,
      ...(options !== undefined ? { options } : {}),
    },
    timeoutMs
  )) as FreezeUnfreezePanesResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `FreezeUnfreezePanes: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
  return typeof ctx?.message === "string" ? ctx.message : "";
}

// ============================================================================
// factset.Spreadsheet.DeleteShape
// ============================================================================
//
// Delete a single shape (chart, ActiveGraph OLE embed, image, or
// other drawing object) from the active worksheet. Closes the
// long-standing `Chart action "delete" not supported by intent
// bridge yet; skipping` warn-skip in `excelService.ts` —
// `ChartModification.action = "delete"` now dispatches here.
//
// Wire shape (captured 2026-05-12):
//
//   request : {
//     type: "factset.spreadsheet.deleteShape",
//     shape: { id: number }
//   }
//
// **No sheetName in capture.** The captured payload does not include
// a sheet identifier, so the wrapper treats this as **active-sheet
// only** for now and asks FactSet to confirm cross-sheet support
// (see `f2_docs/excel/finster-docs/missing-intents-v0.md` §2.2). When
// cross-sheet lands, extend `DeleteShapeRef` with `sheetName?` and
// thread it through.

/**
 * Shape reference for `deleteShapeViaIntents`. `id` is the host-
 * assigned numeric identifier returned by chart-family writes
 * (`AddExcelChart` returns `result.id`; the ActiveGraph path
 * surfaces ids via the OLE inspector).
 *
 * `sheetName` is OPTIONAL today — the host's captured payload doesn't
 * include it, so passing one is a best-effort hint for a future host
 * version with cross-sheet support. The wrapper sends it only when
 * set so today's host build doesn't reject the call as malformed.
 */
export interface DeleteShapeRef {
  id: number;
  /** TODO: confirm cross-sheet support with FactSet (see §2.2 of
   *  `missing-intents-v0.md`). Today's captured payload doesn't carry
   *  this field. */
  sheetName?: string;
}

interface DeleteShapeResponse {
  type?: string;
  success?: unknown;
  message?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Delete a shape from the active worksheet via
 * `factset.Spreadsheet.DeleteShape`.
 *
 * Resolves on host success; rejects if the transport rejects, the
 * host returns an explicit `success === false`, or the timeout
 * elapses. Reachable via `ShapeModification.action = "delete"`.
 */
export async function deleteShapeViaIntents(
  shape: DeleteShapeRef,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  if (typeof shape.id !== "number" || !Number.isFinite(shape.id)) {
    throw new Error("DeleteShape: `shape.id` is required (numeric)");
  }
  const shapePayload: Record<string, unknown> = { id: shape.id };
  if (shape.sheetName !== undefined) {
    shapePayload.sheetName = shape.sheetName;
  }
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.DeleteShape",
    {
      type: "factset.spreadsheet.deleteShape",
      shape: shapePayload,
    },
    timeoutMs
  )) as DeleteShapeResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `DeleteShape: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.ModifyShapes
// ============================================================================
//
// Move and/or resize one or more shapes on the worksheet. Pairs with
// `DeleteShape` to close the chart-write surface gap in §4.3 of
// `missing-intents-v0.md` (the `Chart action "update" not supported`
// warn-skip). The host applies each entry in order and bulk-batches
// the redraw.
//
// Wire shape (captured 2026-05-12):
//
//   request : {
//     type: "factset.spreadsheet.modifyShapes",
//     shapeModifications: [
//       {
//         shape: { id: number, sheetName?: string },
//         top?: number,      // points from sheet top
//         left?: number,     // points from sheet left
//         height?: number,   // points
//         width?: number     // points
//       },
//       ...
//     ]
//   }
//
// All four numeric fields are optional independently — pass only the
// ones you want to change. Units are Excel points (1pt = 1/72 inch),
// matching `AddExcelChart`'s `position` convention. The cell→points
// translation happens upstream (see `excelHostOps::modifyShapes`).

/**
 * One shape modification entry in a `modifyShapes` batch. `shape.id`
 * is required; every other field is optional and only the ones you
 * set get applied (e.g. pass `top` + `left` to move without
 * resizing).
 */
export interface ShapeModificationEntry {
  shape: { id: number; sheetName?: string };
  /** Vertical offset in points from the sheet's top edge. */
  top?: number;
  /** Horizontal offset in points from the sheet's left edge. */
  left?: number;
  /** Height in points. */
  height?: number;
  /** Width in points. */
  width?: number;
}

interface ModifyShapesResponse {
  type?: string;
  success?: unknown;
  message?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Move and/or resize one or more shapes via
 * `factset.Spreadsheet.ModifyShapes`.
 *
 * Resolves on host success; rejects if the transport rejects, the
 * host returns an explicit `success === false`, or the timeout
 * elapses. Reachable via `ShapeModification.action = "modify"`.
 */
export async function modifyShapesViaIntents(
  shapeModifications: ShapeModificationEntry[],
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  if (!Array.isArray(shapeModifications) || shapeModifications.length === 0) {
    throw new Error(
      "ModifyShapes: `shapeModifications[]` must have at least one entry"
    );
  }
  for (const entry of shapeModifications) {
    if (
      !entry ||
      typeof entry !== "object" ||
      typeof entry.shape?.id !== "number" ||
      !Number.isFinite(entry.shape.id)
    ) {
      throw new Error(
        "ModifyShapes: every entry must include a numeric `shape.id`"
      );
    }
  }
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.ModifyShapes",
    {
      type: "factset.spreadsheet.modifyShapes",
      shapeModifications,
    },
    timeoutMs
  )) as ModifyShapesResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `ModifyShapes: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.SelectShapes
// ============================================================================
//
// Programmatically set the host's active shape selection. Pure
// cursor / UX action — does not mutate the workbook, only updates
// "which shapes are currently selected". Useful as a follow-up after
// `ModifyShapes` so the user lands on the shapes the assistant just
// touched, and as a standalone "show me chart 2" verb from chat.
//
// Wire shape (captured 2026-05-12):
//
//   request : {
//     type: "factset.spreadsheet.selectShapes",
//     shapeIds: number[],
//     sheetName?: string,
//     workbookName?: string
//   }
//   response: { success: boolean, error?: string }
//   // e.g. failure: { success: false,
//   //                 error: "Shape with ID 2 was not found" }
//
// The host's `error` string is propagated verbatim through
// `formatHostFailureSuffix` when `success === false`.

/**
 * Per-call options for `selectShapesViaIntents`. `shapeIds[]` is the
 * only required field; sheet / workbook targeting is optional and
 * defaults to the active surfaces.
 */
export interface SelectShapesOptions {
  shapeIds: number[];
  sheetName?: string;
  workbookName?: string;
}

interface SelectShapesResponse {
  type?: string;
  success?: unknown;
  error?: unknown;
  message?: unknown;
  reason?: unknown;
}

/**
 * Select one or more shapes via `factset.Spreadsheet.SelectShapes`.
 *
 * Resolves on host success; rejects if the transport rejects, the
 * host returns `success === false` (typical: missing-id case
 * `"Shape with ID 2 was not found"`), or the timeout elapses.
 * Reachable via `ShapeModification.action = "select"`.
 */
export async function selectShapesViaIntents(
  options: SelectShapesOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  if (!Array.isArray(options.shapeIds) || options.shapeIds.length === 0) {
    throw new Error("SelectShapes: `shapeIds[]` must have at least one id");
  }
  for (const id of options.shapeIds) {
    if (typeof id !== "number" || !Number.isFinite(id)) {
      throw new Error(
        `SelectShapes: every \`shapeIds\` entry must be a finite number, got ${JSON.stringify(id)}`
      );
    }
  }
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.SelectShapes",
    {
      type: "factset.spreadsheet.selectShapes",
      shapeIds: options.shapeIds,
      ...(options.sheetName !== undefined ? { sheetName: options.sheetName } : {}),
      ...(options.workbookName !== undefined
        ? { workbookName: options.workbookName }
        : {}),
    },
    timeoutMs
  )) as SelectShapesResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `SelectShapes: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.HideColumns
// ============================================================================

interface HideColumnsResponse {
  type?: string;
  success?: unknown;
  message?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Hide one or more columns via `factset.Spreadsheet.HideColumns`.
 *
 * The `address.address` payload must be a **column-shaped** A1 string
 * (e.g. `"A:A"`, `"B:D"`, `"$A:$C"`). Cell-shaped (`"A1:C1"`) and
 * row-shaped (`"5:10"`) addresses are rejected by the host — callers
 * should validate at the use site before dispatching here.
 *
 * Response shape isn't yet captured in `intent-details.md`; the wrapper
 * accepts both `{ success: ... }` and `{ message: ... }` defensively.
 */
export async function hideColumnsViaIntents(
  address: RangeAddress,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.HideColumns",
    {
      type: "factset.spreadsheet.hideColumns",
      address,
    },
    timeoutMs
  )) as HideColumnsResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `HideColumns: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.HideRows
// ============================================================================

interface HideRowsResponse {
  type?: string;
  success?: unknown;
  message?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Hide one or more rows via `factset.Spreadsheet.HideRows`.
 *
 * The `address.address` payload must be a **row-shaped** A1 string
 * (e.g. `"5:5"`, `"$5:$10"`). Cell- and column-shaped addresses are
 * rejected by the host. The captured sample uses absolute (`$`-anchored)
 * notation; the unanchored form (`"5:10"`) is plausible but not yet
 * captured.
 */
export async function hideRowsViaIntents(
  address: RangeAddress,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.HideRows",
    {
      type: "factset.spreadsheet.hideRows",
      address,
    },
    timeoutMs
  )) as HideRowsResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `HideRows: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.ShowColumns
// ============================================================================

interface ShowColumnsResponse {
  type?: string;
  success?: unknown;
  message?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Show (unhide) one or more columns via `factset.Spreadsheet.ShowColumns`.
 *
 * Address-shape contract mirrors `hideColumnsViaIntents`: the
 * `address.address` payload must be column-shaped (`"A:A"`, `"B:D"`,
 * `"$A:$C"`). Cell- or row-shaped addresses are rejected by the host.
 */
export async function showColumnsViaIntents(
  address: RangeAddress,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.ShowColumns",
    {
      type: "factset.spreadsheet.showColumns",
      address,
    },
    timeoutMs
  )) as ShowColumnsResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `ShowColumns: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.ShowRows
// ============================================================================

interface ShowRowsResponse {
  type?: string;
  success?: unknown;
  message?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Show (unhide) one or more rows via `factset.Spreadsheet.ShowRows`.
 *
 * Address-shape contract mirrors `hideRowsViaIntents`: the
 * `address.address` payload must be row-shaped (`"5:5"`, `"5:10"`,
 * `"$5:$10"`). Cell- or column-shaped addresses are rejected by the host.
 */
export async function showRowsViaIntents(
  address: RangeAddress,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.ShowRows",
    {
      type: "factset.spreadsheet.showRows",
      address,
    },
    timeoutMs
  )) as ShowRowsResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `ShowRows: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.GroupColumns / GroupRows / UngroupColumns / UngroupRows
// ============================================================================
//
// Outline-grouping primitives — pair groups of adjacent columns or rows
// under an Excel outline level so the user can collapse / expand them
// from the row/column header gutter. Mirror the wire shape of the
// `Hide*` / `Show*` sibling intents: a single `RangeAddress` payload,
// no per-call options. Outline-level support (nested groupings) is
// host-side and not currently exposed by Finster's wrapper; track
// extending that as a follow-up if the planner ever needs it (see
// `#open-outline-levels` in `missing-intents-v0.md` §6).
//
// Wire shapes (captured 2026-05-12):
//
//   request : { type: "factset.spreadsheet.{group|ungroup}{Columns|Rows}",
//               address: <RangeAddress> }
//   response: { success: true }   // (captured) for ungroup*; group*
//                                  response shape not yet captured —
//                                  the wrapper accepts the same shape
//                                  defensively.
//
// **Address-shape discipline.** `address.address` must be
// **column-shaped** (`"A:C"`, `"$A:$C"`) for `groupColumns` /
// `ungroupColumns`, or **row-shaped** (`"5:10"`, `"$5:$10"`) for
// `groupRows` / `ungroupRows`. Cell-shaped (`"A1:C1"`) and the
// orthogonal shape are rejected by the host. Callers should validate
// at the use site via `isColumnShapedAddress` / `isRowShapedAddress`
// in `excelService.ts` before dispatching here — the wrappers
// themselves don't try to second-guess the address, mirroring
// `hideColumns` / `hideRows`.

interface GroupAddressResponse {
  type?: string;
  success?: unknown;
  message?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Group one or more adjacent columns into an outline level via
 * `factset.Spreadsheet.GroupColumns`.
 *
 * Address must be column-shaped (`"A:C"`, `"$A:$C"`); cell- or
 * row-shaped inputs are rejected by the host. Reachable via
 * `RangeModification.action = "groupColumns"`.
 */
export async function groupColumnsViaIntents(
  address: RangeAddress,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GroupColumns",
    {
      type: "factset.spreadsheet.groupColumns",
      address,
    },
    timeoutMs
  )) as GroupAddressResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `GroupColumns: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

/**
 * Group one or more adjacent rows into an outline level via
 * `factset.Spreadsheet.GroupRows`.
 *
 * Address must be row-shaped (`"5:10"`, `"$5:$10"`); cell- or
 * column-shaped inputs are rejected by the host. Reachable via
 * `RangeModification.action = "groupRows"`.
 */
export async function groupRowsViaIntents(
  address: RangeAddress,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GroupRows",
    {
      type: "factset.spreadsheet.groupRows",
      address,
    },
    timeoutMs
  )) as GroupAddressResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `GroupRows: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

/**
 * Remove an outline grouping on one or more adjacent columns via
 * `factset.Spreadsheet.UngroupColumns`.
 *
 * Address must be column-shaped (`"A:C"`, `"$A:$C"`). Reachable via
 * `RangeModification.action = "ungroupColumns"`. Host response is
 * `{ success: true }` on the happy path.
 */
export async function ungroupColumnsViaIntents(
  address: RangeAddress,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.UngroupColumns",
    {
      type: "factset.spreadsheet.ungroupColumns",
      address,
    },
    timeoutMs
  )) as GroupAddressResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `UngroupColumns: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

/**
 * Remove an outline grouping on one or more adjacent rows via
 * `factset.Spreadsheet.UngroupRows`.
 *
 * Address must be row-shaped (`"5:10"`, `"$5:$10"`). Reachable via
 * `RangeModification.action = "ungroupRows"`. Host response is
 * `{ success: true }` on the happy path.
 */
export async function ungroupRowsViaIntents(
  address: RangeAddress,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.UngroupRows",
    {
      type: "factset.spreadsheet.ungroupRows",
      address,
    },
    timeoutMs
  )) as GroupAddressResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `UngroupRows: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.SelectRange
// ============================================================================
//
// Routing oddity (read carefully — different from every other
// `factset.Spreadsheet.*` wrapper above):
//
//   This intent routes to `appId: "OI Remote Iframe"`, NOT the default
//   `com.factset.workstation` target. The wire capture in
//   `excel-docs/intent-details.md` shows the host happily replying with
//   `factset.spreadsheet.selectRangeResult` only when the relay appId
//   is set; sending it to `com.factset.workstation` returns the usual
//   `Unknown intent: factset.Spreadsheet.SelectRange` error envelope.
//   We pass `SELECT_RANGE_APP_ID` as the `raiseIntent` override so the
//   message lands on the right relay, the same way `previewChart` and
//   the download wrappers do for the sibling `factset.*` namespace.
//
// Wire shape (captured payload pair):
//
//   request : { type: "factset.spreadsheet.selectRange",
//               range: <RangeAddress> }
//   response: { type: "factset.spreadsheet.selectRangeResult",
//               message: "Range selected successfully" }
//
// `range` is a full `RangeAddress` record — sheet targeting via
// `worksheetName` is supported, so callers can select cells on a sheet
// other than the active one without an extra `ActivateWorksheet` round
// trip. The host activates the target sheet implicitly as part of the
// select.

const SELECT_RANGE_APP_ID = { appId: "OI Remote Iframe" };

interface SelectRangeResponse {
  type?: string;
  message?: unknown;
  success?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Programmatically set the host's active selection to `range` via
 * `factset.Spreadsheet.SelectRange`.
 *
 * Pure cursor / UX action — does not write any data, only moves the
 * spreadsheet's "what's currently highlighted" state. Useful as a
 * follow-up after a fill or format so the user lands on the cells
 * the assistant just touched, and as a standalone "show me A1:C10"
 * verb from chat.
 *
 * Returns the host's `message` string when present (the captured
 * sample is `"Range selected successfully"`); resolves to `""`
 * otherwise. Rejects only if the transport itself fails (timeout,
 * host-level error envelope, or an explicit `success === false`
 * reply — none of which appear in the captured happy path).
 */
export async function selectRangeViaIntents(
  range: RangeAddress,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<string> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.SelectRange",
    {
      type: "factset.spreadsheet.selectRange",
      range,
    },
    timeoutMs,
    SELECT_RANGE_APP_ID
  )) as SelectRangeResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `SelectRange: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
  return typeof ctx?.message === "string" ? ctx.message : "";
}

// ============================================================================
// factset.Spreadsheet.GetRangeSnapshot
// ============================================================================
//
// Render a range as a base64-encoded PNG thumbnail. Read-side
// counterpart to a screenshot — the host renders the cells as they
// appear in the workbook and returns the PNG bytes for the caller to
// decode (chat-bubble previews, "what does this range look like"
// affordances, snapshot-on-export flows).
//
// Catalog row landed 2026-05-11 in `f2_docs/excel/excel-tools.md`. The
// wire shape was first wired Finster-side off a user-supplied capture
// and now matches the officially-documented contract verbatim.
//
// Wire shape (per excel-tools.md, captured 2026-05-12):
//
//   request : {
//     type: "factset.spreadsheet.getRangeSnapshot",
//     address: <RangeReferenceAddress>     // single area; multi-area errors
//   }
//   response: { snapshot: "<base64 PNG bytes>" }
//
// Routing: `appId: "OI Remote Iframe"`, mandatory — same constraint
// as the sibling `SelectRange` wrapper above. The catalog example in
// `f2_docs/excel/excel-tools.md` shows this explicitly, and omitting
// the override causes `com.factset.workstation` to reply with the
// usual `Unknown intent: factset.Spreadsheet.GetRangeSnapshot` error
// envelope (we previously had a stale "default transport route picks
// the right relay so we don't pin it here" note here — that was
// wishful thinking and was crashing the wrapper at runtime).
//
// Returns the raw base64 string (no `data:image/png;base64,` prefix —
// callers that need a data URI should prepend it).
//
// **Clipboard caveat (per excel-tools.md).** Uses Excel's native
// `Range.CopyPicture` under the hood — invoking this clears any
// existing dotted-outline "copied range" indicator and may interrupt
// paste-special affordances the user was mid-flight with. Treat as a
// best-effort UI tool, not something to call on a hot path.

const GET_RANGE_SNAPSHOT_APP_ID = { appId: "OI Remote Iframe" };

interface GetRangeSnapshotResponse {
  type?: string;
  snapshot?: unknown;
  error?: unknown;
  message?: unknown;
  reason?: unknown;
}

/**
 * Capture a range as a base64 PNG via
 * `factset.Spreadsheet.GetRangeSnapshot`.
 *
 * **Read-side / out-of-band tool — NOT planner-exposed.** Callers
 * are UI flows (chat-bubble range previews, the "save this range as
 * image" affordance) rather than the LLM planner. The dispatcher in
 * `excelHostOps.ts` is bridge-only (no Office.js fallback exists for
 * "render this range as a PNG").
 *
 * Routed through `appId: "OI Remote Iframe"` (same relay as
 * `selectRangeViaIntents` — see the catalog header above for why).
 *
 * Returns the raw base64 string. Rejects if the transport rejects,
 * the host replies with an error, the timeout elapses, or the
 * response is missing the `snapshot` field.
 */
export async function getRangeSnapshotViaIntents(
  address: RangeAddress,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<string> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetRangeSnapshot",
    {
      type: "factset.spreadsheet.getRangeSnapshot",
      address,
    },
    timeoutMs,
    GET_RANGE_SNAPSHOT_APP_ID
  )) as GetRangeSnapshotResponse | undefined;
  if (typeof ctx?.snapshot !== "string" || ctx.snapshot.length === 0) {
    throw new Error(
      `GetRangeSnapshot: response did not include a string \`snapshot\`${formatHostFailureSuffix(ctx)}`
    );
  }
  return ctx.snapshot;
}

// ============================================================================
// factset.Spreadsheet.GetWorksheetsAsBase64
// ============================================================================
//
// Export one or more worksheets — or the entire workbook — as a
// base64-encoded `.xlsx` blob. Read-side counterpart to
// `factset.Spreadsheet.InsertWorksheetsFromBase64`: pairs naturally
// for round-trip flows (export → modify externally → re-insert) and
// powers the "download these sheets as xlsx" UI affordance.
//
// **Cataloged 2026-05-12** in `f2_docs/excel/excel-tools.md`
// (⚗️ Experimental). The wrapper was wired Finster-side pre-doc off
// a user-supplied capture; the now-published catalog row matches
// the captured wire shape verbatim, so this is a doc-side resolution
// only. The catalog also confirms `fdc3.nothing` is accepted as an
// alternate context type when no options are needed (full-workbook
// export); the wrapper currently always emits the explicit
// `factset.spreadsheet.getWorksheetsAsBase64` shape with an `options`
// object — the host accepts both, so the wrapper's invariant of
// "always send `options: {}`" stays.
//
// **CAUTION — PascalCase option keys.** The host's `options.*` keys
// for this intent diverge from the lowerCamelCase convention every
// other catalog intent uses (`WorkbookName` / `SheetNames` instead of
// `workbookName` / `sheetNames`). The new catalog row formalises
// (rather than fixes) the divergence; the wrapper accepts a
// lowerCamelCase TypeScript interface and translates to the wire's
// PascalCase shape internally so callers stay consistent with the
// rest of the API. Tracked in `missing-intents-v0.md` §2.2 as a
// remaining FactSet ask to align with the lowerCamelCase convention.
//
// Wire shape (per excel-tools.md, captured 2026-05-12):
//
//   request : {
//     type: "factset.spreadsheet.getWorksheetsAsBase64"
//             | "fdc3.nothing",
//     options?: {                  // omitted when type is "fdc3.nothing"
//       WorkbookName?: string,    // PascalCase — see CAUTION above
//       SheetNames?: string[]     // PascalCase — see CAUTION above
//     }
//   }
//   response: { result: "<base64 .xlsx bytes>" }
//
// Routing: `appId: "OI Remote Iframe"` per the catalog row. The
// wrapper passes `GET_WORKSHEETS_AS_BASE64_APP_ID` so the message
// lands on the right relay rather than the default
// `com.factset.workstation` (same constraint as `SelectRange` /
// `GetRangeSnapshot`).

const GET_WORKSHEETS_AS_BASE64_APP_ID = { appId: "OI Remote Iframe" };

/**
 * Per-call options for `getWorksheetsAsBase64ViaIntents`. **Both
 * fields are optional** — pass nothing to export the entire active
 * workbook; pass `sheetNames` to export only the listed sheets;
 * pass `workbookName` to target a non-active open workbook.
 *
 * Callers send lowerCamelCase here; the wrapper translates to the
 * host's PascalCase wire keys (`WorkbookName` / `SheetNames`)
 * internally.
 */
export interface GetWorksheetsAsBase64Options {
  workbookName?: string;
  sheetNames?: string[];
}

interface GetWorksheetsAsBase64Response {
  type?: string;
  result?: unknown;
  error?: unknown;
  message?: unknown;
  reason?: unknown;
}

/**
 * Export one or more worksheets as a base64 `.xlsx` via
 * `factset.Spreadsheet.GetWorksheetsAsBase64`.
 *
 * **Read-side / out-of-band tool — NOT planner-exposed.** Callers
 * are UI flows ("download these sheets as xlsx") rather than the
 * LLM planner. The dispatcher in `excelHostOps.ts` is bridge-only
 * (no Office.js fallback exists for "export as base64 xlsx bytes").
 *
 * Routed through `appId: "OI Remote Iframe"` per the catalog row
 * (same constraint as `selectRangeViaIntents` and
 * `getRangeSnapshotViaIntents`).
 *
 * Returns the raw base64 string. Rejects if the transport rejects,
 * the host replies with an error, the timeout elapses, or the
 * response is missing the `result` field.
 */
export async function getWorksheetsAsBase64ViaIntents(
  options: GetWorksheetsAsBase64Options = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<string> {
  // Translate lowerCamelCase → wire PascalCase. Build an empty
  // object when neither field is set so the request still carries
  // `options: {}` (matches the captured payload's invariant — host
  // does not accept `options` being absent).
  const wireOptions: Record<string, unknown> = {};
  if (options.workbookName !== undefined) {
    wireOptions["WorkbookName"] = options.workbookName;
  }
  if (options.sheetNames !== undefined) {
    if (!Array.isArray(options.sheetNames)) {
      throw new Error("GetWorksheetsAsBase64: `sheetNames` must be a string[]");
    }
    wireOptions["SheetNames"] = options.sheetNames;
  }
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetWorksheetsAsBase64",
    {
      type: "factset.spreadsheet.getWorksheetsAsBase64",
      options: wireOptions,
    },
    timeoutMs,
    GET_WORKSHEETS_AS_BASE64_APP_ID
  )) as GetWorksheetsAsBase64Response | undefined;
  if (typeof ctx?.result !== "string" || ctx.result.length === 0) {
    throw new Error(
      `GetWorksheetsAsBase64: response did not include a string \`result\`${formatHostFailureSuffix(ctx)}`
    );
  }
  return ctx.result;
}

// ============================================================================
// factset.Spreadsheet.GetOpenWorkbooks
// ============================================================================

// `OpenWorkbookInfo` is re-exported from `excel/types` at the top of
// this file. Wire-shape evidence: `excel-docs/intent-details.md` lines
// 588-600 (one captured workbook). Fields are typed permissively at
// the canonical site because the wire payload is example-level rather
// than a contracted schema.

interface GetOpenWorkbooksResponse {
  type?: string;
  workbooks?: unknown;
}

/**
 * List all workbooks currently open in the host via
 * `factset.Spreadsheet.GetOpenWorkbooks`.
 *
 * Returns the raw `workbooks` array filtered to plain objects. Rejects
 * if the host replies with an error, the timeout elapses, or the
 * response is missing `workbooks`. Callers that need stricter typing
 * should narrow individual entries against `OpenWorkbookInfo`.
 */
export async function getOpenWorkbooksViaIntents(
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<OpenWorkbookInfo[]> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetOpenWorkbooks",
    { type: "fdc3.nothing" },
    timeoutMs
  )) as GetOpenWorkbooksResponse | OpenWorkbookInfo[] | undefined;
  const raw = Array.isArray(ctx) ? ctx : ctx?.workbooks;
  if (raw === undefined) {
    throw new Error("GetOpenWorkbooks: response did not include `workbooks`");
  }
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (x): x is OpenWorkbookInfo => x !== null && typeof x === "object"
  );
}

// ============================================================================
// factset.Spreadsheet.GetWorkbookConnections
// ============================================================================

// `WorkbookConnection` is re-exported from `excel/types` at the top of
// this file. Wire-shape evidence: `excel-docs/intent-details.md` lines
// 668-674 shows `connections: []` (empty workbook), so the per-element
// shape is opaque (`Record<string, unknown>`) at the canonical site
// until a non-empty payload is captured. Adapter-level narrowing
// should treat unknown fields as no-ops.

interface GetWorkbookConnectionsResponse {
  type?: string;
  connections?: unknown;
}

/**
 * List external data connections in the workbook via
 * `factset.Spreadsheet.GetWorkbookConnections`.
 *
 * **`workbookName` is required (AI-2617).** `excel-tools.md` line 1017
 * documents it as `Required: yes`, and the catalog's `fdc3.nothing`
 * routing table (`excel-tools.md` line 2922) does NOT include
 * `GetWorkbookConnections` — the host's Zod schema rejects an omitted
 * workbook name with `{ code: "invalid_type", path: ["workbookName"] }`.
 * The wrapper pre-validates and throws a clear error before hitting
 * the wire. Callers must thread the active workbook name from
 * `safeGetActiveExcelContext()` / `WorkbookContent.workbookName` and
 * skip the call when no workbook is active.
 *
 * Rejects if the host replies with an error, the timeout elapses, or
 * the response is missing `connections`.
 */
export async function getWorkbookConnectionsViaIntents(
  workbookName: string,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<WorkbookConnection[]> {
  const trimmed = workbookName?.trim();
  if (!trimmed) {
    throw new Error(
      "GetWorkbookConnections: `workbookName` is required (per excel-tools.md). " +
        "Pass the active workbook name (e.g. from `safeGetActiveExcelContext()`) " +
        "or skip the call when no workbook is active."
    );
  }
  const requestContext: Record<string, unknown> = {
    type: "factset.spreadsheet.getWorkbookConnections",
    workbookName: trimmed,
  };
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetWorkbookConnections",
    requestContext,
    timeoutMs
  )) as GetWorkbookConnectionsResponse | undefined;
  const raw = ctx?.connections;
  if (raw === undefined) {
    throw new Error(
      "GetWorkbookConnections: response did not include `connections`"
    );
  }
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (x): x is WorkbookConnection => x !== null && typeof x === "object"
  );
}

// ============================================================================
// factset.Spreadsheet.GetWorkbookNamedObjects
// ============================================================================

/**
 * Per-call options for `GetWorkbookNamedObjects`. Shape derived from
 * the captured request in `excel-docs/intent-details.md` lines 686-697.
 * `skipHidden: true` filters internal/hidden named ranges (e.g. the
 * ones FactSet's infrastructure pins); `options.properties` lets the
 * caller pull extra fields like `isReferred`.
 *
 * **`workbookName` is required (AI-2617).** `f2_docs/excel/excel-tools.md`
 * documents it as `Required: yes`, and the production `oi-remote-iframe`
 * Zod schema rejects `undefined` with
 * `{ code: "invalid_type", path: ["workbookName"] }`. The wrapper
 * pre-validates and throws a clear error before hitting the wire.
 * Callers should thread the active workbook name from
 * `safeGetActiveExcelContext()` / `WorkbookContent.workbookName` and
 * skip the call entirely when no workbook is active.
 */
export interface GetWorkbookNamedObjectsOptions {
  workbookName: string;
  skipHidden?: boolean;
  properties?: string[];
}

// `WorkbookNamedObject` is re-exported from `excel/types` at the top of
// this file. The captured example does NOT include a response
// (`(not captured)` in `excel-docs/intent-details.md`), so the
// per-element shape is permissive (`Record<string, unknown>`) at the
// canonical site. Adapter-level narrowing should treat unknown fields
// as no-ops.

interface GetWorkbookNamedObjectsResponse {
  type?: string;
  namedObjects?: unknown;
  data?: unknown;
  [key: string]: unknown;
}

function asWorkbookNamedObjectArray(value: unknown): WorkbookNamedObject[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out: WorkbookNamedObject[] = [];
  for (const item of value) {
    if (item !== null && typeof item === "object") {
      out.push(item as WorkbookNamedObject);
      continue;
    }
    if (typeof item === "string" && item.trim().length > 0) {
      out.push({ name: item.trim() });
    }
  }
  return out;
}

function extractWorkbookNamedObjects(ctx: unknown): WorkbookNamedObject[] {
  if (!ctx || typeof ctx !== "object") return [];
  const c = ctx as Record<string, unknown>;

  const direct = asWorkbookNamedObjectArray(c.namedObjects);
  if (direct !== undefined) return direct;

  const dataLevel = c.data;
  if (dataLevel && typeof dataLevel === "object" && !Array.isArray(dataLevel)) {
    const d = dataLevel as Record<string, unknown>;
    const dataDirect = asWorkbookNamedObjectArray(d.namedObjects);
    if (dataDirect !== undefined) return dataDirect;

    const body = d.body;
    if (body && typeof body === "object" && !Array.isArray(body)) {
      const b = body as Record<string, unknown>;
      const bodyDirect = asWorkbookNamedObjectArray(b.namedObjects);
      if (bodyDirect !== undefined) return bodyDirect;

      const innerData = b.data;
      const innerArray = asWorkbookNamedObjectArray(innerData);
      if (innerArray !== undefined) return innerArray;
      if (innerData && typeof innerData === "object" && !Array.isArray(innerData)) {
        const innerNamed = asWorkbookNamedObjectArray(
          (innerData as Record<string, unknown>).namedObjects
        );
        if (innerNamed !== undefined) return innerNamed;
      }
    }
  }

  const rootDataArray = asWorkbookNamedObjectArray(c.data);
  if (rootDataArray !== undefined) return rootDataArray;

  return [];
}

/**
 * List named ranges and named formulas in the workbook via
 * `factset.Spreadsheet.GetWorkbookNamedObjects`.
 *
 * The response payload is **not captured** in `intent-details.md`;
 * we accept the natural `namedObjects` field name (matching the
 * intent name) and degrade to `[]` if the field is missing or
 * non-array. Rejects only on transport failure or when
 * `workbookName` is missing (pre-validation; see
 * `GetWorkbookNamedObjectsOptions`).
 */
export async function getWorkbookNamedObjectsViaIntents(
  options: GetWorkbookNamedObjectsOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<WorkbookNamedObject[]> {
  // AI-2617: `workbookName` is required by spec + by the
  // `oi-remote-iframe` Zod schema. Fail fast with a clear error rather
  // than emit a payload the host will reject.
  const workbookName = options?.workbookName?.trim();
  if (!workbookName) {
    throw new Error(
      "GetWorkbookNamedObjects: `workbookName` is required (per excel-tools.md). " +
        "Pass the active workbook name (e.g. from `safeGetActiveExcelContext()`) " +
        "or skip the call when no workbook is active."
    );
  }
  const requestContext: Record<string, unknown> = {
    type: "factset.spreadsheet.getWorkbookNamedObjects",
    workbookName,
    ...(options.skipHidden !== undefined
      ? { skipHidden: options.skipHidden }
      : {}),
    ...(options.properties !== undefined && options.properties.length > 0
      ? { options: { properties: options.properties } }
      : {}),
  };
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetWorkbookNamedObjects",
    requestContext,
    timeoutMs
  )) as GetWorkbookNamedObjectsResponse | undefined;
  return extractWorkbookNamedObjects(ctx);
}

// ============================================================================
// factset.Spreadsheet.AddNamedObject
// ============================================================================

/**
 * Per-call options for `AddNamedObject`.
 *
 * `name` is required. Supply either:
 *  - `refersTo`: A1-style reference/formula string (e.g. `"Sheet1!$B$2:$B$10"`), or
 *  - `address`:  Structured range address (dispatcher convenience).
 */
export interface AddNamedObjectOptions {
  name: string;
  refersTo?: string;
  address?: RangeAddress;
  nameType?: "Range" | "Formula" | (string & {});
  workbookName?: string;
  scope?: number;
  isVisible?: boolean;
  // Back-compat alias for callers that still use `visible`.
  visible?: boolean;
  comment?: string;
}

interface AddNamedObjectResponse {
  type?: string;
  success?: unknown;
  message?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Create a workbook named object (named range / formula) via
 * `factset.Spreadsheet.AddNamedObject`.
 *
 * Captured wire payload (2026-05-06):
 *
 *   {
 *     type: "factset.spreadsheet.addNamedObject",
 *     name: "MyRange",
 *     nameType: "Range",
 *     refersTo: "A1:B2",
 *     options: { isVisible: true, scope: -1, workbookName: "Book1" }
 *   }
 *
 * Response:
 *   { type: "factset.spreadsheet.addNamedObjectResult", success: true }
 *
 * The intent routes through `appId: "OI Remote Iframe"` (same relay as
 * the sibling `factset.*` template/chart family).
 */
const NAMED_OBJECTS_APP_ID = { appId: "OI Remote Iframe" };

function rangeAddressToNamedObjectRefersTo(address: RangeAddress): string {
  const rawAddress = address.address?.trim();
  if (!rawAddress) return "";
  // Defensive: if caller already provided a sheet-qualified address,
  // keep it as-is.
  if (rawAddress.includes("!")) return rawAddress;
  const ws = address.worksheetName?.trim();
  if (!ws) return rawAddress;
  const escapedWs = ws.replace(/'/g, "''");
  const needsQuoting = /[^A-Za-z0-9_]/.test(ws);
  const sheetPrefix = needsQuoting ? `'${escapedWs}'` : escapedWs;
  return `${sheetPrefix}!${rawAddress}`;
}

export async function addNamedObjectViaIntents(
  options: AddNamedObjectOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const name = options.name?.trim();
  if (!name) {
    throw new Error("AddNamedObject: `name` is required");
  }
  const refersToFromString = options.refersTo?.trim();
  const refersToFromAddress = options.address
    ? rangeAddressToNamedObjectRefersTo(options.address)
    : "";
  const refersTo = refersToFromString || refersToFromAddress;
  if (!refersTo) {
    throw new Error(
      "AddNamedObject: provide either `refersTo` or `address`"
    );
  }
  const isVisible = options.isVisible ?? options.visible;
  const hasOptionsBag =
    isVisible !== undefined ||
    options.scope !== undefined ||
    options.workbookName !== undefined;
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.AddNamedObject",
    {
      type: "factset.spreadsheet.addNamedObject",
      name,
      nameType: options.nameType ?? "Range",
      refersTo,
      ...(options.comment !== undefined ? { comment: options.comment } : {}),
      ...(hasOptionsBag
        ? {
            options: {
              ...(isVisible !== undefined ? { isVisible } : {}),
              ...(options.scope !== undefined ? { scope: options.scope } : {}),
              ...(options.workbookName !== undefined
                ? { workbookName: options.workbookName }
                : {}),
            },
          }
        : {}),
    },
    timeoutMs,
    NAMED_OBJECTS_APP_ID
  )) as AddNamedObjectResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `AddNamedObject: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.RemoveNamedObject
// ============================================================================

/**
 * Per-call options for `RemoveNamedObject`.
 */
export interface RemoveNamedObjectOptions {
  name: string;
  workbookName?: string;
  scope?: number;
}

interface RemoveNamedObjectResponse {
  type?: string;
  success?: unknown;
  message?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Remove a workbook named object (named range / formula) via
 * `factset.Spreadsheet.RemoveNamedObject`.
 *
 * Captured wire payload (2026-05-06):
 *
 *   {
 *     type: "factset.spreadsheet.removeNamedObject",
 *     name: "MyRange",
 *     options: { scope: -1, workbookName: "Book1" }
 *   }
 *
 * Response:
 *   { type: "factset.spreadsheet.removeNamedObjectResult", success: true }
 *
 * Routes through `appId: "OI Remote Iframe"`.
 */
export async function removeNamedObjectViaIntents(
  options: RemoveNamedObjectOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const name = options.name?.trim();
  if (!name) {
    throw new Error("RemoveNamedObject: `name` is required");
  }
  const hasOptionsBag =
    options.scope !== undefined || options.workbookName !== undefined;
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.RemoveNamedObject",
    {
      type: "factset.spreadsheet.removeNamedObject",
      name,
      ...(hasOptionsBag
        ? {
            options: {
              ...(options.scope !== undefined ? { scope: options.scope } : {}),
              ...(options.workbookName !== undefined
                ? { workbookName: options.workbookName }
                : {}),
            },
          }
        : {}),
    },
    timeoutMs,
    NAMED_OBJECTS_APP_ID
  )) as RemoveNamedObjectResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `RemoveNamedObject: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet.Search
// ============================================================================

/**
 * Per-call options for `Search`.
 */
export interface SpreadsheetSearchOptions {
  query: string;
  workbookName?: string;
  worksheetName?: string;
  matchCase?: boolean;
  matchWholeCell?: boolean;
  searchIn?: string;
  maxResults?: number;
}

/**
 * One search hit returned by `searchViaIntents`.
 */
export type SpreadsheetSearchMatch = Record<string, unknown>;

/**
 * Result of `searchViaIntents`.
 *
 * `matches` is best-effort normalized from whichever array field the host
 * returned. `raw` carries the full unwrapped context for diagnostics.
 */
export interface SpreadsheetSearchResult {
  matches: SpreadsheetSearchMatch[];
  raw: Record<string, unknown>;
}

interface SearchResponse {
  type?: string;
  success?: unknown;
  matches?: unknown;
  results?: unknown;
  data?: unknown;
  error?: unknown;
  reason?: unknown;
  [key: string]: unknown;
}

function asObjectArray(value: unknown): SpreadsheetSearchMatch[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out = value.filter(
    (entry): entry is SpreadsheetSearchMatch =>
      !!entry && typeof entry === "object" && !Array.isArray(entry)
  );
  return out.length > 0 ? out : [];
}

function findFirstObjectArray(
  value: unknown,
  maxDepth: number = 6
): SpreadsheetSearchMatch[] | undefined {
  if (maxDepth < 0) return undefined;
  const direct = asObjectArray(value);
  if (direct !== undefined) return direct;
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  for (const child of Object.values(value as Record<string, unknown>)) {
    const hit = findFirstObjectArray(child, maxDepth - 1);
    if (hit !== undefined) return hit;
  }
  return undefined;
}

function extractSearchMatches(ctx: unknown): SpreadsheetSearchMatch[] {
  if (!ctx || typeof ctx !== "object") return [];
  const c = ctx as Record<string, unknown>;

  const directMatches = asObjectArray(c.matches);
  if (directMatches !== undefined) return directMatches;

  const directResults = asObjectArray(c.results);
  if (directResults !== undefined) return directResults;

  const directData = asObjectArray(c.data);
  if (directData !== undefined) return directData;

  const dataLevel = c["data"];
  if (dataLevel && typeof dataLevel === "object" && !Array.isArray(dataLevel)) {
    const d = dataLevel as Record<string, unknown>;
    const m = asObjectArray(d.matches);
    if (m !== undefined) return m;
    const r = asObjectArray(d.results);
    if (r !== undefined) return r;
    const body = d.body;
    if (body && typeof body === "object" && !Array.isArray(body)) {
      const innerData = (body as Record<string, unknown>)["data"];
      const bodyDataMatches = asObjectArray(innerData);
      if (bodyDataMatches !== undefined) return bodyDataMatches;
      if (
        innerData &&
        typeof innerData === "object" &&
        !Array.isArray(innerData)
      ) {
        const m2 = asObjectArray((innerData as Record<string, unknown>).matches);
        if (m2 !== undefined) return m2;
        const r2 = asObjectArray((innerData as Record<string, unknown>).results);
        if (r2 !== undefined) return r2;
      }
    }
  }

  return findFirstObjectArray(c) ?? [];
}

/**
 * Search workbook content via `factset.Spreadsheet.Search`.
 *
 * The host response envelope is not yet captured; this wrapper returns
 * best-effort normalized `matches[]` plus the full raw context object.
 */
export async function searchViaIntents(
  options: SpreadsheetSearchOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<SpreadsheetSearchResult> {
  const query = options.query?.trim();
  if (!query) {
    throw new Error("Search: `query` is required");
  }
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.Search",
    {
      type: "factset.spreadsheet.search",
      // Host validation expects `searchTerm` (captured 2026-05-06 from
      // runtime error path `["searchTerm"]`). Keep `query` for
      // compatibility with older hosts that may still read it.
      searchTerm: query,
      query,
      ...(options.workbookName !== undefined
        ? { workbookName: options.workbookName }
        : {}),
      ...(options.worksheetName !== undefined
        ? { worksheetName: options.worksheetName }
        : {}),
      ...(options.matchCase !== undefined ? { matchCase: options.matchCase } : {}),
      ...(options.matchWholeCell !== undefined
        ? { matchWholeCell: options.matchWholeCell }
        : {}),
      ...(options.searchIn !== undefined ? { searchIn: options.searchIn } : {}),
      ...(options.maxResults !== undefined ? { maxResults: options.maxResults } : {}),
    },
    timeoutMs
  )) as SearchResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(`Search: host reported success=false${formatHostFailureSuffix(ctx)}`);
  }
  return {
    matches: extractSearchMatches(ctx),
    raw:
      ctx && typeof ctx === "object" && !Array.isArray(ctx)
        ? (ctx as Record<string, unknown>)
        : {},
  };
}

// ============================================================================
// factset.Spreadsheet.BeginUndo / factset.Spreadsheet.EndUndo
// ============================================================================

/**
 * Per-call options for `BeginUndo`. Both fields are *spec-optional* but
 * the production `oi-remote-iframe` Zod schema rejects an omitted
 * `captureRange` with `{ code: "invalid_union", path: ["captureRange"] }`
 * (AI-2617). Until FactSet relaxes the schema or fixes the catalog,
 * callers MUST emit a `captureRange` — `applyWorkbookModificationsViaIntents`
 * computes one from the incoming `modifications` payload via
 * `computeUndoCaptureRange`, with a degenerate `Sheet!A1` sentinel
 * fallback for pure structural / shape-only edits.
 *
 * Filed upstream as
 * `#open-beginundo-capturerange-schema-mismatch` in
 * `f2_docs/excel/finster-docs/missing-intents-v0.md`. The wrapper keeps
 * `captureRange?: RangeAddress` typed-optional for forward-compat with
 * the future schema fix.
 *
 * `displayName` is the label that appears in Excel's undo history
 * (`Edit → Undo {displayName}`). Default in
 * `applyWorkbookModificationsViaIntents` is `"FactSet AI apply"` so a
 * planner-driven multi-intent batch shows up as a single named entry
 * the user can roll back atomically.
 */
export interface BeginUndoOptions {
  captureRange?: RangeAddress;
  displayName?: string;
}

interface BeginUndoResponse {
  type?: string;
  success?: unknown;
  message?: unknown;
  error?: unknown;
  reason?: unknown;
}

interface EndUndoResponse {
  type?: string;
  success?: unknown;
  message?: unknown;
  error?: unknown;
  reason?: unknown;
}

/**
 * Begin an undo entry via `factset.Spreadsheet.BeginUndo`.
 *
 * Groups subsequent host mutations into a single user-facing undo entry
 * — paired with `endUndoViaIntents` it implements the cross-call undo
 * bracket asked for by `missing-intents-v0.md` `#open-cross-call-undo`.
 *
 * **Schema gotcha (AI-2617): callers MUST emit `options.captureRange`.**
 * The `oi-remote-iframe` Zod schema rejects an omitted `captureRange`
 * with `invalid_union` even though `excel-tools.md` marks it optional —
 * see `BeginUndoOptions` JSDoc for the upstream filing
 * (`#open-beginundo-capturerange-schema-mismatch`). The wrapper keeps the
 * field typed-optional for forward-compat, but emitting `undefined`
 * makes the host reject and breaks the undo bracket for the entire
 * batch.
 *
 * Resolves on host success (typical body: `{ message: "Undo entry begun
 * successfully" }`, no `success` field). Rejects on transport failure,
 * timeout, or an explicit `success === false` envelope; the latter is
 * accepted defensively for forward-compat. Not idempotent — each
 * `BeginUndo` should be matched by exactly one `EndUndo` call. Callers
 * wrapping `BeginUndo` in their own try/catch MUST swallow the failure
 * and continue; an undo bracket failure is not a user-facing error.
 */
export async function beginUndoViaIntents(
  options?: BeginUndoOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.BeginUndo",
    {
      type: "factset.spreadsheet.beginUndo",
      ...(options?.captureRange !== undefined
        ? { captureRange: options.captureRange }
        : {}),
      ...(options?.displayName !== undefined
        ? { displayName: options.displayName }
        : {}),
    },
    timeoutMs
  )) as BeginUndoResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `BeginUndo: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

/**
 * End the current undo entry via `factset.Spreadsheet.EndUndo`.
 *
 * Closes the bracket opened by `beginUndoViaIntents`. Sends
 * `fdc3.nothing` (the catalog uses `EndUndo` with no input fields).
 * Typical success body is `{ message: "Undo entry ended successfully" }`,
 * no `success` field. Same failure-handling contract as `BeginUndo`:
 * callers wrapping the call in their own try/catch MUST swallow the
 * failure and continue.
 */
export async function endUndoViaIntents(
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.EndUndo",
    { type: "fdc3.nothing" },
    timeoutMs
  )) as EndUndoResponse | undefined;
  if (ctx?.success === false) {
    throw new Error(
      `EndUndo: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}

// ============================================================================
// factset.Spreadsheet note / threaded-comment intents
// ============================================================================
//
// Excel exposes two distinct annotation families:
//   - Comment: threaded purple comments, with author metadata.
//   - Note: legacy yellow sticky notes, represented as plain text.
//
// The production catalog intentionally reuses the singular response context
// types for the GetAll variants:
//   GetComment / GetAllComments -> factset.spreadsheet.getCommentResult
//   GetNote    / GetAllNotes    -> factset.spreadsheet.getNoteResult
// The payload shapes still differ, so each wrapper parses its own operation
// contract instead of discriminating on `type`.

const NOTE_COMMENT_APP_ID = { appId: "OI Remote Iframe" };

/** Request for adding or replacing a threaded Excel comment. */
export interface SetCommentOptions {
  referenceAddress: RangeAddress;
  text: string;
  replaceExisting?: boolean;
}

/** Request for adding or replacing an Excel note. */
export interface SetNoteOptions {
  referenceAddress: RangeAddress;
  text: string;
  replaceExisting?: boolean;
}

/** Optional workbook/sheet filters shared by both GetAll variants. */
export interface GetAllAnnotationsOptions {
  workbookName?: string;
  worksheetNames?: string[];
}

/** One threaded comment returned by GetComment. */
export interface SpreadsheetCellComment {
  text: string;
  authorEmail?: string;
  /** Some host builds use `author` instead of `authorEmail`. */
  author?: string;
}

/** One threaded comment returned by GetAllComments. */
export interface SpreadsheetCommentEntry extends SpreadsheetCellComment {
  address: string;
}

/** One note returned by GetAllNotes. */
export interface SpreadsheetNoteEntry {
  address: string;
  text: string;
}

interface AnnotationResponse {
  type?: string;
  success?: unknown;
  content?: unknown;
  error?: unknown;
  message?: unknown;
  reason?: unknown;
}

function assertSingleCellReference(
  referenceAddress: RangeAddress,
  operationName: string
): void {
  if (!referenceAddress || typeof referenceAddress !== "object") {
    throw new Error(`${operationName}: \`referenceAddress\` is required`);
  }
  const rawAddress = referenceAddress.address?.trim();
  if (!rawAddress) {
    throw new Error(`${operationName}: \`referenceAddress.address\` is required`);
  }
  if (!isSingleCellAddress(rawAddress)) {
    throw new Error(
      `${operationName}: \`referenceAddress.address\` must target one cell (for example, A1)`
    );
  }
  if (
    referenceAddress.referenceType !== "absolute" &&
    referenceAddress.referenceType !== "relative"
  ) {
    throw new Error(
      `${operationName}: \`referenceAddress.referenceType\` must be "absolute" or "relative"`
    );
  }
}

function assertReferenceAddresses(
  referenceAddresses: RangeAddress[],
  operationName: string
): void {
  if (!Array.isArray(referenceAddresses) || referenceAddresses.length === 0) {
    throw new Error(
      `${operationName}: \`referenceAddresses[]\` must contain at least one cell`
    );
  }
  referenceAddresses.forEach((referenceAddress, index) =>
    assertSingleCellReference(referenceAddress, `${operationName}[${index}]`)
  );
}

function assertAnnotationText(text: string, operationName: string): void {
  if (typeof text !== "string" || text.trim().length === 0) {
    throw new Error(`${operationName}: \`text\` must be a non-empty string`);
  }
}

function throwOnAnnotationFailure(
  operationName: string,
  ctx: AnnotationResponse | undefined
): void {
  if (ctx?.success === false) {
    throw new Error(
      `${operationName}: host reported success=false${formatHostFailureSuffix(ctx)}`
    );
  }
  if (ctx?.success !== true) {
    throw new Error(
      `${operationName}: response did not report success=true${formatHostFailureSuffix(ctx)}`
    );
  }
}

function annotationListContext(
  type: "factset.spreadsheet.getAllComments" | "factset.spreadsheet.getAllNotes",
  options?: GetAllAnnotationsOptions
): Record<string, unknown> {
  const workbookName = options?.workbookName?.trim();
  if (options?.workbookName !== undefined && !workbookName) {
    throw new Error(`${type}: \`workbookName\` must be non-empty when supplied`);
  }
  let worksheetNames: string[] | undefined;
  if (options?.worksheetNames !== undefined) {
    if (!Array.isArray(options.worksheetNames)) {
      throw new Error(`${type}: \`worksheetNames\` must be a string[]`);
    }
    if (options.worksheetNames.some((name) => typeof name !== "string")) {
      throw new Error(`${type}: every \`worksheetNames\` entry must be a string`);
    }
    worksheetNames = options.worksheetNames.map((name) => name.trim());
    if (worksheetNames.some((name) => name.length === 0)) {
      throw new Error(`${type}: every \`worksheetNames\` entry must be non-empty`);
    }
    if (worksheetNames.length === 0) worksheetNames = undefined;
  }
  if (!workbookName && !worksheetNames) {
    return { type: "fdc3.nothing" };
  }
  return {
    type,
    ...(workbookName ? { workbookName } : {}),
    ...(worksheetNames ? { worksheetNames } : {}),
  };
}

function asCommentArray(
  value: unknown,
  operationName: string,
  requireAddress: boolean
): Array<SpreadsheetCellComment | SpreadsheetCommentEntry> {
  if (!Array.isArray(value)) {
    throw new Error(`${operationName}: response did not include an array \`content\``);
  }
  return value.map((entry, index) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      throw new Error(`${operationName}: content[${index}] was not an object`);
    }
    const raw = entry as Record<string, unknown>;
    if (typeof raw.text !== "string") {
      throw new Error(`${operationName}: content[${index}].text was not a string`);
    }
    if (requireAddress && typeof raw.address !== "string") {
      throw new Error(`${operationName}: content[${index}].address was not a string`);
    }
    return {
      ...(requireAddress ? { address: raw.address as string } : {}),
      text: raw.text,
      ...(typeof raw.authorEmail === "string"
        ? { authorEmail: raw.authorEmail }
        : {}),
      ...(typeof raw.author === "string" ? { author: raw.author } : {}),
    };
  });
}

function asSingleNoteText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    if (value.length === 1) return asSingleNoteText(value[0]);
    throw new Error("GetNote: response included more than one Note");
  }
  if (typeof value === "object") {
    const text = (value as Record<string, unknown>).text;
    if (typeof text === "string") return text;
  }
  throw new Error(
    `GetNote: response included unsupported content (${Array.isArray(value) ? "array" : typeof value})`
  );
}

/**
 * Get the threaded comments attached to one cell. An unannotated cell returns
 * an empty array.
 */
export async function getCommentViaIntents(
  referenceAddress: RangeAddress,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  signal?: AbortSignal
): Promise<SpreadsheetCellComment[]> {
  assertSingleCellReference(referenceAddress, "GetComment");
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetComment",
    {
      type: "factset.spreadsheet.getComment",
      referenceAddress,
    },
    timeoutMs,
    NOTE_COMMENT_APP_ID,
    signal
  )) as AnnotationResponse | undefined;
  throwOnAnnotationFailure("GetComment", ctx);
  return asCommentArray(ctx?.content, "GetComment", false) as SpreadsheetCellComment[];
}

/** Add or replace a threaded comment on one cell. */
export async function setCommentViaIntents(
  options: SetCommentOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  assertSingleCellReference(options?.referenceAddress, "SetComment");
  assertAnnotationText(options?.text, "SetComment");
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.SetComment",
    {
      type: "factset.spreadsheet.setComment",
      referenceAddress: options.referenceAddress,
      text: options.text,
      ...(options.replaceExisting !== undefined
        ? { replaceExisting: options.replaceExisting }
        : {}),
    },
    timeoutMs,
    NOTE_COMMENT_APP_ID
  )) as AnnotationResponse | undefined;
  throwOnAnnotationFailure("SetComment", ctx);
}

/** Delete threaded comments from one or more cells. Missing comments succeed. */
export async function deleteCommentViaIntents(
  referenceAddresses: RangeAddress[],
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  assertReferenceAddresses(referenceAddresses, "DeleteComment");
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.DeleteComment",
    {
      type: "factset.spreadsheet.deleteComment",
      referenceAddresses,
    },
    timeoutMs,
    NOTE_COMMENT_APP_ID
  )) as AnnotationResponse | undefined;
  throwOnAnnotationFailure("DeleteComment", ctx);
}

/** List threaded comments for the requested workbook/sheets. */
export async function getAllCommentsViaIntents(
  options?: GetAllAnnotationsOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  signal?: AbortSignal
): Promise<SpreadsheetCommentEntry[]> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetAllComments",
    annotationListContext("factset.spreadsheet.getAllComments", options),
    timeoutMs,
    NOTE_COMMENT_APP_ID,
    signal
  )) as AnnotationResponse | undefined;
  throwOnAnnotationFailure("GetAllComments", ctx);
  return asCommentArray(
    ctx?.content,
    "GetAllComments",
    true
  ) as SpreadsheetCommentEntry[];
}

/**
 * Get the yellow sticky-note text attached to one cell.
 *
 * Host builds vary between plain-text and annotation-array response shapes.
 * Normalize valid empty and single-Note variants while continuing to reject
 * ambiguous or malformed payloads.
 */
export async function getNoteViaIntents(
  referenceAddress: RangeAddress,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  signal?: AbortSignal
): Promise<string> {
  assertSingleCellReference(referenceAddress, "GetNote");
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetNote",
    {
      type: "factset.spreadsheet.getNote",
      referenceAddress,
    },
    timeoutMs,
    NOTE_COMMENT_APP_ID,
    signal
  )) as AnnotationResponse | undefined;
  throwOnAnnotationFailure("GetNote", ctx);
  return asSingleNoteText(ctx?.content);
}

/** Add or replace a yellow sticky note on one cell. */
export async function setNoteViaIntents(
  options: SetNoteOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  assertSingleCellReference(options?.referenceAddress, "SetNote");
  assertAnnotationText(options?.text, "SetNote");
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.SetNote",
    {
      type: "factset.spreadsheet.setNote",
      referenceAddress: options.referenceAddress,
      text: options.text,
      ...(options.replaceExisting !== undefined
        ? { replaceExisting: options.replaceExisting }
        : {}),
    },
    timeoutMs,
    NOTE_COMMENT_APP_ID
  )) as AnnotationResponse | undefined;
  throwOnAnnotationFailure("SetNote", ctx);
}

/** Delete notes from one or more cells. Missing notes succeed. */
export async function deleteNoteViaIntents(
  referenceAddresses: RangeAddress[],
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  assertReferenceAddresses(referenceAddresses, "DeleteNote");
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.DeleteNote",
    {
      type: "factset.spreadsheet.deleteNote",
      referenceAddresses,
    },
    timeoutMs,
    NOTE_COMMENT_APP_ID
  )) as AnnotationResponse | undefined;
  throwOnAnnotationFailure("DeleteNote", ctx);
}

/** List yellow sticky notes for the requested workbook/sheets. */
export async function getAllNotesViaIntents(
  options?: GetAllAnnotationsOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  signal?: AbortSignal
): Promise<SpreadsheetNoteEntry[]> {
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.GetAllNotes",
    annotationListContext("factset.spreadsheet.getAllNotes", options),
    timeoutMs,
    NOTE_COMMENT_APP_ID,
    signal
  )) as AnnotationResponse | undefined;
  throwOnAnnotationFailure("GetAllNotes", ctx);
  if (!Array.isArray(ctx?.content)) {
    throw new Error("GetAllNotes: response did not include an array `content`");
  }
  return ctx.content.map((entry, index) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      throw new Error(`GetAllNotes: content[${index}] was not an object`);
    }
    const raw = entry as Record<string, unknown>;
    if (typeof raw.address !== "string" || typeof raw.text !== "string") {
      throw new Error(
        `GetAllNotes: content[${index}] must contain string address and text fields`
      );
    }
    return { address: raw.address, text: raw.text };
  });
}

// ============================================================================
// factset.Spreadsheet.InsertTtfApiResponse (Text-to-Formula)
// ============================================================================
//
// This wrapper is the wire-side surface for the TTF (Text-to-Formula) intent.
// The host accepts a pre-built `apiResponse` blob describing one or more
// FDS/FQL formulas (formula text + per-input cell anchors + ticker /
// validation metadata) and writes them into the workbook in a single host
// call.
//
// TTF is wired end-to-end inside Finster — the older "STUB — pending
// TTF API client" comment was stale and has been replaced. The full
// wiring is:
//
//   - HTTP client      — `services/factset/ttfApi.ts` (`POST /api/factset/
//                        ttf/generate`, `GET /api/factset/ttf/result`).
//   - OAuth gate       — `excel/excelService.ts::computeTtfRunContext` —
//                        TTF is opt-in per session and disabled until the
//                        user's auth context returns a valid TTF scope; the
//                        gate decides whether to attempt the dispatch path
//                        for each cell.
//   - Cell dispatch    — `services/factset/ttfCellDispatch.ts::
//                        tryDispatchCellViaTtf` — per-cell entry point used
//                        by the plan executor. On success we call
//                        `insertTtfApiResponseViaIntents` (this function);
//                        on failure we fall back to the LLM-emitted
//                        formula and surface the failure category via
//                        `console.warn` + the optional activity-log
//                        hook.
//   - Planner exposure — `excel/excelFdsPrompts.ts::TTF_INSTRUCTION_PROMPT`,
//                        merged into both the execution and planning
//                        prompts in `excelAIService.ts` so the model knows
//                        TTF can produce parameterised formulas.
//
// Compared with the old behaviour (plain `=FDS(...)` strings through
// `InsertContentsToRange`), TTF wires the inputs to anchor cells so the
// resulting formula honours the user's existing date/ticker inputs without
// the LLM having to re-discover them. Failures stay non-fatal: the dispatch
// path catches `oauth`, `rate-limit`, `parse`, and `host` errors and falls
// back to the LLM-emitted formula so the step still applies.
//
// The matching `WorkbookOperation.action = "insertTtfApiResponse"` slot is
// now applied by `applyWorkbookOperationViaIntents`; there is no warn-skip
// path anymore.

/**
 * One per-input anchor in a TTF result. `cell` is an A1 address on the
 * active sheet; `value` is the literal value the host should treat as
 * the input (typically a date or numeric string).
 */
export interface TtfApiResponseFormulaInput {
  cell: string;
  value: string;
}

/**
 * Map of input name (e.g. `"StartDate"`) to its anchor cell + value.
 * Mirrors the captured shape under `fql_conversion.inputs`.
 */
export type TtfApiResponseFormulaInputs = Record<
  string,
  TtfApiResponseFormulaInput
>;

/**
 * The FQL form of the formula plus the named-input table. The host uses
 * this to wire each `{InputName}` placeholder to the cell anchors in
 * `inputs`.
 */
export interface TtfApiResponseFqlConversion {
  fql_with_inputs: string;
  inputs?: TtfApiResponseFormulaInputs;
}

/**
 * Open-ended metadata block. Today the only observed field is
 * `short_description`; left as an index signature so the catalog can
 * grow without a Finster-side change.
 */
export interface TtfApiResponseFormulaDetails {
  short_description?: string;
  [key: string]: unknown;
}

/**
 * TTF-side validation outcome. `status === false` means the host SHOULD
 * still accept the payload but the formula failed an upstream check;
 * Finster does not gate on this today (we'll surface it only when we
 * wire the producer).
 */
export interface TtfApiResponseValidation {
  status: boolean;
  [key: string]: unknown;
}

/**
 * One formula result inside a TTF API response. `formula` is the raw
 * `=FDS(...)` (or `=FDSC` / `=FDSZ`) string the host will write; the
 * remaining fields are metadata the host uses for routing and the user
 * may surface for traceability.
 */
export interface TtfApiResponseResult {
  formula: string;
  ticker?: string[];
  caccess_check?: string;
  validation?: TtfApiResponseValidation;
  fql_conversion?: TtfApiResponseFqlConversion;
  formula_details?: TtfApiResponseFormulaDetails;
  formula_conversation?: TtfApiResponseFormulaConversation;
}

/** Commentary block returned by the TTF API explaining the formula's configuration. */
export interface TtfApiResponseFormulaConversation {
  commentary?: string;
  id?: string;
}

/**
 * The TTF API response envelope. `results` is plural — a single intent
 * call can place multiple formulas at once.
 */
export interface TtfApiResponse {
  results: TtfApiResponseResult[];
}

/**
 * Per-call options for `InsertTtfApiResponse`.
 *
 *  - `refresh`         — refresh the inserted formulas after writing.
 *                        Defaults to host-side default if omitted.
 *  - `insertWithDates` — when true, insert anchor cells for any date
 *                        inputs as part of the same operation.
 */
export interface InsertTtfApiResponseOptions {
  refresh?: boolean;
  insertWithDates?: boolean;
}

/**
 * Insert TTF formulas into the active spreadsheet via the
 * `factset.Spreadsheet.InsertTtfApiResponse` intent.
 *
 * Producers in `ttfCellDispatch.ts::tryDispatchCellViaTtf` build the
 * `TtfApiResponse` from the upstream client in `ttfApi.ts`; this
 * function just hands the typed blob to the host.
 *
 * Typical success body is `{ message: "TTF formulas inserted
 * successfully" }`. We don't read the message back, matching the
 * `InsertContentsToRange` precedent. Rejects on transport failure
 * or timeout; the caller in `tryDispatchCellViaTtf` catches and
 * falls back to the LLM-emitted formula so the step still applies
 * (and the failure category is surfaced via `console.warn` + the
 * optional activity-log hook).
 */
export async function insertTtfApiResponseViaIntents(
  apiResponse: TtfApiResponse,
  options?: InsertTtfApiResponseOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<void> {
  await raiseIntent(
    "factset.Spreadsheet.InsertTtfApiResponse",
    {
      type: "factset.spreadsheet.insertTtfApiResponse",
      apiResponse,
      options: options ?? {},
    },
    timeoutMs
  );
}

// ============================================================================
// factset.Spreadsheet.OpenWorkbook / SaveWorkbook / SaveWorkbookAs
// ============================================================================

/**
 * Supported host policies for updating external links while opening a workbook.
 */
export type OpenWorkbookUpdateLinks =
  | "never"
  | "always"
  | "prompt"
  | "externalOnly"
  | "remoteOnly";

/**
 * Optional host behavior for an OpenWorkbook operation.
 */
export interface OpenWorkbookOptions {
  displayAlerts?: boolean;
  updateLinks?: OpenWorkbookUpdateLinks;
}

const OPEN_WORKBOOK_UPDATE_LINKS_INT: Record<
  Exclude<OpenWorkbookUpdateLinks, "prompt">,
  number
> = {
  never: 0,
  externalOnly: 1,
  remoteOnly: 2,
  always: 3,
};

/**
 * Result returned by the OpenWorkbook bridge operation.
 */
export interface OpenWorkbookResult {
  success: boolean;
  error?: string;
}

interface OpenWorkbookResponse {
  success?: unknown;
  error?: unknown;
}

/**
 * Open a saved workbook through the FactSet Spreadsheet host.
 *
 * @param filePath - Non-empty absolute path to a workbook the host can read.
 * @param options - Optional alert and external-link update behavior.
 * @param timeoutMs - Maximum transport wait in milliseconds.
 * @returns The host success state and an optional diagnostic.
 * @throws When `filePath` is empty or whitespace-only.
 */
export async function openWorkbookViaIntents(
  filePath: string,
  options?: OpenWorkbookOptions,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<OpenWorkbookResult> {
  if (typeof filePath !== "string" || filePath.trim().length === 0) {
    throw new Error("OpenWorkbook: `filePath` must be a non-empty string.");
  }
  const requestContext = {
    type: "factset.spreadsheet.openWorkbook",
    filePath,
    ...(options !== undefined
      ? {
          options: {
            ...(options.displayAlerts !== undefined
              ? { displayAlerts: options.displayAlerts }
              : {}),
            ...(options.updateLinks !== undefined &&
            options.updateLinks !== "prompt"
              ? {
                  updateLinks:
                    OPEN_WORKBOOK_UPDATE_LINKS_INT[options.updateLinks],
                }
              : {}),
          },
        }
      : {}),
  };
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.OpenWorkbook",
    requestContext,
    timeoutMs
  )) as OpenWorkbookResponse | undefined;
  assertLifecycleSuccessField("OpenWorkbook", ctx);
  const success = ctx.success === true;
  const error =
    typeof ctx?.error === "string" && ctx.error.length > 0
      ? ctx.error
      : undefined;
  return error ? { success, error } : { success };
}

/**
 * Result returned by SaveWorkbook and SaveWorkbookAs bridge operations.
 */
export interface SaveWorkbookResult {
  success: boolean;
  fullName?: string;
  error?: string;
}

interface SaveWorkbookResponse {
  success?: unknown;
  fullName?: unknown;
  error?: unknown;
}

/**
 * Save the active workbook or a specifically named open workbook.
 *
 * @param targetWorkbookName - Exact host workbook name, or `undefined` for active.
 * @param timeoutMs - Maximum transport wait in milliseconds.
 * @returns The host success state, authoritative full path, and optional error.
 */
export async function saveWorkbookViaIntents(
  targetWorkbookName?: string,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<SaveWorkbookResult> {
  const requestContext =
    targetWorkbookName !== undefined
      ? {
          type: "factset.spreadsheet.saveWorkbook",
          targetWorkbookName,
        }
      : { type: "fdc3.nothing" };
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.SaveWorkbook",
    requestContext,
    timeoutMs
  )) as SaveWorkbookResponse | undefined;
  return normalizeSaveWorkbookResponse(ctx, "SaveWorkbook");
}

/**
 * Save an active or named workbook to a new path.
 *
 * @param filename - Non-empty absolute destination path including filename.
 * @param targetWorkbookName - Exact source workbook name, or `undefined` for active.
 * @param timeoutMs - Maximum transport wait in milliseconds.
 * @returns The host success state, authoritative full path, and optional error.
 * @throws When `filename` is empty or whitespace-only.
 */
export async function saveWorkbookAsViaIntents(
  filename: string,
  targetWorkbookName?: string,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<SaveWorkbookResult> {
  if (typeof filename !== "string" || filename.trim().length === 0) {
    throw new Error("SaveWorkbookAs: `filename` must be a non-empty string.");
  }
  const requestContext = {
    type: "factset.spreadsheet.saveWorkbookAs",
    filename,
    ...(targetWorkbookName !== undefined ? { targetWorkbookName } : {}),
  };
  const ctx = (await raiseIntent(
    "factset.Spreadsheet.SaveWorkbookAs",
    requestContext,
    timeoutMs
  )) as SaveWorkbookResponse | undefined;
  return normalizeSaveWorkbookResponse(ctx, "SaveWorkbookAs");
}

/**
 * Normalize the shared host response shape for save operations.
 *
 * @param ctx - Raw host response.
 * @param operationName - Host operation used in malformed-response diagnostics.
 * @returns A stable save result with non-empty optional fields only.
 */
function normalizeSaveWorkbookResponse(
  ctx: SaveWorkbookResponse | undefined,
  operationName: "SaveWorkbook" | "SaveWorkbookAs"
): SaveWorkbookResult {
  assertLifecycleSuccessField(operationName, ctx);
  const success = ctx.success === true;
  const fullName =
    typeof ctx?.fullName === "string" && ctx.fullName.length > 0
      ? ctx.fullName
      : undefined;
  const error =
    typeof ctx?.error === "string" && ctx.error.length > 0
      ? ctx.error
      : undefined;
  return {
    success,
    ...(fullName ? { fullName } : {}),
    ...(error ? { error } : {}),
  };
}

/**
 * Require an explicit boolean acknowledgement from lifecycle bridge mutations.
 *
 * @param operationName - Host operation used in the diagnostic.
 * @param ctx - Raw response returned by the intent bridge.
 * @throws When the bridge omits a boolean `success` field.
 */
function assertLifecycleSuccessField<
  T extends { success?: unknown; error?: unknown },
>(
  operationName: string,
  ctx: T | undefined
): asserts ctx is T & { success: boolean } {
  if (typeof ctx?.success !== "boolean") {
    throw new Error(
      `${operationName}: response did not report success=true or success=false${formatHostFailureSuffix(ctx)}`
    );
  }
}
